import React, { useState, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import {
  Play,
  Square,
  RotateCcw,
  Plus,
  Trash2,
  BookOpen,
  Edit,
  Menu,
  X,
  FileMusic,
  Download,
  Share,
  Lock,
  Copy,
  Clipboard,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import "./styles.css";
import { Midi } from "@tonejs/midi";
import * as Tone from 'tone';
import { parseMidi } from "midi-file";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import { Filter } from "bad-words";
import flowFrameLogo from "./data/FlowFrame Logo.png";
import HamburgerActions from "./components/HamburgerActions";
import { parseMusicXML } from "./components/musicxml-import/musicxmlParser";
import { buildTempoState } from "./components/musicxml-import/buildTempoState";
import { uploadPdfForOmr, OmrUploadError } from "./components/musicxml-import/uploadPdfForOmr";
import ImportSheetMusicModal from "./components/musicxml-import/ImportSheetMusicModal";
import { pdfjs } from "react-pdf";
import { LoadPatternModal } from "./components/LoadPatternModal";
import AboutTab from "./components/AboutTab";
import TutorialOverlay from "./components/TutorialOverlay";
import { AutocompleteInput } from "./components/AutocompleteInput";
import { PracticeRoomTab } from "./components/PracticeRoomTab";
import { SignUpForm } from "./components/SignUpForm";
import BulkEditorModal from "./components/BulkEditorModal";
import LoginForm from "./components/LoginForm";
import UsernameSelectionModal from "./components/UsernameSelectionModal";
import ExportToLibraryModal from "./components/ExportToLibraryModal";
import ExportMp3Modal from "./components/ExportMp3Modal";
import { createMp3Recorder, downloadBlob, defaultMp3Filename } from "./utils/exportToMp3";
import { generateUsername } from "./utils/usernameGenerator";
import BeatHighlighter from "./components/BeatHighlighter";
import RehearsalAnnouncer from "./components/RehearsalAnnouncer";
import { warmUpSpeech } from "./utils/speech";
import { setupPatternLoader } from "./utils/patternLoaderSetup";
import { FreeFlowInvitationsBadge } from './components/FreeFlow';
import FlowTrain from './components/FlowTrain/FlowTrain';
// import { trackViewContent } from "./utils/analytics";
import { 
  trackEvent, 
  trackUserLogin, 
  trackUserLogout, 
  getPatternFeatures,
  getPatternComplexity,
  getTimeSignaturesUsed,
  initializeSessionTracking,
  trackSessionHeartbeat,
  calculateSessionDuration
} from "./utils/analytics";

import { getSharedPattern } from './utils/sharingDb';
import TimeSignatureColorsModal from "./components/TimeSignatureColorsModal";
import { SimpleMetronome } from "./components/SimpleMetronome";
import { RecordingService } from './utils/recordingService';
import { listResumable, deleteUpload } from './utils/uploadPersistence';
import { resumeStreamingUpload } from './utils/streamingUploader';
import { canStreamWebmVideo } from './utils/mediaCapabilities';
import { getFeatureFlags } from './utils/featureFlags';
import UploadQueuePill from './components/UploadQueuePill';
import { getSampler } from './utils/samplerManager';
import LandingPage from './components/LandingPage';
import { useAutosave } from './hooks/useAutosave';
import { SavePatternModal } from "./components/SavePatternModal";
import ToastNotification from './components/ToastNotification';
import { handleManualSave, handleSaveAs } from './utils/savePattern';
import FullPatternModal from './components/FullPatternModal';

import {
  parseMeasureSignatureLine,
  parseMeasureRanges,
  parseAccentString,
  parseBeatsInput,
  parseBarString,
} from "./utils/parsers";

import {
  INSTRUMENTATIONS,
  GENRES,
  POPULAR_COMPOSERS,
  COMMON_TAGS,
  TICKS_PER_QUARTER,
  DEFAULT_TEMPO,
  INITIAL_TEMPO,
  INITIAL_MUTE_RANDOM,
  INITIAL_MUTE_PERCENT,
  INITIAL_LOOP_MODE,
  DEFAULT_TEMPO_CHANGE,
  ALL_SUBDIVISIONS,
  CHORDS_ET,
  CHORDS_JI,
  SOUND_OPTIONS,
  NOTE_OPTIONS,
  OCTAVE_OPTIONS,
  NOTE_OPTIONS_12,
  QUARTER_SHARP_OPTIONS,
  DYNAMICS_MAP,
  NOTE_VALUE_OPTIONS,
  validDenominators,
  STRESS_PATTERNS,
  DEFAULT_CRESC_FACTOR,
  DEFAULT_DIM_FACTOR,
  SOUND_MAP,
  DRONE_MIN_FREQ,
  HARMONY_MIN_FREQ,
  MAX_FREQ,
  JI_SEMITONE_RATIOS,
  TEMPERAMENT_SYSTEMS,
  TIME_SIGNATURE_COLORS,
  DEFAULT_TIME_SIGNATURE_COLORS,
  DRONE_SOUND_OPTIONS,
} from "./data/musicData";

import {
  patternUsesLockedFeatures,
  determinePatternTier,
  TierBadge,
} from "./components/PatternTierUtils";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  useAuthState,
  linkWithCredential,
} from "firebase/auth";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import {
  doc,
  addDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  collection,
  onSnapshot,
  deleteDoc,
  query,
  where,
  orderBy,
  getFirestore,
  count,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { app } from "./firebase";
import { initNotifications } from './utils/notificationService';

import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAInstallInstructions from './components/PWAInstallInstructions';
import { startPrefetch } from './utils/prefetchUserData';
import { invalidateAll as clearPrefetchCache } from './utils/prefetchCache';
import { clearUserProfileCache } from './utils/instructorDb';
import { storageManager } from './utils/storageManager';

const PracticeTrackerTab = lazy(() => import('./PracticeTrackerTab'));
const LibraryTab = lazy(() => import('./components/LibraryTab').then(module => ({ default: module.LibraryTab })));
const FreeFlow = lazy(() => import('./components/FreeFlow'));
const CommunityTab = lazy(() => import('./components/CommunityTab'));
const ScoresTab = lazy(() => import('./components/scores/ScoresTab'));
const ScoreViewer = lazy(() => import('./components/scores/ScoreViewer'));
const ScoreViewerMini = lazy(() => import('./components/scores/ScoreViewerMini'));
const Tuner = lazy(() => import('./components/tuner/Tuner'));
const OrgAdminDashboard = lazy(() => import('./components/OrgAdminDashboard'));
const JoinOrgPage = lazy(() => import('./components/JoinOrgPage'));
const BackingTracksTab = lazy(() => import('./components/BackingTracksTab'));

const filter = new Filter();

const userData = {
  subscriptionTier: "free",
  createdAt: Date.now(),
};

const googleProvider = new GoogleAuthProvider();

/**
 * Redirect the user to the Stripe-hosted pricing table
 * so they can pick either Basic or Pro.
 */
const handleUpgrade = (user) => {
  const pricingTableContainer = document.createElement("div");
  document.body.appendChild(pricingTableContainer);

  const pricingTable = document.createElement("stripe-pricing-table");
  pricingTable.setAttribute(
    "pricing-table-id",
    "prctbl_1RAxlNJxm9txLODovxvSWZBN"
  );
  pricingTable.setAttribute(
    "publishable-key",
    "pk_live_51R8gLXJxm9txLODoduD4BBoIKjKwpoXZiqOTejyNH98JHY1wGMVfiv3CKdAAhtIc1RCTBJIa4qCcLdN7xqke944600Pm9rI5Nz"
  );
  if (user?.uid) {
    pricingTable.setAttribute("client-reference-id", user.uid);
  }
  if (user?.email) {
    pricingTable.setAttribute("customer-email", user.email);
  }

  // Add custom styles to the pricing table element to ensure horizontal layout
  pricingTable.style.maxWidth = "100%";
  pricingTable.style.overflow = "auto";

  // Add custom CSS to force horizontal layout
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    stripe-pricing-table {
      --stripe-pricing-table-spacing-unit: 8px;
      --stripe-pricing-table-layout: columns !important;
      --stripe-pricing-table-group-padding: 0 !important;
      width: 100%;
    }
    stripe-pricing-table::part(prices) {
      display: flex !important;
      flex-direction: row !important;
      justify-content: center !important;
      gap: 20px !important;
    }
  `;
  document.head.appendChild(styleSheet);

  pricingTableContainer.appendChild(pricingTable);

  // Update container styling for better visibility and scrolling
  pricingTableContainer.style.position = "fixed";
  pricingTableContainer.style.top = "0";
  pricingTableContainer.style.left = "0";
  pricingTableContainer.style.width = "100%";
  pricingTableContainer.style.height = "100%";
  pricingTableContainer.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
  pricingTableContainer.style.zIndex = "1000";
  pricingTableContainer.style.display = "flex";
  pricingTableContainer.style.justifyContent = "center";
  pricingTableContainer.style.alignItems = "center";
  pricingTableContainer.style.padding = "40px"; // Increased padding
  pricingTableContainer.style.overflow = "auto"; // Enable scrolling if needed

  // Style the actual content container
  const contentWrapper = document.createElement("div");
  contentWrapper.style.backgroundColor = "white";
  contentWrapper.style.borderRadius = "8px";
  contentWrapper.style.padding = "20px";
  contentWrapper.style.maxHeight = "90vh";
  contentWrapper.style.overflowY = "auto";
  contentWrapper.style.width = "90%";
  contentWrapper.style.maxWidth = "1200px"; // Set a max-width for larger screens

  contentWrapper.appendChild(pricingTable);
  pricingTableContainer.appendChild(contentWrapper);

  // Update close button styling and position
  const closeButton = document.createElement("button");
  closeButton.textContent = "×";
  closeButton.style.position = "absolute";
  closeButton.style.top = "20px";
  closeButton.style.right = "20px";
  closeButton.style.fontSize = "32px";
  closeButton.style.color = "white";
  closeButton.style.background = "none";
  closeButton.style.border = "none";
  closeButton.style.cursor = "pointer";
  closeButton.style.width = "40px";
  closeButton.style.height = "40px";
  closeButton.style.display = "flex";
  closeButton.style.alignItems = "center";
  closeButton.style.justifyContent = "center";
  closeButton.style.zIndex = "1001";

  closeButton.onclick = () => {
    document.body.removeChild(pricingTableContainer);
    document.head.removeChild(styleSheet);
  };

  pricingTableContainer.appendChild(closeButton);
};

/** A small helper for playing the "click" sound. */
function playClick(
  audioCtx,
  masterGainNode,
  isAccent,
  soundObj,
  beatVolume,
  when
) {
  if (soundObj.type === "none") {
    return;
  }
  const startTime = Math.max(when, audioCtx.currentTime + 0.005);
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.connect(gain).connect(masterGainNode);

  // Check if we should use accent pitch (considering noPitchAccent property)
  const useAccentPitch = isAccent && !soundObj.noPitchAccent;

  if (soundObj.type === "clicker") {
    // A small click
    osc.type = "square";
    const freq = useAccentPitch ? 2000 : 1600;
    const vol = isAccent ? 1.2 : 0.6;
    gain.gain.setValueAtTime(vol * beatVolume, startTime);
    gain.gain.linearRampToValueAtTime(0, startTime + 0.01);
    osc.frequency.setValueAtTime(freq, startTime);
    osc.start(startTime);
    osc.stop(startTime + 0.01);
    return;
  }

  // Otherwise, e.g. "sine"
  osc.type = soundObj.type || "sine";
  const freqVal = useAccentPitch
    ? soundObj.accentFrequency || 1000
    : soundObj.frequency || 800;
  osc.frequency.setValueAtTime(freqVal, startTime);

  const baseVolume = isAccent ? 1.2 : 0.7;
  gain.gain.setValueAtTime(baseVolume * beatVolume, startTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.07);

  osc.start(startTime);
  osc.stop(startTime + 0.08);
}

// This function applies the stress pattern (or default=1.0) to a *single* measure
function applyStressVolumesIfAny(measures, stressOn) {
  if (!stressOn) return measures;

  measures.forEach((m) => {
    const top = m.numerator;
    const bottom = m.denominator;
    
    // Skip measures with custom groupings already applied
    const hasCustomGrouping = 
      (top === 7 && (m.g2p2p3 || m.g3p2p2 || m.g2p3p2)) || 
      (top === 5 && (m.g2plus3 || m.g3plus2));
      
    if (hasCustomGrouping) {
      // Skip this measure - preserve its custom grouping
      return;
    }
    
    const patternArr = STRESS_PATTERNS[top];
    if (patternArr && [1, 2, 4, 8, 16, 32].includes(bottom)) {
      m.beatSounds.forEach((beatObj, idx) => {
        beatObj.userVolume =
          idx < patternArr.length
            ? patternArr[idx]
            : patternArr[patternArr.length - 1];
      });
    } else {
      m.beatSounds.forEach((beatObj) => {
        beatObj.userVolume = 1.0;
      });
    }
  });

  return measures;
}

/** Create a new beat with default "clicker" */
function createNewBeat() {
  return {
    id: SOUND_OPTIONS[0].id,
    name: SOUND_OPTIONS[0].name,
    type: SOUND_OPTIONS[0].type,
    color: SOUND_OPTIONS[0].color,
    userVolume: 1.0, // store the user's chosen amplitude (the base)
    beatVolume: 1.0, // the "final" amplitude used for playback
    noPitchAccent: false,
    // subdivisions
    subdivisions: [
      {
        // By default, create one "entry" that says "no subdivision."
        // subCount = 1 means "no subdiv" for now.
        subCount: 1,
        accentedSubs: [],
        mutedSubs: [],
      },
      // You can push more objects here if you want multiple from the start,
      // or just start with one and add more later in the UI.
    ],

    // volumes
    beatVolume: 1.0,

    // drone/chord
    dronePitchNote: null, // "Off" by default
    dronePitchOctave: 4, // default to Octave 4
    droneVolume: 1.0,
    hasHarmony: false,
    selectedHarmony: "Major",
    justIntonation: false,
    temperamentSystem: "Equal Temperament",
    jiReferencePitch: 0,
    copyDroneHarmonyPartialsToNext: false,

    // Show/hide partials
    usePartials: false,
    selectedPartials: [],
    useCustomNotes: false,
    selectedCustomNotes: [],

    // Quarter tones
    useQuarterTones: false,
    selectedQuarterTones: [],
    quarterToneOctave: 4,
    droneSound: {
      id: "sine",
      type: "oscillator",
      oscillatorType: "sine"
    },
    copyDroneHarmonyPartialsToNext: false,
    applyDroneSoundToAllBeats: false,
  };
}

function createNewMeasure(stressOn, initialNumber = 1) {
  const numerator = 4; // Default, but will be changed later
  const denominator = 4;
  const numBeats = STRESS_PATTERNS[numerator]
    ? STRESS_PATTERNS[numerator].length
    : numerator;

  // 1) Build the measure object
  const measure = {
    id: uuidv4(),
    numerator: 4,
    denominator: 4,
    hasModulation: false,
    fromSubdivision: { name: "Quarter note", value: 4 },
    toSubdivision: { name: "Quarter note", value: 4 },
    applyBeatOneDroneToAllBeats: false,
    beatSounds: Array(numBeats)
      .fill(null)
      .map(() => createNewBeat()),
    onlyTuplets: false,
    tuplets: [],
    dynamicMarking: "",
    cresc: false,
    dim: false,
  };

  // 2) If stress is on, see if 4/4 is in our patterns. Then apply or do nothing
  if (stressOn && STRESS_PATTERNS[numerator]) {
    measure.beatSounds.forEach((beatObj, idx) => {
      beatObj.userVolume =
        idx < STRESS_PATTERNS[numerator].length
          ? STRESS_PATTERNS[numerator][idx]
          : 1.0;
    });
  }

  return measure;
}

function resetAllBeatVolumesToUserVolumes(measures) {
  for (const meas of measures) {
    for (const beat of meas.beatSounds) {
      beat.beatVolume = beat.userVolume;
    }
  }
}

/**
 * applyAllDynamicsToAllMeasures:
 *  - Loops over every measure i
 *  - Looks up measure i's dynamic marking => currentBaseVol
 *  - Looks up measure (i+1)'s dynamic marking => nextBaseVol (if it exists)
 *  - If measure i has cresc => only ramp *up*
 *  - If measure i has dim   => only ramp *down*
 *  - If neither => ramp from i's dynamic to (i+1)'s dynamic as-is.
 */
function applyAllDynamicsToAllMeasures(measures) {
  for (let i = 0; i < measures.length; i++) {
    const measure = measures[i];

    // 1) Lookup current measure's dynamic marking
    const thisKey = (measure.dynamicMarking || "").trim().toLowerCase();
    const currentBaseVol = DYNAMICS_MAP[thisKey] || 1.0;

    // 2) Check if there's a "next measure" dynamic
    let nextBaseVol = currentBaseVol;
    let hasNext = false;
    if (i + 1 < measures.length) {
      const nextKey = (measures[i + 1].dynamicMarking || "")
        .trim()
        .toLowerCase();
      if (nextKey) {
        hasNext = true;
        nextBaseVol = DYNAMICS_MAP[nextKey] || 1.0;
      }
    }

    // 3) Check if measure i is cresc or dim
    const isCresc = measure.cresc;
    const isDim = measure.dim;

    // 4) Decide how measure i "ends" – i.e., what volume we ramp to
    let endVol = currentBaseVol; // fallback if no changes
    if (isCresc) {
      // If next measure is louder => end at next measure's dynamic
      if (hasNext && nextBaseVol > currentBaseVol) {
        endVol = nextBaseVol;
      }
      // Otherwise ramp up by a default factor
      else {
        endVol = currentBaseVol * DEFAULT_CRESC_FACTOR;
      }
    } else if (isDim) {
      // If next measure is softer => end at next measure's dynamic
      if (hasNext && nextBaseVol < currentBaseVol) {
        endVol = nextBaseVol;
      }
      // Otherwise ramp down by default factor
      else {
        endVol = currentBaseVol * DEFAULT_DIM_FACTOR;
      }
    }
    // (If neither cresc nor dim is set, we leave endVol = currentBaseVol
    //  or optionally ramp from currentBaseVol -> nextBaseVol if you want a crossfade.)

    // 5) Apply your single-measure ramp from currentBaseVol -> endVol
    const numBeats = measure.beatSounds.length;
    for (let b = 0; b < numBeats; b++) {
      const beat = measure.beatSounds[b];

      // (A) We read beat.userVolume as the "raw" volume the user set
      const userVolume = beat.userVolume;
      // e.g. 1.0, 0.3, etc. – never permanently changed by measure dynamics

      // fraction from 0..1 across this measure
      const fraction = numBeats > 1 ? b / (numBeats - 1) : 0;

      // (B) Exponential approach
      const ratio = endVol / Math.max(0.0001, currentBaseVol);
      const dynamicFactor = currentBaseVol * Math.pow(ratio, fraction);

      // (C) If you prefer linear, comment out above, then do:
      // const dynamicFactor =
      //   currentBaseVol + fraction * (endVol - currentBaseVol);

      // 6) Final beat volume = userVolume * measure-level factor
      beat.beatVolume = userVolume * dynamicFactor;
    }

    // When measure i ends, measure i+1 will do its own logic,
    // which can cause a sudden jump if measure i ended at a different vol
    // than measure i+1's base dynamic.
  }
}

{/* Black key component */}
const BlackKey = ({ octave, semitone, isPlaying, toggleNote, idx, bIndex, isSelected, isMobile }) => {
  // Correct mapping for all 5 black keys
  const sharpNames = {
    1: "C#",
    3: "D#",
    6: "F#",
    8: "G#",
    10: "A#"
  };
  
  const noteName = sharpNames[semitone % 12];
  
  return (
    <div 
      onClick={() => !isPlaying && toggleNote(idx, bIndex, semitone, octave)}
      className={`
        absolute w-[60%] h-5 sm:h-7 -right-[30%] top-0 z-10
        ${isSelected ? 'bg-brand-700 hover:bg-brand-700' : 'bg-gray-800 hover:bg-gray-700'}
        flex items-center justify-center
        text-white text-[10px] font-medium
        cursor-pointer rounded-b-sm
        transition-colors
      `}
    >
      {noteName}
    </div>
  );
};

function AccentInput({ defaultValue = "", onAccentsChange }) {
  const [localText, setLocalText] = React.useState(defaultValue);

  function handleChange(e) {
    setLocalText(e.target.value);
  }

  function handleBlur() {
    const parsed = parseAccentString(localText);
    if (onAccentsChange) {
      onAccentsChange(parsed);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      e.target.blur(); // triggers handleBlur
    }
  }

  return (
    // 1) Wrap input in a <div> that has a border & padding:
    <div
      style={{
        border: "1px solid #999",
        borderRadius: "4px",
        padding: "4px",
        display: "inline-block", // or "block" if you want 100% width
      }}
    >
      <input
        type="text"
        value={localText}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder='e.g. "1,3"'
        style={{
          // 2) Remove the default border so it doesn't double up
          border: "none",
          outline: "none",
        }}
      />
    </div>
  );
}

/** Tempo changes */
function computeEffectiveTempo(
  globalBarCount,
  localBeat,
  measureNumerator,
  baseTempo,
  useTempoChanges,
  tempoChanges,
  loopCountRef
) {
  let effectiveTempo = baseTempo;
  if (!useTempoChanges || !tempoChanges.length) {
    return effectiveTempo;
  }

  const currentMeasureFloat = globalBarCount + localBeat / measureNumerator;
  const sortedChanges = [...tempoChanges].sort(
    (a, b) => a.startMeasure - b.startMeasure
  );

  // Loop over each tempo-change object:
  for (const tc of sortedChanges) {
    // 1) If "incremental" with scope="whole", apply the loopCountRef logic:
    if (tc.type === "incremental" && tc.scope === "whole") {
      const newTempo = tc.startTempo + loopCountRef.current * tc.stepSize;
      if (tc.startTempo < tc.endTempo) {
        effectiveTempo = Math.min(newTempo, tc.endTempo);
      } else {
        effectiveTempo = Math.max(newTempo, tc.endTempo);
      }
    }

    // 2) Now destructure the fields from tc so we can do measure-based logic:
    const {
      startMeasure,
      endMeasure,
      startTempo,
      endTempo,
      type,
      stepSize = 5,
      stepEvery = 1,
      // and so forth
    } = tc;

    console.log(`Input Tempos - baseTempo: ${baseTempo}, tempoChange startTempo: ${tc.startTempo}, endTempo: ${tc.endTempo}`);

    const startIdx = startMeasure - 1;
    const endIdx = endMeasure - 1;

    if (type === "sudden" && currentMeasureFloat >= startIdx) {
      effectiveTempo = endTempo;
    }

    // Are we within this change's measure range?
    if (currentMeasureFloat >= startIdx) {
      if (currentMeasureFloat > endIdx) {
        // Past the end => fix effectiveTempo to endTempo
        effectiveTempo = endTempo;
      } else {
        // We are somewhere between startMeasure and endMeasure
        const fraction =
          endIdx - startIdx > 0
            ? (currentMeasureFloat - startIdx) / (endIdx - startIdx)
            : 0;

        console.log(`Tempo Debug - Measure: ${globalBarCount + 1}, Beat: ${localBeat}, CurrentFloat: ${currentMeasureFloat}, StartIdx: ${startIdx}, EndIdx: ${endIdx}, Fraction: ${fraction}, EffectiveTempo: ${startTempo + fraction * (endTempo - startTempo)}`);

        switch (type) {
          case "linear":
            effectiveTempo = startTempo + fraction * (endTempo - startTempo);
            break;

          case "exponential":
            if (startTempo > 0 && endTempo > 0) {
              const ratio = endTempo / startTempo;
              effectiveTempo = startTempo * Math.pow(ratio, fraction);
            }
            break;

          case "incremental":
            // If scope === "specific," do localLoopCount logic
            if (tc.scope === "specific") {
              const localCount = tc.localLoopCount || 0;
              const newTempo = startTempo + localCount * stepSize;
              if (startTempo < endTempo) {
                effectiveTempo = Math.min(newTempo, endTempo);
              } else {
                effectiveTempo = Math.max(newTempo, endTempo);
              }
            }
            // If scope === "whole," we already handled it above
            break;

          case "sudden":
          default:
            effectiveTempo = endTempo;
            break;
        }
      }
    }
    // Because we want the *last* matching change to override earlier ones,
    // we do NOT "break" here; we let it keep looping.
  }

  return effectiveTempo;
}


function clampDrone(freq) {
  while (freq < DRONE_MIN_FREQ) {
    freq *= 2;
  }
  while (freq > MAX_FREQ) {
    freq /= 2;
  }
  return freq;
}

function clampHarmony(freq) {
  while (freq < HARMONY_MIN_FREQ) {
    freq *= 2;
  }
  while (freq > MAX_FREQ) {
    freq /= 2;
  }
  return freq;
}

function applyModulation(
  activeModRef,
  effectiveTempo,
  denominator,
  noteValueFactor
) {
  console.log(`applyModulation called with:`, {
    isActive: activeModRef.current.isActive,
    effectiveTempo,
    denominator,
    noteValueFactor,
  });

  // If no modulation is active, just calculate the standard base ms value
  if (!activeModRef.current.isActive) {
    // final BPM for denominator notes:
    const finalBPM = effectiveTempo * noteValueFactor * (denominator / 4);
    return 60_000 / finalBPM;
  }

  // CRITICAL FIX: Use the current effectiveTempo (which includes tempo changes)
  // instead of the stored sourceTempoReference (which is fixed at modulation start)
  const { fromSubdivision, toSubdivision } = activeModRef.current;
  const fromVal = fromSubdivision ? fromSubdivision.value : 4;
  const toVal = toSubdivision ? toSubdivision.value : 4;

  // Calculate base ms using the CURRENT effective tempo (includes tempo changes)
  const finalBPM = effectiveTempo * noteValueFactor * (denominator / 4);
  let baseMs = 60_000 / finalBPM;

  // Then apply the modulation ratio
  const ratio = toVal / fromVal;
  console.log(`Modulation values:`, {
    fromVal,
    toVal,
    effectiveTempo, // Now using current tempo instead of sourceTempoReference
    ratio,
  });

  const result = baseMs * ratio;
  console.log(`Final modulation result: ${result}`);
  return result;
}

/** Calculate frequency from semitone, octave, and tuning reference */
function getFrequency(semitone, octave, aTuning = 440) {
  // Convert semitone and octave to MIDI note number
  // MIDI note 69 is A4 (440Hz)
  const midiNote = semitone + (octave * 12);
  
  // Calculate frequency: f = reference * 2^((n-69)/12)
  return aTuning * Math.pow(2, (midiNote - 69) / 12);
}

// ... existing code (imports, helpers, etc.) ...

/**
 * scheduleDroneOrChordChange
 * Handles drones / chords for each beat.
 * – Keeps all existing oscillator logic.
 * – Adds support for sampled instruments (Tone.js Sampler).
 * – Uses caching via getSampler  ➜  fast the next time.
 */
async function scheduleDroneOrChordChange(
  audioCtx,
  masterGainNode,
  partialOscRefs,
  chordOscRefs,
  beatObj,
  startTimeSec,
  aTuning,
  activeChordRef,
  beatSec,
  totalMeasureDurationSec,
  muteDrones,
  loopPitchOffsetRef,
  modulateOnLoop,
  subscriptionTier,
  muteRandomDrones,
  muteRandomDronePercentage,
  currentMeasure,
  modLoopInterval,
  modLoopDirection,
  nextBeatObj = null,
  scheduledBeat = 0,
  measureObj = null
) {
  /* ---------- 0. Quick "mute / cleanup" guard ---------- */
  const componentState = window.metronomeComponentState || {};
  const actualMuteRandomDrones = componentState.muteRandomDrones ?? muteRandomDrones;
  const actualPct = componentState.muteRandomDronePercentage ?? muteRandomDronePercentage;
  const fadeIn    = Math.min(1, Math.max(0, (currentMeasure - 3) / 3));
  const shouldMuteRandomly =
    actualMuteRandomDrones &&
    currentMeasure >= 3 &&
    Math.random() * 100 <= actualPct * fadeIn;

  if (muteDrones || shouldMuteRandomly) {
    if (activeChordRef.current.signature !== 'none') {
      [...partialOscRefs.current, ...chordOscRefs.current].forEach(({ osc, gainNode, cleanup }) => {
        try { cleanup ? cleanup() : osc.stop(); } catch {}
        osc.disconnect();  gainNode.disconnect();
      });
      partialOscRefs.current = [];   chordOscRefs.current = [];
      activeChordRef.current.signature = 'none';
    }
    return;
  }

  /* ---------- 1.  Pitch-offset (loop modulation) ---------- */
  const pitchOffset =
    modLoopInterval > 0
      ? (modulateOnLoop ? loopPitchOffsetRef.current
                        : (modLoopDirection === 'up' ?  modLoopInterval
                                                     : -modLoopInterval))
      : 0;

  /* ---------- 2.  Fundamental & frequencies ---------- */
  const originalNote = beatObj.dronePitchNote;
  const rawFinal     = (originalNote ?? 0) + pitchOffset;
  const minAllowed   = (originalNote ?? 0) - 24;
  const maxAllowed   = (originalNote ?? 0) + 24;

  let clampedSemi = rawFinal;
  while (clampedSemi > maxAllowed) clampedSemi = minAllowed + (clampedSemi - maxAllowed);
  while (clampedSemi < minAllowed) clampedSemi = maxAllowed - (minAllowed - clampedSemi);

  let fundamentalFreq = null;
  if (originalNote !== null) {
    const etFreq = getFrequency(clampedSemi, beatObj.dronePitchOctave, aTuning);

    if (beatObj.temperamentSystem === 'Just Intonation') {
      const refNote = beatObj.jiReferencePitch ?? 0;
      const semi = ((clampedSemi % 12) + 12) % 12; // normalize to 0-11
      const interval = ((semi - refNote) + 12) % 12;

      if (interval === 0) {
        // Drone IS the reference note — use ET frequency
        fundamentalFreq = clampDrone(etFreq);
      } else {
        // Find the closest reference note at or below the drone
        const droneMidi = clampedSemi + (beatObj.dronePitchOctave * 12);
        let refOctave = Math.floor(droneMidi / 12);
        let closestRefMidi = refOctave * 12 + refNote;
        if (closestRefMidi > droneMidi) {
          closestRefMidi -= 12;
        }
        const refFreq = aTuning * Math.pow(2, (closestRefMidi - 69) / 12);
        const ratio = JI_SEMITONE_RATIOS[interval];
        fundamentalFreq = clampDrone(
          ratio ? refFreq * (ratio.numerator / ratio.denominator) : etFreq
        );
      }
    } else if (beatObj.temperamentSystem && beatObj.temperamentSystem !== 'Equal Temperament') {
      // Historical temperaments — apply cents offset
      const temperament = TEMPERAMENT_SYSTEMS[beatObj.temperamentSystem];
      if (temperament?.offsets) {
        const noteIndex = ((clampedSemi % 12) + 12) % 12;
        const cents = temperament.offsets[noteIndex];
        fundamentalFreq = clampDrone(etFreq * Math.pow(2, cents / 1200));
      } else {
        fundamentalFreq = clampDrone(etFreq);
      }
    } else {
      // Equal Temperament (default)
      fundamentalFreq = clampDrone(etFreq);
    }
  }

  /* ---------- 3.  Build chordFreqs (all your existing logic) ---------- */
  let chordFreqs = [];
  const dynFactor = beatObj.beatVolume / beatObj.userVolume || 1.0;

  if (beatObj.useCustomNotes && beatObj.selectedCustomNotes?.length) {
    if (beatObj.temperamentSystem === 'Equal Temperament' || !beatObj.temperamentSystem) {
      chordFreqs = beatObj.selectedCustomNotes.map(mn =>
        aTuning * Math.pow(2, ((mn + pitchOffset) - 69) / 12)
      );
    } else if (beatObj.temperamentSystem === 'Just Intonation') {
      const refNote = beatObj.dronePitchNote ?? beatObj.jiReferencePitch ?? 0;

      chordFreqs = beatObj.selectedCustomNotes.map(mn => {
        const actualMidiNote = mn + pitchOffset;
        const semi = actualMidiNote % 12;
        
        // If this note is the same semitone as reference, keep ET frequency
        if (semi === refNote) {
          return aTuning * Math.pow(2, (actualMidiNote - 69) / 12);
        }
        
        // Find the closest reference note at or below this target note
        function findClosestReferenceBelow(targetMidiNote, refSemitone) {
          let candidateOctave = Math.floor(targetMidiNote / 12);
          
          while (candidateOctave >= 0) {
            const candidateRefNote = candidateOctave * 12 + refSemitone;
            if (candidateRefNote <= targetMidiNote) {
              return candidateRefNote;
            }
            candidateOctave--;
          }
          return refSemitone; // Fallback to octave 0
        }
        
        const closestRefMidiNote = findClosestReferenceBelow(actualMidiNote, refNote);
        const interval = (semi - refNote + 12) % 12;
        const ratio = JI_SEMITONE_RATIOS[interval];
        
        if (!ratio) {
          return aTuning * Math.pow(2, (actualMidiNote - 69) / 12);
        }
        
        // Calculate reference frequency and apply JI ratio
        const refFreq = aTuning * Math.pow(2, (closestRefMidiNote - 69) / 12);
        return refFreq * (ratio.numerator / ratio.denominator);
      });
    } else {
      // historical temperaments …
      const temperament = TEMPERAMENT_SYSTEMS[beatObj.temperamentSystem];
      chordFreqs = beatObj.selectedCustomNotes.map(mn => {
        const et = aTuning * Math.pow(2, (mn - 69) / 12);
        const cents = temperament.offsets[(mn % 12 + pitchOffset) % 12];
        return et * Math.pow(2, cents / 1200);
      });
    }
  } else if (fundamentalFreq !== null) {
    // When using partials, only include fundamental if partial "1" is selected
    if (beatObj.usePartials && beatObj.selectedPartials?.length > 0) {
      // Only add fundamental if partial 1 is explicitly selected
      if (beatObj.selectedPartials.includes(1)) {
        chordFreqs = [fundamentalFreq];
      } else {
        chordFreqs = []; // No fundamental, only partials will play
      }
    } else {
      // Normal behavior when not using partials
      chordFreqs = [fundamentalFreq];
    }
    if (beatObj.hasHarmony) {
      if (beatObj.temperamentSystem === 'Just Intonation') {
        (CHORDS_JI[beatObj.selectedHarmony] || []).forEach(c =>
          chordFreqs.push(clampHarmony(fundamentalFreq * Math.pow(2, c / 1200)))
        );
      } else if (beatObj.temperamentSystem && beatObj.temperamentSystem !== 'Equal Temperament') {
        const temperament = TEMPERAMENT_SYSTEMS[beatObj.temperamentSystem];
        (CHORDS_ET[beatObj.selectedHarmony] || []).forEach(st => {
          const cents = temperament.offsets[(st % 12)];
          const et    = fundamentalFreq * Math.pow(2, st / 12);
          chordFreqs.push(clampHarmony(et * Math.pow(2, cents / 1200)));
        });
      } else {
        (CHORDS_ET[beatObj.selectedHarmony] || []).forEach(st =>
          chordFreqs.push(clampHarmony(fundamentalFreq * Math.pow(2, st / 12)))
        );
      }
    }
  }

 /* ---------- 4. Handle samplers differently from oscillators ---------- */
 const droneSound = beatObj.droneSound || { type: 'oscillator', oscillatorType: 'sine' };

 if (droneSound.type === 'sampler' && fundamentalFreq !== null) {
  try {
    const sampler = await getSampler(droneSound.instrument, masterGainNode);
    if (sampler) {
      // ✅ Fix: Add 12 to get correct MIDI note (C4 = 60, not 48)
      const baseMidiNote = beatObj.dronePitchNote + (beatObj.dronePitchOctave * 12) + 12 + pitchOffset;
      const notesToPlay = [baseMidiNote];
      
      // Add harmony notes if enabled
      if (beatObj.hasHarmony) {
        const harmonyIntervals = CHORDS_ET[beatObj.selectedHarmony] || [];
        harmonyIntervals.forEach(st => {
          notesToPlay.push(baseMidiNote + st);
        });
      }
      
      // Add custom notes if enabled
      if (beatObj.useCustomNotes && beatObj.selectedCustomNotes?.length) {
        beatObj.selectedCustomNotes.forEach(mn => {
          notesToPlay.push(mn + pitchOffset);
        });
      }

      const volume = (beatObj.droneVolume * dynFactor) * 0.7;
      const noteDuration = beatSec * 0.9;
      
      const { playSampleWithTemperament } = await import('./utils/samplerManager.js');

      // ✅ Trigger sustained notes with temperament
      notesToPlay.forEach(midiNote => {
        try {
          playSampleWithTemperament(
            sampler,
            midiNote,
            noteDuration,
            Tone.now(),
            volume,
            beatObj.temperamentSystem || 'Equal Temperament',
            beatObj.jiReferencePitch || 0,
            aTuning
          );
        } catch (noteError) {
          console.warn(`Could not play note ${midiNote}:`, noteError);
        }
      });

      // Store reference for cleanup
      chordOscRefs.current.push({
        osc: sampler,
        gainNode: sampler.output,
        cleanup: () => {
          try {
            notesToPlay.forEach(midiNote => {
              const noteName = Tone.Frequency(midiNote, 'midi').toNote();
              sampler.triggerRelease(noteName, Tone.now());
            });
          } catch (releaseError) {
            console.warn('Error releasing sampler notes:', releaseError);
          }
        }
      });
    }
  } catch (err) {
    console.warn('Sampler failed – reverting to oscillator:', err);
  }
}

  // Calculate if we should sustain to next beat
const shouldReuseFromPreviousBeat = scheduledBeat > 0 && 
beatObj.dronePitchNote === measureObj.beatSounds[scheduledBeat - 1]?.dronePitchNote &&
beatObj.dronePitchOctave === measureObj.beatSounds[scheduledBeat - 1]?.dronePitchOctave &&
beatObj.hasHarmony === measureObj.beatSounds[scheduledBeat - 1]?.hasHarmony &&
beatObj.selectedHarmony === measureObj.beatSounds[scheduledBeat - 1]?.selectedHarmony &&
beatObj.useCustomNotes === measureObj.beatSounds[scheduledBeat - 1]?.useCustomNotes &&
JSON.stringify(beatObj.selectedCustomNotes) === JSON.stringify(measureObj.beatSounds[scheduledBeat - 1]?.selectedCustomNotes) &&
beatObj.temperamentSystem === measureObj.beatSounds[scheduledBeat - 1]?.temperamentSystem &&
beatObj.useQuarterTones === measureObj.beatSounds[scheduledBeat - 1]?.useQuarterTones &&
JSON.stringify(beatObj.selectedQuarterTones) === JSON.stringify(measureObj.beatSounds[scheduledBeat - 1]?.selectedQuarterTones) &&
beatObj.quarterToneOctave === measureObj.beatSounds[scheduledBeat - 1]?.quarterToneOctave &&
beatObj.usePartials === measureObj.beatSounds[scheduledBeat - 1]?.usePartials &&
JSON.stringify(beatObj.selectedPartials) === JSON.stringify(measureObj.beatSounds[scheduledBeat - 1]?.selectedPartials) &&
// Compare only the essential droneSound properties
(beatObj.droneSound?.type || 'oscillator') === (measureObj.beatSounds[scheduledBeat - 1]?.droneSound?.type || 'oscillator') &&
(beatObj.droneSound?.oscillatorType || 'sine') === (measureObj.beatSounds[scheduledBeat - 1]?.droneSound?.oscillatorType || 'sine') &&
(beatObj.droneSound?.instrument || null) === (measureObj.beatSounds[scheduledBeat - 1]?.droneSound?.instrument || null);

// Keep the original shouldSustain for stop time scheduling
const shouldSustain = nextBeatObj && 
beatObj.dronePitchNote === nextBeatObj.dronePitchNote &&
beatObj.dronePitchOctave === nextBeatObj.dronePitchOctave &&
beatObj.hasHarmony === nextBeatObj.hasHarmony &&
beatObj.selectedHarmony === nextBeatObj.selectedHarmony &&
beatObj.useCustomNotes === nextBeatObj.useCustomNotes &&
JSON.stringify(beatObj.selectedCustomNotes) === JSON.stringify(nextBeatObj?.selectedCustomNotes) &&
beatObj.temperamentSystem === nextBeatObj.temperamentSystem &&
beatObj.useQuarterTones === nextBeatObj.useQuarterTones &&
JSON.stringify(beatObj.selectedQuarterTones) === JSON.stringify(nextBeatObj?.selectedQuarterTones) &&
beatObj.quarterToneOctave === nextBeatObj.quarterToneOctave &&
beatObj.usePartials === nextBeatObj.usePartials &&
JSON.stringify(beatObj.selectedPartials) === JSON.stringify(nextBeatObj?.selectedPartials) &&
// Compare only the essential droneSound properties
(beatObj.droneSound?.type || 'oscillator') === (nextBeatObj?.droneSound?.type || 'oscillator') &&
(beatObj.droneSound?.oscillatorType || 'sine') === (nextBeatObj?.droneSound?.oscillatorType || 'sine') &&
(beatObj.droneSound?.instrument || null) === (nextBeatObj?.droneSound?.instrument || null);

/* ---------- 5. Handle oscillators (existing logic) ---------- */
// Force cleanup if no chordFreqs but we have active oscillators
const hasActiveQuarterTones = beatObj.useQuarterTones && beatObj.selectedQuarterTones?.length > 0;
const hasActivePartials = beatObj.usePartials && beatObj.selectedPartials?.length > 0;

if (chordFreqs.length === 0 && !hasActiveQuarterTones && !hasActivePartials && (partialOscRefs.current.length > 0 || chordOscRefs.current.length > 0)) {
  console.log('🧹 FORCE CLEANUP: No chordFreqs, quarter tones, or partials but active oscillators exist', {
    currentMeasure,
    currentBeat: scheduledBeat + 1,
    partialOscCount: partialOscRefs.current.length,
    chordOscCount: chordOscRefs.current.length,
    chordFreqsLength: chordFreqs.length,
    hasActiveQuarterTones,
    hasActivePartials,
    dronePitchNote: beatObj.dronePitchNote
  });
  
  // Stop all existing oscillators
  [...partialOscRefs.current, ...chordOscRefs.current].forEach(({ osc, gainNode, cleanup }, index) => {
    try {
      console.log(`🧹 Force stopping oscillator ${index}`);
      cleanup ? cleanup() : osc.stop();
      osc.disconnect();
      gainNode.disconnect();
    } catch (e) {
      console.log(`🧹 Error force stopping oscillator ${index}:`, e);
    }
  });
  partialOscRefs.current = [];
  chordOscRefs.current = [];
  activeChordRef.current.signature = 'none';
}

// Check if we have any oscillator-based sounds (regular drones, quarter tones, or partials)
const hasOscillatorSounds = chordFreqs.length > 0 || 
  (beatObj.useQuarterTones && beatObj.selectedQuarterTones?.length > 0) ||
  (beatObj.usePartials && beatObj.selectedPartials?.length > 0);

if (droneSound.type !== 'sampler' && hasOscillatorSounds) {
  
    // Create a more focused signature that captures the essential sound properties
    const oscillatorSignature = JSON.stringify({
      dronePitchNote: beatObj.dronePitchNote,
      dronePitchOctave: beatObj.dronePitchOctave,
      hasHarmony: beatObj.hasHarmony,
      selectedHarmony: beatObj.selectedHarmony,
      useCustomNotes: beatObj.useCustomNotes,
      selectedCustomNotes: beatObj.selectedCustomNotes,
      temperamentSystem: beatObj.temperamentSystem,
      droneType: droneSound.type || 'oscillator',
      oscillatorType: droneSound.oscillatorType || 'sine',
      instrument: droneSound.instrument || null,
      // Add partials and quarter tones to the signature
      usePartials: beatObj.usePartials,
      selectedPartials: beatObj.selectedPartials,
      useQuarterTones: beatObj.useQuarterTones,
      selectedQuarterTones: beatObj.selectedQuarterTones,
      quarterToneOctave: beatObj.quarterToneOctave
    });

console.log('🔄 REUSE DEBUG:', {
  shouldReuseFromPreviousBeat,
  shouldSustain,
  currentBeat: scheduledBeat + 1,
  previousBeatPitch: scheduledBeat > 0 ? measureObj.beatSounds[scheduledBeat - 1]?.dronePitchNote : 'N/A',
  currentBeatPitch: beatObj.dronePitchNote,
  activeSignature: activeChordRef.current.signature.substring(0, 100) + '...',
  newSignature: oscillatorSignature.substring(0, 100) + '...',
  signaturesMatch: activeChordRef.current.signature === oscillatorSignature,
  previousBeatDroneSound: scheduledBeat > 0 ? measureObj.beatSounds[scheduledBeat - 1]?.droneSound : null,
  currentBeatDroneSound: beatObj.droneSound
});

console.log('🔄 SIGNATURE DEBUG:', {
  currentBeat: scheduledBeat + 1,
  currentMeasure: currentMeasure + 1,
  activeSignature: activeChordRef.current.signature === '' ? 'EMPTY' : 'EXISTS',
  newSignature: 'COMPUTED',
  signaturesMatch: activeChordRef.current.signature === oscillatorSignature,
  hasPartials: beatObj.usePartials && beatObj.selectedPartials?.length > 0,
  partialCount: beatObj.selectedPartials?.length || 0,
  // Add these new fields:
  willTakeReusePath: activeChordRef.current.signature !== '' && 
    activeChordRef.current.signature === oscillatorSignature && 
    shouldReuseFromPreviousBeat,
  shouldReuseFromPreviousBeat: shouldReuseFromPreviousBeat,
  activeSignatureFull: activeChordRef.current.signature.substring(0, 200),
  newSignatureFull: oscillatorSignature.substring(0, 200)
});

console.log('🔍 REUSE CONDITION DEBUG:', {
  currentBeat: scheduledBeat + 1,
  activeSignatureExists: activeChordRef.current.signature !== '',
  signaturesMatch: activeChordRef.current.signature === oscillatorSignature,
  shouldReuseFromPreviousBeat: shouldReuseFromPreviousBeat,
  activeSignature: activeChordRef.current.signature.substring(0, 100),
  newSignature: oscillatorSignature.substring(0, 100),
  quarterTonesInSignature: oscillatorSignature.includes('quarterTone'),
  useQuarterTones: beatObj.useQuarterTones,
  selectedQuarterTones: beatObj.selectedQuarterTones
});

// Update the early return condition
if (activeChordRef.current.signature !== '' && 
  activeChordRef.current.signature === oscillatorSignature && 
  shouldReuseFromPreviousBeat) {
console.log('✅ REUSING OSCILLATORS FROM PREVIOUS BEAT');

// Still need to update stop time if shouldSustain changed
if (!shouldSustain) {
  // Schedule stop time for existing oscillators
  chordOscRefs.current.forEach(({ gainNode }) => {
    const stopTime = startTimeSec + beatSec;
    gainNode.gain.setValueAtTime(gainNode.gain.value, stopTime - 0.01);
    gainNode.gain.linearRampToValueAtTime(0, stopTime);
  });
}
return;
}

// Stop previous oscillators
if (activeChordRef.current.signature !== 'none') {
  console.log('🧹 CLEANUP DEBUG: About to clean up oscillators', {
    partialOscCount: partialOscRefs.current.length,
    chordOscCount: chordOscRefs.current.length,
    currentBeat: scheduledBeat + 1,
    currentMeasure: currentMeasure + 1,
    reason: 'signature changed'
  });
  
  [...partialOscRefs.current, ...chordOscRefs.current].forEach(({ osc, gainNode, cleanup }, index) => {
    try { 
      console.log(`🧹 Stopping oscillator ${index}:`, osc.type, osc.frequency.value);
      cleanup ? cleanup() : osc.stop(); 
    } catch (e) {
      console.log(`🧹 Error stopping oscillator ${index}:`, e);
    }
    osc.disconnect(); gainNode.disconnect();
  });
  partialOscRefs.current = []; chordOscRefs.current = [];
} else {
  console.log('🧹 CLEANUP DEBUG: No cleanup - signature is "none"');
}
activeChordRef.current.signature = oscillatorSignature;

// Create new oscillators with scheduled stop times
chordFreqs.forEach(freq => {
  const osc = audioCtx.createOscillator();
  osc.type = droneSound.oscillatorType || 'sine';
  osc.frequency.setValueAtTime(freq * 2, startTimeSec);

  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(
    (beatObj.droneVolume * dynFactor) / chordFreqs.length,
    startTimeSec
  );

  osc.connect(gain).connect(masterGainNode);
  osc.start(startTimeSec);

  // Schedule stop time if not sustaining to next beat
  if (!shouldSustain) {
    const stopTime = startTimeSec + beatSec;
    // Fade out slightly before stopping to avoid clicks
    gain.gain.setValueAtTime(gain.gain.value, stopTime - 0.01);
    gain.gain.linearRampToValueAtTime(0, stopTime);
    osc.stop(stopTime);
  }

  chordOscRefs.current.push({ osc, gainNode: gain });
});
}

/* ---------- 7.  Partials & quarter-tones (move inside main oscillator block) ---------- */
if (fundamentalFreq && beatObj.usePartials && beatObj.selectedPartials?.length) {
  beatObj.selectedPartials.forEach(p => {
    const osc = audioCtx.createOscillator();
    osc.type = droneSound.oscillatorType || 'sine';
    const g   = audioCtx.createGain();
    osc.frequency.setValueAtTime(clampDrone(fundamentalFreq * p), startTimeSec);
    g.gain.setValueAtTime(
      (beatObj.droneVolume * dynFactor) / (2 * beatObj.selectedPartials.length),
      startTimeSec
    );
    osc.connect(g).connect(masterGainNode);
    osc.start(startTimeSec);
    
    // Schedule stop time if not sustaining to next beat
    if (!shouldSustain) {
      const stopTime = startTimeSec + beatSec;
      // Fade out slightly before stopping to avoid clicks
      g.gain.setValueAtTime(g.gain.value, stopTime - 0.01);
      g.gain.linearRampToValueAtTime(0, stopTime);
      osc.stop(stopTime);
    }
    
    partialOscRefs.current.push({ osc, gainNode: g });
  });
}

// Add this debug logging right before the quarter tones section (around line 1252)
console.log('🎵 QUARTER TONE DEBUG:', {
  useQuarterTones: beatObj.useQuarterTones,
  selectedQuarterTones: beatObj.selectedQuarterTones,
  quarterToneOctave: beatObj.quarterToneOctave,
  droneVolume: beatObj.droneVolume,
  dynFactor: dynFactor,
  calculatedVolume: (beatObj.droneVolume * dynFactor) / (beatObj.selectedQuarterTones?.length || 1),
  shouldSustain: shouldSustain,
  shouldReuseFromPreviousBeat: shouldReuseFromPreviousBeat,
  currentBeat: scheduledBeat + 1,
  startTimeSec: startTimeSec,
  beatSec: beatSec
});

if (beatObj.useQuarterTones && beatObj.selectedQuarterTones?.length) {
  console.log('🎵 CREATING QUARTER TONE OSCILLATORS:', beatObj.selectedQuarterTones.length);
  
  beatObj.selectedQuarterTones.forEach((qt, index) => {
    const midi = qt + 12 * (beatObj.quarterToneOctave - 4);
    const freq = aTuning * Math.pow(2, (midi - 69) / 12);
    const osc  = audioCtx.createOscillator();
    const g    = audioCtx.createGain();
    osc.type = droneSound.oscillatorType || 'sine'; 
    osc.frequency.setValueAtTime(freq, startTimeSec);
    
    const volume = (beatObj.droneVolume * dynFactor) / beatObj.selectedQuarterTones.length;
    console.log(`🎵 Quarter tone ${index + 1}: MIDI=${midi}, freq=${freq.toFixed(2)}Hz, volume=${volume.toFixed(3)}`);
    
    g.gain.setValueAtTime(volume, startTimeSec);
    osc.connect(g).connect(masterGainNode);
    osc.start(startTimeSec);
    
    // Schedule stop time if not sustaining to next beat
    if (!shouldSustain) {
      const stopTime = startTimeSec + beatSec;
      console.log(`🎵 Quarter tone ${index + 1}: Scheduling stop at ${stopTime.toFixed(3)}s`);
      // Fade out slightly before stopping to avoid clicks
      g.gain.setValueAtTime(g.gain.value, stopTime - 0.01);
      g.gain.linearRampToValueAtTime(0, stopTime);
      osc.stop(stopTime);
    } else {
      console.log(`🎵 Quarter tone ${index + 1}: SUSTAINING to next beat`);
    }
    
    partialOscRefs.current.push({ osc, gainNode: g });
  });
} else {
  console.log('🎵 NO QUARTER TONES:', {
    useQuarterTones: beatObj.useQuarterTones,
    selectedQuarterTonesLength: beatObj.selectedQuarterTones?.length
  });
}
}
/**
 * scheduleTuplet: produce tup.subCount clicks from startSec → startSec + totalSec
 * Also handles nested tuplets and an optional chord/drone on the first tuplet beat.
 */
function scheduleTuplet(
  audioCtx,
  masterGainNode,
  partialOscRefs,
  chordOscRefs,
  tup,
  startSec,
  totalSec,
  firstBeatDrone,
  aTuning,
  activeChordRef,
  muteDrones,
  skipParentClick,
  muteRandomDrones,
  muteRandomDronePercentage,
  globalMeasureCountRef,
  loopPitchOffsetRef,
  modulateOnLoop,
  subscriptionTier,
  modLoopInterval,
  modLoopDirection
) {
  if (!tup.subCount || tup.subCount < 1) return;

  const subIntervalSec = totalSec / tup.subCount;

  for (let i = 0; i < tup.subCount; i++) {
    let beepTime = startSec + i * subIntervalSec;
    if (beepTime < audioCtx.currentTime) {
      beepTime = audioCtx.currentTime + 0.01;
    }

    // Check if this subdivision should be muted
    const subdivisionNumber = i + 1; // 1-indexed like beats
    const isMuted = (tup.mutedSubdivisions || []).includes(subdivisionNumber);

    // Skip the parent beep if skipParentClick === true OR if this subdivision is muted
    if (!skipParentClick && !isMuted) {
      const soundObj =
        SOUND_OPTIONS.find((opt) => opt.id === tup.soundId) || SOUND_OPTIONS[0];
      if (soundObj) {
        playClick(
          audioCtx,
          masterGainNode,
          /* isAccent */ false,
          soundObj,
          1.0,
          beepTime
        );
      }
    }

    // ALWAYS check for nested tuplets, so they can play independently
    if (tup.nestedTuplets?.length) {
      tup.nestedTuplets.forEach((nested) => {
        // If the nested tuplet is muted, skip it:
        if (nested.muted) return;
        if (!nested.selectedBeats || nested.selectedBeats.length < 1) return;
        if (!nested.subCount || nested.subCount < 1) return;

        const nestedMin = Math.min(...nested.selectedBeats);
        const nestedMax = Math.max(...nested.selectedBeats);
        if (i + 1 === nestedMin) {
          const nestedSpanCount = nestedMax - nestedMin + 1;
          const nestedTotalSec = nestedSpanCount * subIntervalSec;

          scheduleNestedTuplet(
            audioCtx,
            masterGainNode,
            nested,
            beepTime,
            nestedTotalSec
          );
        }
      });
    }
  }

  // Optional chord/drone:
  if (firstBeatDrone && firstBeatDrone.dronePitch !== "off" && !muteDrones) {
    let chordTime = startSec;
    if (chordTime < audioCtx.currentTime) {
      chordTime = audioCtx.currentTime + 0.01;
    }
    scheduleDroneOrChordChange(
      audioCtx,
      masterGainNode,
      partialOscRefs,
      chordOscRefs,
      firstBeatDrone,
      chordTime,
      aTuning,
      activeChordRef,
      subIntervalSec,
      totalSec,
      muteDrones,
      loopPitchOffsetRef,
      modulateOnLoop,
      subscriptionTier,
      muteRandomDrones,
      muteRandomDronePercentage,
      globalMeasureCountRef,
      modLoopInterval,
      modLoopDirection,
      nextBeatObj,
      currentBeatRef.current, // Current beat index
  measures[measureIndex] 
    );
  }
}

/**
 * scheduleNestedTuplet: produce nested.subCount clicks over nestedTotalSec
 * from beepTime → beepTime + nestedTotalSec
 */
function scheduleNestedTuplet(
  audioCtx,
  masterGainNode,
  nested,
  beepTime,
  nestedTotalSec
) {
  // If the nested tuplet is muted, skip it entirely.
  if (nested.muted) return;

  if (!nested.subCount || nested.subCount < 1) return;

  const nestedIntervalSec = nestedTotalSec / nested.subCount;
  for (let k = 0; k < nested.subCount; k++) {
    let nestTime = beepTime + k * nestedIntervalSec;
    if (nestTime < audioCtx.currentTime) {
      nestTime = audioCtx.currentTime + 0.01;
    }

    // Check if this subdivision should be muted
    const subdivisionNumber = k + 1; // 1-indexed like beats
    const isMuted = (nested.mutedSubdivisions || []).includes(
      subdivisionNumber
    );

    if (!isMuted) {
      // Example: find the nested sound
      const nestedSound = SOUND_OPTIONS.find(
        (opt) => opt.id === nested.soundId
      );
      if (nestedSound) {
        playClick(audioCtx, masterGainNode, false, nestedSound, 1.0, nestTime);
      }
    }
  }
}

/**
 * The scheduling hook (Alternative Approach):
 * We schedule as many beats as needed in a while-loop,
 * storing each scheduled beat's start/end times, and
 * highlight the currently playing measure/beat on each frame.
 */

export function useLookaheadScheduler(
  isPlaying,
  measures,
  tempo,
  useTempoChanges,
  enableAdditionalTempoChanges,
  tempoChanges,
  muteRandomBeats,
  mutePercentage,
  loopMode,
  masterGain,
  aTuning,
  activeModulationRef,
  setModulationDisplay,
  currentBarRef,
  currentBeatRef,
  setCurrentBar,
  setCurrentBeat,
  globalMeasureCountRef,
  partialOscRefs,
  chordOscRefs,
  audioContextRef,
  masterGainNodeRef,
  activeChordRef,
  loopCountRef,
  muteBeats,
  muteDrones,
  muteTuplets,
  muteSubs,
  stressOn,
  countInQueueRef,
  loopStartMeasure,
  loopEndMeasure,
  modulateOnLoop,
  modLoopInterval,
  modLoopDirection,
  loopPitchOffsetRef,
  subscriptionTier,
  selectedNoteValue,
  muteRandomDrones,
  muteRandomDronePercentage,
  rehearsalLetters,
  loopIncrementalSettings,
  specificLoopCountRef,
  countInMeasures,
  loopCountInEnabled
) {


  // Helper function to create count-in measures
  const createCountInMeasures = useCallback((measureObj, tempo) => {
    if (countInMeasures <= 0) return [];
    
    // Create effective tempo changes array with loop incremental settings
    let effectiveTempoChanges = [...tempoChanges];

    if (loopMode === "specific" && loopIncrementalSettings?.enabled) {
      // Create a proper tempo change from loop incremental settings
      const loopIncrementalChange = {
        type: "incremental",
        scope: loopIncrementalSettings.scope,
        startMeasure: loopStartMeasure,
        endMeasure: loopEndMeasure,
        startTempo: loopIncrementalSettings.startTempo,
        endTempo: loopIncrementalSettings.endTempo,
        stepSize: loopIncrementalSettings.stepSize,
        stepEvery: loopIncrementalSettings.stepEvery,
        localLoopCount: specificLoopCountRef.current
      };
      effectiveTempoChanges.push(loopIncrementalChange);
    }
    
    const measureTempo = computeEffectiveTempo(
      currentBarRef.current,
      0,
      measureObj.numerator,
      tempo,
      useTempoChanges || loopIncrementalSettings?.enabled,
      effectiveTempoChanges,
      loopCountRef
    );

    // Mirror the target measure's per-beat userVolume so the count-in
    // sounds out the same on/off (and partial-volume) pattern the user set.
    // Subdivisions are intentionally flattened to a single main-beat click.
    const sourceBeats = Array.isArray(measureObj.beatSounds) ? measureObj.beatSounds : [];
    const beatVolumes = Array.from({ length: measureObj.numerator }, (_, i) => {
      const v = sourceBeats[i]?.userVolume;
      return typeof v === 'number' ? Math.max(0, Math.min(1, v)) : 1.0;
    });
    const allMuted = beatVolumes.every((v) => v <= 0.05);
    const effectiveVolumes = allMuted
      ? beatVolumes.map(() => 1.0)
      : beatVolumes;

    const virtualMeasures = [];
    for (let i = 0; i < countInMeasures; i++) {
      virtualMeasures.push({
        numerator: measureObj.numerator,
        denominator: measureObj.denominator,
        beatSounds: effectiveVolumes.map((vol) => ({
          id: "countInClick",
          name: "Count-In",
          type: "sine",
          userVolume: vol,
          beatVolume: 1.0,
          dronePitchNote: null,
          subdivisions: [
            { subCount: 1, accentedSubs: [], mutedSubs: [] },
          ],
        })),
        isCountIn: true,
        overrideTempo: measureTempo,
      });
    }
    return virtualMeasures;
  }, [countInMeasures, tempoChanges, loopMode, loopIncrementalSettings, loopStartMeasure, loopEndMeasure, specificLoopCountRef, useTempoChanges, loopCountRef]);
  

  
  // 1) Keep a reference to the old tempo for proportional scaling
  const oldTempoRef = useRef(tempo);

  // 2) Optionally scale all start/end/step tempos whenever the *main* tempo changes
  useEffect(() => {
    if (isPlaying && tempo !== oldTempoRef.current && oldTempoRef.current > 0) {
      const ratio = tempo / oldTempoRef.current;
      tempoChanges.forEach((tc) => {
        if (tc.startTempo != null) {
          tc.startTempo = Math.round(tc.startTempo * ratio);
        }
        if (tc.endTempo != null) {
          tc.endTempo = Math.round(tc.endTempo * ratio);
        }
        if (tc.stepSize != null) {
          tc.stepSize = Math.round(tc.stepSize * ratio);
        }
      });
    }
    oldTempoRef.current = tempo;
  }, [tempo, isPlaying, tempoChanges]);

  // Schedule audio well ahead of the wall clock so main-thread stalls (React
  // renders, GC, tab jank) can't push a click past its start time. A click
  // scheduled late gets clamped to "now" by playClick and lands audibly off
  // the grid — at /8 denominators (double the beat rate) this produced the
  // "every other click sounds different" artifact. 350ms of headroom with a
  // 30ms wakeup keeps clicks queued ~200-350ms early even under heavy jank.
  const scheduleAheadTime = 0.35;
  const schedulerWakeupMs = 30;
  const measureEndTimeRef = useRef(0);
  const schedulerIdRef = useRef(null);
  // True once measureEndTimeRef has been primed for the current playback run.
  // Lets the start/stop effect distinguish "playback just started" from
  // "schedulerLoop identity changed mid-play" (e.g. tempo drag) — re-priming
  // mid-play would rewind the beat clock and double-schedule clicks that are
  // already queued inside the look-ahead window.
  const schedulingPrimedRef = useRef(false);

  // Keep track of all scheduled beats for the child <BeatHighlighter />
  const scheduledBeatsRef = useRef([]);

  let lastBeatSecValue = 0;

  const schedulerLoop = useCallback(() => {

  // Prepare enhanced tempo changes once for this entire scheduling cycle
  let enhancedTempoChanges = [...tempoChanges];

  if (loopMode === "specific" && loopIncrementalSettings?.enabled) {
    // Create a proper tempo change from loop incremental settings
    const loopIncrementalChange = {
      type: "incremental",
      scope: loopIncrementalSettings.scope,
      startMeasure: loopStartMeasure,
      endMeasure: loopEndMeasure,
      startTempo: loopIncrementalSettings.startTempo,
      endTempo: loopIncrementalSettings.endTempo,
      stepSize: loopIncrementalSettings.stepSize,
      stepEvery: loopIncrementalSettings.stepEvery,
      localLoopCount: specificLoopCountRef.current
    };
    enhancedTempoChanges.push(loopIncrementalChange);
  }

  const useEnhancedTempoChanges = useTempoChanges || loopIncrementalSettings?.enabled;

    if (!audioContextRef.current) return;
    const audioCtx = audioContextRef.current;
    const now = audioCtx.currentTime;

    // 1) Clean up old beats from scheduledBeatsRef that finished long ago
    scheduledBeatsRef.current = scheduledBeatsRef.current.filter(
      (b) => b.endTimeSec > now - 0.5
    );
    

    // 2) SCHEDULE NEW BEATS IF WE DON'T HAVE ENOUGH SCHEDULED
    while (measureEndTimeRef.current < now + scheduleAheadTime) {
      // -- Handle any queued COUNT-IN measures first --
      if (countInQueueRef.current.length > 0) {
        const ci = countInQueueRef.current[0];

        // e.g. use a default or override tempo for the count-in
        const barTempo = ci.overrideTempo ?? 60;

        const getCurrentTempoChange = (measureIndex, tempoChanges) => {
          // Find the active tempo change for this measure
          const sortedChanges = [...tempoChanges].sort((a, b) => a.startMeasure - b.startMeasure);
          let activeChange = null;
          
          for (const tc of sortedChanges) {
            if (measureIndex >= tc.startMeasure - 1) {
              if (measureIndex <= tc.endMeasure - 1 || tc.type === "sudden") {
                activeChange = tc;
              }
            }
          }
          return activeChange;
        };
        
        const activeTempoChange = getCurrentTempoChange(currentBarRef.current, tempoChanges);
        const noteValueForThisMeasure = activeTempoChange?.noteValue || selectedNoteValue;
        const noteValueFactor = noteValueForThisMeasure?.factor ?? 1;
        const finalBPM = barTempo * (noteValueFactor * (ci.denominator / 4));

        // Then convert to ms/click
        const beatMs = 60000 / finalBPM;
        const beatSec = beatMs / 1000;

        const measureStartTime = measureEndTimeRef.current;

        // SCHEDULE each count-in beat
        for (let b = 0; b < ci.numerator; b++) {
          const beepTime = measureStartTime + b * beatSec;
          const beatVol = typeof ci.beatSounds?.[b]?.userVolume === 'number'
            ? ci.beatSounds[b].userVolume
            : 1.0;

          // Skip muted beats — tempo still elapses, but no click plays.
          if (beatVol > 0.05) {
            playClick(
              audioCtx,
              masterGainNodeRef.current,
              b === 0,
              { id: "countInClick", userVolume: 1.0, type: "sine" },
              beatVol,
              beepTime
            );
          }

          // We'll store in scheduledBeatsRef for highlight (even muted beats
          // advance the visual highlighter).
          scheduledBeatsRef.current.push({
            measureIndex: -9999, // or any placeholder
            beatIndex: b,
            startTimeSec: beepTime,
            endTimeSec: beepTime + beatSec,
          });
        }

        measureEndTimeRef.current += ci.numerator * beatSec;
        countInQueueRef.current.shift();
        continue;
      }

      // Otherwise, schedule normal measures
      const measureIndex = currentBarRef.current % measures.length;
      const measureObj = measures[measureIndex];

      // 3) Compute effective tempo for this bar
      const scheduledBar = currentBarRef.current;
      const scheduledBeat = currentBeatRef.current;

      // Create effective tempo changes array with loop incremental settings
      let effectiveTempo;

         // For loop specific measures, we need to use the actual measure number, not the global count
         const actualMeasureNumber = loopMode === "specific" ? 
         (measureObj.measureNumber || measureIndex + 1) : 
         globalMeasureCountRef.current;
  

      // Handle loop incremental settings directly
      if (loopMode === "specific" && loopIncrementalSettings.enabled) {
        // Calculate tempo based on loop count
        const currentLoopTempo = loopIncrementalSettings.startTempo + 
          (specificLoopCountRef.current * loopIncrementalSettings.stepSize);
        
        // Apply min/max limits
        if (loopIncrementalSettings.startTempo < loopIncrementalSettings.endTempo) {
          effectiveTempo = Math.min(currentLoopTempo, loopIncrementalSettings.endTempo);
        } else {
          effectiveTempo = Math.max(currentLoopTempo, loopIncrementalSettings.endTempo);
        }
        
        // Still apply other tempo changes on top of the loop incremental tempo
        if (useTempoChanges) {
          effectiveTempo = computeEffectiveTempo(
            scheduledBar,
            scheduledBeat,
            measureObj.numerator,
            effectiveTempo, // Use the loop incremental tempo as the base
            useTempoChanges,
            tempoChanges,
            loopCountRef
          );
        }
      } else {
        // Normal tempo calculation
        effectiveTempo = computeEffectiveTempo(
          scheduledBar,
          scheduledBeat,
          measureObj.numerator,
          tempo, 
          useTempoChanges,
          tempoChanges,
          loopCountRef
        );
      }

      const getCurrentTempoChange = (measureIndex, tempoChanges) => {
        // Find the active tempo change for this measure
        const sortedChanges = [...tempoChanges].sort((a, b) => a.startMeasure - b.startMeasure);
        let activeChange = null;
        
        for (const tc of sortedChanges) {
          if (measureIndex >= tc.startMeasure - 1) {
            if (measureIndex <= tc.endMeasure - 1 || tc.type === "sudden") {
              activeChange = tc;
            }
          }
        }
        return activeChange;
      };
      
      const activeTempoChange = getCurrentTempoChange(scheduledBar, tempoChanges);
      const noteValueForThisMeasure = activeTempoChange?.noteValue || selectedNoteValue;
      const noteValueFactor = noteValueForThisMeasure?.factor ?? 1;

      // 1) 'effectiveTempo' is the BPM of the chosen note (base tempo or modded).
      // 2) measureObj.denominator is your time signature denominator (e.g. 8).
      // 3) noteValueFactor is how many quarter-notes your chosen note is (e.g. 1.5 if dotted quarter).

      const finalBPM =
        effectiveTempo * (noteValueFactor * (measureObj.denominator / 4));

      // Then convert to ms/click
      let beatMs = 60000 / finalBPM;

      // If previous measure had a metric modulation
      if (
        scheduledBeat === 0 &&
        scheduledBar > 0 &&
        measures[(scheduledBar - 1) % measures.length].hasModulation
      ) {
        const prevMeas = measures[(scheduledBar - 1) % measures.length];

        // Add these logs:
        console.log(`Applying modulation at measure ${scheduledBar}:`);
        console.log(
          `From: ${prevMeas.fromSubdivision?.name} (${prevMeas.fromSubdivision?.value})`
        );
        console.log(
          `To: ${prevMeas.toSubdivision?.name} (${prevMeas.toSubdivision?.value})`
        );
        console.log(`Current effective tempo: ${effectiveTempo}`);

        // Save the current effective tempo as our source reference
        // This ensures that if we have multiple modulations in sequence,
        // each one builds on the actual current tempo after previous modulations
        const currentEffectiveTempo = effectiveTempo;

        // If we already have an active modulation, we need to calculate
        // what the effective tempo is after applying the current modulation
        if (activeModulationRef.current.isActive) {
          // Use the same logic as applyModulation but to get tempo instead of ms
          const prevFromVal =
            activeModulationRef.current.fromSubdivision?.value || 4;
          const prevToVal =
            activeModulationRef.current.toSubdivision?.value || 4;
          const prevRatio = prevToVal / prevFromVal;

          // Add this log:
          console.log(`Chaining modulation:`, {
            prevFromVal,
            prevToVal,
            prevRatio,
            currentEffectiveTempo,
          });

          // Apply the effect of current modulation to get the true current tempo
          const modifiedEffectiveTempo =
            activeModulationRef.current.sourceTempoReference *
            (prevFromVal / prevToVal);

          console.log(`Modified effective tempo: ${modifiedEffectiveTempo}`);

          activeModulationRef.current = {
            isActive: true,
            fromSubdivision: prevMeas.fromSubdivision,
            toSubdivision: prevMeas.toSubdivision,
            sourceTempoReference: modifiedEffectiveTempo,
          };
        } else {
          activeModulationRef.current = {
            isActive: true,
            fromSubdivision: prevMeas.fromSubdivision,
            toSubdivision: prevMeas.toSubdivision,
            sourceTempoReference: currentEffectiveTempo,
          };
        }

        setModulationDisplay({
          isActive: true,
          fromName: prevMeas.fromSubdivision.name,
          toName: prevMeas.toSubdivision.name,
        });
      }

      // 4) apply metric modulation if active
      if (activeModulationRef.current.isActive) {
        // Add these logs:
        console.log(`Applying active modulation:`);
        console.log(
          `From: ${activeModulationRef.current.fromSubdivision?.name} (${activeModulationRef.current.fromSubdivision?.value})`
        );
        console.log(
          `To: ${activeModulationRef.current.toSubdivision?.name} (${activeModulationRef.current.toSubdivision?.value})`
        );
        console.log(
          `Source tempo: ${activeModulationRef.current.sourceTempoReference}`
        );

        // Where beatMs is calculated:
        const beforeBeatMs = beatMs;
        beatMs = applyModulation(
          activeModulationRef,
          effectiveTempo,
          measureObj.denominator,
          noteValueFactor
        );
        console.log(`beatMs before: ${beforeBeatMs}, after: ${beatMs}`);
      }

      const beatSec = beatMs / 1000;
      const measureDurationSec = measureObj.numerator * beatSec;

      let thisMeasureStartSec;
      if (currentBeatRef.current === 0) {
        thisMeasureStartSec = measureEndTimeRef.current;
      } else {
        thisMeasureStartSec =
          measureEndTimeRef.current - currentBeatRef.current * lastBeatSecValue;
      }

      const thisBeatStart = measureEndTimeRef.current;

      // Possibly random mutes
      const currentMeasure = globalMeasureCountRef.current;
      const transitionMeasures = 3;
      const startMeasure = 3; // When we start applying muting
      let effectiveMutePercentage = 0;

      if (currentMeasure >= startMeasure) {
        // Calculate transition percentage (0 to 1) over the transition period
        const transitionProgress = Math.min(
          1,
          (currentMeasure - startMeasure) / transitionMeasures
        );

        // Apply the transition to the target mute percentage
        effectiveMutePercentage = mutePercentage * transitionProgress;
      }

      const doPlay =
        !muteRandomBeats ||
        currentMeasure < startMeasure ||
        Math.random() * 100 > effectiveMutePercentage;

      if (scheduledBeat < measureObj.beatSounds.length) {
        // Move beatSound declaration outside the if block
        const isMainAccent = scheduledBeat === 0;
        let beatSound = measureObj.beatSounds[scheduledBeat];
      
                if (!beatSound) {
          // Continue to next iteration or create a default beat
          measureEndTimeRef.current += beatSec;
          currentBeatRef.current++;
          continue; // Skip this beat
        }

        if (!measureObj.onlyTuplets && doPlay) {
          // If "applyBeatOneDroneToAllBeats" is on
          if (
            measureObj.applyBeatOneDroneToAllBeats &&
            measureObj.beatSounds[0]
          ) {
            const firstDrone = measureObj.beatSounds[0];
            beatSound = {
              ...beatSound,
              dronePitchNote: firstDrone.dronePitchNote,
              dronePitchOctave: firstDrone.dronePitchOctave,
              droneVolume: firstDrone.droneVolume,
              hasHarmony: firstDrone.hasHarmony,
              selectedHarmony: firstDrone.selectedHarmony,
              justIntonation: firstDrone.justIntonation,
              temperamentSystem: firstDrone.temperamentSystem,
              jiReferencePitch: firstDrone.jiReferencePitch,
              usePartials: firstDrone.usePartials,
              selectedPartials: [...firstDrone.selectedPartials],
              useQuarterTones: firstDrone.useQuarterTones,
              selectedQuarterTones: [
                ...(firstDrone.selectedQuarterTones || []),
              ],
              quarterToneOctave: firstDrone.quarterToneOctave,
            };
          }

          // Subdivisions
          beatSound.subdivisions.forEach((sub, subIndex) => {
            const subCount = sub.subCount || 1;
            const subInterval = beatSec / subCount;

            for (let s = 0; s < subCount; s++) {
              if (subIndex > 0 && s === 0) continue;
              const beepTime =
                audioCtx.currentTime +
                (thisBeatStart - audioCtx.currentTime) +
                s * subInterval;

              const isMainBeat = subIndex === 0 && s === 0;

              const isAccent =
                sub.accentedSubs.includes(s + 1) ||
                (isMainBeat && isMainAccent);
      
              const isMuted = sub.mutedSubs.includes(s + 1);
              if (isMuted) {
                continue; // skip playing
              }
      
              if (isMainBeat) {
                if (!muteBeats) {
                  playClick(
                    audioCtx,
                    masterGainNodeRef.current,
                    isAccent,
                    beatSound,
                    beatSound.beatVolume,
                    beepTime
                  );
                }
              } else {
                if (!muteSubs) {
                  playClick(
                    audioCtx,
                    masterGainNodeRef.current,
                    isAccent,
                    beatSound,
                    beatSound.beatVolume,
                    beepTime
                  );
                }
              }
            }
          });
        } else if (!measureObj.onlyTuplets) {
          // Fallback case respects both conditions
          if (!muteBeats && doPlay) {
            playClick(
              audioCtx,
              masterGainNodeRef.current,
              scheduledBeat === 0,
              beatSound,
              beatSound.beatVolume || 1.0,
              thisBeatStart
            );
          }
        }
        // If measureObj.onlyTuplets is true AND doPlay is false, we don't play anything
      }
      
      // Drone scheduling
      const beatObj = measureObj.beatSounds[scheduledBeat];
      if (beatObj) {  // ✅ REMOVED "&& doPlay" 
        // Get next beat for sustain decision
        let nextBeatObj = null;
        
        // Check if there's a next beat in this measure
        if (scheduledBeat + 1 < measureObj.numerator) {
          nextBeatObj = measureObj.beatSounds[scheduledBeat + 1];
        } else {
          // Check next measure's first beat if we're at the end of current measure
          let nextMeasureIndex;
          
          // Handle different loop modes properly
          if (loopMode === "specific" && currentBarRef.current >= loopEndMeasure - 1) {
            // If we're at the end of a specific loop, the next measure is the loop start
            nextMeasureIndex = loopStartMeasure - 1;
          } else if (loopMode === "continue" && currentBarRef.current >= measures.length - 1) {
            // If we're in continue mode at the last measure, there's no next measure
            nextMeasureIndex = -1;
          } else {
            // Normal progression or regular loop
            nextMeasureIndex = (currentBarRef.current + 1) % measures.length;
          }
          
          if (nextMeasureIndex >= 0 && nextMeasureIndex < measures.length) {
            const nextMeasure = measures[nextMeasureIndex];
            if (nextMeasure && nextMeasure.beatSounds && nextMeasure.beatSounds.length > 0) {
              nextBeatObj = nextMeasure.beatSounds[0];
            }
          }
        }

        scheduleDroneOrChordChange(
          audioCtx,
          masterGainNodeRef.current,
          partialOscRefs,
          chordOscRefs,
          beatObj,
          thisBeatStart,
          aTuning,
          activeChordRef,
          beatSec,
          measureDurationSec,
          muteDrones,
          loopPitchOffsetRef,
          modulateOnLoop,
          subscriptionTier,
          muteRandomDrones,
          muteRandomDronePercentage,
          globalMeasureCountRef.current,
          modLoopInterval,
          modLoopDirection,
          nextBeatObj,
          currentBeatRef.current,
          measures[measureIndex]
        );
      }

      // Tuplets
      if (doPlay && measureObj.tuplets?.length > 0 && !muteTuplets) {
        measureObj.tuplets.forEach((tup) => {
          if (!tup.selectedBeats || tup.selectedBeats.length === 0) return;
          const minBeat = Math.min(...tup.selectedBeats);
          if (scheduledBeat + 1 === minBeat) {
            const rangeBeats = Math.max(...tup.selectedBeats) - minBeat + 1;
            const totalSec = rangeBeats * beatSec;
            const startSec = thisMeasureStartSec + scheduledBeat * beatSec;
            const firstBeatDrone = measureObj.applyBeatOneDroneToAllBeats
              ? measureObj.beatSounds[0]
              : null;

            scheduleTuplet(
              audioCtx,
              masterGainNodeRef.current,
              partialOscRefs,
              chordOscRefs,
              tup,
              startSec,
              totalSec,
              firstBeatDrone,
              aTuning,
              activeChordRef,
              muteDrones,
              tup.muted,
              muteRandomDrones,
              muteRandomDronePercentage,
              globalMeasureCountRef.current,
              loopPitchOffsetRef,
              modulateOnLoop,
              subscriptionTier,
              modLoopInterval,
            modLoopDirection
            );
          }
        });
      }

      // Collect the scheduled beat for highlight
      scheduledBeatsRef.current.push({
        measureIndex: currentBarRef.current,
        measureNumber: measures[currentBarRef.current].measureNumber,
        beatIndex: currentBeatRef.current,
        startTimeSec: thisBeatStart,
        endTimeSec: thisBeatStart + beatSec,
      });

      measureEndTimeRef.current += beatSec;
      currentBeatRef.current++;

      // If we finished the measure, reset and increment
      if (currentBeatRef.current >= measureObj.numerator) {
        currentBeatRef.current = 0;
        const oldBar = currentBarRef.current;
        currentBarRef.current++;
        globalMeasureCountRef.current++;

        // "specific" incremental
        for (let c = 0; c < tempoChanges.length; c++) {
          const change = tempoChanges[c];
          if (change.type === "incremental" && change.scope === "specific") {
            const barArr = parseBarString(change.specificBars || "");
            if (!barArr.length) continue;
            const minBar = Math.min(...barArr);
            const maxBar = Math.max(...barArr);

            if (oldBar === maxBar && currentBarRef.current === minBar) {
              if (!change.localLoopCount) {
                change.localLoopCount = 0;
              }
              change.localLoopCount++;
            }
          }
        }
        // possibly clamp bar index:
        for (let c = 0; c < tempoChanges.length; c++) {
          const change = tempoChanges[c];
          if (change.type === "incremental" && change.scope === "specific") {
            const barArr = parseBarString(change.specificBars || "");
            if (!barArr.length) continue;
            const minBar = Math.min(...barArr);
            const maxBar = Math.max(...barArr);

            if (currentBarRef.current > maxBar) {
              currentBarRef.current = minBar;
              if (!change.localLoopCount) {
                change.localLoopCount = 0;
              }
              change.localLoopCount++;
            }
          }
        }

        // Normal looping logic
        if (currentBarRef.current >= measures.length && loopMode === "loop") {
          currentBarRef.current = 0;
          // Fully reset the modulation state
          activeModulationRef.current = {
            isActive: false,
            fromSubdivision: null,
            toSubdivision: null,
            sourceTempoReference: tempo,
          };
          setModulationDisplay({ isActive: false, fromName: "", toName: "" });
          activeChordRef.current.signature = "";

          // If incremental tempo changes are "whole" scope, increment counters
          for (let c = 0; c < tempoChanges.length; c++) {
            const change = tempoChanges[c];
            if (change.type === "incremental" && change.scope === "whole") {
              loopCountRef.current++;
            }
          }
          

          // If user wants pitch mod on each loop:
          if (modulateOnLoop && modLoopInterval > 0) {
            const sign = modLoopDirection === "up" ? +1 : -1;
            loopPitchOffsetRef.current += sign * modLoopInterval;
          }
        } else if (
          currentBarRef.current >= measures.length &&
          loopMode === "continue"
        ) {
          currentBarRef.current = measures.length - 1;
        } else if (
          loopMode === "specific" &&
          currentBarRef.current >= loopEndMeasure
        ) {
          currentBarRef.current = loopStartMeasure - 1;

          // Increment specific loop counter when using incremental tempo
          if (loopIncrementalSettings.enabled && loopIncrementalSettings.scope === "specific") {
            specificLoopCountRef.current++;
            console.log("Loop completed, incremental count:", specificLoopCountRef.current); // Debug log
          }

          // Add count-in measures for loop restart if enabled
          // This now uses the UPDATED loop count
          if (loopCountInEnabled && countInMeasures > 0) {
            const measureObj = measures[loopStartMeasure - 1] || measures[0];
            const virtualMeasures = createCountInMeasures(measureObj, tempo);
            countInQueueRef.current = virtualMeasures;
          }
        
        // Fully reset the modulation state on specific loop too
        activeModulationRef.current = {
          isActive: false,
          fromSubdivision: null,
          toSubdivision: null,
          sourceTempoReference: tempo,
        };
        setModulationDisplay({ isActive: false, fromName: "", toName: "" });
      }
      }

      lastBeatSecValue = beatSec;
    }

    // 7) Loop again
    schedulerIdRef.current = window.setTimeout(schedulerLoop, schedulerWakeupMs);
  }, [
    isPlaying,
    measures,
    tempo,
    useTempoChanges,
    enableAdditionalTempoChanges,
    tempoChanges,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    aTuning,
    currentBarRef,
    currentBeatRef,
    setCurrentBar,
    setCurrentBeat,
    globalMeasureCountRef,
    partialOscRefs,
    chordOscRefs,
    audioContextRef,
    masterGainNodeRef,
    activeModulationRef,
    setModulationDisplay,
    activeChordRef,
    muteBeats,
    muteDrones,
    muteTuplets,
    muteSubs,
    stressOn,
    countInQueueRef,
    loopStartMeasure,
    loopEndMeasure,
    loopCountRef,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    loopPitchOffsetRef,
    selectedNoteValue,
    muteRandomDrones,
    muteRandomDronePercentage,
    rehearsalLetters
  ]);

  // Smoothly update the master gain
  useEffect(() => {
    if (audioContextRef.current && masterGainNodeRef.current) {
      const now = audioContextRef.current.currentTime;
      masterGainNodeRef.current.gain.cancelScheduledValues(now);
      masterGainNodeRef.current.gain.setValueAtTime(
        masterGainNodeRef.current.gain.value,
        now
      );
      masterGainNodeRef.current.gain.linearRampToValueAtTime(
        masterGain,
        now + 0.1
      );
    }
  }, [masterGain, audioContextRef, masterGainNodeRef]);

  // Start/stop scheduling
  useEffect(() => {
    if (!isPlaying) {
      // Reset pitch offset
      loopPitchOffsetRef.current = 0;
      schedulingPrimedRef.current = false;
      // Cancel the scheduler
      if (schedulerIdRef.current) {
        clearTimeout(schedulerIdRef.current);
        schedulerIdRef.current = null;
      }
      return;
    }

    // Prime the beat clock only when playback actually starts. This effect
    // also re-runs when schedulerLoop's identity changes mid-play (tempo
    // drag, etc.) — re-priming then would rewind measureEndTimeRef behind
    // clicks already scheduled inside the look-ahead window and play them
    // twice.
    if (!schedulingPrimedRef.current && audioContextRef.current) {
      // Add a pre-scheduling buffer time to ensure first measure is accurate
      // This ensures we have enough time to schedule all beats properly
      const bufferTime = 0.2; // 200ms buffer before first beat
      measureEndTimeRef.current =
        audioContextRef.current.currentTime + bufferTime;
      schedulingPrimedRef.current = true;
    }

    schedulerIdRef.current = window.setTimeout(schedulerLoop, schedulerWakeupMs);

    return () => {
      if (schedulerIdRef.current) {
        clearTimeout(schedulerIdRef.current);
        schedulerIdRef.current = null;
      }

      import('./utils/samplerManager').then(({ disposeSamplers }) => {
        disposeSamplers();
      });
    };
  }, [isPlaying, schedulerLoop, audioContextRef, loopPitchOffsetRef]);

  return { measureEndTimeRef, schedulerIdRef, scheduledBeatsRef };
}


/** 3) Main Component */
export default function MetronomeComponent() {

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Add this useEffect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tab switch state
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedId = urlParams.get("shared");
    const tabParam = urlParams.get("tab");
    
    // If there's a shared pattern, go directly to metronome
    if (sharedId) {
      return "metronome";
    }
    
    // If there's a specific tab requested, use that
    const validTabs = [
      "metronome",
      "library",
      "about",
      "practiceTracker",
      "practiceRoom",
      "community",
      "scores",
      "orgAdmin",
    ];
    
    if (tabParam && validTabs.includes(tabParam)) {
      return tabParam;
    }

    // Default to landing page for regular visits
    return "landing";
  });

  // Pending notification deep-link target (cold-open via URL or warm SW message).
  // Consumed by PracticeTrackerTab/PerformView, then cleared.
  const [pendingPerfNav, setPendingPerfNav] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    if (!id) return null;
    return {
      performanceId: id,
      showReflection: urlParams.get('showReflection') === 'true',
    };
  });
  const clearPendingPerfNav = useCallback(() => setPendingPerfNav(null), []);

  // Strip notification deep-link params from the URL on mount. The values
  // have already been read into state; the rest of the app is URL-less, so
  // a lingering ?tab=...&id=... in the address bar feels out of place.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('id')) return;
    ['tab', 'mode', 'id', 'showReflection'].forEach(p => url.searchParams.delete(p));
    window.history.replaceState({}, document.title, url.pathname + (url.search || ''));
  }, []);

  const [showFullPatternModal, setShowFullPatternModal] = useState(false);
  const [unreadFlowTrackerCount, setUnreadFlowTrackerCount] = useState(0);

  // Global tuner state (persists across tab changes)
  const [showTuner, setShowTuner] = useState(false);
  const openTuner = useCallback(() => setShowTuner(true), []);
  const closeTuner = useCallback(() => setShowTuner(false), []);

  // Global score viewer state (persists across tab changes)
  const [globalScoreViewer, setGlobalScoreViewer] = useState(null);
  // { fileUrl, title, scoreId, userId, initialPage, minimized, pageNumber, numPages }
  const globalScoreViewerRef = useRef(null);
  globalScoreViewerRef.current = globalScoreViewer;

  const openGlobalScoreViewer = useCallback((data) => {
    setGlobalScoreViewer({ ...data, minimized: false });
  }, []);

  const minimizeGlobalScoreViewer = useCallback((viewerState) => {
    setGlobalScoreViewer((prev) => prev ? {
      ...prev,
      minimized: true,
      pageNumber: viewerState?.pageNumber || prev.pageNumber,
      numPages: viewerState?.numPages || prev.numPages,
    } : null);
  }, []);

  const maximizeGlobalScoreViewer = useCallback(() => {
    setGlobalScoreViewer((prev) => prev ? { ...prev, minimized: false } : null);
  }, []);

  const closeGlobalScoreViewer = useCallback(() => {
    if (globalScoreViewerRef.current?.fileUrl) {
      try { URL.revokeObjectURL(globalScoreViewerRef.current.fileUrl); } catch {}
    }
    setGlobalScoreViewer(null);
  }, []);

  const handleGlobalScorePageChange = useCallback((page) => {
    setGlobalScoreViewer((prev) => prev ? { ...prev, pageNumber: page } : null);
  }, []);

  // Add this function near the top of your MetronomeComponent
  const loadPatternInPWA = async (patternId) => {
    try {
      console.log("[PWA Debug] Attempting to load pattern in PWA context:", patternId);
      
      // Re-initialize the Firebase app explicitly for PWA context
      const db = getFirestore();
      const docRef = doc(db, "sharedPatterns", patternId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.error("[PWA Debug] Document not found in PWA context");
        alert("Pattern not found in PWA context. Please try opening in browser.");
        return false;
      }
      
      // Get the data and process it
      const data = docSnap.data();
      console.log("[PWA Debug] Got data in PWA context:", data);
      
      // Extract the pattern data
      const patternData = data.patternData;
      if (!patternData) {
        throw new Error("No pattern data in document");
      }
      
      // Load the pattern
      clearQuickSubRulesWithoutApplying();
      setPatternName(patternData.name || "Shared Pattern");
      setMeasures(patternData.measures || []);
      setTempo(patternData.tempo || 120);
      setUseTempoChanges(patternData.useTempoChanges || false);
      setEnableAdditionalTempoChanges(patternData.enableAdditionalTempoChanges || false);
      setTempoChanges(patternData.tempoChanges || []);
      setATuning(patternData.aTuning || 440);
      setMuteRandomBeats(patternData.muteRandomBeats || false);
      setMutePercentage(patternData.mutePercentage || 0);
      setLoopMode(patternData.loopMode || INITIAL_LOOP_MODE);
      setStartMeasureNumber(patternData.startMeasureNumber || 1);
      setStressOn(patternData.stressOn || false);
      setRehearsalLetters(patternData.rehearsalLetters || {});
      setTimeSignatureColors(patternData.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS);
      setModulateOnLoop(patternData.modulateOnLoop || false);
      setModLoopInterval(patternData.modLoopInterval || 0);
      setModLoopDirection(patternData.modLoopDirection || "up");

      specificLoopCountRef.current = 0;

    // Reset loop incremental settings if not in the pattern data
    if (!patternData.loopIncrementalSettings) {
      setLoopIncrementalSettings({
        enabled: false,
        scope: "specific",
        stepSize: 5,
        startTempo: patternData.tempo || 120,
        endTempo: (patternData.tempo || 120) + 60,
        stepEvery: 1,
        useStepEvery: false
      });
    }
      
      if (patternData.selectedNoteValue) {
        setSelectedNoteValue(patternData.selectedNoteValue);
      }

      // Set a default folder for shared patterns
      setFolderName("Shared");
      
      // Switch to metronome tab
      setActiveTab("metronome");
      
      return true;
    } catch (error) {
      console.error("[PWA Debug] PWA pattern loading error:", error);
      alert("Error loading pattern in PWA: " + error.message);
      return false;
    }
  };

  // Add global recording state
  // Feature flags read once per mount. Overrides via window.__FF_FLAGS__ or
  // localStorage 'ff.featureFlags.v1' take effect on next hard reload — good
  // enough for a kill-switch and safer than resolving on every render.
  const [featureFlags] = useState(() => getFeatureFlags());

  const [globalRecordingService] = useState(() => new RecordingService());
  const [globalRecordingState, setGlobalRecordingState] = useState({
    isRecording: false,
    recordingItemId: null,
    recordingTime: 0,
    isProcessing: false,
    mediaType: 'audio' // 'audio' | 'video'
  });

  // Upload queue — tracks streaming uploads across the whole app so the
  // UploadQueuePill + per-recording status chips render a consistent picture.
  // Entries are keyed by recordingId.
  const [uploadQueue, setUploadQueue] = useState({});

  const patchUploadQueueEntry = (recordingId, patch) => {
    setUploadQueue((prev) => {
      const existing = prev[recordingId] || { recordingId };
      return {
        ...prev,
        [recordingId]: typeof patch === 'function' ? patch(existing) : { ...existing, ...patch }
      };
    });
  };

  const removeUploadQueueEntry = (recordingId) => {
    setUploadQueue((prev) => {
      if (!prev[recordingId]) return prev;
      const next = { ...prev };
      delete next[recordingId];
      return next;
    });
  };

  const retryUpload = async (recordingId) => {
    patchUploadQueueEntry(recordingId, { status: 'uploading', errorMessage: null });
    try {
      const meta = await resumeStreamingUpload(recordingId);
      if (!meta) {
        patchUploadQueueEntry(recordingId, { status: 'failed', errorMessage: 'Recording could not be resumed.' });
        return;
      }
      patchUploadQueueEntry(recordingId, { status: 'processing', progress: 100 });
    } catch (e) {
      patchUploadQueueEntry(recordingId, { status: 'failed', errorMessage: e?.message || 'Retry failed' });
    }
  };

  const dismissUpload = async (recordingId) => {
    try { await deleteUpload(recordingId); } catch (e) { /* noop */ }
    removeUploadQueueEntry(recordingId);
  };

  // Add recording timer effect
  useEffect(() => {
    let interval = null;
    if (globalRecordingState.isRecording) {
      interval = setInterval(() => {
        setGlobalRecordingState(prev => ({
          ...prev,
          recordingTime: prev.recordingTime + 1
        }));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [globalRecordingState.isRecording]);

  // Ensure camera (and mic) are released if the user navigates away or hides
  // the tab mid-recording. For video especially, a dangling track leaves the
  // camera indicator on, which is jarring. The streaming uploader's chunks
  // already on the server stay there; the IDB record survives so the next
  // visit can resume (or write the manifest if everything uploaded).
  useEffect(() => {
    const teardown = () => {
      try {
        if (globalRecordingService && typeof globalRecordingService._forceReleaseStreams === 'function') {
          globalRecordingService._forceReleaseStreams();
        }
      } catch (e) { /* noop */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' &&
          globalRecordingState.isRecording &&
          globalRecordingState.mediaType === 'video') {
        teardown();
      }
    };
    window.addEventListener('beforeunload', teardown);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', teardown);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [globalRecordingState.isRecording, globalRecordingState.mediaType, globalRecordingService]);

  // Upload-queue effects that depend on currentUser live further down, after
  // currentUser is declared (see useEffects near the auth/user setup). They
  // can't live here because `currentUser` is in the temporal dead zone at
  // this point of the component body.

  // Add global recording handlers.
  //
  // Audio uses the legacy blob-upload path (record locally, upload at session
  // finalize to `recordings/{uid}/{sessionId}/...` which has a public-read
  // Storage rule). This bypasses the assembleRecording/transcodeMedia CFs so
  // audio keeps working regardless of function IAM state.
  //
  // Video uses the streaming path (chunks during capture + assembly CF +
  // transcodeMedia CF). If the CF Firestore writes fail, the VideoPlayer's
  // raw-path fallback still lets the user play the file — the only user-
  // visible consequence is the pill staying at "Processing" until the doc
  // updates. That's a separate IAM fix at the Cloud level.
  const handleStartPracticeRecording = async (itemId, audioSettings = null, mode = 'audio') => {
    try {
      console.log('Starting practice recording for item:', itemId, 'mode:', mode);

      if (!currentUser?.uid) {
        alert('You need to be signed in to record.');
        return;
      }

      // Feature-flag guards. These mirror the UnifiedRecorder UI gating
      // but also defend the handler in case the UI is bypassed (stale
      // component, keyboard shortcut, etc.).
      if (!featureFlags.allowAudioRecording && !featureFlags.allowVideoRecording) {
        alert('Recording is temporarily disabled.');
        return;
      }
      if (mode === 'video' && !featureFlags.allowVideoRecording) {
        if (!featureFlags.allowAudioRecording) {
          alert('Recording is temporarily disabled.');
          return;
        }
        console.warn('Video recording disabled by feature flag; falling back to audio.');
        return handleStartPracticeRecording(itemId, audioSettings, 'audio');
      }
      if (mode === 'audio' && !featureFlags.allowAudioRecording) {
        alert('Audio recording is temporarily disabled.');
        return;
      }

      const isVideoMode = mode === 'video';
      setGlobalRecordingState(prev => ({ ...prev, isProcessing: true, mediaType: isVideoMode ? 'video' : 'audio' }));

      if (!isVideoMode) {
        // ----- Legacy audio path -----
        const legacyOptions = { recordingMode: 'music' };
        if (audioSettings) {
          legacyOptions.qualitySettings = {
            sampleRate: audioSettings.quality.sampleRate,
            channels: audioSettings.quality.channels,
            bitrate: audioSettings.quality.bitrate
          };
          if (audioSettings.device && audioSettings.device.deviceId) {
            legacyOptions.deviceId = audioSettings.device.deviceId;
          }
          if (audioSettings.device) {
            const gain = audioSettings.device.gain;
            const targetLevel = audioSettings.device.targetLevel;
            if (gain !== undefined || targetLevel !== undefined) {
              legacyOptions.gainSettings = {
                gain: gain !== undefined ? gain : 1.0,
                targetLevel: targetLevel !== undefined ? targetLevel : 0.6
              };
            }
          }
        }
        legacyOptions.onMaxDurationReached = () => {
          handleStopPracticeRecording().catch(() => {});
        };
        await globalRecordingService.startRecording(legacyOptions);
        setGlobalRecordingState({
          isRecording: true,
          recordingItemId: itemId,
          recordingTime: 0,
          isProcessing: false,
          mediaType: 'audio',
          isStreaming: false
        });
        console.log(`Legacy audio recording started for item: ${itemId}`);
        return;
      }

      // ----- Video path -----
      // Browsers that can emit WebM from MediaRecorder use the streaming
      // pipeline (chunked uploads during capture + assembly CF). iOS/WebKit
      // can only emit MP4, which doesn't concatenate, so it falls back to
      // the same legacy single-blob upload that audio uses. The
      // `allowStreamingVideo` flag also forces everyone onto the legacy
      // path if the CF pipeline is misbehaving.
      const useStreaming = canStreamWebmVideo() && !!featureFlags.allowStreamingVideo;

      if (!useStreaming) {
        // Legacy video path (single blob, uploaded at session finalize).
        const legacyVideoOptions = {
          mode: 'video',
          recordingMode: 'music',
          onMaxDurationReached: () => {
            handleStopPracticeRecording().catch(() => {});
          }
        };
        try {
          await globalRecordingService.startRecording(legacyVideoOptions);
        } catch (error) {
          if (error?.name === 'NotAllowedError' ||
              error?.name === 'NotFoundError' ||
              error?.name === 'OverconstrainedError') {
            console.warn('Video capture unavailable, falling back to audio:', error?.name);
            alert('Camera unavailable — falling back to audio recording.');
            setGlobalRecordingState(prev => ({ ...prev, isProcessing: false }));
            return handleStartPracticeRecording(itemId, audioSettings, 'audio');
          }
          throw error;
        }
        setGlobalRecordingState({
          isRecording: true,
          recordingItemId: itemId,
          recordingTime: 0,
          isProcessing: false,
          mediaType: 'video',
          isStreaming: false
        });
        console.log(`Legacy video recording started for item: ${itemId}`);
        return;
      }

      // Streaming video path
      const recordingId = `recording_${Date.now()}`;
      const sessionId = `session_${Date.now()}`; // placeholder — overwritten at finalize

      const recordingOptions = {
        mode: 'video',
        recordingMode: 'music',
        userId: currentUser.uid,
        recordingId,
        itemId,
        sessionId,
        ownerType: 'session',
        onMaxDurationReached: () => {
          handleStopPracticeRecording().catch(() => {});
        },
        onStatus: (evt) => {
          patchUploadQueueEntry(evt.recordingId, {
            recordingId: evt.recordingId,
            itemId: evt.itemId,
            sessionId: evt.sessionId,
            mediaType: evt.mediaType,
            status: evt.status,
            progress: evt.progress || 0,
            bytesUploaded: evt.bytesUploaded || 0,
            totalBytes: evt.totalBytes || 0,
            errorMessage: evt.errorMessage || null,
            updatedAt: Date.now()
          });
        }
      };

      try {
        await globalRecordingService.startStreamingRecording(recordingOptions);
      } catch (error) {
        if (error?.name === 'NotAllowedError' ||
            error?.name === 'NotFoundError' ||
            error?.name === 'OverconstrainedError') {
          console.warn('Video capture unavailable, falling back to audio:', error?.name);
          alert('Camera unavailable — falling back to audio recording.');
          setGlobalRecordingState(prev => ({ ...prev, isProcessing: false }));
          return handleStartPracticeRecording(itemId, audioSettings, 'audio');
        }
        throw error;
      }

      setGlobalRecordingState({
        isRecording: true,
        recordingItemId: itemId,
        recordingTime: 0,
        isProcessing: false,
        mediaType: 'video',
        isStreaming: true
      });
      console.log(`Streaming video recording started for item: ${itemId} (${recordingId})`);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setGlobalRecordingState(prev => ({ ...prev, isProcessing: false }));
      alert(`Failed to start recording: ${error.message}`);
    }
  };

  // handleStopPracticeRecording — dispatches based on which recorder is
  // active. Streaming uploads (WebM video) finalize via the uploader. Legacy
  // recordings (audio everywhere, video on iOS) resolve a single Blob that
  // will be uploaded at session finalize.
  const handleStopPracticeRecording = async () => {
    const prevItemId = globalRecordingState.recordingItemId;
    const prevMediaType = globalRecordingState.mediaType || 'audio';
    const prevIsStreaming = !!globalRecordingState.isStreaming;
    try {
      setGlobalRecordingState(prev => ({ ...prev, isProcessing: true }));

      let metadata = null;
      if (prevIsStreaming) {
        metadata = await globalRecordingService.stopStreamingRecording();
      } else {
        const blob = await globalRecordingService.stopRecording();
        if (blob && prevItemId) {
          metadata = globalRecordingService.saveRecordingLocally(
            blob,
            prevItemId,
            `session_${Date.now()}`,
            { mediaType: prevMediaType }
          );
        }
      }

      if (metadata && prevItemId) {
        console.log('Recording finalized:', metadata);
        setGlobalCompletedRecordings(prev => ({
          ...prev,
          [prevItemId]: metadata
        }));
      }

      setGlobalRecordingState({
        isRecording: false,
        recordingItemId: null,
        recordingTime: 0,
        isProcessing: false,
        mediaType: 'audio',
        isStreaming: false
      });
    } catch (error) {
      console.error('Failed to stop recording:', error);
      setGlobalRecordingState(prev => ({
        ...prev,
        isProcessing: false,
        isRecording: false,
        recordingItemId: null,
        recordingTime: 0,
        mediaType: 'audio',
        isStreaming: false
      }));
      alert(`Failed to save recording: ${error.message}`);
    }
  };

  // Update the renderGlobalRecordingIndicator function:
  const renderGlobalRecordingIndicator = () => {
    if (!globalRecordingState.isRecording) return null;

    const formatRecordingTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isVideo = globalRecordingState.mediaType === 'video';
    return (
      <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2">
        <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
        <span className="font-medium">
          {isVideo ? '🎥 Recording Video' : 'Recording Practice Item'}
        </span>
        <span className="font-mono">
          {formatRecordingTime(globalRecordingState.recordingTime)}
        </span>
        <button
          onClick={handleStopPracticeRecording}
          className="ml-2 px-2 py-1 bg-white text-red-500 rounded text-sm hover:bg-gray-100"
          disabled={globalRecordingState.isProcessing}
        >
          {globalRecordingState.isProcessing ? 'Stopping...' : 'Stop'}
        </button>
      </div>
    );
  };

  const [globalCompletedRecordings, setGlobalCompletedRecordings] = useState({});

  // Update the handleStopRecording function:
  const handleStopRecording = async () => {
    try {
      setGlobalRecordingState(prev => ({ ...prev, isProcessing: true }));
      const blob = await globalRecordingService.stopRecording();
      
      if (blob && globalRecordingState.recordingItemId) {
        const metadata = globalRecordingService.saveRecordingLocally(
          blob, 
          globalRecordingState.recordingItemId, 
          `session_${Date.now()}`
        );
        
        console.log('Recording completed and saved locally:', metadata);
        
        // Store the completed recording for the practice tracker
        setGlobalCompletedRecordings(prev => ({
          ...prev,
          [globalRecordingState.recordingItemId]: metadata
        }));
      }
      
      setGlobalRecordingState({
        isRecording: false,
        recordingItemId: null,
        recordingTime: 0,
        isProcessing: false
      });
    } catch (error) {
      console.error('Failed to stop recording:', error);
      setGlobalRecordingState(prev => ({ 
        ...prev, 
        isProcessing: false,
        isRecording: false,
        recordingItemId: null,
        recordingTime: 0
      }));
      alert(`Failed to save recording: ${error.message}`);
    }
  };

  // Add function to delete global recordings
  const handleDeleteGlobalRecording = (itemId, recordingId) => {
    const recording = globalCompletedRecordings[itemId];
    if (recording) {
      globalRecordingService.deleteLocalRecording(recording);
      setGlobalCompletedRecordings(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    }
  };

  const APP_DRONE_SOUND_OPTIONS = DRONE_SOUND_OPTIONS.map(category => {
    if (category.category === "Instruments") {
      return {
        ...category,
        sounds: category.sounds.filter(sound => sound.id !== 'tanpura')
      };
    }
    return category;
  });

  const [overviewScrollPosition, setOverviewScrollPosition] = useState(0);
  const [detailScrollPosition, setDetailScrollPosition] = useState(0);
  const overviewScrollRef = useRef(null);
  const detailScrollRef = useRef(null);

  // Add scroll functions
  const scrollOverview = (direction) => {
    const container = overviewScrollRef.current;
    if (!container) return;
    
    const scrollAmount = direction === 'left' ? -400 : 400;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const scrollDetail = (direction) => {
    const container = detailScrollRef.current;
    if (!container) return;
    
    const scrollAmount = direction === 'left' ? -600 : 600;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const [tempoChangesScrollPosition, setTempoChangesScrollPosition] = useState(0);
  const tempoChangesScrollRef = useRef(null);

  // Add scroll function for tempo changes
  const scrollTempoChanges = (direction) => {
    const container = tempoChangesScrollRef.current;
    if (!container) return;
    
    const cardWidth = isMobileView ? 280 : 400;
    const gap = 32; // gap-8 = 32px
    const totalCardWidth = cardWidth + gap;
    const containerWidth = container.clientWidth;
    
    // Calculate which tempo change is currently most centered
    const currentScrollLeft = container.scrollLeft;
    const currentCenterPosition = currentScrollLeft + (containerWidth / 2);
    const currentCardIndex = Math.round(currentCenterPosition / totalCardWidth);
    
    // Calculate the target card index based on direction
    let targetCardIndex;
    if (direction === 'left') {
      targetCardIndex = Math.max(0, currentCardIndex - 1);
    } else {
      targetCardIndex = Math.min(tempoChanges.length - 1, currentCardIndex + 1);
    }
    
    // Calculate the scroll position to center the target card
    const targetCardPosition = targetCardIndex * totalCardWidth;
    const targetScrollPosition = targetCardPosition - (containerWidth / 2) + (cardWidth / 2);
    
    // Ensure we don't scroll past the boundaries
    const maxScrollLeft = container.scrollWidth - containerWidth;
    const finalScrollPosition = Math.max(0, Math.min(targetScrollPosition, maxScrollLeft));
    
    container.scrollTo({
      left: finalScrollPosition,
      behavior: 'smooth'
    });
  };


// Handler to receive practice session updates from PracticeTrackerTab
const handlePracticeSessionUpdate = useCallback((sessionActive, sessionPaused, practiceItems, now) => {
  setPracticeSessionData({
    sessionActive,
    sessionPaused,
    practiceItems: practiceItems || [],
    now: now || Date.now()
  });
}, []);


  // Add practice alarm state and logic (GLOBAL - works across all tabs)
  const [alarmSettings, setAlarmSettings] = useState(() => {
    try {
      const saved = storageManager.getUserItem('practiceAlarmSettings');
      return saved ? JSON.parse(saved) : { enabled: true, chimeVolume: 0.5 };
    } catch (error) {
      return { enabled: true, chimeVolume: 0.5 };
    }
  });

    // Add practice session monitoring state
const [practiceSessionData, setPracticeSessionData] = useState({
  sessionActive: false,
  sessionPaused: false,
  practiceItems: [],
  now: Date.now()
});

const [globalAlarmedItems, setGlobalAlarmedItems] = useState(new Set());

  // Enhanced createChimeSound that better integrates with the metronome
  const createChimeSound = useCallback(() => {
    if (!alarmSettings.enabled) return;
    
    try {
      // Use the metronome's audio context if available, otherwise create a new one
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      
      // Use the metronome's master gain if available, otherwise connect directly to destination
      const destination = masterGainNodeRef.current || audioContext.destination;
      
      // Resume audio context if it's suspended (common on mobile)
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          playChime(audioContext, destination);
        });
      } else {
        playChime(audioContext, destination);
      }
    } catch (error) {
      console.warn('Could not create chime sound:', error);
      // Fallback to vibration on mobile
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
      }
    }
  }, [alarmSettings.enabled, alarmSettings.chimeVolume]);

  // Helper function to get display time for practice items (moved from PracticeTrackerTab)
const getDisplayTimeMs = useCallback((item, currentNow) => {
  if (item.isRunning && item.startTime != null && currentNow) {
    const runningTime = currentNow - item.startTime;
    return (item.actualElapsedMs || 0) + runningTime;
  }
  return item.actualElapsedMs || 0;
}, []);

const triggerAlarm = useCallback((itemTitle) => {
  console.log(`🔔 GLOBAL ALARM: Triggered for item "${itemTitle}"`);
  
  // Try to play sound - call it directly without dependency
  if (alarmSettings.enabled) {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      const createTone = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(frequency, startTime);
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(alarmSettings.chimeVolume * 0.3, startTime + 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const currentTime = audioContext.currentTime;
      createTone(523.25, currentTime, 0.8); // C5
      createTone(659.25, currentTime + 0.2, 0.8); // E5
      createTone(783.99, currentTime + 0.4, 0.8); // G5
      
    } catch (error) {
      console.warn('Could not create chime sound:', error);
    }
  }
  
  // Vibration fallback for mobile
  if ('vibrate' in navigator) {
    navigator.vibrate([200, 100, 200]);
  }
  // Browser notification as backup (temporarily disabled for mobile compatibility)
  // TODO: Implement proper service worker notifications
  console.log(`Practice alarm triggered for: ${itemTitle}`);

  // Browser notification as backup
  // if ('Notification' in window && Notification.permission === 'granted') {
  //  new Notification(`Practice Timer: ${itemTitle}`, {
  //    body: `You've reached your projected practice time!`,
  //    icon: '/favicon.ico',
  //    badge: '/favicon.ico',
  //    tag: 'practice-alarm',
  //    requireInteraction: true
  //  });
  // }
}, [alarmSettings.enabled, alarmSettings.chimeVolume]); // Remove createChimeSound from deps


  // Enhanced playChime function that uses the proper audio destination
  const playChime = (audioContext, destination) => {
    const createTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(destination);
      
      oscillator.frequency.setValueAtTime(frequency, startTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(alarmSettings.chimeVolume * 0.3, startTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const currentTime = audioContext.currentTime;
    createTone(523.25, currentTime, 0.8); // C5
    createTone(659.25, currentTime + 0.2, 0.8); // E5
    createTone(783.99, currentTime + 0.4, 0.8); // G5
  };


  // Handlers for practice session state (to be passed to PracticeTrackerTab)
  const handlePracticeSessionChange = useCallback((isActive, isPaused, items, currentNow) => {
    setPracticeSessionActive(isActive);
    setPracticeSessionPaused(isPaused);
    setPracticeItems(items || []);
    if (currentNow) setPracticeNow(currentNow);
  }, []);

  const handlePracticeProjectionChange = useCallback((itemId) => {
    setGlobalAlarmedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);

  // Add URL parameter handling to detect and switch tabs on load
  useEffect(() => {
    // Check for URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get("tab");
    const sharedId = urlParams.get("shared");
    const validTabs = [
      "metronome",
      "library",
      "about",
      "practiceTracker",
      "practiceRoom",
      "community",
      "scores",
      "orgAdmin",
    ];

    // Handle join invite code
    const joinCode = urlParams.get("join");
    if (joinCode) {
      setJoinInviteCode(joinCode);
      setActiveTab("joinOrg");
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Handle shared pattern if present
      // Handle shared pattern if present
      if (sharedId) {
      const loadSharedPattern = async () => {
        try {
          console.log("Loading shared pattern with ID:", sharedId);
          const response = await getSharedPattern(sharedId);
          console.log("Full response from getSharedPattern:", response);
          
          // Try to get pattern data from either location
          let patternData = response.patternData;
          if (!patternData) {
            // If not in patternData, the response itself might be the pattern data
            patternData = response;
          }
          
          console.log("Pattern data to be used:", patternData);
          
          if (!patternData || !patternData.measures) {
            throw new Error(`Invalid pattern data structure. Data received: ${JSON.stringify(patternData)}`);
          }
          
          // Load the pattern data into the editor
          clearQuickSubRulesWithoutApplying();
          setPatternName(patternData.name || "Shared Pattern");
          setMeasures(patternData.measures || []);
          setTempo(patternData.tempo || 120);
          setUseTempoChanges(patternData.useTempoChanges || false);
          setEnableAdditionalTempoChanges(patternData.enableAdditionalTempoChanges || false);
          setTempoChanges(patternData.tempoChanges || []);
          setATuning(patternData.aTuning || 440);
          setMuteRandomBeats(patternData.muteRandomBeats || false);
          setMutePercentage(patternData.mutePercentage || 0);
          setLoopMode(patternData.loopMode || "none");
          setStartMeasureNumber(patternData.startMeasureNumber || 1);
          setStressOn(patternData.stressOn || false);
          setRehearsalLetters(patternData.rehearsalLetters || {});
          setTimeSignatureColors(patternData.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS);
          setModulateOnLoop(patternData.modulateOnLoop || false);
          setModLoopInterval(patternData.modLoopInterval || 0);
          setModLoopDirection(patternData.modLoopDirection || "up");
          
          if (patternData.selectedNoteValue) {
            setSelectedNoteValue(patternData.selectedNoteValue);
          }

          // Set a default folder for shared patterns
          setFolderName("Shared");
          
          // Switch to metronome tab
          setActiveTab("metronome");
          
          // Clean up the URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error("Error loading shared pattern:", error);
          alert(`Failed to load shared pattern: ${error.message}`);
          
          // If pattern loading fails, still handle tab switching if a valid tab was specified
          if (tabParam && validTabs.includes(tabParam)) {
            setActiveTab(tabParam);
          }
        }
      };

      loadSharedPattern();
    }
    // If no shared pattern, handle tab switching
    else if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  // Global alarm checking (runs regardless of active tab)
useEffect(() => {
  if (!practiceSessionData.sessionActive || practiceSessionData.sessionPaused || !alarmSettings.enabled) return;

  const checkAlarms = () => {
    practiceSessionData.practiceItems.forEach(item => {
      if (!item.isRunning || !item.projectedMinutes || item.projectedMinutes <= 0) return;
      
      const currentTimeMs = getDisplayTimeMs(item, practiceSessionData.now);
      const projectedTimeMs = item.projectedMinutes * 60 * 1000;
      
      if (currentTimeMs >= projectedTimeMs && !globalAlarmedItems.has(item.id)) {
        triggerAlarm(item.title);
        setGlobalAlarmedItems(prev => new Set([...prev, item.id]));
      }
    });
  };

  checkAlarms();
}, [practiceSessionData, alarmSettings.enabled, globalAlarmedItems, triggerAlarm, getDisplayTimeMs]);

// Update practice timer globally every second
useEffect(() => {
  let interval;
  if (practiceSessionData.sessionActive && !practiceSessionData.sessionPaused) {
    interval = setInterval(() => {
      setPracticeSessionData(prev => ({
        ...prev,
        now: Date.now()
      }));
    }, 1000);
  }
  return () => {
    if (interval) clearInterval(interval);
  };
}, [practiceSessionData.sessionActive, practiceSessionData.sessionPaused]);

// Reset alarmed items when no timers are running
useEffect(() => {
  const runningItems = practiceSessionData.practiceItems.filter(item => item.isRunning);
  if (runningItems.length === 0) {
    setGlobalAlarmedItems(new Set());
  }
}, [practiceSessionData.practiceItems]);

  // Replace your PWA linking handler with this
  useEffect(() => {
    // Check if we're in PWA mode
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    console.log("[PWA Debug] Running in PWA mode:", isPWA);
    
    // Initial URL check on load
    const checkUrlForPattern = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sharedId = urlParams.get("shared");
      
      if (sharedId) {
        console.log("[PWA Debug] Found shared ID in URL:", sharedId);
        
        if (isPWA) {
          // Use dedicated PWA loader
          loadPatternInPWA(sharedId);
        } else {
          // Standard browser version can use normal flow
          // (this will be handled by the other useEffect)
        }
      }
    };
    
    // Check immediately on component mount
    checkUrlForPattern();
    
    // Listen for PWA-specific events
    window.addEventListener('hashchange', checkUrlForPattern);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NAVIGATE_TO_PATTERN') {
          const sharedId = event.data.sharedId;
          console.log("[PWA Debug] Received message to load pattern:", sharedId);
          loadPatternInPWA(sharedId);
        }
      });
    }
    
    // For debugging - make function available globally
    window.debugPWAPattern = {
      forceLoad: loadPatternInPWA,
      checkUrl: checkUrlForPattern,
      isPWA: isPWA
    };
    
    return () => {
      window.removeEventListener('hashchange', checkUrlForPattern);
    };
  }, []);

  useEffect(() => {
    // Handle deep links when app is already running
    const handleAppLink = (e) => {
      const url = new URL(e.url);
      const urlParams = new URLSearchParams(url.search);
      const sharedId = urlParams.get("shared");
      
      if (sharedId) {
        console.log("Handling deep link with shared ID:", sharedId);
        // Call your pattern loading function
        loadSharedPattern(sharedId);
      }
    };
    
    // For PWA - listen for appinstalled event
    window.addEventListener('appinstalled', (event) => {
      console.log('App was installed', event);
    });

    // Listen for URL changes in PWA context
    window.addEventListener('hashchange', () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sharedId = urlParams.get("shared");
      if (sharedId) {
        console.log("Detected hashchange with shared ID:", sharedId);
        loadSharedPattern(sharedId);
      }
    });

    // Register for potential web app handlers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NAVIGATE_TO_PATTERN') {
          const sharedId = event.data.sharedId;
          console.log("Received navigate message with shared ID:", sharedId);
          loadSharedPattern(sharedId);
        }
      });
    }
  }, []);


  useEffect(() => {
    const setupNotifications = async () => {
      try {
        const success = await initNotifications();
        console.log('Notifications initialized:', success);
      } catch (error) {
        console.error('Error setting up notifications:', error);
      }
    };

    setupNotifications();
  }, []);

  // Listen for SW notification clicks. Switch to the practice tracker tab and
  // buffer the target performance for downstream components to consume.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'NAVIGATE_TO_PERFORMANCE' && event.data.performanceId) {
        setActiveTab('practiceTracker');
        setPendingPerfNav({
          performanceId: event.data.performanceId,
          showReflection: event.data.showReflection !== false,
        });
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // useEffect(() => {
    // Import the trackViewContent function if it's not already imported at the top
    // import { trackViewContent } from "./utils/analytics";
    
    // Create page content mapping based on active tab
    // const tabContentMap = {
      // metronome: {
      //   content_type: 'page',
      //   content_name: 'FlowFrame Editor',
      //   content_category: 'Tool'
    //  },
      // library: {
      //   content_type: 'page',
      //   content_name: 'FlowFrame Library',
      //   content_category: 'Browse'
      // },
      // practiceTracker: {
      //   content_type: 'page',
      //   content_name: 'FlowTracker',
      //   content_category: 'Practice'
      // },
      // freeflow: {
      //   content_type: 'page',
      //   content_name: 'FreeFlow',
      //   content_category: 'Collaboration'
      // },
      // practiceRoom: {
      //   content_type: 'page',
      //   content_name: 'Blog',
      //   content_category: 'Content'
      // },
      // about: {
      //   content_type: 'page',
      //   content_name: 'About',
      //   content_category: 'Information'
      // },
      // community: {
      //   content_type: 'page',
      //   content_name: 'Community',
      //   content_category: 'Social'
      // }
    // };
    
    // Track the view event with content based on current tab
    // if (tabContentMap[activeTab]) {
    //   trackViewContent(tabContentMap[activeTab]);
    // }
  // }, [activeTab]); // Re-run whenever activeTab changes

  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);

  const [isLoadingPattern, setIsLoadingPattern] = useState(false);
  // Sub-view inside the "metronome" top-level activeTab. Four siblings:
  //   - 'flowframe' (default): the full pattern editor
  //   - 'metronome':           the SimpleMetronome view
  //   - 'backing':             the Backing Tracks chord-chart editor
  //   - 'freeflow':            the FreeFlow improvisation tool
  const [metronomeView, setMetronomeView] = useState('flowframe');
  // Legacy derived flag — kept so the few remaining callsites that just
  // want "are we in the simplified metronome view?" stay readable.
  const isSimpleView = metronomeView === 'metronome';

  // Simple function to check if user has any work
  const hasAnyWork = () => {
    return patternName.trim() !== '' || 
          measures.length > 1 || 
          measures[0]?.numerator !== 4 || 
          measures[0]?.denominator !== 4 ||
          tempo !== INITIAL_TEMPO;
  };

  // Sub-view toggle for the metronome activeTab. Switching between the
  // 'flowframe' and 'metronome' views resets the metronome pattern (their
  // state models conflict). Switching to/from 'backing' or 'freeflow'
  // does not — both own their own state and shouldn't disturb the
  // metronome's pattern.
  const handleViewToggle = (newView) => {
    if (newView === metronomeView) return;
    if (isPlaying) {
      alert("Please stop playback before switching views.");
      return;
    }

    const isFlowMetroSwitch =
      (metronomeView === 'flowframe' && newView === 'metronome') ||
      (metronomeView === 'metronome' && newView === 'flowframe');

    if (metronomeView === 'flowframe' && newView === 'metronome' && hasAnyWork()) {
      const confirmed = window.confirm(
        "Switching to Simple View will reset your current pattern. Continue?"
      );
      if (!confirmed) return;
    }

    if (isFlowMetroSwitch) handleReset();
    setMetronomeView(newView);
  };
    
  const [isPlaying, setIsPlaying] = useState(false);
  const [tempo, setTempo] = useState(INITIAL_TEMPO);

  // Separate state for the tempo input field display value (allows empty string)
  const [tempoInput, setTempoInput] = useState('60');
  
  // Track the last valid tempo for input field reversion
  const lastValidTempoRef = useRef(tempo);

   // Keep lastValidTempoRef and tempoInput synchronized with tempo changes
   useEffect(() => {
    lastValidTempoRef.current = tempo;
    setTempoInput(tempo.toString());
  }, [tempo]);

  const [scaleByPercentage, setScaleByPercentage] = useState(false);
  const [baseTempo, setBaseTempo] = useState(INITIAL_TEMPO);
  const [tempoPercentage, setTempoPercentage] = useState(100);
  const [enablePercentageScaling, setEnablePercentageScaling] = useState(() => {
    const saved = localStorage.getItem("enablePercentageScaling");
    return saved === "true";
  });

  const [announceRehearsalMarks, setAnnounceRehearsalMarks] = useState(() => {
    return localStorage.getItem("announceRehearsalMarks") === "true";
  });
  const [rehearsalCountInMeasures, setRehearsalCountInMeasures] = useState(() => {
    const saved = parseInt(localStorage.getItem("rehearsalCountInMeasures"), 10);
    return [0, 1, 2, 3, 4].includes(saved) ? saved : 0;
  });
  
  const MAX_TAPS = 8;
  const [tapTimes, setTapTimes] = useState([]);
  const [muteRandomBeats, setMuteRandomBeats] = useState(INITIAL_MUTE_RANDOM);
  const [mutePercentage, setMutePercentage] = useState(INITIAL_MUTE_PERCENT);
  const [muteRandomDrones, setMuteRandomDrones] = useState(false);
  const [muteRandomDronePercentage, setMuteRandomDronePercentage] =
    useState(50);

  const [isLoading, setIsLoading] = useState(false);
  const precomputedTimingsRef = useRef(null);
  const startTimeRef = useRef(0);

  const specificLoopCountRef = useRef(0);

  useEffect(() => {
    // Expose current state values for direct access
    window.metronomeComponentState = {
      muteRandomDrones,
      muteRandomDronePercentage,
    };
  }, [muteRandomDrones, muteRandomDronePercentage]);

  const muteRandomDronesRef = useRef(false);
  const muteRandomDronePercentageRef = useRef(50);

  // Then update your state setters to also update the refs
  const updateMuteRandomDrones = (value) => {
    setMuteRandomDrones(value);
    muteRandomDronesRef.current = value;

    if (!value) {
      globalMeasureCountRef.current = 0;
    }
  };

  const updateMuteRandomDronePercentage = (value) => {
    setMuteRandomDronePercentage(value);
    muteRandomDronePercentageRef.current = value;
  };

  const [loopMode, setLoopMode] = useState(INITIAL_LOOP_MODE);
  const [loopStartMeasure, setLoopStartMeasure] = useState(1);
  const [loopEndMeasure, setLoopEndMeasure] = useState(1);

  const [loopIncrementalSettings, setLoopIncrementalSettings] = useState({
    enabled: false,
    scope: "specific",
    stepSize: 5,
    startTempo: 60,      
    endTempo: 120,       
    stepEvery: 1,
    useStepEvery: false,
  });

  const [isOverviewCollapsed, setIsOverviewCollapsed] = useState(false);

  const [showInfo, setShowInfo] = useState(false);
  const [isTempoLocked, setIsTempoLocked] = useState(false);

  const [masterGain, setMasterGain] = useState(0.7);
  const [aTuning, setATuning] = useState(440);

  const [muteBeats, setMuteBeats] = useState(false);
  const [muteDrones, setMuteDrones] = useState(false);
  const [muteTuplets, setMuteTuplets] = useState(false);
  const [muteSubs, setMuteSubs] = useState(false);

  // Quick Subdivisions: up to 4 rules, each { id, enabled, denominator, subCount }
  const [quickSubRules, setQuickSubRules] = useState([]);
  // Always mirrors current quickSubRules; read it from event handlers / play start.
  const quickSubRulesRef = useRef([]);

  const [stressOn, setStressOn] = useState(false);

  const [highlightedMeasures, setHighlightedMeasures] = useState([]);
  const [highlightInput, setHighlightInput] = useState("");

  // let user label the first measure as #1, #12, etc.
  const [startMeasureNumber, setStartMeasureNumber] = useState(1);

  // Separate state for the input field display value (allows empty string)
  const [startMeasureInput, setStartMeasureInput] = useState('1');
  
  // Track the last valid start measure number for input field reversion
  const lastValidStartMeasureRef = useRef(startMeasureNumber);

  // Add to the state declarations in MetronomeComponent
  const [rehearsalLetters, setRehearsalLetters] = useState({}); // { measureIndex: 'A', anotherIndex: 'B' }
  const [editingRehearsalLetter, setEditingRehearsalLetter] = useState(null);
  const [tempRehearsalLetter, setTempRehearsalLetter] = useState('');
  // Add these lines after line 2257 (after the tempRehearsalLetter state)
  const [timeSignatureColors, setTimeSignatureColors] = useState(DEFAULT_TIME_SIGNATURE_COLORS);
  const [showTimeSignatureColorsModal, setShowTimeSignatureColorsModal] = useState(false);

  const getTimeSignatureColorStyle = (timeSignatureKey, timeSignatureColors) => {
    const colorValue = timeSignatureColors[timeSignatureKey] || "#ffffff";
    
    if (colorValue.startsWith('#')) {
      // Custom hex color - use inline style
      return { backgroundColor: colorValue };
    }
    
    // Tailwind class - find the hex value for consistent preview
    const colorEntry = TIME_SIGNATURE_COLORS.find(color => color.value === colorValue);
    return colorEntry ? { backgroundColor: colorEntry.hex } : { backgroundColor: '#ffffff' };
  };
  
  const getTimeSignatureColorClass = (timeSignatureKey, timeSignatureColors) => {
    const colorValue = timeSignatureColors[timeSignatureKey] || "";
    return colorValue.startsWith('#') ? '' : colorValue;
  };

  const [showSilentModeWarning, setShowSilentModeWarning] = useState(false);

  const [selectedNoteValue, setSelectedNoteValue] = useState({
    label: "Quarter Note",
    factor: 1,
  });

  // measures
  const [measures, setMeasures] = useState(() => {
    const first = createNewMeasure(stressOn); // or (stressOn) if you want
    first.measureNumber = 1;
    return [first];
  });
  const [currentBeat, setCurrentBeat] = useState(0);
  const [currentBar, setCurrentBar] = useState(0);
  const globalMeasureCountRef = useRef(0);

  const shouldSimplifyUI = isPlaying && measures.length > 50;

  // tempo changes
  const [useTempoChanges, setUseTempoChanges] = useState(false);
  const [enableAdditionalTempoChanges, setEnableAdditionalTempoChanges] =
    useState(false);
  const [tempoChanges, setTempoChanges] = useState([
    { ...DEFAULT_TEMPO_CHANGE },
  ]);
  const oldTempoRef = useRef(tempo);

  // When tempo changes are enabled, percentage scaling becomes the only way
  // to alter the tempo — force scaleByPercentage on and clear any tempo lock
  // so the percentage controls remain usable.
  useEffect(() => {
    if (!useTempoChanges) return;
    if (!scaleByPercentage) {
      setScaleByPercentage(true);
      setOriginalTempoChanges((prev) =>
        prev.length > 0 ? prev : [...tempoChanges]
      );
      setBaseTempo(tempo);
      setTempoPercentage(100);
    }
    if (isTempoLocked) setIsTempoLocked(false);
    // eslint-disable-next-line
  }, [useTempoChanges]);

  const [nestedTupletsUI, setNestedTupletsUI] = useState(null);

  const loopCountRef = useRef(0);
  const [countInMeasures, setCountInMeasures] = useState(0);
  const countInQueueRef = useRef([]);
  const [loopCountInEnabled, setLoopCountInEnabled] = useState(false);

  const activeModulationRef = useRef({
    isActive: false,
    fromSubdivision: null,
    toSubdivision: null,
    sourceTempoReference: tempo,
  });
  const [modulationDisplay, setModulationDisplay] = useState({
    isActive: false,
    fromName: "",
    toName: "",
  });

  const [editingMeasureIndex, setEditingMeasureIndex] = useState(null);
  const [tempNumber, setTempNumber] = useState("");
  const [ghostNumer, setGhostNumer] = useState(4);
  const [ghostDenom, setGhostDenom] = useState(4);

  // patterns
  const [patternName, setPatternName] = useState("");
  const [savedPatterns, setSavedPatterns] = useState([]);
  const [folderName, setFolderName] = useState("Uncategorized");
  const [selectedFolder, setSelectedFolder] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedFolderForDeletion, setSelectedFolderForDeletion] =
    useState("");

  // audio references
  const audioContextRef = useRef(null);
  const masterGainNodeRef = useRef(null);
  const partialOscRefs = useRef([]);
  const chordOscRefs = useRef([]);

  // new: track the active chord so we can skip re-initializing if it hasn't changed
  const activeChordRef = useRef({ signature: "" });

  // scheduling references
  const currentBarRef = useRef(0);
  const currentBeatRef = useRef(0);
  const loopPitchOffsetRef = useRef(0);

  const [collapsedBeats, setCollapsedBeats] = useState({});
  const [previousCollapsedState, setPreviousCollapsedState] = useState(null);

  const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
  const [bulkEditorResetCount, setBulkEditorResetCount] = useState(0);

  const measureDetailRefs = useRef([]); // array of references

  const patternOverviewRef = useRef(null);

  const [modulateOnLoop, setModulateOnLoop] = useState(false);
  const [modLoopInterval, setModLoopInterval] = useState(0); // will store semitones
  const [modLoopDirection, setModLoopDirection] = useState("up");

  const [isPracticeToolsExpanded, setIsPracticeToolsExpanded] = useState(true);

  const [currentUser, setCurrentUser] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState("free");

  // Live-subscribe to each in-flight upload's mediaRecordings doc so the
  // queue pill can auto-dismiss once the recording is playable.
  //
  // We treat the upload as "done for the user" as soon as the assembly CF
  // has written the merged .webm (signalled by `storagePath` appearing on
  // the mediaRecordings doc, which our VideoPlayer can play directly). HLS
  // transcoding continues in the background but the user is unblocked —
  // keeping the pill up until `status === 'ready'` made it look stuck for
  // minutes on slow transcode paths.
  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    const entries = Object.entries(uploadQueue).filter(([, e]) =>
      e && (e.status === 'processing' || e.status === 'uploading' || e.status === 'finalizing')
    );
    if (entries.length === 0) return undefined;
    const unsubs = entries.map(([recordingId]) => {
      try {
        return globalRecordingService.subscribeMediaRecording(
          currentUser.uid,
          recordingId,
          (data) => {
            if (!data) return;
            const assembled = !!data.storagePath;
            if (data.status === 'ready' || (data.status === 'processing' && assembled)) {
              patchUploadQueueEntry(recordingId, { status: 'ready', progress: 100 });
              setTimeout(() => {
                removeUploadQueueEntry(recordingId);
                deleteUpload(recordingId).catch(() => {});
              }, 2500);
            } else if (data.status === 'failed') {
              patchUploadQueueEntry(recordingId, {
                status: 'failed',
                errorMessage: data.error || 'Upload failed'
              });
            }
          }
        );
      } catch (e) {
        return () => {};
      }
    });
    return () => {
      unsubs.forEach((u) => { try { u(); } catch (e) { /* noop */ } });
    };
  // eslint-disable-next-line
  }, [uploadQueue, currentUser?.uid]);

  // Resume-on-mount: scan IDB for any upload that was in flight during the
  // previous page load and pick up where we left off.
  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const pending = await listResumable();
        if (cancelled) return;
        for (const rec of pending) {
          if (rec.userId !== currentUser.uid) continue;

          // Check the server's record of this recording before re-resuming.
          // If the mediaRecordings doc already has storagePath (assembly
          // done) or status='ready', there's nothing to redo — just clean
          // the stale IDB entry and skip. This prevents the pill from
          // re-appearing on every page load for completed recordings.
          try {
            const snap = await getDoc(doc(db, 'users', currentUser.uid, 'mediaRecordings', rec.recordingId));
            if (cancelled) return;
            if (snap.exists()) {
              const data = snap.data() || {};
              if (data.status === 'ready' || data.storagePath) {
                await deleteUpload(rec.recordingId).catch(() => {});
                continue;
              }
              if (data.status === 'failed') {
                await deleteUpload(rec.recordingId).catch(() => {});
                continue;
              }
            } else {
              // No server record at all — the upload never even started
              // against the server side. Clean up IDB and move on.
              await deleteUpload(rec.recordingId).catch(() => {});
              continue;
            }
          } catch (e) {
            // Couldn't reach Firestore — proceed to resume and let the
            // network retry logic handle it.
          }

          patchUploadQueueEntry(rec.recordingId, {
            recordingId: rec.recordingId,
            itemId: rec.itemId,
            sessionId: rec.sessionId,
            mediaType: rec.mediaType,
            status: 'uploading',
            progress: 0,
            bytesUploaded: 0,
            totalBytes: (rec.chunks || []).reduce((a, c) => a + (c.size || 0), 0),
            errorMessage: null,
            updatedAt: Date.now(),
            resuming: true
          });
          try {
            const meta = await resumeStreamingUpload(rec.recordingId);
            if (cancelled) return;
            if (meta) {
              patchUploadQueueEntry(rec.recordingId, { status: 'processing', progress: 100 });
            } else {
              patchUploadQueueEntry(rec.recordingId, {
                status: 'failed',
                errorMessage: 'Recording could not be resumed'
              });
            }
          } catch (e) {
            patchUploadQueueEntry(rec.recordingId, {
              status: 'failed',
              errorMessage: e?.message || 'Resume failed'
            });
          }
        }
      } catch (e) {
        console.warn('[uploads] resume-on-mount failed:', e);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [currentUser?.uid]);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  // When Google sign-in collides with an existing password account, we stash
  // the pending Google credential here and prompt the user to sign in with
  // their password; on success we link the Google credential to that account.
  const pendingGoogleCredentialRef = useRef(null);
  const [linkAccountMessage, setLinkAccountMessage] = useState(null);
  // Email-verification banner state. We show a banner for any signed-in user
  // with emailVerified === false so they can verify before Firebase silently
  // replaces their account on a future Google sign-in.
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState('idle'); // idle | sending | sent | error | checking
  const [, setVerifyTick] = useState(0); // bump after user.reload() to re-evaluate emailVerified
  // Reset verify-banner state when the signed-in user changes.
  useEffect(() => {
    setVerifyBannerDismissed(false);
    setVerifyStatus('idle');
  }, [currentUser?.uid]);
  const [userOrgId, setUserOrgId] = useState(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [canCreateOrg, setCanCreateOrg] = useState(false);
  const [joinInviteCode, setJoinInviteCode] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("join") || null;
  });

  const [showPWAInstructions, setShowPWAInstructions] = useState(false);
  const [hasUnreadCommunity, setHasUnreadCommunity] = useState(false);

  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [libraryUsername, setLibraryUsername] = useState("");
  const [generatedUsername, setGeneratedUsername] = useState("");
  const [exportToLibraryPattern, setExportToLibraryPattern] = useState(null);
  const [showExportMp3Modal, setShowExportMp3Modal] = useState(false);
  // Always points at the latest handleStartStop closure, so async export code
  // can call it after state updates (loopMode, startMeasureNumber) without
  // capturing a stale callback.
  const handleStartStopRef = useRef(null);
  const [patternTags, setPatternTags] = useState({
    composer: "",
    instrumentation: "",
    genre: "",
    custom: [],
  });

  const patternCache = useRef(new Map());

  const oscillatorPool = useRef([]);
  const gainNodePool = useRef([]);

  const [planLabelInput, setPlanLabelInput] = useState("");
  const [planReflectionQuestions, setPlanReflectionQuestions] = useState({});


  const [practiceItems, setPracticeItems] = useState([]);
  const [practiceSessionActive, setPracticeSessionActive] = useState(false);
  const [practiceSessionPaused, setPracticeSessionPaused] = useState(false);
  const [practiceNow, setPracticeNow] = useState(Date.now());
  
  // Create refs to store the latest state setters
  const stateSettersRef = useRef({
    setPracticeItems,
    setPlanLabelInput,
    setPlanReflectionQuestions,
    setActiveTab  // Include your tab switching function
  });
  
  // Keep refs updated with the latest function references
  useEffect(() => {
    stateSettersRef.current = {
      setPracticeItems,
      setPlanLabelInput,
      setPlanReflectionQuestions,
      setActiveTab
    };
  }, [setPracticeItems, setPlanLabelInput, setPlanReflectionQuestions, setActiveTab]);
  
  // Set up global practice plan loader
  useEffect(() => {
    // Create a simpler global function that doesn't rely on stateSettersRef
    window.loadPracticePlan = (planData) => {
      console.log("1. App: loadPracticePlan called with data", planData);
      
      try {
        // Store the plan data globally so it can be accessed from anywhere
        window.currentPracticePlan = planData;
        
        console.log("2. App: Current activeTab =", activeTab);
        // Switch to the practice tracker tab
        setActiveTab('practiceTracker');
        
        // Set mode with a small delay to ensure tab switching completes
        setTimeout(() => {
          console.log("3. App: After setting activeTab to practiceTracker");
          if (window.setPracticeTrackerMode) {
            console.log("4. App: Calling setPracticeTrackerMode with 'planning'");
            window.setPracticeTrackerMode('planning');
          } else {
            console.error("5. App: setPracticeTrackerMode is not defined");
          }
        }, 50);
        
        return true;
      } catch (error) {
        console.error("Error loading practice plan:", error);
        return false;
      }
    };
    
    return () => {
      delete window.loadPracticePlan;
      delete window.currentPracticePlan;
    };
  }, []);

  useEffect(() => {
    console.log("4. App: activeTab changed to:", activeTab);
  }, [activeTab]);

  // Track page views when switching tabs
useEffect(() => {
  const tabContentMap = {
    metronome: {
      content_type: 'page',
      content_name: 'FlowFrame Editor',
      content_category: 'Tool'
    },
    library: {
      content_type: 'page',
      content_name: 'FlowFrame Library',
      content_category: 'Browse'
    },
    practiceTracker: {
      content_type: 'page',
      content_name: 'FlowTracker',
      content_category: 'Practice'
    },
    freeflow: {
      content_type: 'page',
      content_name: 'FreeFlow',
      content_category: 'Collaboration'
    },
    practiceRoom: {
      content_type: 'page',
      content_name: 'Blog',
      content_category: 'Content'
    },
    about: {
      content_type: 'page',
      content_name: 'About',
      content_category: 'Information'
    },
    community: {
      content_type: 'page',
      content_name: 'Community',
      content_category: 'Social'
    }
  };
  
  if (tabContentMap[activeTab]) {
    trackEvent('page_view', tabContentMap[activeTab]);
  }
}, [activeTab]);
// Scroll to top when tab changes
useEffect(() => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });

  // Clear community notification badge when visiting the Community tab
  if (activeTab === "community" && currentUser && hasUnreadCommunity) {
    updateDoc(doc(db, "users", currentUser.uid), {
      hasUnreadCommunity: false
    }).catch(err => console.error("Error clearing community notification:", err));
  }
}, [activeTab]);

  // Initialize pool once

  // Initialize pool once
  useEffect(() => {
    if (audioContextRef.current && oscillatorPool.current.length === 0) {
      // Pre-create oscillators and gain nodes (20-30 should be enough)
      for (let i = 0; i < 30; i++) {
        const osc = audioContextRef.current.createOscillator();
        const gain = audioContextRef.current.createGain();
        gain.gain.value = 0; // Start silent
        osc.connect(gain);
        gain.connect(masterGainNodeRef.current);
        osc.start();
        oscillatorPool.current.push({ osc, inUse: false });
        gainNodePool.current.push({ node: gain, inUse: false });
      }
    }

    return () => {
      // Clean up on unmount
      oscillatorPool.current.forEach(({ osc }) => {
        try {
          osc.stop();
        } catch (e) {}
      });
      oscillatorPool.current = [];
      gainNodePool.current = [];
    };
  }, [audioContextRef.current]);

  // Effect to check tutorial status on mount
  useEffect(() => {
    const checkTutorialStatus = () => {
      const completed = localStorage.getItem("tutorialCompleted") === "true";
      setTutorialCompleted(completed);
      setShowTutorial(!completed);
    };
    checkTutorialStatus();
  }, []);

  // Effect to persist enablePercentageScaling setting
  useEffect(() => {
    localStorage.setItem("enablePercentageScaling", enablePercentageScaling.toString());
  }, [enablePercentageScaling]);

  useEffect(() => {
    localStorage.setItem("announceRehearsalMarks", announceRehearsalMarks ? "true" : "false");
  }, [announceRehearsalMarks]);

  useEffect(() => {
    localStorage.setItem("rehearsalCountInMeasures", String(rehearsalCountInMeasures));
  }, [rehearsalCountInMeasures]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      const previousUser = currentUser;
      setCurrentUser(user);
      
      if (user && !previousUser) {
        storageManager.setUser(user.uid);
        storageManager.migrateUnprefixedKeys(user.uid);
        fetchUserTier(user.uid).then(tier => {
          if (tier === "basic" || tier === "pro") {
            startPrefetch(user.uid);
          }
        });
        initializeSessionTracking(user);
        import('./PracticeTrackerTab');

        const isPageRefresh = performance.navigation?.type === performance.navigation?.TYPE_RELOAD ||
                             performance.getEntriesByType?.('navigation')[0]?.type === 'reload';
        
        if (!isPageRefresh) {
          const loginMethod = localStorage.getItem('lastLoginMethod') || 'unknown';
          trackUserLogin(loginMethod, user);
        }
        
      } else if (!user && previousUser) {
        trackUserLogout(previousUser);
        setSubscriptionTier("free");
        setUserOrgId(null);
        setIsOrgAdmin(false);
        clearPrefetchCache();
        clearUserProfileCache();

        setGlobalScoreViewer(null);
        setPracticeSessionData({ sessionActive: false, sessionPaused: false, practiceItems: [], now: Date.now() });
        setGlobalCompletedRecordings({});
        setGlobalAlarmedItems(new Set());

        storageManager.clearAllUserData(previousUser.uid);
        storageManager.clearUser();
      } else if (user) {
        storageManager.setUser(user.uid);
        storageManager.migrateUnprefixedKeys(user.uid);
        fetchUserTier(user.uid).then(tier => {
          if (tier === "basic" || tier === "pro") {
            startPrefetch(user.uid);
          }
        });
        initializeSessionTracking(user);
        import('./PracticeTrackerTab');
      } else {
        setSubscriptionTier("free");
      }
    });
    
    return () => unsubscribe();
  }, [currentUser]);

  // Real-time listener for community notification badge
  useEffect(() => {
    if (!currentUser) {
      setHasUnreadCommunity(false);
      return;
    }
    const userRef = doc(db, "users", currentUser.uid);
    const unsubNotif = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        setHasUnreadCommunity(!!snap.data().hasUnreadCommunity);
      }
    });
    return () => unsubNotif();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      // If logged out, clear any previously loaded patterns
      setSavedPatterns([]);
      return;
    }

    // Listen to the current user's patterns subcollection
    const patternsRef = collection(db, "users", currentUser.uid, "patterns");
    const unsubscribe = onSnapshot(patternsRef, async (snapshot) => {
      const loadedPatterns = [];
      
      // Process each pattern document
      for (const docSnap of snapshot.docs) {
        const patternData = docSnap.data();
        
        if (patternData.isChunkedPattern) {
          // This is a chunked pattern - load chunks and reconstruct
          try {
            const chunksRef = collection(docSnap.ref, 'chunks');
            const chunksSnapshot = await getDocs(chunksRef);
            const chunks = [];
            
            chunksSnapshot.forEach(chunkDoc => {
              chunks.push(chunkDoc.data());
            });
            
            // Reconstruct the full pattern
            const reconstructedPattern = reconstructPatternFromChunks(patternData, chunks);
            
            loadedPatterns.push({
              id: docSnap.id,
              ...reconstructedPattern,
              // Add metadata to track that this was chunked
              _wasChunked: true,
              _chunkCount: chunks.length
            });
            
            console.log(`Loaded chunked pattern "${patternData.name}" with ${chunks.length} chunks`);
          } catch (error) {
            console.error('Error loading chunked pattern:', error);
            // Fallback: load the main document without chunks
            loadedPatterns.push({
              id: docSnap.id,
              ...patternData,
              _loadError: 'Failed to load chunks'
            });
          }
        } else {
          // Regular pattern - load normally
          loadedPatterns.push({
            id: docSnap.id,
            ...patternData
          });
        }
      }
      
      setSavedPatterns(loadedPatterns);
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
  
    // Track session heartbeat every 5 minutes
    const heartbeatInterval = setInterval(() => {
      trackSessionHeartbeat();
    }, 5 * 60 * 1000); // 5 minutes
  
    return () => clearInterval(heartbeatInterval);
  }, [currentUser]);

  // Add effect to handle visibility change to keep audio running in background
  useEffect(() => {
    if (!isPlaying) return;
    
    // Function to ensure audio context is running
    const ensureAudioActive = () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
    };
    
    // Call immediately to ensure active at start
    ensureAudioActive();
    
    // Listen to visibilitychange event
    document.addEventListener('visibilitychange', ensureAudioActive);
    
    return () => {
      document.removeEventListener('visibilitychange', ensureAudioActive);
    };
  }, [isPlaying, audioContextRef.current]);

  // Detect silent mode and audio issues
useEffect(() => {
  // Only run if audioContext exists and is initialized
  if (!audioContextRef.current) {
    // If no audio context, hide warning
    setShowSilentModeWarning(false);
    return;
  }

  const audioCtx = audioContextRef.current;
  
  const checkAudioState = () => {
    if (audioCtx.state === 'suspended') {
      setShowSilentModeWarning(true);
    } else {
      setShowSilentModeWarning(false);
    }
  };

  // Check initially
  checkAudioState();

  // Listen for state changes
  audioCtx.addEventListener('statechange', checkAudioState);

  return () => {
    audioCtx.removeEventListener('statechange', checkAudioState);
  };
}, [audioContextRef.current]); // This dependency might be the issue

// Effect 1: Handle scrolling when startMeasureNumber is explicitly changed by user
useEffect(() => {
  if (!isPlaying) {
    // Add a delay to allow users to finish typing longer numbers
    const scrollTimeout = setTimeout(() => {
      // Find the measure index that corresponds to the start measure number
      const measureIndex = measures.findIndex(m => m.measureNumber === startMeasureNumber);
      
      if (measureIndex !== -1) {
        // Expand the measure in detail editor (without vertical scrolling)
        setCollapsedBeats((prev) => ({
          ...prev,
          [measureIndex]: false,
        }));
        
        // Scroll the overview horizontally to center the measure
        if (overviewScrollRef.current) {
          // Calculate scroll position to center the measure
          // Each measure card is approximately 280px + 24px gap = 304px per measure
          const measureWidth = 304;
          const containerWidth = overviewScrollRef.current.clientWidth;
          const targetScrollPosition = (measureIndex * measureWidth) - (containerWidth / 2) + (measureWidth / 2);
          
          overviewScrollRef.current.scrollTo({
            left: Math.max(0, targetScrollPosition),
            behavior: 'smooth'
          });
        }
        
        // Scroll the detail editor horizontally to center the measure
        if (detailScrollRef.current) {
          // Each measure card in detail view is approximately 600px + 32px gap = 632px per measure
          const detailMeasureWidth = 632;
          const detailContainerWidth = detailScrollRef.current.clientWidth;
          const detailTargetScrollPosition = (measureIndex * detailMeasureWidth) - (detailContainerWidth / 2) + (detailMeasureWidth / 2);
          
          detailScrollRef.current.scrollTo({
            left: Math.max(0, detailTargetScrollPosition),
            behavior: 'smooth'
          });
        }
      }
    }, 800); // 800ms delay to allow typing longer numbers
    
    // Cleanup timeout if effect runs again
    return () => clearTimeout(scrollTimeout);
  }
}, [startMeasureNumber, isPlaying]); // REMOVED 'measures' dependency

// Effect 2: Update input display when measures change (but don't scroll)
useEffect(() => {
  if (measures.some(m => m.measureNumber === startMeasureNumber)) {
    lastValidStartMeasureRef.current = startMeasureNumber;
    setStartMeasureInput(startMeasureNumber.toString());
  }
}, [startMeasureNumber, measures]);

  async function handleGoogleSignIn() {
    try {
      localStorage.setItem('lastLoginMethod', 'google');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log("Google sign-in success:", user.uid);

      // 1) Check if the user doc exists in Firestore
      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        // 2) If no doc, create one with isInstructor field.
        // CRITICAL: this block has historically fired for some existing
        // users (suspected mobile Auth-token timing), wiping subscriptionTier
        // and fcmTokens. We omit subscriptionTier so a paid user can never
        // be reset to "free" here (fetchUserTier defaults missing → "free"),
        // and merge:true ensures we only fill in absent fields rather than
        // replacing the document.
        console.warn("Google sign-in: creating Firestore user doc (docSnap.exists()===false) for uid", user.uid);
        await setDoc(userRef, {
          email: user.email,
          createdAt: serverTimestamp(),
          isInstructor: false, // Default to not an instructor
          displayName: user.displayName || user.email.split('@')[0],
          photoURL: user.photoURL
        }, { merge: true });
      } else {
        // 3) If doc exists but doesn't have a displayName, update it
        const userData = docSnap.data();
        if (!userData.displayName && user.displayName) {
          await updateDoc(userRef, {
            displayName: user.displayName,
            updatedAt: serverTimestamp()
          });
        }
      }

      // Now update your UI state as needed:
      setCurrentUser(user);
      setShowLoginForm(false);
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        // An account already uses this email with a different sign-in method.
        // This app only offers password + Google, so that method is
        // necessarily password — stash the pending Google credential and
        // prompt for the password to link them (handled in the LoginForm
        // onLoginSuccess, which calls linkWithCredential).
        //
        // We deliberately do NOT call fetchSignInMethodsForEmail to confirm
        // the existing method: Email Enumeration Protection is enabled on this
        // project, which makes that API return an empty array and previously
        // made this entire branch dead code (the link prompt never showed).
        const pendingCred = GoogleAuthProvider.credentialFromError(err);
        const email = err.customData?.email;
        if (pendingCred) {
          pendingGoogleCredentialRef.current = pendingCred;
          setLinkAccountMessage(
            email
              ? `An account already exists for ${email}. Sign in with your password to connect Google to that account.`
              : `An account already exists with this email. Sign in with your password to connect Google to that account.`
          );
          // Force the login (password) tab/title — the user may have reached
          // the Google button from the Sign Up modal.
          setShowSignUpForm(false);
          setShowLoginForm(true);
          return;
        }
      }
      console.error("Google sign-in error:", err);
    }
  }

  async function handleSendVerificationEmail() {
    if (!currentUser) return;
    setVerifyStatus('sending');
    try {
      await sendEmailVerification(currentUser);
      setVerifyStatus('sent');
    } catch (err) {
      console.error("Send verification email failed:", err);
      setVerifyStatus('error');
    }
  }

  async function handleCheckVerified() {
    if (!currentUser) return;
    setVerifyStatus('checking');
    try {
      await currentUser.reload();
      setVerifyTick((t) => t + 1);
      setVerifyStatus(currentUser.emailVerified ? 'idle' : 'error');
    } catch (err) {
      console.error("Reload user failed:", err);
      setVerifyStatus('error');
    }
  }

  async function handleSignOut(setShowLoginForm) {
    try {
      await signOut(auth);
      setCurrentUser(null);
      console.log("User signed out");
      if (setShowLoginForm) {
        setShowLoginForm(false);
      } else {
        // If called directly from main component
        setShowLoginForm(false);
      }
    } catch (err) {
      console.error("Sign-out error:", err);
    }
  }

  async function fetchUserTier(uid) {
    try {
      const docRef = doc(db, "users", uid);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const userData = snap.data();
        const tier = userData.subscriptionTier || "free";
        setSubscriptionTier(tier);
        setUserOrgId(userData.organizationId || null);
        setIsOrgAdmin(userData.isOrgAdmin || false);
        setCanCreateOrg(userData.canCreateOrg === true);
        return tier;
      } else {
        setSubscriptionTier("free");
        setUserOrgId(null);
        setIsOrgAdmin(false);
        setCanCreateOrg(false);
        return "free";
      }
    } catch (error) {
      console.error("Failed to fetch tier:", error);
      setSubscriptionTier("free");
      setUserOrgId(null);
      setIsOrgAdmin(false);
      setCanCreateOrg(false);
      return "free";
    }
  }

  // Check if pattern data exceeds Firestore 1MB limit
  function exceedsFirestoreLimit(data) {
    const dataSize = new Blob([JSON.stringify(data)]).size;
    return dataSize > 1048576; // 1MB in bytes
  }

// Split large pattern into chunks for storage
function splitPatternForStorage(patternData) {
  if (!exceedsFirestoreLimit(patternData)) {
    return {
      mainDoc: patternData,
      chunks: [],
      needsChunking: false
    };
  }

  console.log('Pattern exceeds 1MB, splitting into chunks...');
  
  // Separate measures from main document
  const { measures, ...mainDoc } = patternData;
  
  // Calculate optimal chunk size (aim for ~800KB per chunk to stay under 1MB)
  const chunkSize = 50; // measures per chunk - adjust based on complexity
  const chunks = [];
  
  if (measures && Array.isArray(measures)) {
    for (let i = 0; i < measures.length; i += chunkSize) {
      chunks.push({
        chunkIndex: Math.floor(i / chunkSize),
        measures: measures.slice(i, i + chunkSize),
        startMeasure: i,
        endMeasure: Math.min(i + chunkSize - 1, measures.length - 1)
      });
    }
  }

  // Enhanced main document with chunking metadata
  const enhancedMainDoc = {
    ...mainDoc,
    isChunkedPattern: true,
    totalMeasures: measures?.length || 0,
    chunkCount: chunks.length,
    chunkSize: chunkSize,
    originalSize: new Blob([JSON.stringify(patternData)]).size
  };

  console.log(`Split pattern into ${chunks.length} chunks (${measures?.length || 0} measures)`);
  
  return {
    mainDoc: enhancedMainDoc,
    chunks,
    needsChunking: true
  };
}

// Reconstruct pattern from chunks
function reconstructPatternFromChunks(mainDoc, chunks) {
  if (!mainDoc.isChunkedPattern) {
    return mainDoc;
  }

  // Sort chunks by index to ensure correct order
  const sortedChunks = chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
  
  // Reconstruct measures array
  const measures = [];
  sortedChunks.forEach(chunk => {
    if (chunk.measures && Array.isArray(chunk.measures)) {
      measures.push(...chunk.measures);
    }
  });

  // Remove chunking metadata and restore original structure
  const { 
    isChunkedPattern, 
    totalMeasures, 
    chunkCount, 
    chunkSize, 
    originalSize,
    ...reconstructed 
  } = mainDoc;
  
  return {
    ...reconstructed,
    measures
  };
}

// Load a single chunked pattern by ID
async function loadChunkedPattern(patternId) {
  if (!currentUser) return null;
  
  try {
    const patternRef = doc(db, "users", currentUser.uid, "patterns", patternId);
    const patternDoc = await getDoc(patternRef);
    
    if (!patternDoc.exists()) {
      throw new Error('Pattern not found');
    }
    
    const patternData = patternDoc.data();
    
    if (!patternData.isChunkedPattern) {
      // Not chunked, return as-is
      return {
        id: patternDoc.id,
        ...patternData
      };
    }
    
    // Load chunks
    const chunksRef = collection(patternRef, 'chunks');
    const chunksSnapshot = await getDocs(chunksRef);
    const chunks = [];
    
    chunksSnapshot.forEach(chunkDoc => {
      chunks.push(chunkDoc.data());
    });
    
    // Reconstruct and return
    const reconstructedPattern = reconstructPatternFromChunks(patternData, chunks);
    
    return {
      id: patternDoc.id,
      ...reconstructedPattern,
      _wasChunked: true,
      _chunkCount: chunks.length
    };
    
  } catch (error) {
    console.error('Error loading chunked pattern:', error);
    throw error;
  }
}

// Validate that a chunked pattern has all its chunks
function validateChunkedPattern(pattern, chunks) {
  if (!pattern.isChunkedPattern) return true;
  
  const expectedChunks = pattern.chunkCount || 0;
  const actualChunks = chunks.length;
  
  if (actualChunks !== expectedChunks) {
    console.warn(`Pattern "${pattern.name}" expects ${expectedChunks} chunks but found ${actualChunks}`);
    return false;
  }
  
  // Check for missing chunk indices
  const chunkIndices = chunks.map(c => c.chunkIndex).sort((a, b) => a - b);
  for (let i = 0; i < expectedChunks; i++) {
    if (!chunkIndices.includes(i)) {
      console.warn(`Pattern "${pattern.name}" missing chunk ${i}`);
      return false;
    }
  }
  
  return true;
}

  const handleSavePattern = async (options = {}) => {
    const {
      isAutosave = false,
      showToast = null,
      hasPromptedForName = false,
      setHasPromptedForName = null,
      setPatternName: setPatternNameCallback = null,
      overridePatternName = null,  // New parameter
      overrideFolderName = null    // New parameter
    } = options;
  
    if (!currentUser) {
      if (isAutosave) {
        return; // Silent fail for autosave
      }
      alert("Please log in to save patterns.");
      return;
    }
  
    // For autosave: additional checks
    if (isAutosave) {
      // Don't autosave while playing
      if (isPlaying) return;
      
      // Don't autosave if there's no meaningful content
      if (measures.length === 1 && 
          measures[0].numerator === 4 && 
          measures[0].denominator === 4 && 
          tempo === INITIAL_TEMPO && 
          !patternName.trim() && 
          !overridePatternName) {
        return;
      }
    }
  
    // Use override names if provided, otherwise use current state
    let nameTrimmed = overridePatternName || patternName.trim();
    const folderTrimmed = overrideFolderName || folderName.trim() || "Uncategorized";
  
    // Handle name validation differently for autosave vs manual
    if (!nameTrimmed) {
      if (isAutosave) {
        // For autosave: this should not happen with the new modal approach
        // but keep as fallback
        nameTrimmed = "Autosave";
        if (showToast) showToast('Saving as "Autosave" - you can rename it later', 'info');
      } else {
        // For manual save: keep existing behavior - require name
        alert("Please enter a pattern name.");
        return;
      }
    }
  
    console.log(`🔍 ${isAutosave ? 'AUTO' : 'MANUAL'} SAVE DEBUG - Starting save for:`, nameTrimmed, folderTrimmed);
  
    // 2) Build the new pattern object (same as original)
    const newPattern = {
      updatedAt: serverTimestamp(),
      name: nameTrimmed,
      folder: folderTrimmed,
      tempo,
      useTempoChanges: !!useTempoChanges,
      enableAdditionalTempoChanges: !!enableAdditionalTempoChanges,
      tempoChanges,
      aTuning,
      muteRandomBeats,
      mutePercentage,
      loopMode,
      measures,
      startMeasureNumber,
      stressOn,
      selectedNoteValue,
      modulateOnLoop: !!modulateOnLoop,
      modLoopInterval,
      modLoopDirection,
      rehearsalLetters,
      timeSignatureColors,
    };
  
    // Add autosave marker if this is an autosave
    if (isAutosave) {
      newPattern.isAutosave = true;
    }
  
    // 3) Remove any undefined fields (same as original)
    const cleanedData = removeUndefinedDeep(newPattern);
  
    // 3.5) Handle large patterns with chunking
    const { mainDoc, chunks, needsChunking } = splitPatternForStorage(cleanedData);

    // 4) Check for existing pattern (same as original)
    const existingPattern = savedPatterns.find(
      (p) => p.name === nameTrimmed && p.folder === folderTrimmed
    );

    const patternsRef = collection(db, "users", currentUser.uid, "patterns");

    // Handle existing pattern differently for autosave vs manual
    if (existingPattern) {
      if (!isAutosave) {
        // Manual save: ask for confirmation (keep original behavior)
        const overwriteConfirmed = window.confirm(
          `A pattern named "${nameTrimmed}" already exists in ` +
            `folder "${folderTrimmed}". Do you want to overwrite it?`
        );
        if (!overwriteConfirmed) {
          return; // user canceled, do nothing
        }
      }
      // Autosave: silently update existing pattern

      // Overwrite (update) the existing pattern with chunking support
      try {
        const docRef = doc(
          db,
          "users",
          currentUser.uid,
          "patterns",
          existingPattern.id
        );

        if (needsChunking) {
          // Save main document
          await setDoc(docRef, mainDoc, { merge: true });
          
          // Save chunks as subcollection
          const batch = writeBatch(db);
          
          // First, delete any existing chunks
          const existingChunksSnapshot = await getDocs(collection(docRef, 'chunks'));
          existingChunksSnapshot.forEach(doc => {
            batch.delete(doc.ref);
          });
          
          // Add new chunks
          chunks.forEach((chunk, index) => {
            const chunkRef = doc(collection(docRef, 'chunks'), `chunk_${index}`);
            batch.set(chunkRef, chunk);
          });
          
          await batch.commit();
          console.log(`Updated chunked pattern with ${chunks.length} chunks`);
        } else {
          // Regular save for small patterns
          await setDoc(docRef, mainDoc, { merge: true });
        }

        // Update cache immediately
        const cacheKey = `${nameTrimmed}-${folderTrimmed}`;
        patternCache.current.set(cacheKey, measures);

        // Optimistically update local state immediately
        setSavedPatterns(prev => {
          const existingPatternIndex = prev.findIndex(p => p.id === existingPattern.id);
          if (existingPatternIndex >= 0) {
            const updated = [...prev];
            updated[existingPatternIndex] = {
              ...existingPattern,
              ...cleanedData, // Use original data for local state
              id: existingPattern.id,
              updatedAt: new Date()
            };
            return updated;
          }
          return prev;
        });

        // Different notifications for autosave vs manual
        if (isAutosave) {
          if (showToast) {
            const sizeInfo = needsChunking ? ` (large pattern: ${chunks.length} chunks)` : '';
            showToast(`"${nameTrimmed}" saved successfully!${sizeInfo}`, 'success');
          }
        } else {
          const sizeInfo = needsChunking ? ` Pattern was split into ${chunks.length} chunks for storage.` : '';
          alert(`Pattern "${nameTrimmed}" in folder "${folderTrimmed}" overwritten!${sizeInfo}`);
        }

        // Track with different event names
        trackEvent(isAutosave ? "pattern_autosave" : "pattern_save", {
          is_new: false,
          folder: folderTrimmed,
          time_signatures_used: getTimeSignaturesUsed(measures),
          features_used: getPatternFeatures(measures, muteRandomBeats),
          is_chunked: needsChunking,
          chunk_count: chunks.length,
          ...(isAutosave && { was_prompted: !patternName.trim() && nameTrimmed !== "Autosave" })
        });

      } catch (err) {
        console.error(`🔍 ${isAutosave ? 'AUTO' : 'MANUAL'} SAVE DEBUG - Error overwriting pattern:`, err);
        
        // Different error handling for autosave vs manual
        if (isAutosave) {
          if (showToast) showToast('Failed to save pattern. Please check your connection.', 'error');
        } else {
          alert("Failed to overwrite pattern. Check console for details.");
        }
      }

      return; // end here after overwriting
    }
  
    // If no existing pattern with the same name & folder, proceed with normal logic
  
    // 5) Enforce tier-based pattern limits (only for new patterns) - same as original
    let allowedCount;
    if (subscriptionTier === "pro") {
      allowedCount = Infinity; // no limit
    } else if (subscriptionTier === "basic") {
      allowedCount = 10;
    } else {
      // default to "free"
      allowedCount = 3;
    }
  
    if (savedPatterns.length >= allowedCount) {
      const errorMessage = isAutosave 
        ? `Pattern limit reached (${allowedCount}). Please upgrade or delete some patterns.`
        : `You have reached the maximum number of saved patterns for your "${subscriptionTier}" plan. Please upgrade or delete old patterns to save a new one.`;
      
      if (isAutosave) {
        if (showToast) showToast(errorMessage, 'warning');
        return;
      } else {
        alert(errorMessage);
        return;
      }
    }
  
   // 6) Write the new pattern to Firestore with chunking support
try {
  const docRef = doc(patternsRef);
  
  if (needsChunking) {
    // Save main document
    await setDoc(docRef, {
      ...mainDoc,
      createdAt: serverTimestamp()
    });
    
    // Save chunks as subcollection
    const batch = writeBatch(db);
    chunks.forEach((chunk, index) => {
      const chunkRef = doc(collection(docRef, 'chunks'), `chunk_${index}`);
      batch.set(chunkRef, chunk);
    });
    
    await batch.commit();
    console.log(`Created new chunked pattern with ${chunks.length} chunks`);
  } else {
    // Regular save for small patterns
    await setDoc(docRef, {
      ...mainDoc,
      createdAt: serverTimestamp()
    });
  }

  // Update cache immediately for new patterns too
  const cacheKey = `${nameTrimmed}-${folderTrimmed}`;
  patternCache.current.set(cacheKey, measures);

  // Optimistically add to local state immediately
  setSavedPatterns(prev => [...prev, {
    ...cleanedData, // Use original data for local state
    id: docRef.id,
    createdAt: new Date(),
    updatedAt: new Date()
  }]);

  // Different notifications for autosave vs manual
  if (isAutosave) {
    if (showToast) {
      const sizeInfo = needsChunking ? ` (large pattern: ${chunks.length} chunks)` : '';
      showToast(`"${nameTrimmed}" saved successfully!${sizeInfo}`, 'success');
    }
  } else {
    const sizeInfo = needsChunking ? ` Pattern was split into ${chunks.length} chunks for storage.` : '';
    alert(`Pattern "${nameTrimmed}" saved to folder "${folderTrimmed}"!${sizeInfo}`);
  }

  // Track with different event names
  trackEvent(isAutosave ? "pattern_autosave" : "pattern_save", {
    is_new: true,
    folder: folderTrimmed,
    measures_count: measures.length,
    time_signatures_used: getTimeSignaturesUsed(measures),
    features_used: getPatternFeatures(measures, muteRandomBeats),
    is_chunked: needsChunking,
    chunk_count: chunks.length,
    ...(isAutosave && { was_prompted: !patternName.trim() && nameTrimmed !== "Autosave" })
  });

} catch (err) {
  console.error(`🔍 ${isAutosave ? 'AUTO' : 'MANUAL'} SAVE DEBUG - Error saving pattern:`, err);
  
  // Remove the optimistically added pattern on error
  setSavedPatterns(prev => prev.filter(p => !(p.name === nameTrimmed && p.folder === folderTrimmed && !p.id)));
  
  // Different error handling for autosave vs manual
  if (isAutosave) {
    if (showToast) showToast('Failed to save pattern. Please check your connection.', 'error');
  } else {
    alert("Failed to save pattern. Check console for details.");
  }
}
  };

// Helper function to save an imported pattern without loading it
const saveImportedPattern = async (pattern) => {
  if (!currentUser) {
    alert("Please log in to save patterns.");
    return;
  }

  const compatibleMeasures = applyBackwardCompatibility(pattern.measures);
  
  const newPattern = {
    name: pattern.name,
    folder: pattern.folder || "Imported", 
    tempo: pattern.tempo || 120,
    useTempoChanges: pattern.useTempoChanges || false,
    enableAdditionalTempoChanges: pattern.enableAdditionalTempoChanges || false,
    tempoChanges: pattern.tempoChanges || [],
    aTuning: pattern.aTuning || 440,
    muteRandomBeats: pattern.muteRandomBeats || false,
    mutePercentage: pattern.mutePercentage || 0,
    loopMode: pattern.loopMode || "none",
    measures: compatibleMeasures,
    startMeasureNumber: pattern.startMeasureNumber || 1,
    stressOn: pattern.stressOn || false,
    selectedNoteValue: pattern.selectedNoteValue || { label: "Quarter Note", factor: 1 },
    modulateOnLoop: pattern.modulateOnLoop || false,
    modLoopInterval: pattern.modLoopInterval || 0,
    modLoopDirection: pattern.modLoopDirection || "up",
    rehearsalLetters: pattern.rehearsalLetters || {},
    timeSignatureColors: pattern.timeSignatureColors || {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const cleanedData = removeUndefinedDeep(newPattern);
  
  // Handle large patterns with chunking
  const { mainDoc, chunks, needsChunking } = splitPatternForStorage(cleanedData);
  
  const patternsRef = collection(db, "users", currentUser.uid, "patterns");
  
  try {
    const docRef = doc(patternsRef);
    
    if (needsChunking) {
      // Save main document
      await setDoc(docRef, mainDoc);
      
      // Save chunks as subcollection
      const batch = writeBatch(db);
      chunks.forEach((chunk, index) => {
        const chunkRef = doc(collection(docRef, 'chunks'), `chunk_${index}`);
        batch.set(chunkRef, chunk);
      });
      
      await batch.commit();
      console.log(`Imported chunked pattern with ${chunks.length} chunks`);
    } else {
      // Regular save for small patterns
      await setDoc(docRef, mainDoc);
    }
    
    // Update local state
    setSavedPatterns(prev => [...prev, {
      ...cleanedData, // Use original data for local state
      id: docRef.id,
      createdAt: new Date(),
      updatedAt: new Date()
    }]);
    
    // Update cache
    const cacheKey = `${pattern.name}-${pattern.folder || "Imported"}`;
    patternCache.current.set(cacheKey, compatibleMeasures);
    
    return true;
  } catch (error) {
    console.error("Error saving imported pattern:", error);
    throw error;
  }
};

  const {
    isAutoSaving,
    lastAutoSave,
    autoSaveEnabled,
    setAutoSaveEnabled,
    performAutosave,
    triggerAutosave,
    toastMessage,       
    setToastMessage,    
    showAutosaveModal,
    pendingAutosave,
    handleAutosaveModalConfirm,
    handleAutosaveModalCancel,
  } = useAutosave({
    // User and app state
    currentUser,
    isPlaying,
    subscriptionTier,
    
    // Pattern data
    measures,
    tempo,
    patternName,
    folderName,
    useTempoChanges,
    enableAdditionalTempoChanges,
    tempoChanges,
    aTuning,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    startMeasureNumber,
    stressOn,
    selectedNoteValue,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    rehearsalLetters,
    timeSignatureColors,
    
    // App constants and utilities
    INITIAL_TEMPO,
    savedPatterns,
    setSavedPatterns,
    patternCache,
    removeUndefinedDeep,

    // Callback functions
    setPatternName,
    setFolderName,
    handleSavePattern,
    
    // Optional configuration
    autoSaveDelay: 120000, // 2 minutes
    showToastNotifications: true,
  });
  
  // Expose performAutosave globally (optional, for manual triggers)
  useEffect(() => {
    window.performAutosave = performAutosave;
    return () => {
      delete window.performAutosave;
    };
  }, [performAutosave]);

  const getCurrentVisibleMeasureIndex = useCallback(() => {
    // Use the start measure as the current measure for keyboard shortcuts
    const startIndex = measures.findIndex(m => m.measureNumber === startMeasureNumber);
    return startIndex !== -1 ? startIndex : 0;
  }, [measures, startMeasureNumber]);


  const [copiedMeasure, setCopiedMeasure] = useState(null);
  const [copiedMeasures, setCopiedMeasures] = useState([]);

  // Single measure copy
  const handleCopyMeasure = useCallback((measureIndex) => {
    const measureToCopy = measures[measureIndex];
    if (!measureToCopy) return;
    
    // Deep copy the measure data (excluding measureNumber and id)
    const copiedData = JSON.parse(JSON.stringify({
      ...measureToCopy,
      // Remove fields that should be unique per measure
      id: undefined,
      measureNumber: undefined
    }));
    
    setCopiedMeasure(copiedData);
    
    // Debug logging to verify what's being copied
    console.log('🔍 COPY DEBUG - Original measure:', measureToCopy);
    console.log('🔍 COPY DEBUG - Copied data:', copiedData);
    console.log('🔍 COPY DEBUG - Beat sounds copied:', copiedData.beatSounds?.length);
    console.log('🔍 COPY DEBUG - First beat drone sound:', copiedData.beatSounds?.[0]?.droneSound);
    console.log('🔍 COPY DEBUG - First beat click sound:', {
      id: copiedData.beatSounds?.[0]?.id,
      name: copiedData.beatSounds?.[0]?.name,
      type: copiedData.beatSounds?.[0]?.type
    });
    
    // Count what's being copied for better feedback
    const beatCount = copiedData.beatSounds?.length || 0;
    const droneCount = copiedData.beatSounds?.filter(beat => beat.dronePitchNote !== null).length || 0;
    const tupletCount = copiedData.tuplets?.length || 0;
    
    let details = [];
    if (beatCount > 0) details.push(`${beatCount} beats`);
    if (droneCount > 0) details.push(`${droneCount} drones`);
    if (tupletCount > 0) details.push(`${tupletCount} tuplets`);
    
    // Visual feedback
    setToastMessage({
      message: `📋 Measure ${measureToCopy.measureNumber} copied${details.length > 0 ? ` (${details.join(', ')})` : ''}!`,
      type: 'info'
    });
  }, [measures, setToastMessage]);

  // Enhanced paste function with debug logging:
  const handlePasteMeasure = useCallback((targetIndex) => {
    if (!copiedMeasure) return;

    let pastedToMeasureNumber;
    
    console.log('🔍 PASTE DEBUG - Starting paste operation');
    console.log('🔍 PASTE DEBUG - Copied measure data:', copiedMeasure);
    console.log('🔍 PASTE DEBUG - Target index:', targetIndex);
    
    // Force a complete state replacement instead of mutation
    setMeasures((prev) => {
      // Create a completely new array to ensure React detects the change
      const newMeasures = prev.map((measure, index) => {
        if (index !== targetIndex) {
          return measure; // Keep other measures unchanged
        }
        
        // For the target measure, create a completely new object
        const targetMeasure = prev[targetIndex];
        pastedToMeasureNumber = targetMeasure.measureNumber;
        
        const pastedMeasure = {
          ...copiedMeasure,
          id: targetMeasure.id,
          measureNumber: targetMeasure.measureNumber,
          // Ensure beatSounds is a completely new array
          beatSounds: copiedMeasure.beatSounds?.map(beat => ({
            ...beat,
            id: beat.id || uuidv4() // Keep original ID structure but ensure uniqueness
          })) || []
        };
        
        console.log('🔍 PASTE DEBUG - Creating new measure:', pastedMeasure);
        
        return pastedMeasure;
      });
      
      // Apply processing to the new array
      applyStressVolumesIfAny(newMeasures, stressOn);
      resetAllBeatVolumesToUserVolumes(newMeasures);
      applyAllDynamicsToAllMeasures(newMeasures);
      
      console.log('🔍 PASTE DEBUG - Final measures array:', newMeasures);
      
      return newMeasures;
    });
    
    // Visual feedback
    setToastMessage({
      message: `✅ Measure pasted to position ${pastedToMeasureNumber}!`,
      type: 'success'
    });
  }, [copiedMeasure, stressOn, setToastMessage]);


  // Add context menu to measure cards
  const handleMeasureRightClick = (e, measureIndex) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      measureIndex: measureIndex,
      options: [
        { label: 'Copy Measure', action: () => handleCopyMeasure(measureIndex) },
        { label: 'Paste Measure', action: () => handlePasteMeasure(measureIndex), disabled: !copiedMeasure },
        { label: 'Duplicate Below', action: () => copyMeasureBelow(measureIndex) },
        { label: 'Delete Measure', action: () => handleDeleteMeasureInOverview(measureIndex) }
      ]
    });
  };

  

  // Add state for the modal
const [showSaveAsModal, setShowSaveAsModal] = useState(false);
const [showSaveModal, setShowSaveModal] = useState(false); 

// Wrapper for the manual save function
const performManualSave = async () => {
  await handleManualSave({
    currentUser,
    patternName,
    setPatternName,
    folderName,
    tempo,
    useTempoChanges,
    enableAdditionalTempoChanges,
    tempoChanges,
    aTuning,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    measures,
    startMeasureNumber,
    stressOn,
    selectedNoteValue,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    rehearsalLetters,
    timeSignatureColors,
    savedPatterns,
    setSavedPatterns,
    subscriptionTier,
    patternCache,
    removeUndefinedDeep,
  });
};

// Wrapper for the save as function
const performSaveAs = async (newName, newFolder) => {
  await handleSaveAs({
    currentUser,
    newName,
    newFolder,
    patternName,
    tempo,
    useTempoChanges,
    enableAdditionalTempoChanges,
    tempoChanges,
    aTuning,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    measures,
    startMeasureNumber,
    stressOn,
    selectedNoteValue,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    rehearsalLetters,
    timeSignatureColors,
    savedPatterns,
    setSavedPatterns,
    subscriptionTier,
    patternCache,
    removeUndefinedDeep,
  });
  
  // Update current workspace to the new pattern
  setPatternName(newName);
  setFolderName(newFolder);
};

  // Register pattern loader for cross-component communication
  useEffect(() => {
    // Set up global pattern loader
    const cleanup = setupPatternLoader((sharedItem) => {
      console.log('Pattern loader received:', sharedItem);
      
      // Extract pattern data from the nested structure
      const patternData = sharedItem.patternData ? sharedItem.patternData : sharedItem;
      
      if (patternData) {
        console.log('Processing pattern data:', patternData);
        
        try {
          // Set all the pattern properties directly here instead of calling loadPatternData
          setPatternName(patternData.name || "Shared Pattern");
          setFolderName(patternData.folder || "Shared");
          setTempo(patternData.tempo || 120);
          setUseTempoChanges(patternData.useTempoChanges || false);
          setEnableAdditionalTempoChanges(patternData.enableAdditionalTempoChanges || false);
          setTempoChanges(patternData.tempoChanges || []);
          setATuning(patternData.aTuning || 440);
          setMuteRandomBeats(patternData.muteRandomBeats || false);
          setMutePercentage(patternData.mutePercentage || 0);
          setLoopMode(patternData.loopMode || "none");
          setTimeSignatureColors(patternData.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS);
          
          // Check if measures exist and set them
          if (patternData.measures && Array.isArray(patternData.measures)) {
            clearQuickSubRulesWithoutApplying();
            setMeasures(patternData.measures);
          }
          
          // Set other properties with fallbacks
          setStartMeasureNumber(patternData.startMeasureNumber || 1);
          setStressOn(patternData.stressOn || false);
          setModulateOnLoop(patternData.modulateOnLoop || false);
          setModLoopInterval(patternData.modLoopInterval || 0);
          setModLoopDirection(patternData.modLoopDirection || "up");
          
          if (patternData.selectedNoteValue) {
            setSelectedNoteValue(patternData.selectedNoteValue);
          } else {
            setSelectedNoteValue({
              label: "Quarter Note",
              factor: 1,
            });
          }
          
          setIsLoadingPattern(false);
          setIsPlaying(false);
          setCurrentBeat(0);
          setCurrentBar(0);
          specificLoopCountRef.current = 0;
          
          // Switch to metronome tab
          setActiveTab('metronome');
          return true;
        } catch (error) {
          console.error("Error loading pattern:", error);
          return false;
        }
      }
      
      return false;
    });
    
    return cleanup;
  }, []);

  // Function to export pattern to library - add to App.js
  async function exportPatternToLibrary(pattern) {
    if (!currentUser) {
      alert("Please log in to share patterns to the library");
      return;
    }

    // Fetch the user's profile to check for username
    try {
      const userRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      if (!userData?.libraryUsername) {
        // If no username is set, store the pattern and show the username modal
        console.log("No username found, showing username selection modal");
        setExportToLibraryPattern(pattern); // Keep the pattern data
        console.log("About to show username modal");
        setShowUsernameModal(true);
        console.log("Username modal should be visible now");
        return;
      }

      // Set local username from Firestore for the export
      const username = userData.libraryUsername;

      // Continue with the actual export process using the username
      try {
        // Create a clean copy of the pattern
        const cleanedData = removeUndefinedDeep({
          name: pattern.name || patternName,
          folder: pattern.folder || folderName,
          description: pattern.description || "",
          tempo,
          useTempoChanges,
          enableAdditionalTempoChanges,
          tempoChanges,
          aTuning,
          muteRandomBeats,
          mutePercentage,
          loopMode,
          measures,
          startMeasureNumber,
          stressOn,
          selectedNoteValue,
          tags: pattern.tags || patternTags,
          creatorName: username,
          creatorId: currentUser.uid,
          createdAt: serverTimestamp(),
          downloadCount: 0,
          rehearsalLetters,
          timeSignatureColors,
        });

        // Add to publicPatterns collection
        const publicPatternsRef = collection(db, "publicPatterns");
        await addDoc(publicPatternsRef, cleanedData);

        alert("Pattern successfully shared to the FlowFrame Library!");
        setExportToLibraryPattern(null); // Clear only after successful export
      } catch (error) {
        console.error("Error sharing pattern to library:", error);
        alert("Failed to share pattern. Please try again.");
      }
    } catch (error) {
      console.error("Error checking username:", error);
      alert("Error checking user profile. Please try again.");
    }
  }

  function saveRehearsalLetter() {
  if (!tempRehearsalLetter.trim()) return;
  
  setRehearsalLetters(prev => ({
    ...prev,
    [editingRehearsalLetter]: tempRehearsalLetter.trim()
  }));
  
  setEditingRehearsalLetter(null);
  setTempRehearsalLetter('');
  }

  function deleteRehearsalLetter() {
    setRehearsalLetters(prev => {
      const newLetters = { ...prev };
      delete newLetters[editingRehearsalLetter];
      return newLetters;
    });
    
    setEditingRehearsalLetter(null);
    setTempRehearsalLetter('');
  }

  /** Toggles the "beats" section for the measure at `mIndex`. */
  function toggleBeatsCollapse(mIndex) {
    setCollapsedBeats((prev) => ({
      ...prev,
      [mIndex]: !prev[mIndex],
    }));
  }

  /** Collapses all measures */
  function collapseAllMeasures() {
    const newCollapsedState = {};
    for (let i = 0; i < measures.length; i++) {
      newCollapsedState[i] = true;
    }
    setCollapsedBeats(newCollapsedState);
  }

  /** Expands all measures */
  function expandAllMeasures() {
    setCollapsedBeats({});
  }

  function returnToOverview() {
    if (patternOverviewRef.current) {
      patternOverviewRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // We'll define our jump function (scroll + expand) shortly:
  function jumpToMeasure(mIndex) {
    // If you have a collapsed state, open it so the measure is visible:
    setCollapsedBeats((prev) => ({
      ...prev,
      [mIndex]: false,
    }));

    // Then scroll smoothly to the measure's div:
    const targetEl = measureDetailRefs.current[mIndex];
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function handleMeasureNumberChange(changedIndex, newNumValue) {
    const newNumber = parseInt(newNumValue, 10);
    if (isNaN(newNumber)) return;

    setMeasures((prev) => {
      const arr = [...prev];
      const oldNumber = arr[changedIndex].measureNumber;
      const diff = newNumber - oldNumber;

      // Shift *all* measures by the same difference
      for (let i = 0; i < arr.length; i++) {
        arr[i].measureNumber += diff;
      }

      // If you want to ensure no measure goes below 1, you can clamp:
      /*
      for (let i = 0; i < arr.length; i++) {
        arr[i].measureNumber = Math.max(1, arr[i].measureNumber);
      }
      */

      // Reapply volumes/dynamics if needed
      applyStressVolumesIfAny(arr, stressOn);
      resetAllBeatVolumesToUserVolumes(arr);
      applyAllDynamicsToAllMeasures(arr);

      return arr;
    });
  }

  function getNextDenominatorUp(current) {
    const idx = validDenominators.indexOf(current);
    return idx < validDenominators.length - 1
      ? validDenominators[idx + 1]
      : current;
  }

  function getNextDenominatorDown(current) {
    const idx = validDenominators.indexOf(current);
    return idx > 0 ? validDenominators[idx - 1] : current;
  }

  // Add state to store original tempo changes (near other useState declarations around line 2980)
  const [originalTempoChanges, setOriginalTempoChanges] = useState([]);

  // Set to true by scaleTempoChangesFromOriginals just before it overwrites
  // tempoChanges, so the sync effect below skips that single pass and doesn't
  // re-derive the originals from the just-scaled values (which would round-trip
  // through Math.round and drift over repeated slides).
  const skipOriginalSyncRef = useRef(false);

  // Keep originalTempoChanges (the 100%-baseline view) in sync with the
  // visible tempoChanges. When the user edits a card mid-scale, the edit
  // is unscaled by the current percentage so the next slide preserves it.
  // Originals are stored as floats to avoid rounding drift across round-trips.
  useEffect(() => {
    if (skipOriginalSyncRef.current) {
      skipOriginalSyncRef.current = false;
      return;
    }
    if (isLoadingPattern) return;
    if (!scaleByPercentage || !tempoPercentage || tempoPercentage === 100) {
      setOriginalTempoChanges(tempoChanges.map((tc) => ({ ...tc })));
      return;
    }
    const unscaleRatio = 100 / tempoPercentage;
    setOriginalTempoChanges(
      tempoChanges.map((tc) => ({
        ...tc,
        startTempo: tc.startTempo != null ? tc.startTempo * unscaleRatio : tc.startTempo,
        endTempo: tc.endTempo != null ? tc.endTempo * unscaleRatio : tc.endTempo,
        stepSize: tc.stepSize != null ? tc.stepSize * unscaleRatio : tc.stepSize,
      }))
    );
    // eslint-disable-next-line
  }, [tempoChanges, isLoadingPattern]);

  // When tempo arrives from outside the percentage-scale UI (e.g., a
  // pattern load, MIDI/MusicXML import, or measures-driven init), baseTempo
  // and tempoPercentage are otherwise left untouched — making "Measure 1 BPM"
  // show the prior pattern's value. Detect the desync (tempo !== baseTempo *
  // tempoPercentage/100) and snap the scale baseline to the new tempo at 100%.
  useEffect(() => {
    const expectedTempo = Math.round(
      baseTempo * ((tempoPercentage || 100) / 100)
    );
    if (tempo !== expectedTempo) {
      setBaseTempo(tempo);
      setTempoPercentage(100);
    }
    // eslint-disable-next-line
  }, [tempo]);

  // Modify the scaleTempoChanges function (around line 4487)
  function scaleTempoChanges(ratio) {
    // Use original values as the base for scaling, not current values
    const baseChanges = originalTempoChanges.length > 0 ? originalTempoChanges : tempoChanges;
    
    setTempoChanges((prev) =>
      baseChanges.map((tc) => ({
        ...tc,
        // Scale from original values, not current values
        startTempo: Math.round(tc.startTempo * ratio),
        endTempo: Math.round(tc.endTempo * ratio),
        stepSize: tc.stepSize != null ? tc.stepSize * ratio : undefined,
      }))
    );
  }

  useEffect(() => {
    // Skip scaling if we're loading a pattern OR if percentage scaling is active
    if (isLoadingPattern || scaleByPercentage) {
      oldTempoRef.current = tempo;
      return;
    }

    // If the new tempo is different, scale the changes
    const oldTempo = oldTempoRef.current;
    if (oldTempo !== tempo && oldTempo > 0) {
      const ratio = tempo / oldTempo;
      scaleTempoChanges(ratio);
    }
    oldTempoRef.current = tempo;
  }, [tempo, isLoadingPattern, scaleByPercentage]);

  function scaleTempoChangesFromOriginals(percentage) {
    if (originalTempoChanges.length === 0) return;

    const ratio = percentage / 100;
    skipOriginalSyncRef.current = true;
    setTempoChanges(
      originalTempoChanges.map((tc) => ({
        ...tc,
        startTempo: Math.round(tc.startTempo * ratio),
        endTempo: Math.round(tc.endTempo * ratio),
        stepSize: tc.stepSize != null ? tc.stepSize * ratio : undefined,
      }))
    );
  }

  function scaleTempoChanges(ratio) {
    setTempoChanges((prev) =>
      prev.map((tc) => ({
        ...tc,
        // only scale if they exist
        startTempo: Math.round(tc.startTempo * ratio),
        endTempo: Math.round(tc.endTempo * ratio),
        stepSize: tc.stepSize != null ? tc.stepSize * ratio : undefined,
        // if you want to keep it integer:
        // stepSize: Math.round(tc.stepSize * ratio)
      }))
    );
  }

  useEffect(() => {
    // Skip scaling if we're loading a pattern
    if (isLoadingPattern) {
      oldTempoRef.current = tempo;
      return;
    }

    // If the new tempo is different, scale the changes
    const oldTempo = oldTempoRef.current;
    if (oldTempo !== tempo && oldTempo > 0) {
      const ratio = tempo / oldTempo;
      scaleTempoChanges(ratio);
    }
    oldTempoRef.current = tempo;
  }, [tempo, isLoadingPattern]);

  function handleDeleteMeasureInOverview(mIndex) {
    setMeasures((prev) => {
      // If you only want to allow deletion if there's more than one measure:
      if (prev.length > 1) {
        const newArr = [...prev];
        
        // Store the measure number before deletion
        const deletedMeasureNumber = newArr[mIndex].measureNumber;
        
        // Remove the measure
        newArr.splice(mIndex, 1);
        
        // Adjust numbering for all measures AFTER the deleted one
        for (let i = mIndex; i < newArr.length; i++) {
          // Decrease the measure number by 1 for all subsequent measures
          newArr[i].measureNumber = deletedMeasureNumber + (i - mIndex);
        }
        
        // Reapply processing logic
        applyStressVolumesIfAny(newArr, stressOn);
        resetAllBeatVolumesToUserVolumes(newArr);
        applyAllDynamicsToAllMeasures(newArr);
        
        return newArr;
      }
      // Otherwise, do nothing or reset to a default:
      return prev;
    });
  }

  async function deleteFolder(folderNameToDelete) {
    if (!currentUser) return;

    const patternsRef = collection(db, "users", currentUser.uid, "patterns");
    const q = query(patternsRef, where("folder", "==", folderNameToDelete));

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      alert(`No patterns found in folder "${folderNameToDelete}"`);
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to move all patterns in folder "${folderNameToDelete}" to "Uncategorized"?`
    );
    if (!confirmed) return;

    const updates = [];
    snapshot.forEach((docSnap) => {
      updates.push(
        updateDoc(docSnap.ref, {
          folder: "Uncategorized",
          updatedAt: serverTimestamp(),
        })
      );
    });

    await Promise.all(updates);
    alert(
      `Moved all patterns from "${folderNameToDelete}" to "Uncategorized".`
    );
  }

  function handleExportMIDI() {
    // You have `measures` and maybe `tempo`, `patternName` in this component's state:
    const fileName = patternName ? `${patternName}.mid` : "FlowFrame.mid";
    exportMIDIImplementation(
      measures,
      tempo,
      fileName,
      tempo,
      useTempoChanges,
      tempoChanges
    );
  }

  function handleGroupingCheckbox(measureIndex, groupingKey, isChecked) {
    setMeasures((prevMeasures) => {
      const newMeasures = [...prevMeasures];
      const m = newMeasures[measureIndex];
      if (!m) return prevMeasures;
  
      // Apply the change
      m[groupingKey] = isChecked;
  
      // Handle 5/4, 5/8, 5/16, 5/32 time signatures
      if (m.numerator === 5 && [4, 8, 16, 32].includes(m.denominator)) {
        // Handle removeNonAccents checkbox
        if (groupingKey === 'removeNonAccents') {
          // Re-apply current grouping with new removeNonAccents setting
          if (m.g2plus3) {
            applyFiveGrouping(m, "2+3", newMeasures);
          } else if (m.g3plus2) {
            applyFiveGrouping(m, "3+2", newMeasures);
          }
        }
        // If we're toggling ON a grouping, apply it and turn OFF other groupings
        else if (isChecked) {
          if (groupingKey === 'g2plus3') {
            m.g3plus2 = false; // Ensure other pattern is turned off
            applyFiveGrouping(m, "2+3", newMeasures);
          } else if (groupingKey === 'g3plus2') {
            m.g2plus3 = false; // Ensure other pattern is turned off
            applyFiveGrouping(m, "3+2", newMeasures);
          }
        } 
        // Only revert to default if ALL patterns are turned OFF
        else if (!m.g2plus3 && !m.g3plus2) {
          revertGroupingToDefault(m, newMeasures);
        }
      }
      
      // Handle 7/4, 7/8, 7/16, 7/32 time signatures
      else if (m.numerator === 7 && [4, 8, 16, 32].includes(m.denominator)) {
        // Handle removeNonAccents checkbox
        if (groupingKey === 'removeNonAccents') {
          // Re-apply current grouping with new removeNonAccents setting
          if (m.g2p2p3) {
            applySevenGrouping(m, "2+2+3", newMeasures);
          } else if (m.g3p2p2) {
            applySevenGrouping(m, "3+2+2", newMeasures);
          } else if (m.g2p3p2) {
            applySevenGrouping(m, "2+3+2", newMeasures);
          }
        }
        // If we're toggling ON a grouping, apply it and turn OFF other groupings
        else if (isChecked) {
          if (groupingKey === 'g2p2p3') {
            m.g3p2p2 = false;
            m.g2p3p2 = false;
            applySevenGrouping(m, "2+2+3", newMeasures);
          } else if (groupingKey === 'g3p2p2') {
            m.g2p2p3 = false;
            m.g2p3p2 = false;
            applySevenGrouping(m, "3+2+2", newMeasures);
          } else if (groupingKey === 'g2p3p2') {
            m.g2p2p3 = false;
            m.g3p2p2 = false;
            applySevenGrouping(m, "2+3+2", newMeasures);
          }
        } 
        // Only revert to default if ALL patterns are turned OFF
        else if (!m.g2p2p3 && !m.g3p2p2 && !m.g2p3p2) {
          revertGroupingToDefault(m, newMeasures);
        }
      }
      
      // Handle compound time signatures: 3/8, 6/8, 9/8, 12/8, 15/8
      else if ([3, 6, 9, 12, 15].includes(m.numerator) && m.denominator === 8) {
        if (groupingKey === 'removeNonAccents') {
          // Apply compound grouping with new removeNonAccents setting
          applyCompoundGrouping(m, newMeasures);
        }
      }
  
      return newMeasures;
    });
  }

  // Now the grouping functions have a third param: `newMeasures`

  function applyFiveGrouping(measureObj, pattern, newMeasures) {
    if (!measureObj.beatSounds || measureObj.beatSounds.length < 5) return;
  
    // Determine volume for non-accented beats
    const nonAccentVolume = measureObj.removeNonAccents ? 0 : 0.3;
  
    if (pattern === "2+3") {
      measureObj.beatSounds[0].userVolume = 1.0;
      measureObj.beatSounds[1].userVolume = nonAccentVolume;
      measureObj.beatSounds[2].userVolume = 1.0;
      measureObj.beatSounds[3].userVolume = nonAccentVolume;
      measureObj.beatSounds[4].userVolume = nonAccentVolume;
    } else {
      measureObj.beatSounds[0].userVolume = 1.0;
      measureObj.beatSounds[1].userVolume = nonAccentVolume;
      measureObj.beatSounds[2].userVolume = nonAccentVolume;
      measureObj.beatSounds[3].userVolume = 1.0;
      measureObj.beatSounds[4].userVolume = nonAccentVolume;
    }
  
    resetAllBeatVolumesToUserVolumes(newMeasures);
    applyAllDynamicsToAllMeasures(newMeasures);
  }
  
  function applySevenGrouping(measureObj, pattern, newMeasures) {
    if (!measureObj.beatSounds || measureObj.beatSounds.length < 7) return;
  
    // Determine volume for non-accented beats
    const nonAccentVolume = measureObj.removeNonAccents ? 0 : 0.3;
  
    if (pattern === "2+2+3") {
      measureObj.beatSounds[0].userVolume = 1.0;
      measureObj.beatSounds[1].userVolume = nonAccentVolume;
      measureObj.beatSounds[2].userVolume = 1.0;
      measureObj.beatSounds[3].userVolume = nonAccentVolume;
      measureObj.beatSounds[4].userVolume = 1.0;
      measureObj.beatSounds[5].userVolume = nonAccentVolume;
      measureObj.beatSounds[6].userVolume = nonAccentVolume;
    } else if (pattern === "3+2+2") {
      measureObj.beatSounds[0].userVolume = 1.0;
      measureObj.beatSounds[1].userVolume = nonAccentVolume;
      measureObj.beatSounds[2].userVolume = nonAccentVolume;
      measureObj.beatSounds[3].userVolume = 1.0;
      measureObj.beatSounds[4].userVolume = nonAccentVolume;
      measureObj.beatSounds[5].userVolume = 1.0;
      measureObj.beatSounds[6].userVolume = nonAccentVolume;
    } else {
      // "2+3+2"
      measureObj.beatSounds[0].userVolume = 1.0;
      measureObj.beatSounds[1].userVolume = nonAccentVolume;
      measureObj.beatSounds[2].userVolume = 1.0;
      measureObj.beatSounds[3].userVolume = nonAccentVolume;
      measureObj.beatSounds[4].userVolume = nonAccentVolume;
      measureObj.beatSounds[5].userVolume = 1.0;
      measureObj.beatSounds[6].userVolume = nonAccentVolume;
    }
  
    resetAllBeatVolumesToUserVolumes(newMeasures);
    applyAllDynamicsToAllMeasures(newMeasures);
  }
  
  function applyCompoundGrouping(measureObj, newMeasures) {
    if (!measureObj.beatSounds || measureObj.beatSounds.length === 0) return;
  
    // Determine volume for non-accented beats
    const nonAccentVolume = measureObj.removeNonAccents ? 0 : 1.0;
  
    // For compound meters, stressed beats are at positions 1, 4, 7, 10, 13...
    // (every 3rd beat starting from 1, which is index 0, 3, 6, 9, 12...)
    measureObj.beatSounds.forEach((beat, index) => {
      // Check if this beat is a stressed beat (1-indexed: 1, 4, 7, 10, 13...)
      // In 0-indexed: 0, 3, 6, 9, 12...
      const isStressed = index % 3 === 0;
      beat.userVolume = isStressed ? 1.0 : nonAccentVolume;
    });
  
    resetAllBeatVolumesToUserVolumes(newMeasures);
    applyAllDynamicsToAllMeasures(newMeasures);
  }

  function revertGroupingToDefault(measureObj, newMeasures) {
    measureObj.beatSounds.forEach((beat) => {
      beat.userVolume = 1.0;
    });

    resetAllBeatVolumesToUserVolumes(newMeasures);
    applyAllDynamicsToAllMeasures(newMeasures);
  }

  function applyBulkMeasureText(inputText) {
    // Split into lines
    const lines = inputText
      .split("\n")
      .map((ln) => ln.trim())
      .filter((ln) => ln.length > 0);

    if (!lines.length) return;

    setMeasures((prevMeasures) => {
      let newMeasures = [...prevMeasures];

      lines.forEach((line) => {
        const parsed = parseMeasureSignatureLine(line);
        if (!parsed) {
          console.warn("Couldn't parse line:", line);
          return; // skip
        }
        const { startMeasure, endMeasure, numerator, denominator } = parsed;
        const maxIndexNeeded = endMeasure - 1; // zero-based

        // 1) Expand measures array if needed
        if (maxIndexNeeded >= newMeasures.length) {
          const needed = maxIndexNeeded - newMeasures.length + 1;
          for (let i = 0; i < needed; i++) {
            newMeasures.push({
              ...createNewMeasure(stressOn),
              measureNumber: newMeasures.length + 1,
            });
          }
        }

        // 2) Update each measure in [startMeasure..endMeasure]
        for (let m = startMeasure - 1; m < endMeasure; m++) {
          const oldMeas = newMeasures[m];

          // Re-size beatSounds to match new numerator
          let updatedBeatSounds = [...oldMeas.beatSounds];

          const oldLength = updatedBeatSounds.length;
          const newLength = numerator; // e.g. "3" if user typed 3/4

          if (newLength > oldLength) {
            // expand
            const extraCount = newLength - oldLength;
            for (let k = 0; k < extraCount; k++) {
              updatedBeatSounds.push(createNewBeat());
            }
          } else if (newLength < oldLength) {
            // truncate
            updatedBeatSounds = updatedBeatSounds.slice(0, newLength);
          }

          newMeasures[m] = {
            ...oldMeas,
            measureNumber: m + 1,
            numerator,
            denominator,
            beatSounds: updatedBeatSounds,
          };
        }
      });

      // 3) Re-apply accent patterns, volumes, etc.
      applyStressVolumesIfAny(newMeasures, stressOn);
      resetAllBeatVolumesToUserVolumes(newMeasures);
      applyAllDynamicsToAllMeasures(newMeasures);

      return newMeasures;
    });
  }

  function applyBulkMeasureText(inputText) {
    // Split into lines
    const lines = inputText
      .split("\n")
      .map((ln) => ln.trim())
      .filter((ln) => ln.length > 0);
     if (!lines.length) return;
     setMeasures((prevMeasures) => {
      let newMeasures = [...prevMeasures];
       lines.forEach((line) => {
        const parsed = parseMeasureSignatureLine(line);
        if (!parsed) {
          console.warn("Couldn't parse line:", line);
          return; // skip
        }
        const {
          startMeasure,
          endMeasure,
          numerator,
          denominator,
          grouping,
          removeNonAccents,
        } = parsed;
        const maxIndexNeeded = endMeasure - 1; // zero-based
         // 1) Expand measures array if needed
        if (maxIndexNeeded >= newMeasures.length) {
          const needed = maxIndexNeeded - newMeasures.length + 1;
          for (let i = 0; i < needed; i++) {
            newMeasures.push({
              ...createNewMeasure(stressOn),
              measureNumber: newMeasures.length + 1,
            });
          }
        }
         // 2) Update each measure in [startMeasure..endMeasure]
        for (let m = startMeasure - 1; m < endMeasure; m++) {
          const oldMeas = newMeasures[m];
           // Re-size beatSounds to match new numerator
          let updatedBeatSounds = [...oldMeas.beatSounds];
           const oldLength = updatedBeatSounds.length;
          const newLength = numerator; // e.g. "3" if user typed 3/4
           if (newLength > oldLength) {
            // expand
            const extraCount = newLength - oldLength;
            for (let k = 0; k < extraCount; k++) {
              updatedBeatSounds.push(createNewBeat());
            }
          } else if (newLength < oldLength) {
            // truncate
            updatedBeatSounds = updatedBeatSounds.slice(0, newLength);
          }
           const updated = {
            ...oldMeas,
            measureNumber: m + 1,
            numerator,
            denominator,
            beatSounds: updatedBeatSounds,
          };
           // Optionally set/remove grouping flags if specified by the bulk editor
          if (typeof removeNonAccents === "boolean") {
            updated.removeNonAccents = removeNonAccents;
          }
           // Only change grouping flags if a grouping was provided
          if (grouping) {
            // Clear 5/7 flags before re-setting
            updated.g2plus3 = false;
            updated.g3plus2 = false;
            updated.g2p2p3 = false;
            updated.g3p2p2 = false;
            updated.g2p3p2 = false;
             if (numerator === 5 && [4, 8, 16, 32].includes(denominator)) {
              if (grouping === "2+3") updated.g2plus3 = true;
              else if (grouping === "3+2") updated.g3plus2 = true;
            } else if (
              numerator === 7 &&
              [4, 8, 16, 32].includes(denominator)
            ) {
              if (grouping === "2+2+3") updated.g2p2p3 = true;
              else if (grouping === "3+2+2") updated.g3p2p2 = true;
              else if (grouping === "2+3+2") updated.g2p3p2 = true;
            }
          }
           newMeasures[m] = updated;
        }
      });
       // 3) Apply grouping volumes if flags are present
      for (let i = 0; i < newMeasures.length; i++) {
        const meas = newMeasures[i];
        if (meas.numerator === 5 && [4, 8, 16, 32].includes(meas.denominator)) {
          if (meas.g2plus3) applyFiveGrouping(meas, "2+3", newMeasures);
          else if (meas.g3plus2) applyFiveGrouping(meas, "3+2", newMeasures);
        } else if (
          meas.numerator === 7 &&
          [4, 8, 16, 32].includes(meas.denominator)
        ) {
          if (meas.g2p2p3) applySevenGrouping(meas, "2+2+3", newMeasures);
          else if (meas.g3p2p2) applySevenGrouping(meas, "3+2+2", newMeasures);
          else if (meas.g2p3p2) applySevenGrouping(meas, "2+3+2", newMeasures);
        } else if ([3, 6, 9, 12, 15].includes(meas.numerator) && meas.denominator === 8) {
          if (meas.removeNonAccents) applyCompoundGrouping(meas, newMeasures);
        }
      }
       // 4) Re-apply stress patterns (will skip measures with custom grouping flags),
      //    ensure current volumes match user volumes, and re-apply dynamics.
      applyStressVolumesIfAny(newMeasures, stressOn);
      resetAllBeatVolumesToUserVolumes(newMeasures);
      applyAllDynamicsToAllMeasures(newMeasures);
       return newMeasures;
    });
  }
 

  function handleAddHighlights() {
    const measuresToToggle = parseMeasureRanges(highlightInput);

    setHighlightedMeasures((prev) => {
      const newSet = new Set(prev);
      for (const m of measuresToToggle) {
        if (newSet.has(m)) {
          newSet.delete(m); // remove if existing
        } else {
          newSet.add(m); // else add
        }
      }
      return Array.from(newSet).sort((a, b) => a - b);
    });

    setHighlightInput("");
  }

  function handleApplyBulkEditorChanges(inputText) {
    applyBulkMeasureText(inputText);
    setBulkEditorOpen(false);
  }

  function handleApplyBulkEditorPitches(edits) {
    if (!Array.isArray(edits) || edits.length === 0) {
      setBulkEditorOpen(false);
      return;
    }
    const editsByMeasure = new Map();
    edits.forEach((e) => {
      if (!editsByMeasure.has(e.measureIdx)) editsByMeasure.set(e.measureIdx, []);
      editsByMeasure.get(e.measureIdx).push(e);
    });
    setMeasures((prev) => {
      const next = [...prev];
      editsByMeasure.forEach((measureEdits, mIdx) => {
        const measure = next[mIdx];
        if (!measure || !Array.isArray(measure.beatSounds)) return;
        const newBeatSounds = [...measure.beatSounds];
        measureEdits.forEach(({ beatIdx, dronePitchNote, dronePitchOctave }) => {
          const beat = newBeatSounds[beatIdx];
          if (!beat) return;
          newBeatSounds[beatIdx] = {
            ...beat,
            dronePitchNote,
            dronePitchOctave,
          };
        });
        next[mIdx] = { ...measure, beatSounds: newBeatSounds };
      });
      return next;
    });
    setBulkEditorOpen(false);
  }

  function removeSingleHighlight(measureNum) {
    setHighlightedMeasures((prev) => prev.filter((m) => m !== measureNum));
  }

  function addNewSubdivision(mIndex, bIndex) {
    setMeasures((prev) => {
      // 1) Make a shallow copy of the entire measures array
      const newMeasures = [...prev];

      // 2) Make a shallow copy of the targeted measure
      const measure = { ...newMeasures[mIndex] };
      newMeasures[mIndex] = measure;

      // 3) Make a shallow copy of the measure's beatSounds array
      const newBeatSounds = [...measure.beatSounds];
      measure.beatSounds = newBeatSounds;

      // 4) Copy the specific beat object
      const oldBeat = newBeatSounds[bIndex];
      const newBeat = { ...oldBeat };
      newBeatSounds[bIndex] = newBeat;

      // 5) Now create a new subdivisions array, instead of mutating in place
      const newSubs = [...newBeat.subdivisions];

      if (newSubs.length < 5) {
        newSubs.push({
          subCount: 1,
          accentedSubs: [],
          mutedSubs: [],
        });
      }

      newBeat.subdivisions = newSubs;

      return newMeasures;
    });
  }

  function removeSubdivision(mIndex, bIndex, subIndex) {
    setMeasures((prev) => {
      const newMeasures = [...prev];
      const measure = newMeasures[mIndex];
      const beat = measure.beatSounds[bIndex];
      beat.subdivisions.splice(subIndex, 1);
      return newMeasures;
    });
  }

  function updateSubdivision(mIndex, bIndex, subIndex, partial) {
    // partial is { subCount: X } or { accentedSubs: [...] }, etc.
    setMeasures((prev) => {
      const newMeasures = [...prev];
      const measure = newMeasures[mIndex];
      const beat = measure.beatSounds[bIndex];
      const oldSub = beat.subdivisions[subIndex];

      beat.subdivisions[subIndex] = { ...oldSub, ...partial };
      return newMeasures;
    });
  }

  function quickSubRulesMeasureUpdater(touchedDenoms, rules) {
    return (measures) =>
      measures.map((m) => {
        if (!touchedDenoms.has(m.denominator)) return m;
        // Last enabled rule for this denominator wins.
        const match = [...rules]
          .reverse()
          .find((r) => r.enabled && r.denominator === m.denominator);
        const subCount = match ? match.subCount : 1;
        return {
          ...m,
          beatSounds: m.beatSounds.map((b) => {
            const firstSub = b.subdivisions?.[0] || {};
            const rest = b.subdivisions ? b.subdivisions.slice(1) : [];
            return {
              ...b,
              subdivisions: [
                { ...firstSub, subCount, accentedSubs: [], mutedSubs: [] },
                ...rest,
              ],
            };
          }),
        };
      });
  }

  function commitQuickSubRules(nextRules) {
    const prevRules = quickSubRulesRef.current;
    quickSubRulesRef.current = nextRules;
    setQuickSubRules(nextRules);

    const touchedDenoms = new Set([
      ...prevRules.map((r) => r.denominator),
      ...nextRules.map((r) => r.denominator),
    ]);
    if (touchedDenoms.size > 0) {
      setMeasures(quickSubRulesMeasureUpdater(touchedDenoms, nextRules));
    }
  }

  function addQuickSubRule() {
    const prev = quickSubRulesRef.current;
    if (prev.length >= 4) return;
    const used = new Set(prev.map((r) => r.denominator));
    const denominator = [4, 8, 2, 16, 1, 32].find((d) => !used.has(d)) ?? 4;
    const next = [
      ...prev,
      { id: uuidv4(), enabled: true, denominator, subCount: 2 },
    ];
    commitQuickSubRules(next);
  }

  function removeQuickSubRule(id) {
    const next = quickSubRulesRef.current.filter((r) => r.id !== id);
    commitQuickSubRules(next);
  }

  function updateQuickSubRule(id, partial) {
    const next = quickSubRulesRef.current.map((r) =>
      r.id === id ? { ...r, ...partial } : r
    );
    commitQuickSubRules(next);
  }

  // Clear rules without rewriting measures (used when loading a pattern or resetting,
  // since the new measures already encode their own subdivisions).
  function clearQuickSubRulesWithoutApplying() {
    quickSubRulesRef.current = [];
    setQuickSubRules([]);
  }

  // Safety net: re-apply currently enabled rules to all matching measures.
  // Called at play start so the scheduler always sees subdivisions consistent with the rules,
  // even if a measure's denominator changed after the rules were set.
  function reapplyQuickSubRulesNow() {
    const rules = quickSubRulesRef.current;
    if (rules.length === 0) return;
    const denoms = new Set(rules.map((r) => r.denominator));
    setMeasures(quickSubRulesMeasureUpdater(denoms, rules));
  }

  function toggleCustomNoteWithOctave(mIndex, bIndex, semitone, octave) {
    setMeasures((prev) =>
      prev.map((measure, i) => {
        if (i !== mIndex) return measure;
        
        // Calculate the MIDI note number
        const midiNote = octave * 12 + semitone;
        
        // Get the original beat that was clicked
        const originalBeat = measure.beatSounds[bIndex];
        if (!originalBeat) return measure;
        
        // Create new selected notes list (add or remove the note)
        let newSelectedNotes = [...(originalBeat.selectedCustomNotes || [])];
        if (newSelectedNotes.includes(midiNote)) {
          // Remove if already selected
          newSelectedNotes = newSelectedNotes.filter(note => note !== midiNote);
        } else {
          // Add if not selected
          newSelectedNotes.push(midiNote);
        }
        newSelectedNotes.sort((a, b) => a - b);
        
        // Check if we should apply to all beats
        const applyToAllBeats = measure.applyBeatOneDroneToAllBeats;
        
        // Update all beats or just the clicked one
        const newBeatSounds = measure.beatSounds.map((beat, j) => {
          // If not applying to all beats, only update the clicked beat
          if (!applyToAllBeats && j !== bIndex) return beat;
          
          return {
            ...beat,
            useCustomNotes: true, // Force this on when selecting notes
            selectedCustomNotes: newSelectedNotes
          };
        });
        
        return {
          ...measure,
          beatSounds: newBeatSounds
        };
      })
    );
  }

  const { measureEndTimeRef, schedulerIdRef, scheduledBeatsRef } =
    useLookaheadScheduler(
      isPlaying,
      measures,
      tempo,
      useTempoChanges,
      enableAdditionalTempoChanges,
      tempoChanges,
      muteRandomBeats,
      mutePercentage,
      loopMode,
      masterGain,
      aTuning,
      activeModulationRef,
      setModulationDisplay,
      currentBarRef,
      currentBeatRef,
      setCurrentBar,
      setCurrentBeat,
      globalMeasureCountRef,
      partialOscRefs,
      chordOscRefs,
      audioContextRef,
      masterGainNodeRef,
      activeChordRef,
      loopCountRef,
      muteBeats,
      muteDrones,
      muteTuplets,
      muteSubs,
      stressOn,
      countInQueueRef,
      loopStartMeasure,
      loopEndMeasure,
      modulateOnLoop,
      modLoopInterval,
      modLoopDirection,
      loopPitchOffsetRef,
      subscriptionTier,
      selectedNoteValue,
      muteRandomDrones,
      muteRandomDronePercentage,
      rehearsalLetters,
      loopIncrementalSettings,
      specificLoopCountRef,
      countInMeasures,
      loopCountInEnabled
    );

  function precomputePatternTimings(
    measures,
    tempo,
    useTempoChanges,
    tempoChanges,
    loopMode,
    startMeasure,
    selectedNoteValue,
    loopCountRef,
    loopIncrementalSettings,
    loopStartMeasure,
    loopEndMeasure,
    specificLoopCountRef
  ) {
    const result = {
      measureTimings: [],
      totalDuration: 0,
    };

    let currentTime = 0;
    const startIndex = startMeasure - 1;

    // First pass: calculate all measure durations and timings
    for (let i = 0; i < measures.length; i++) {
      const measureIndex = (startIndex + i) % measures.length;
      const measure = measures[measureIndex];

      // Create effective tempo changes array with loop incremental settings
      let effectiveTempoChanges = [...tempoChanges];

      if (loopMode === "specific" && loopIncrementalSettings?.enabled) {
        // Create a proper tempo change from loop incremental settings
        const loopIncrementalChange = {
          type: "incremental",
          scope: loopIncrementalSettings.scope,
          startMeasure: loopStartMeasure,
          endMeasure: loopEndMeasure,
          startTempo: loopIncrementalSettings.startTempo,
          endTempo: loopIncrementalSettings.endTempo,
          stepSize: loopIncrementalSettings.stepSize,
          stepEvery: loopIncrementalSettings.stepEvery,
          localLoopCount: specificLoopCountRef.current
        };
        effectiveTempoChanges.push(loopIncrementalChange);
      }
      
      const effectiveTempo = computeEffectiveTempo(
        measureIndex,  
        0,             
        measure.numerator, 
        tempo,
        useTempoChanges || loopIncrementalSettings?.enabled,
        effectiveTempoChanges,
        loopCountRef
      );

      // Calculate beat duration in seconds
      const getCurrentTempoChange = (measureIndex, tempoChanges) => {
        // Find the active tempo change for this measure
        const sortedChanges = [...tempoChanges].sort((a, b) => a.startMeasure - b.startMeasure);
        let activeChange = null;
        
        for (const tc of sortedChanges) {
          if (measureIndex >= tc.startMeasure - 1) {
            if (measureIndex <= tc.endMeasure - 1 || tc.type === "sudden") {
              activeChange = tc;
            }
          }
        }
        return activeChange;
      };
      
      const activeTempoChange = getCurrentTempoChange(measureIndex, effectiveTempoChanges);
      const noteValueForThisMeasure = activeTempoChange?.noteValue || selectedNoteValue;
      const noteValueFactor = noteValueForThisMeasure?.factor ?? 1;
      const finalBPM =
        effectiveTempo * (noteValueFactor * (measure.denominator / 4));
      const beatSec = 60 / finalBPM;

      // Calculate measure duration
      const measureDuration = measure.numerator * beatSec;

      // Store beat timings for this measure
      const beatTimings = [];
      for (let beat = 0; beat < measure.numerator; beat++) {
        beatTimings.push({
          startTime: currentTime + beat * beatSec,
          duration: beatSec,
        });
      }

      // Store measure info
      result.measureTimings.push({
        index: measureIndex,
        startTime: currentTime,
        duration: measureDuration,
        beats: beatTimings,
      });

      currentTime += measureDuration;
    }

    result.totalDuration = currentTime;
    return result;
  }

  function precomputeModulations(measures, startMeasure, tempo) {
    // Start with no active modulation
    let activeModulation = {
      isActive: false,
      fromSubdivision: null,
      toSubdivision: null,
      sourceTempoReference: tempo,
    };
  
    // Begin with the base tempo and track effective tempo through all changes
    let effectiveTempo = tempo;
  
    // First, find the index of the start measure in the measures array
    const startMeasureIndex = measures.findIndex(m => m.measureNumber === startMeasure);
    if (startMeasureIndex === -1) {
      console.warn(`Start measure ${startMeasure} not found, using first measure`);
      return { activeModulation, effectiveTempo };
    }
  
    // Process all measures from the beginning up to (but not including) the start measure
    // We need to process them in order by measureNumber, not array index
    const sortedMeasures = [...measures].sort((a, b) => a.measureNumber - b.measureNumber);
    
    for (const measure of sortedMeasures) {
      // Stop when we reach the start measure
      if (measure.measureNumber >= startMeasure) {
        break;
      }
  
      // Apply any metric modulation from this measure
      if (measure.hasModulation) {
        const fromVal = measure.fromSubdivision?.value || 4;
        const toVal = measure.toSubdivision?.value || 4;
  
        // If there's already an active modulation, chain it
        if (activeModulation.isActive) {
          // Calculate the tempo after applying current modulation
          const modifiedTempo =
            activeModulation.sourceTempoReference *
            (activeModulation.fromSubdivision.value /
              activeModulation.toSubdivision.value);
  
          // Set up the new modulation based on this modified tempo
          activeModulation = {
            isActive: true,
            fromSubdivision: measure.fromSubdivision,
            toSubdivision: measure.toSubdivision,
            sourceTempoReference: modifiedTempo,
          };
        } else {
          // First modulation - use current effective tempo
          activeModulation = {
            isActive: true,
            fromSubdivision: measure.fromSubdivision,
            toSubdivision: measure.toSubdivision,
            sourceTempoReference: effectiveTempo,
          };
        }
  
        // Calculate the new effective tempo after this modulation
        effectiveTempo = activeModulation.sourceTempoReference * (toVal / fromVal);
      }
    }
  
    return { activeModulation, effectiveTempo };
  }

  function precomputeTempoChanges(measures, startMeasure, baseTempo, useTempoChanges, tempoChanges) {
    if (!useTempoChanges || !tempoChanges.length) {
      return baseTempo;
    }
  
    // Find the position of the start measure in the sequence
    const sortedMeasures = [...measures].sort((a, b) => a.measureNumber - b.measureNumber);
    const startMeasurePosition = sortedMeasures.findIndex(m => m.measureNumber === startMeasure);
    
    if (startMeasurePosition === -1) {
      return baseTempo;
    }
  
    // Apply all tempo changes that should have occurred before the start measure
    let effectiveTempo = baseTempo;
    
    for (const tc of tempoChanges) {
      const { startMeasure: tcStart, endMeasure: tcEnd, type } = tc;
      
      // Skip tempo changes that start at or after our start measure
      if (tcStart >= startMeasure) {
        continue;
      }
      
      // Apply tempo changes that completed before our start measure
      if (tcEnd < startMeasure) {
        switch (type) {
          case "sudden":
          case "linear":
          case "exponential":
            effectiveTempo = tc.endTempo;
            break;
          case "incremental":
            // For incremental changes, calculate where we would be
            if (tc.scope === "whole") {
              // This would need loop count, which we don't have here
              // Leave as base tempo for now
            } else {
              // Apply incremental change up to start measure
              const measuresInRange = Math.min(tcEnd, startMeasure - 1) - tcStart + 1;
              effectiveTempo = tc.startTempo + (tc.stepSize * measuresInRange);
            }
            break;
        }
      }
      // For tempo changes that span across our start measure, 
      // calculate the interpolated value at the start measure
      else if (tcStart < startMeasure && tcEnd >= startMeasure) {
        const fraction = (startMeasure - tcStart) / (tcEnd - tcStart);
        
        switch (type) {
          case "linear":
            effectiveTempo = tc.startTempo + fraction * (tc.endTempo - tc.startTempo);
            break;
          case "exponential":
            if (tc.startTempo > 0 && tc.endTempo > 0) {
              const ratio = tc.endTempo / tc.startTempo;
              effectiveTempo = tc.startTempo * Math.pow(ratio, fraction);
            }
            break;
          case "sudden":
            effectiveTempo = tc.endTempo;
            break;
        }
      }
    }
    
    return effectiveTempo;
  }

  const handleStartStop = useCallback(() => {
    // Warm up the speech synth synchronously inside the click handler when
    // starting playback with rehearsal-mark announcements enabled. This is
    // the most reliable user-gesture context for spinning up the engine
    // before any real speak() call, and it gives the synth maximum time
    // before the first downbeat.
    if (!isPlaying && announceRehearsalMarks) {
      warmUpSpeech();
    }
    if (isPlaying) {
      if (startTimeRef.current > 0) {
        const playDuration = (Date.now() - startTimeRef.current) / 1000;
       trackEvent("pattern_stop", {
         pattern_id: patternName || "unnamed",
         duration_seconds: Math.round(playDuration)
       });
      }
      // -- STOPPING --
      setIsPlaying(false);
      setCurrentBeat(0);
      setCurrentBar(0);
      currentBarRef.current = 0;
      currentBeatRef.current = 0;
      globalMeasureCountRef.current = 0;
      activeChordRef.current.signature = "";
      specificLoopCountRef.current = 0;

      // Clean up audio resources
      if (audioContextRef.current) {
        // Stop oscillators
        partialOscRefs.current.forEach(({ osc }) => {
          try {
            osc.stop();
          } catch (e) {}
        });
        chordOscRefs.current.forEach(({ osc }) => {
          try {
            osc.stop();
          } catch (e) {}
        });

        // Clear references
        partialOscRefs.current = [];
        chordOscRefs.current = [];
      }

      return;
    }
    // DEBUG: Log all relevant values
    console.log('=== FEATURE CHECK DEBUG ===');
    console.log('subscriptionTier:', subscriptionTier);
    console.log('useTempoChanges:', useTempoChanges);
    console.log('modulateOnLoop:', modulateOnLoop);
    console.log('measures count:', measures.length);
    
    const featureCheck = patternUsesLockedFeatures(measures, subscriptionTier, useTempoChanges, modulateOnLoop);
    
    console.log('featureCheck result:', featureCheck);
    console.log('=========================');
    
    if (featureCheck.locked) {

       // Track locked feature attempt
      trackEvent("feature_locked", {
      feature_name: featureCheck.feature,
        required_tier: featureCheck.requiredTier,
        user_tier: subscriptionTier
      });

      alert(
        `This pattern uses ${featureCheck.feature} which requires a ${featureCheck.requiredTier} subscription. Please upgrade to play this pattern.`
      );
      return;
    }


        // Track pattern play event
      trackEvent("pattern_play", {
        pattern_id: patternName || "unnamed",
        pattern_source: patternName ? "user_created" : "temporary",
        pattern_complexity: getPatternComplexity(measures),
        measures_count: measures.length,
        tempo: tempo,
        loop_mode: loopMode,
        features_used: getPatternFeatures(measures, muteRandomBeats)
      }
  );
    // -- STARTING --

      // Auto-collapse for large patterns (>20 measures)
  if (measures.length > 20) {
    // Save current collapsed state
    setPreviousCollapsedState(collapsedBeats);
    // Collapse all measures
    const newCollapsedState = {};
    for (let i = 0; i < measures.length; i++) {
      newCollapsedState[i] = true;
    }
    setCollapsedBeats(newCollapsedState);
  }

    // Show loading indicator for complex patterns on mobile
    const isComplexPattern = measures.length > 50 && window.innerWidth < 768;
    if (isComplexPattern) {
      setIsLoading(true);
    }

    // Reset references
    loopPitchOffsetRef.current = 0;
    loopCountRef.current = 0;
    precomputedTimingsRef.current = null;

    // Use setTimeout to defer heavy computation
    setTimeout(
      () => {
        // Initialize timing values
        // Initialize timing values
      let startMeas = loopMode === "specific" ? loopStartMeasure : startMeasureNumber;

      // Find the index of the measure that matches the user's requested measure number
const measureIndex = measures.findIndex(m => m.measureNumber === startMeas);

// If found, use that index, otherwise fall back to the first measure
currentBarRef.current = measureIndex !== -1 ? measureIndex : 0;
currentBeatRef.current = 0;

// CRITICAL FIX: Set globalMeasureCountRef to reflect the actual position in the pattern
// This ensures tempo changes work correctly when starting mid-pattern
if (measureIndex !== -1) {
  // Calculate how many measures we've "skipped" to get to the start measure
  const sortedMeasures = [...measures].sort((a, b) => a.measureNumber - b.measureNumber);
  const startMeasurePosition = sortedMeasures.findIndex(m => m.measureNumber === startMeas);
  globalMeasureCountRef.current = startMeasurePosition >= 0 ? startMeasurePosition : 0;
} else {
  globalMeasureCountRef.current = 0;
}
      activeChordRef.current.signature = "";

      // 2) Build count-in measures if requested
      if (countInMeasures > 0) {
        const measureObj = measures[currentBarRef.current];
        // Create effective tempo changes array with loop incremental settings
        let effectiveTempoChanges = [...tempoChanges];

        if (loopMode === "specific" && loopIncrementalSettings?.enabled) {
          // Create a proper tempo change from loop incremental settings
          const loopIncrementalChange = {
            type: "incremental",
            scope: loopIncrementalSettings.scope,
            startMeasure: loopStartMeasure,
            endMeasure: loopEndMeasure,
            startTempo: loopIncrementalSettings.startTempo,
            endTempo: loopIncrementalSettings.endTempo,
            stepSize: loopIncrementalSettings.stepSize,
            stepEvery: loopIncrementalSettings.stepEvery,
            localLoopCount: specificLoopCountRef.current
          };
          effectiveTempoChanges.push(loopIncrementalChange);
        }
        
        const measureTempo = computeEffectiveTempo(
          currentBarRef.current,
          0,
          measureObj.numerator,
          tempo,
          useTempoChanges || loopIncrementalSettings?.enabled,
          effectiveTempoChanges,
          loopCountRef
        );

        // Mirror the target measure's per-beat userVolume so the count-in
        // sounds out the same on/off pattern the user set. Falls back to
        // uniform 1.0 if every beat is muted.
        const sourceBeats = Array.isArray(measureObj.beatSounds) ? measureObj.beatSounds : [];
        const beatVolumes = Array.from({ length: measureObj.numerator }, (_, i) => {
          const v = sourceBeats[i]?.userVolume;
          return typeof v === 'number' ? Math.max(0, Math.min(1, v)) : 1.0;
        });
        const allMuted = beatVolumes.every((v) => v <= 0.05);
        const effectiveVolumes = allMuted
          ? beatVolumes.map(() => 1.0)
          : beatVolumes;

        const virtualMeasures = [];
        for (let i = 0; i < countInMeasures; i++) {
          virtualMeasures.push({
            numerator: measureObj.numerator,
            denominator: measureObj.denominator,
            beatSounds: effectiveVolumes.map((vol) => ({
              id: "countInClick",
              name: "Count-In",
              type: "sine",
              userVolume: vol,
              beatVolume: 1.0,
              dronePitchNote: null,
              subdivisions: [
                { subCount: 1, accentedSubs: [], mutedSubs: [] },
              ],
            })),
            isCountIn: true,
            overrideTempo: measureTempo,
          });
        }
        countInQueueRef.current = virtualMeasures;
      } else {
        countInQueueRef.current = [];
      }

      // Pre-compute beat timings for the entire pattern
      const precomputedBeats = precomputePatternTimings(
        measures,
        tempo,
        useTempoChanges,
        tempoChanges,
        loopMode,
        startMeasureNumber,
        selectedNoteValue,
        loopCountRef
      );

      // Store pre-computed timings
      precomputedTimingsRef.current = precomputedBeats;

      // Create AudioContext, Gains, and Oscillators
      const audioCtx = new (window.AudioContext ||
        window.webkitAudioContext)();

      // Check if audio context starts suspended
      if (audioCtx.state === 'suspended') {
        setShowSilentModeWarning(true);
        // Try to resume it
        audioCtx.resume().catch(console.warn);
      }

      // Add event listener to resume audio context when suspended
      audioCtx.onstatechange = () => {
        if (audioCtx.state === 'suspended' && isPlaying) {
          audioCtx.resume();
          setShowSilentModeWarning(true);
        } else if (audioCtx.state === 'running') {
          setShowSilentModeWarning(false);
        }
      };
      audioContextRef.current = audioCtx;
      startTimeRef.current = audioCtx.currentTime;

      const mainGain = audioCtx.createGain();
      mainGain.gain.value = masterGain;
      mainGain.connect(audioCtx.destination);
      masterGainNodeRef.current = mainGain;

      // Create essential oscillators first (potentially fewer on mobile)
      const isMobile = window.innerWidth < 768;
      const initialOscCount = isMobile ? Math.min(8, measures.length) : 15;

      // Partials
      partialOscRefs.current = [];
      for (let i = 0; i < initialOscCount; i++) {
        const osc = audioCtx.createOscillator();
        osc.type = "sine";
        const g = audioCtx.createGain();
        g.gain.value = 0;
        osc.connect(g).connect(mainGain);
        osc.start();
        partialOscRefs.current.push({ osc, gainNode: g });
      }

      // Chord oscillators
      chordOscRefs.current = [];
      for (let i = 0; i < 4; i++) {
        const osc = audioCtx.createOscillator();
        osc.type = "sine";
        const g = audioCtx.createGain();
        g.gain.value = 0;
        osc.connect(g).connect(mainGain);
        osc.start();
        chordOscRefs.current.push({ osc, gainNode: g });
      }

      // Create remaining oscillators after a short delay if needed
      if (initialOscCount < 15 && !isMobile) {
        setTimeout(() => {
          for (let i = initialOscCount; i < 15; i++) {
            const osc = audioCtx.createOscillator();
            osc.type = "sine";
            const g = audioCtx.createGain();
            g.gain.value = 0;
            osc.connect(g).connect(mainGain);
            osc.start();
            partialOscRefs.current.push({ osc, gainNode: g });
          }
        }, 100);
      }

      // Reset UI states
      setCurrentBeat(0);
      setCurrentBar(currentBarRef.current);

      // Precompute and apply modulations for the start measure
if (startMeas > 1) {
  // First, calculate what the tempo should be after any tempo changes
  const precomputedTempo = precomputeTempoChanges(
    measures, 
    startMeas, 
    tempo, 
    useTempoChanges, 
    tempoChanges
  );
  
  // Then calculate modulations using the precomputed tempo
  const { activeModulation, effectiveTempo } = precomputeModulations(
    measures,
    startMeas,
    precomputedTempo
  );

        // Apply the modulation state
        activeModulationRef.current = activeModulation;

        // Update the display
        if (activeModulation.isActive) {
          setModulationDisplay({
            isActive: true,
            fromName: activeModulation.fromSubdivision.name,
            toName: activeModulation.toSubdivision.name,
          });
        } else {
          setModulationDisplay({ isActive: false, fromName: "", toName: "" });
        }
      } else {
        // Starting from measure 1, no modulations to apply
        activeModulationRef.current = {
          isActive: false,
          fromSubdivision: null,
          toSubdivision: null,
          sourceTempoReference: INITIAL_TEMPO,
        };
        setModulationDisplay({ isActive: false, fromName: "", toName: "" });
      }

      // Hide loading indicator and start playback
      reapplyQuickSubRulesNow();
      setIsLoading(false);
      setIsPlaying(true);
      },
      isComplexPattern ? 50 : 10
    ); // More delay for complex patterns
  }, [
    isPlaying,
    masterGain,
    tempo,
    startMeasureNumber,
    measures,
    countInMeasures,
    useTempoChanges,
    tempoChanges,
    loopMode,
    loopStartMeasure,
    selectedNoteValue,
    collapsedBeats,
    announceRehearsalMarks,
  ]);

  // Cleanup on STOP
  useEffect(() => {
    if (!isPlaying) {
      // 1) Remove leftover highlights
      document
        .querySelectorAll(".highlighted-beat")
        .forEach((el) => el.classList.remove("highlighted-beat"));

      // 2) Cancel any animations
      if (schedulerIdRef.current) {
        clearTimeout(schedulerIdRef.current);
        schedulerIdRef.current = null;
      }

      // 3) Close & null-out the audio context
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }
      masterGainNodeRef.current = null;

      // 4) Stop and disconnect all partial oscillators
      partialOscRefs.current.forEach(({ osc, gainNode }) => {
        try {
          osc.stop();
        } catch {}
        osc.disconnect();
        gainNode.disconnect();
      });
      partialOscRefs.current = [];

      // 5) Stop and disconnect all chord oscillators
      chordOscRefs.current.forEach(({ osc, gainNode }) => {
        try {
          osc.stop();
        } catch {}
        osc.disconnect();
        gainNode.disconnect();
      });
      chordOscRefs.current = [];
    }
  }, [isPlaying, schedulerIdRef]);

  // Keep handleStartStopRef pointing at the latest handleStartStop. Async
  // operations (MP3 export) need this because they update state (loopMode,
  // startMeasureNumber, countInMeasures) and then trigger play — the new
  // closure with those updated deps lands here once React commits the render.
  useEffect(() => {
    handleStartStopRef.current = handleStartStop;
  }, [handleStartStop]);

  // ------------------------------------------------------------- MP3 export
  // Renders the selected measure range to MP3 by live-capturing the existing
  // metronome graph (no scheduler refactor). Steps:
  //   1) snapshot the playback-shaping state that the user has set
  //   2) apply export-specific overrides (force startMeasureNumber, set
  //      loopMode='loop' to suppress any 'specific' loop-back, suppress
  //      count-in measures)
  //   3) wait for React to commit so handleStartStopRef points at a closure
  //      that reads the overridden values
  //   4) kick off playback, hook a MediaRecorder onto masterGainNode + Tone
  //      destination, capture for the precomputed duration of the selection
  //   5) stop the recorder, stop playback, restore the snapshot, encode the
  //      captured audio to MP3, trigger the download
  const handleExportMp3 = useCallback(
    async ({ startMeasure, endMeasure, onProgress }) => {
      if (!measures || measures.length === 0) {
        throw new Error('No measures to export.');
      }
      const startIdx = Math.max(0, Math.min(measures.length - 1, startMeasure - 1));
      const endIdx = Math.max(startIdx, Math.min(measures.length - 1, endMeasure - 1));

      // Compute total seconds for the selected range, naively (ignores
      // mid-export tempo changes — those are a v2 concern). Each measure's
      // beat length follows the same finalBPM formula as the scheduler.
      const noteFactor = selectedNoteValue?.factor ?? 1;
      let durationSec = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        const m = measures[i];
        const finalBPM = tempo * noteFactor * (m.denominator / 4);
        const beatSec = 60 / finalBPM;
        durationSec += m.numerator * beatSec;
      }
      const TAIL_BUFFER = 0.4; // let drones / harmony tails finish ringing

      const snapshot = {
        loopMode,
        startMeasureNumber,
        countInMeasures,
      };
      const startMeasureNum =
        measures[startIdx]?.measureNumber ?? startIdx + 1;

      try {
        // Apply overrides. setLoopMode('loop') keeps any pre-existing
        // 'specific' range from wrapping mid-export; we stop manually after
        // durationSec elapses so 'loop' never actually wraps.
        setLoopMode('loop');
        setStartMeasureNumber(startMeasureNum);
        setCountInMeasures(0);

        // Give React two frames to commit + the handleStartStopRef effect
        // to run, so the next call sees the overridden closure.
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

        // Kick off playback. handleStartStop creates audioCtx + masterGain
        // inside a small setTimeout, so we poll until they exist.
        handleStartStopRef.current?.();

        const waitForAudio = async () => {
          const t0 = performance.now();
          while (performance.now() - t0 < 2000) {
            if (audioContextRef.current && masterGainNodeRef.current) return true;
            await new Promise((r) => setTimeout(r, 20));
          }
          return false;
        };
        const ready = await waitForAudio();
        if (!ready) throw new Error('Audio engine did not start within 2 seconds.');

        const recorder = createMp3Recorder({
          audioCtx: audioContextRef.current,
          masterGainNode: masterGainNodeRef.current,
        });
        recorder.start({ silent: true });

        if (onProgress) onProgress({ phase: 'recording', progress: 0 });

        // Drive a progress bar from the AudioContext clock so it tracks
        // actual playback (not wall-clock — they can diverge if the tab is
        // throttled).
        const startCtxTime = audioContextRef.current.currentTime;
        const progressTimer = setInterval(() => {
          const ctx = audioContextRef.current;
          if (!ctx) return;
          const elapsed = ctx.currentTime - startCtxTime;
          const p = Math.max(0, Math.min(1, elapsed / durationSec));
          if (onProgress) onProgress({ phase: 'recording', progress: p });
        }, 100);

        await new Promise((r) => setTimeout(r, (durationSec + TAIL_BUFFER) * 1000));
        clearInterval(progressTimer);
        if (onProgress) onProgress({ phase: 'recording', progress: 1 });

        // Stop the recorder before tearing down the audio graph so onstop
        // fires while the MediaStreamDestination is still alive.
        const capturedBlob = await recorder.stop();

        // Now stop playback — handleStartStop closes the audio context.
        if (isPlaying || audioContextRef.current) {
          handleStartStopRef.current?.();
        }

        // Encode → MP3. webmBlobToMp3 spins up its own decode context if the
        // original one is already closed (which it is by this point).
        if (onProgress) onProgress({ phase: 'encoding', progress: 0 });
        const mp3Blob = await recorder.encodeToMp3(capturedBlob, (p) => {
          if (onProgress) onProgress({ phase: 'encoding', progress: p });
        });
        if (onProgress) onProgress({ phase: 'encoding', progress: 1 });

        downloadBlob(mp3Blob, defaultMp3Filename(patternName));
      } finally {
        // Always restore the user's pre-export settings.
        setLoopMode(snapshot.loopMode);
        setStartMeasureNumber(snapshot.startMeasureNumber);
        setCountInMeasures(snapshot.countInMeasures);
      }
    },
    [
      measures,
      tempo,
      selectedNoteValue,
      loopMode,
      startMeasureNumber,
      countInMeasures,
      patternName,
      isPlaying,
    ],
  );

  const toggleAllPartials = useCallback((mIndex, bIndex, shouldSelectAll) => {
  setMeasures((prev) =>
    prev.map((mm, i) => {
      if (i !== mIndex) return mm;
      const oldBeat = mm.beatSounds[bIndex];
      if (!oldBeat) return mm;
      
      const newSelected = shouldSelectAll 
        ? Array.from({ length: 14 }, (_, i) => i + 1) // Select all 14 partials
        : []; // Deselect all partials
      
      const newBeat = { ...oldBeat, selectedPartials: newSelected };
      return {
        ...mm,
        beatSounds: mm.beatSounds.map((snd, j) =>
          j === bIndex ? newBeat : snd
        ),
      };
    })
  );
}, []);

  function handleAddCustomMeasure(numer, denom) {
    setMeasures((prev) => {
      const newNum =
        prev.length > 0 ? prev[prev.length - 1].measureNumber + 1 : 1;

      // Build the new measure object...
      const newMeasure = {
        ...createNewMeasure(stressOn),
        numerator: numer,
        denominator: denom,
        measureNumber: newNum,
      };

      // If createNewMeasure defaults to 4 beats, fix the size:
      const diff = newMeasure.numerator - newMeasure.beatSounds.length;
      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          newMeasure.beatSounds.push(createNewBeat());
        }
      } else if (diff < 0) {
        newMeasure.beatSounds = newMeasure.beatSounds.slice(
          0,
          newMeasure.numerator
        );
      }

      // Append it and apply your usual re-accent or dynamic logic
      const updated = [...prev, newMeasure];
      applyStressVolumesIfAny(updated, stressOn);
      resetAllBeatVolumesToUserVolumes(updated);
      applyAllDynamicsToAllMeasures(updated);

      return updated;
    });

    // Transfer any rehearsal letter from ghost to the new measure
  if (rehearsalLetters['ghost']) {
    const newMeasureIndex = measures.length; // This will be the index of the new measure
    setRehearsalLetters(prev => {
      const updated = { ...prev };
      updated[newMeasureIndex] = updated['ghost'];
      delete updated['ghost'];
      return updated;
    });
  }

    // Auto-scroll to show the newly added measure
    setTimeout(() => {
      scrollOverview('right');
      scrollDetail('right');
    }, 100);
  }

  // Reset everything
  const handleReset = useCallback(() => {
    // 2) Stop playback and reset basic playback counters
    setIsPlaying(false);
    setCurrentBeat(0);
    setCurrentBar(0);
    globalMeasureCountRef.current = 0;
    loopCountRef.current = 0;
    specificLoopCountRef.current = 0;

    // 3) Reset tempo and tapping states
    setTempo(INITIAL_TEMPO);
    setTapTimes([]);

    setScaleByPercentage(false);
    setBaseTempo(INITIAL_TEMPO);
    setTempoPercentage(100);
    setEnablePercentageScaling(false);

    // 4) Reset toggles for random mute, loop mode, and tempo changes
    setMuteRandomBeats(INITIAL_MUTE_RANDOM);
    setMutePercentage(INITIAL_MUTE_PERCENT);
    setLoopMode(INITIAL_LOOP_MODE);
    setUseTempoChanges(false);
    setEnableAdditionalTempoChanges(false);
    setTempoChanges([{ ...DEFAULT_TEMPO_CHANGE }]);

    // 5) Reset all other toggles: stress, beats, subs, tuplets, drones, etc.
    setStressOn(false);
    setMuteBeats(false);
    setMuteSubs(false);
    setMuteDrones(false);
    setMuteTuplets(false);

    // Clear Quick Subdivisions rules without applying them to the (about-to-be-rebuilt) measures.
    clearQuickSubRulesWithoutApplying();

    // Reset count-in measures
    setCountInMeasures(0);

    // Reset "Modulate on Loop" settings
    setModulateOnLoop(false);
    setModLoopInterval(0);
    setModLoopDirection("up");

    setLoopIncrementalSettings({
      enabled: false,
      scope: "specific",
      stepSize: 5,
      startTempo: INITIAL_TEMPO,
      endTempo: INITIAL_TEMPO + 60,
      stepEvery: 1,
      useStepEvery: false
    });

    setLoopMode(INITIAL_LOOP_MODE);

    // Reset "Mute random harmony" settings
    setMuteRandomDrones(false);
    setMuteRandomDronePercentage(50);
    muteRandomDronesRef.current = false;

    // 6) Reset volume/tuning and collapse states
    setMasterGain(0.7);
    setATuning(440);
    setIsOverviewCollapsed(false);

    setSelectedNoteValue({
      label: "Quarter Note",
      factor: 1,
    });

    // 7) Clear measure-editing states and references
    setEditingMeasureIndex(null);
    setTempNumber("");
    setBulkEditorResetCount((prev) => prev + 1);
    setBulkEditorOpen(false);
    setNestedTupletsUI(null);
    setEditingTupletUI(null);
    setCollapsedBeats({});
    measureDetailRefs.current = {};

    // 8) Reset highlighting states
    setHighlightedMeasures([]);
    setHighlightInput("");
    setTimeSignatureColors(DEFAULT_TIME_SIGNATURE_COLORS);
    setShowTimeSignatureColorsModal(false);
    setRehearsalLetters({});

    // 9) Reset pattern/folder UI
    setPatternName("");
    setFolderName("Uncategorized");
    setSelectedFolder("");
    setNewFolderName("");
    setSelectedFolderForDeletion("");

    // 10) Clear chord/partial references
    partialOscRefs.current = [];
    chordOscRefs.current = [];
    activeChordRef.current.signature = "";

    // 11) Build a fresh first measure (measureNumber = 1) and re-apply measure logic
    const firstMeasure = {
      ...createNewMeasure(false),
      measureNumber: 1,
    };
    const newMeasures = [firstMeasure];
    applyStressVolumesIfAny(newMeasures, false);
    resetAllBeatVolumesToUserVolumes(newMeasures);
    applyAllDynamicsToAllMeasures(newMeasures);

    // 12) Force measures state to rebuild (delayed to ensure a clean re-render)
    setMeasures([]);
    setTimeout(() => {
      setMeasures([...newMeasures]);
    }, 0);

    // 13) Reset modulation and measure numbering
    activeModulationRef.current = {
      isActive: false,
      fromSubdivision: null,
      toSubdivision: null,
      sourceTempoReference: INITIAL_TEMPO,
    };
    setModulationDisplay({ isActive: false, fromName: "", toName: "" });
    setStartMeasureNumber(1);
  }, [stressOn]);

  function removeUndefinedDeep(obj) {
    if (Array.isArray(obj)) {
      return obj.map(removeUndefinedDeep);
    } else if (obj && typeof obj === "object") {
      const copy = {};
      for (let [key, val] of Object.entries(obj)) {
        if (val !== undefined) {
          copy[key] = removeUndefinedDeep(val);
        }
      }
      return copy;
    }
    return obj;
  }

  async function handleRenamePattern(patternId, newName, newFolder = null) {
    if (!currentUser) return;

    // Find the pattern to rename
    const patternToRename = savedPatterns.find((p) => p.id === patternId);
    if (!patternToRename) {
      alert("Could not find pattern to rename.");
      return;
    }

    // Check if new name is empty
    const newNameTrimmed = newName.trim();
    if (!newNameTrimmed) {
      alert("Pattern name cannot be empty.");
      return;
    }

    // Check if a pattern with this name already exists in the same folder
    const folderToCheck = newFolder || patternToRename.folder;
    const patternExists = savedPatterns.some(
      (p) =>
        p.id !== patternId && // exclude the current pattern
        p.name === newNameTrimmed &&
        p.folder === folderToCheck
    );

    if (patternExists) {
      alert(
        `A pattern named "${newNameTrimmed}" already exists in folder "${folderToCheck}".`
      );
      return;
    }

    // Update the pattern in Firebase
    const docRef = doc(db, "users", currentUser.uid, "patterns", patternId);
    const updateData = {
      name: newNameTrimmed,
      updatedAt: serverTimestamp(),
    };

    if (newFolder) {
      updateData.folder = newFolder;
    }

    try {
      await updateDoc(docRef, updateData);
      alert(`Pattern renamed to "${newNameTrimmed}"!`);

      // If this is the currently loaded pattern, update the pattern name
      if (patternName === patternToRename.name) {
        setPatternName(newNameTrimmed);
        if (newFolder) {
          setFolderName(newFolder);
        }
      }
    } catch (err) {
      console.error("Error renaming pattern:", err);
      alert("Failed to rename pattern. Check console for details.");
    }
  }

  function handleLoadPattern(selectedName, folderName) {
    setIsLoadingPattern(true);
    clearQuickSubRulesWithoutApplying();
    // Check if pattern is already in cache
    const cacheKey = `${selectedName}-${folderName}`;
  
    console.log(`🔍 LOAD DEBUG - Looking for pattern: "${selectedName}" in folder "${folderName}"`);
    console.log(`🔍 LOAD DEBUG - Cache key: "${cacheKey}"`);
    console.log(`🔍 LOAD DEBUG - Cache has key:`, patternCache.current.has(cacheKey));
    console.log(`🔍 LOAD DEBUG - Available patterns:`, savedPatterns.map(p => `${p.folder}/${p.name}`));
  
    let patternMeasures;
  
    if (patternCache.current.has(cacheKey)) {
      console.log(`CACHE HIT: Loading ${selectedName} from cache!`);
      patternMeasures = patternCache.current.get(cacheKey);
      console.log(`🔍 LOAD DEBUG - Cached measures count:`, patternMeasures?.length);
      
      // Apply backward compatibility transformations
      patternMeasures = applyBackwardCompatibility(patternMeasures);
      
      setMeasures(patternMeasures);
  
      // Still need to set other pattern properties
      const p = savedPatterns.find(
        (pat) => pat.name === selectedName && pat.folder === folderName
      );
      if (p) {
        setPatternName(p.name);
        setFolderName(p.folder);
        setTempo(p.tempo);
        setUseTempoChanges(p.useTempoChanges);
        setEnableAdditionalTempoChanges(p.enableAdditionalTempoChanges);
        setTempoChanges(p.tempoChanges);
        setATuning(p.aTuning);
        setMuteRandomBeats(p.muteRandomBeats);
        setMutePercentage(p.mutePercentage);
        setLoopMode(p.loopMode);
        setStartMeasureNumber(p.startMeasureNumber || 1);
        setStressOn(p.stressOn ?? false);
        setModulateOnLoop(p.modulateOnLoop ?? false);
        setModLoopInterval(p.modLoopInterval ?? 0);
        setModLoopDirection(p.modLoopDirection ?? "up");
        setTimeSignatureColors(p.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS);
        setLoopMode(p.loopMode || INITIAL_LOOP_MODE);
  
        // Load selectedNoteValue if it exists, otherwise use default
        if (p.selectedNoteValue) {
          setSelectedNoteValue(p.selectedNoteValue);
        } else {
          setSelectedNoteValue({
            label: "Quarter Note",
            factor: 1,
          });
        }
        setRehearsalLetters(p.rehearsalLetters || {});
      }
    } else {
      // Find pattern
      const p = savedPatterns.find(
        (pat) => pat.name === selectedName && pat.folder === folderName
      );
      if (!p) return;
  
      // Apply backward compatibility transformations
      const compatibleMeasures = applyBackwardCompatibility(p.measures);
      
      setPatternName(p.name);
      setFolderName(p.folder);
      setTempo(p.tempo);
      setUseTempoChanges(p.useTempoChanges);
      setEnableAdditionalTempoChanges(p.enableAdditionalTempoChanges);
      setTempoChanges(p.tempoChanges);
      setATuning(p.aTuning);
      setMuteRandomBeats(p.muteRandomBeats);
      setMutePercentage(p.mutePercentage);
      setLoopMode(p.loopMode);
      setMeasures(compatibleMeasures);
      
      // Ensure measureNumber is set properly
      setMeasures((prev) =>
        prev.map((m, i) => ({
          ...m,
          measureNumber: m.measureNumber ?? i + 1,
        }))
      );
  
      setStartMeasureNumber(p.startMeasureNumber || 1);
      setStressOn(p.stressOn ?? false);
      setModulateOnLoop(p.modulateOnLoop ?? false);
      setModLoopInterval(p.modLoopInterval ?? 0);
      setModLoopDirection(p.modLoopDirection ?? "up");
      setTimeSignatureColors(p.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS);
  
      // Manage cache
      if (patternCache.current.size > 20) {
        const firstKey = patternCache.current.keys().next().value;
        patternCache.current.delete(firstKey);
      }
      patternCache.current.set(cacheKey, compatibleMeasures);
  
      // Load selectedNoteValue if it exists, otherwise use default
      if (p.selectedNoteValue) {
        setSelectedNoteValue(p.selectedNoteValue);
      } else {
        setSelectedNoteValue({
          label: "Quarter Note",
          factor: 1,
        });
      }

      setLoopMode(p.loopMode || INITIAL_LOOP_MODE);
      
      patternMeasures = compatibleMeasures;
      setRehearsalLetters(p.rehearsalLetters || {});
    }
  
    // Auto-collapse for large patterns (over 50 measures on mobile, over 100 measures on any device)
    const isMobile = window.innerWidth < 768;
    if (patternMeasures && ((patternMeasures.length > 50 && isMobile) || patternMeasures.length > 100)) {
      // Collapse Overview
      setIsOverviewCollapsed(true);
  
      // Collapse all measure beats
      const newCollapsedState = {};
      for (let i = 0; i < patternMeasures.length; i++) {
        newCollapsedState[i] = true;
      }
      setCollapsedBeats(newCollapsedState);
  
      // Show notification after a short delay so it appears after the pattern loads
      setTimeout(() => {
        const reason = patternMeasures.length > 100 
          ? "This large pattern has been automatically collapsed for better performance. You can expand any section by clicking on the Chevron (V) icon."
          : "This large pattern has been automatically collapsed for better mobile performance. You can expand any section by clicking on the Chevron (V) icon.";
        alert(reason);
      }, 100);
    }
  
    setIsPlaying(false);
    setCurrentBeat(0);
    setCurrentBar(0);
    specificLoopCountRef.current = 0;

    setTimeout(() => {
      setIsLoadingPattern(false);
    }, 0);
  
    alert(`Pattern "${selectedName}" from folder "${folderName}" loaded!`);
    setActiveTab("metronome");
  }
  
  // Helper function for backward compatibility
  function applyBackwardCompatibility(measures) {
    if (!measures) return [];
    
    return measures.map(measure => {
      if (!measure.beatSounds) return measure;
      
      const updatedBeatSounds = measure.beatSounds.map(beatSound => {
        let updatedBeat = {...beatSound};
        
        // 1. Backward compatibility for custom notes (convert semitones to MIDI notes)
        if (updatedBeat.selectedCustomNotes?.length > 0) {
          // Check if notes are in old format (values typically 0-11 only)
          const isOldFormat = updatedBeat.selectedCustomNotes.every(note => note < 12);
          
          if (isOldFormat) {
            // Convert to new format with octave information
            const defaultOctave = updatedBeat.dronePitchOctave || 4;
            updatedBeat.selectedCustomNotes = updatedBeat.selectedCustomNotes.map(
              semitone => defaultOctave * 12 + semitone
            );
          }
        }
        
        // 2. Backward compatibility for just intonation reference pitch
        if (updatedBeat.justIntonation && updatedBeat.jiReferencePitch === undefined) {
          // Use drone pitch as reference if available, otherwise default to C (0)
          updatedBeat.jiReferencePitch = updatedBeat.dronePitchNote !== null 
            ? updatedBeat.dronePitchNote 
            : 0;
        }
        
        return updatedBeat;
      });
      
      return {...measure, beatSounds: updatedBeatSounds};
    });
  }

  async function handleDeletePattern(patternId) {
    if (!currentUser) return;

    // 1) Find the correct pattern in the local array (if needed)
    const patternToDelete = savedPatterns.find((p) => p.id === patternId);
    if (!patternToDelete) {
      alert("Could not find pattern in Firestore.");
      return;
    }

    // 2) Build a doc reference by ID
    const docRef = doc(db, "users", currentUser.uid, "patterns", patternId);

    // 3) Confirm with user or just delete
    const confirmed = window.confirm(
      `Are you sure you want to delete "${patternToDelete.name}" from folder "${patternToDelete.folder}"?`
    );
    if (!confirmed) return;

    await deleteDoc(docRef);

    alert(`Pattern "${patternToDelete.name}" deleted from Firestore.`);
  }

  // MIDI import/export placeholders
  const handleImportMIDI = () => {
    /* parseMidi logic here */
  };

  async function onImportMIDI(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const arrayBuf = await file.arrayBuffer();
    const midiData = new Uint8Array(arrayBuf);

    const parsed = parseMidi(midiData);
    // "parsed" is an object with .header, .tracks[], etc.

    // For simplicity, assume everything (tempo/time-sig) is in track[0].
    // If you have a type-1 MIDI with multiple separate tracks,
    // you may want to combine or look for the track with meta events.
    const track = parsed.tracks[0];

    // We'll keep a running "current numerator/denominator" and "current tempo".
    let currentNumerator = 4;
    let currentDenominator = 4;
    let currentTempo = 120;

    // We'll accumulate measures in an array:
    const newMeasures = [];
    let currentMeasure = buildEmptyMeasure(
      currentNumerator,
      currentDenominator,
      currentTempo
    );

    // We'll keep track of how many ticks have elapsed since the last measure boundary.
    let measureTickCounter = 0;
    // jsmidgen default is 128 ticks per quarter.
    // If the file used a different PPQ, you get that in parsed.header.ticksPerBeat.
    // For now, let's call it "ppq".
    const ppq = parsed.header.ticksPerBeat; // e.g. 480, 960, or 128

    // A helper to compute total ticks in one measure, given the current time signature:
    function measureLengthInTicks(num, den) {
      // e.g. 4/4 => 4 quarters => 4 * ppq
      // 3/8 => 3 eighths => 3 * (ppq/2)
      const factor = den / 4;
      return Math.floor((num * ppq) / factor);
    }

    let currentMeasureMaxTicks = measureLengthInTicks(
      currentNumerator,
      currentDenominator
    );

    for (let i = 0; i < track.length; i++) {
      const event = track[i];

      // 1) Accumulate deltaTime in "measureTickCounter"
      measureTickCounter += event.deltaTime;

      // 2) If meta event is time signature => update currentNumerator, currentDenominator,
      //    then close out the current measure if we're mid-measure, and start a new measure.
      if (event.meta && event.type === "timeSignature") {
        const [num, denom, clocks, bb] = event.timeSignature;
        // denom is 2^negative power. e.g. if denom=4, the raw byte is 2
        // But parseMidi usually decodes it properly as the real denominator.
        currentNumerator = num;
        currentDenominator = denom;

        // "seal" the old measure if measureTickCounter < currentMeasureMaxTicks,
        // or you might just forcibly end it.
        // Start a new measure, etc.
        if (measureTickCounter > 0) {
          // For simplicity, store the partial measure:
          newMeasures.push(currentMeasure);
        }
        currentMeasure = buildEmptyMeasure(
          currentNumerator,
          currentDenominator,
          currentTempo
        );

        measureTickCounter = 0;
        currentMeasureMaxTicks = measureLengthInTicks(num, denom);
        continue;
      }

      // 3) If meta event is setTempo => update currentTempo
      if (event.meta && event.type === "setTempo") {
        // event.setTempo is in microseconds per quarter note
        // BPM = 60,000,000 / microsecondsPerQuarter
        const microsecondsPerQuarter = event.setTempo;
        const bpm = Math.round(60000000 / microsecondsPerQuarter);
        currentTempo = bpm;
        // Optionally store a "tempo change" on the measure object or separate.
        currentMeasure.tempo = bpm;
        continue;
      }

      // 4) If this is a note-on or note-off, handle it
      if (event.type === "noteOn" || event.type === "noteOff") {
        const channel = event.channel;
        // We only care about channel 1 for the click
        if (channel !== 1) continue;

        const noteNumber = event.noteNumber; // e.g. 60, 62, etc.
        const velocity = event.velocity;

        // If noteOn with velocity>0 => that's a click
        if (event.type === "noteOn" && velocity > 0) {
          // We know how far into the measure we are: measureTickCounter
          // fractionOfMeasure = measureTickCounter / currentMeasureMaxTicks
          // beatsInMeasure = currentNumerator
          // => beatIndex = fractionOfMeasure * currentNumerator (approx)

          const fraction = measureTickCounter / currentMeasureMaxTicks;
          const approxBeat = Math.floor(fraction * currentNumerator);

          // Safely clamp
          if (approxBeat >= currentNumerator) {
            // Means we've actually rolled over into the next measure.
            // So close out the current measure and open a new one.
            newMeasures.push(currentMeasure);
            currentMeasure = buildEmptyMeasure(
              currentNumerator,
              currentDenominator,
              currentTempo
            );
            measureTickCounter = measureTickCounter - currentMeasureMaxTicks;
          }

          // Insert a short "beatSounds" entry.
          // Decide if it's accent:
          const isAccent = velocity >= ACCENT_THRESHOLD;
          // Map noteNumber -> sound ID if you want. E.g. if noteNumber=60 => "clicker", etc.
          const foundId = findSoundIdForNoteNumber(noteNumber) || "clicker";

          // Build or update the beatSound
          currentMeasure.beatSounds[approxBeat] = {
            id: foundId,
            userVolume: isAccent ? 1.2 : 1.0,
            // other fields as needed
          };
        }
      }

      // 5) If measureTickCounter >= currentMeasureMaxTicks,
      //    that means we've gone past this measure boundary => start a new measure
      while (measureTickCounter >= currentMeasureMaxTicks) {
        newMeasures.push(currentMeasure);

        // Subtract the measure length from measureTickCounter
        measureTickCounter -= currentMeasureMaxTicks;
        // Start next measure
        currentMeasure = buildEmptyMeasure(
          currentNumerator,
          currentDenominator,
          currentTempo
        );
        currentMeasureMaxTicks = measureLengthInTicks(
          currentNumerator,
          currentDenominator
        );
      }
    } // end track events

    // flush final measure
    if (measureTickCounter > 0) {
      newMeasures.push(currentMeasure);
    }

    // Finally, update your React state:
    setMeasures(newMeasures);
    setTempo(newMeasures[0]?.tempo || 120);
    // etc.

    alert(
      "Imported MIDI successfully! Measures, time signatures, and tempo loaded."
    );
  }

  function buildEmptyMeasure(num, den, bpm) {
    return {
      numerator: num,
      denominator: den,
      tempo: bpm,
      beatSounds: Array(num).fill(null),
    };
  }

  function findSoundIdForNoteNumber(n) {
    // Reverse-lookup from SOUND_TO_MIDI
    // e.g. if n=62 => "click".
    // This is purely up to you:
    for (const [soundId, obj] of Object.entries(SOUND_TO_MIDI)) {
      if (obj.note === n) return soundId;
    }
    return null;
  }

  function exportMIDIImplementation(
    measures,
    defaultTempo = 120,
    fileName = "FlowFrame.mid",
    globalTempo = 120,
    useTempoChanges = false,
    tempoChanges = []
  ) {
    // 1) Create a new MIDI container
    const midi = new Midi();

    // Instead of setting ppq directly, we'll use it as is (defaults to 480)
    const PPQ = midi.header.ppq;

    // 2) Create a single track (or multiple, if you prefer).
    const track = midi.addTrack();

    // We'll keep track of the running "currentTick" so we place events measure by measure.
    let currentTick = 0;

    // Set initial tempo
    midi.header.tempos.push({
      ticks: 0,
      bpm: globalTempo || defaultTempo,
    });

    // Pre-process tempo changes to be at the right ticks
    let processedTempoChanges = [];

    if (useTempoChanges && tempoChanges && tempoChanges.length > 0) {
      // First, calculate tick positions for all measures
      let measureTickPositions = [];
      let runningTick = 0;

      measures.forEach((measure, idx) => {
        const numerator = measure.numerator ?? 4;
        const denominator = measure.denominator ?? 4;
        measureTickPositions[idx] = runningTick;

        const measureQuarters = numerator * (4 / denominator);
        const measureTicks = Math.floor(measureQuarters * PPQ);
        runningTick += measureTicks;
      });

      // Now map tempo changes to their absolute tick positions
      tempoChanges.forEach((change) => {
        // Measure numbers in UI are 1-based, convert to 0-based for array indexing
        const measureIdx = change.measure - 1;

        if (measureIdx >= 0 && measureIdx < measures.length) {
          processedTempoChanges.push({
            ticks: measureTickPositions[measureIdx],
            bpm: change.tempo,
          });
        }
      });

      // Add all tempo changes to the header
      midi.header.tempos = [
        { ticks: 0, bpm: globalTempo || defaultTempo },
        ...processedTempoChanges,
      ];
    }

    measures.forEach((measure, idx) => {
      // Extract numerator, denominator
      const numerator = measure.numerator ?? 4;
      const denominator = measure.denominator ?? 4;

      // 3) Insert a time signature event at the measure boundary
      midi.header.timeSignatures.push({
        ticks: currentTick,
        timeSignature: [numerator, denominator],
        measures: 0,
      });

      // 5) Compute how many ticks this measure occupies
      const measureQuarters = numerator * (4 / denominator);
      const measureTicks = Math.floor(measureQuarters * PPQ);

      // 6) Add a short placeholder note so the DAW sees actual events in the bar.
      const half = measureTicks > 2 ? Math.floor(measureTicks / 2) : 1;

      track.addNote({
        midi: 60,
        velocity: 1,
        ticks: currentTick + half,
        durationTicks: 15,
      });

      // 7) Advance currentTick to the next measure boundary
      currentTick += measureTicks;
    });

    // 8) Convert the @tonejs/midi data structure to a raw Uint8Array
    const out = midi.toArray();

    // 9) Trigger a download in the browser
    const blob = new Blob([out], { type: "audio/midi" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(
      `Exported MIDI with ${processedTempoChanges.length} tempo changes: ${fileName}`
    );
  }

  // 1. Export Metronome Data (JSON)
  const handleExportMetronomeData = () => {
    // Gather everything you want to export
    // For example:
    const exportData = {
      version: 1, // optional
      patternName,
      measures,
      tempo,
      useTempoChanges,
      enableAdditionalTempoChanges,
      tempoChanges,
      aTuning,
      muteRandomBeats,
      mutePercentage,
      loopMode,
      startMeasureNumber,
      masterGain,
      timeSignatureColors,
    };

    // Convert to JSON string
    const jsonStr = JSON.stringify(exportData, null, 2);

    // Trigger download
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = patternName
      ? `FlowFrame_${patternName}.ff`
      : "FlowFrame.ff";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // measure editing callbacks
  const updateMeasure = useCallback(
    (index, updates) => {
      setMeasures((prev) => {
        const newMeasures = prev.map((m, i) => {
          if (i !== index) return m; // Unchanged measure

          let updated = { ...m, ...updates };

          // **1) Handle numerator change (resize beatSounds)**
          if (
            typeof updates.numerator === "number" &&
            updates.numerator !== m.numerator
          ) {
            const newNumBeats = updates.numerator;

            if (newNumBeats > m.numerator) {
              // **Expand beatSounds while preserving existing values**
              updated.beatSounds = [
                ...m.beatSounds,
                ...Array(newNumBeats - m.numerator)
                  .fill(null)
                  .map(() => createNewBeat()),
              ];
            } else {
              // **Truncate beatSounds**
              updated.beatSounds = m.beatSounds.slice(0, newNumBeats);
            }
          }

          // **2) Handle applyBeatOneDroneToAllBeats**
          if (
            updates.applyBeatOneDroneToAllBeats === true &&
            updated.beatSounds.length > 0
          ) {
            const firstBeat = updated.beatSounds[0];
            updated.beatSounds = updated.beatSounds.map((beat, bIndex) => {
              if (bIndex === 0) return beat; // Keep first beat unchanged
              return {
                ...beat,
                dronePitchNote: firstBeat.dronePitchNote,
                dronePitchOctave: firstBeat.dronePitchOctave,
                droneVolume: firstBeat.droneVolume,
                hasHarmony: firstBeat.hasHarmony,
                selectedHarmony: firstBeat.selectedHarmony,
                justIntonation: firstBeat.justIntonation,
                temperamentSystem: firstBeat.temperamentSystem,  // Add this line
                jiReferencePitch: firstBeat.jiReferencePitch,    // Add this line
                usePartials: firstBeat.usePartials,
                selectedPartials: [...(firstBeat.selectedPartials || [])],
                useCustomNotes: firstBeat.useCustomNotes,
                selectedCustomNotes: [...(firstBeat.selectedCustomNotes || [])],
                useQuarterTones: firstBeat.useQuarterTones,
                selectedQuarterTones: [
                  ...(firstBeat.selectedQuarterTones || []),
                ],
                quarterToneOctave: firstBeat.quarterToneOctave,
              };
            });
          }

          return updated;
        });

        // **3) Reapply stress volumes, beat volumes, and dynamics**
        applyStressVolumesIfAny(newMeasures, stressOn);
        resetAllBeatVolumesToUserVolumes(newMeasures);
        applyAllDynamicsToAllMeasures(newMeasures);

        return newMeasures;
      });
    },
    [stressOn]
  );

  // Cycle a single beat's userVolume through [1.0, 0, 1/3, 2/3] on each click.
  // Skips applyStressVolumesIfAny so manual per-beat edits aren't overwritten.
  const cycleBeatVolume = useCallback((measureIndex, beatIndex) => {
    const VOLUME_CYCLE = [1.0, 0, 1 / 3, 2 / 3];
    setMeasures((prev) => {
      const newMeasures = prev.map((m, i) => {
        if (i !== measureIndex) return m;
        const newBeatSounds = m.beatSounds.map((b, bi) => {
          if (bi !== beatIndex) return b;
          const current = typeof b.userVolume === "number" ? b.userVolume : 1.0;
          let closestIdx = 0;
          let closestDist = Infinity;
          for (let k = 0; k < VOLUME_CYCLE.length; k++) {
            const d = Math.abs(VOLUME_CYCLE[k] - current);
            if (d < closestDist) {
              closestDist = d;
              closestIdx = k;
            }
          }
          const next = VOLUME_CYCLE[(closestIdx + 1) % VOLUME_CYCLE.length];
          return { ...b, userVolume: next };
        });
        return { ...m, beatSounds: newBeatSounds };
      });
      resetAllBeatVolumesToUserVolumes(newMeasures);
      applyAllDynamicsToAllMeasures(newMeasures);
      return newMeasures;
    });
  }, []);


  const updateBeatSound = useCallback(
    (mIndex, bIndex, newBeatObj) => {
      setMeasures((prev) => {
        const newMeasures = prev.map((measure, i) => {
          if (i !== mIndex) {
            return measure; // unchanged measure
          }

          // 1) Grab the old beat so we can track old pitch vs. new pitch, old useCustomNotes, etc.
          const oldBeat = measure.beatSounds[bIndex];

          // 2) Merge old beat with any newly provided fields
          let updatedBeat = {
            ...oldBeat,
            ...newBeatObj,
          };

          // ─────────────────────────────────────────────────────
          // A) "Any notes" logic: keep drone pitch in sync
          // ─────────────────────────────────────────────────────
          const oldWasOff = !oldBeat.useCustomNotes;
          const newIsOn = newBeatObj.useCustomNotes === true; // might be undefined
          const oldPitch = oldBeat.dronePitchNote;
          const newPitch = updatedBeat.dronePitchNote;

          // (A1) If user just turned on "Any notes," add the current drone pitch to selectedCustomNotes
          if (oldWasOff && newIsOn && newPitch !== null) {
            if (!updatedBeat.selectedCustomNotes.includes(newPitch)) {
              updatedBeat = {
                ...updatedBeat,
                selectedCustomNotes: [
                  ...updatedBeat.selectedCustomNotes,
                  newPitch,
                ].sort((a, b) => a - b),
              };
            }
          }

          // (A2) If "Any notes" is ON and the drone pitch changed, remove the old pitch & add the new pitch
          const oldPitchHasChanged = oldPitch !== newPitch;
          if (updatedBeat.useCustomNotes && oldPitchHasChanged) {
            let newList = [...updatedBeat.selectedCustomNotes];

            // remove the old pitch if it wasn't null
            if (oldPitch !== null) {
              newList = newList.filter((x) => x !== oldPitch);
            }
            // add the new pitch if it's not null
            if (newPitch !== null && !newList.includes(newPitch)) {
              newList.push(newPitch);
            }

            updatedBeat = {
              ...updatedBeat,
              selectedCustomNotes: newList.sort((a, b) => a - b),
            };
          }

          // 3) Replace beatSounds[bIndex] with the newly updated beat
          let newBeatSounds = measure.beatSounds.map((snd, j) =>
            j === bIndex ? updatedBeat : snd
          );

          if (newBeatObj.applyDroneSoundToAllBeats && newBeatObj.droneSound) {
            // Apply the current beat's drone sound to ALL beats in the measure
            newBeatSounds = newBeatSounds.map((beat) => ({
              ...beat,
              droneSound: newBeatObj.droneSound
            }));
          }

          // 4) If "copy to next beat" is checked and there's a valid next beat
          if (
            updatedBeat.copyDroneHarmonyPartialsToNext &&
            bIndex < measure.numerator - 1
          ) {
            const clonedBeat = JSON.parse(JSON.stringify(updatedBeat));
            // Optionally disable the "copy" checkbox in the clone
            clonedBeat.copyDroneHarmonyPartialsToNext = false;
            newBeatSounds[bIndex + 1] = clonedBeat;
          }

          // 5) If editing beat #1 AND "applyBeatOneDroneToAllBeats" is on,
          //    sync the rest of the beats (including "any notes" fields)
          if (bIndex === 0 && measure.applyBeatOneDroneToAllBeats) {
            newBeatSounds = newBeatSounds.map((oldB, j) => {
              if (j === 0) return updatedBeat; // already updated
              return {
                ...oldB,
                dronePitchNote: updatedBeat.dronePitchNote,
                dronePitchOctave: updatedBeat.dronePitchOctave,
                droneVolume: updatedBeat.droneVolume,
                hasHarmony: updatedBeat.hasHarmony,
                selectedHarmony: updatedBeat.selectedHarmony,
                justIntonation: updatedBeat.justIntonation,
                temperamentSystem: updatedBeat.temperamentSystem,
      jiReferencePitch: updatedBeat.jiReferencePitch,
                usePartials: updatedBeat.usePartials,
                selectedPartials: [...(updatedBeat.selectedPartials || [])],

                // Now copy "any notes" fields too:
                useCustomNotes: updatedBeat.useCustomNotes,
                selectedCustomNotes: [
                  ...(updatedBeat.selectedCustomNotes || []),
                ],
                useQuarterTones: updatedBeat.useQuarterTones,
                selectedQuarterTones: [
                  ...(updatedBeat.selectedQuarterTones || []),
                ],
              };
            });
            console.log("measure.beatSounds after copy:", newBeatSounds);
          }

          // Return the updated measure object
          return {
            ...measure,
            beatSounds: newBeatSounds,
          };
        });

        // 6) Re-apply stress volumes, reset final volumes, and measure-level dynamics
        applyStressVolumesIfAny(newMeasures, stressOn);
        resetAllBeatVolumesToUserVolumes(newMeasures);
        applyAllDynamicsToAllMeasures(newMeasures);

        // 7) Done
        return newMeasures;
      });
    },
    [stressOn]
  );

  const insertMeasureBelow = useCallback(
    (mIndex) => {
      setMeasures((prev) => {
        const arr = [...prev];

        const oldNumber = arr[mIndex].measureNumber;
        const newNumber = oldNumber + 1;

        // 1) Create the new measure with correct measureNumber
        const newM = {
          ...createNewMeasure(stressOn),
          measureNumber: newNumber,
        };

        // 2) Insert it at the right position
        arr.splice(mIndex + 1, 0, newM);

        // 3) Adjust numbering for all measures AFTER the inserted one
        for (let i = mIndex + 1; i < arr.length; i++) {
          arr[i].measureNumber = oldNumber + (i - mIndex);
        }

        // 4) Reapply your processing logic
        applyStressVolumesIfAny(arr, stressOn);
        resetAllBeatVolumesToUserVolumes(arr);
        applyAllDynamicsToAllMeasures(arr);

        return arr;
      });

      // Auto-scroll to show the newly added measure
      setTimeout(() => {
        scrollOverview('right');
        scrollDetail('right');
      }, 100);
    },
    [stressOn]
  );

  const copyMeasureBelow = useCallback(
    (mIndex) => {
      setMeasures((prev) => {
        // Make a copy of the current measures
        const arr = [...prev];
  
        // 1) Copy the target measure and parse out its measureNumber
        const measureToCopy = JSON.parse(JSON.stringify(arr[mIndex]));
        const oldNumber = measureToCopy.measureNumber;
        const newNumber = oldNumber + 1;
  
        // 2) Generate new unique ID for the copied measure using uuidv4
        measureToCopy.id = uuidv4();
  
        // 3) Give the copied measure the next measureNumber
        measureToCopy.measureNumber = newNumber;
  
        // 4) Insert it right below the original
        arr.splice(mIndex + 1, 0, measureToCopy);
  
        // 5) Renumber everything after that so measure numbers remain sequential
        for (let i = mIndex + 2; i < arr.length; i++) {
          arr[i].measureNumber = oldNumber + (i - mIndex);
        }
  
        // Preserve your existing volume/dynamics logic
        applyStressVolumesIfAny(arr, stressOn);
        resetAllBeatVolumesToUserVolumes(arr);
        applyAllDynamicsToAllMeasures(arr);
        return arr;
      });

       // Auto-scroll to show the newly copied measure
      setTimeout(() => {
        scrollOverview('right');
        scrollDetail('right');
      }, 100);
    },
    [stressOn]
  );

  const togglePartial = useCallback((mIndex, bIndex, partialNumber) => {
    setMeasures((prev) =>
      prev.map((mm, i) => {
        if (i !== mIndex) return mm;
        const oldBeat = mm.beatSounds[bIndex];
        if (!oldBeat) return mm;
        const already = oldBeat.selectedPartials.includes(partialNumber);
        let newSelected;
        if (already) {
          newSelected = oldBeat.selectedPartials.filter(
            (x) => x !== partialNumber
          );
        } else {
          newSelected = [...oldBeat.selectedPartials, partialNumber].sort(
            (a, b) => a - b
          );
        }
        const newBeat = { ...oldBeat, selectedPartials: newSelected };
        return {
          ...mm,
          beatSounds: mm.beatSounds.map((snd, j) =>
            j === bIndex ? newBeat : snd
          ),
        };
      })
    );
  }, []);

  // TapTempo
  const handleTapTempo = useCallback(() => {
    const now = Date.now();

    setTapTimes((prevTimes) => {
      // If user waited too long between taps (>2 seconds), start fresh
      if (prevTimes.length && now - prevTimes[prevTimes.length - 1] > 2000) {
        return [now];
      }

      // Add new timestamp & keep only the last MAX_TAPS
      const newTimes = [...prevTimes, now].slice(-MAX_TAPS);

      // With two or more taps, compute intervals & average
      if (newTimes.length > 1) {
        const intervals = [];
        for (let i = 1; i < newTimes.length; i++) {
          intervals.push(newTimes[i] - newTimes[i - 1]);
        }

        // Average ms per tap
        const avgIntervalMs =
          intervals.reduce((a, b) => a + b) / intervals.length;

        // Convert ms per tap to BPM = 60,000 ms in a minute
        const newBPM = Math.round(60000 / avgIntervalMs);
        
        if (scaleByPercentage) {
          // When scaling by percentage, update base tempo and keep current percentage
          setBaseTempo(newBPM);
          setTempo(Math.round(newBPM * (tempoPercentage / 100)));
        } else {
          setTempo(newBPM);
        }
      }

      return newTimes;
    });
  }, [scaleByPercentage, tempoPercentage]);

  const handleResetTap = useCallback(() => {
    setTapTimes([]);
  }, []);

  // ********** TUPLET EDITING **********
  const [tupletsUI, setTupletsUI] = useState(null);

  // This is new: we store measureIndex, tupleIndex, beatsInput, subCount, etc.
  const [editingTupletUI, setEditingTupletUI] = useState(null);
  const [editingNestedTupletUI, setEditingNestedTupletUI] = useState(null);

  /**
   * startEditTuplet loads the existing tuplet from measureObj, populates editingTupletUI
   */
  function startEditTuplet(mIndex, tupleIndex) {
    const measureObj = measures[mIndex];
    if (!measureObj) return;
    const oldTuplet = measureObj.tuplets[tupleIndex];
    if (!oldTuplet) return;

    // Build comma string from selectedBeats array
    const beatsStr = oldTuplet.selectedBeats.join(",");
    // Get mutedSubdivisions as comma string
    const mutedSubsStr = (oldTuplet.mutedSubdivisions || []).join(",");

    let nestedIsOn = false;
    let nestedArrStr = "";
    let nestedSubCount = "";
    let nestedSoundId = "clicker";
    let nestedMutedSubsStr = "";
    if (oldTuplet.nestedTuplets && oldTuplet.nestedTuplets.length > 0) {
      nestedIsOn = true;
      nestedArrStr = oldTuplet.nestedTuplets[0].selectedBeats.join(",");
      nestedSubCount = String(oldTuplet.nestedTuplets[0].subCount);
      nestedSoundId = oldTuplet.nestedTuplets[0].soundId;
      nestedMutedSubsStr = (
        oldTuplet.nestedTuplets[0].mutedSubdivisions || []
      ).join(",");
    }

    setEditingTupletUI({
      measureIndex: mIndex,
      tupleIndex,
      beatsInput: beatsStr,
      subCount: String(oldTuplet.subCount),
      soundId: oldTuplet.soundId,
      mutedSubdivisions: mutedSubsStr,
      addNested: nestedIsOn,
      nestedBeatsInput: nestedArrStr,
      nestedSubCount,
      nestedSoundId,
      nestedMutedSubdivisions: nestedMutedSubsStr,
    });
  }

  /**
   * saveEditedTuplet updates measureObj.tuplets[tupleIndex] with the new data
   */
  function saveEditedTuplet() {
    if (!editingTupletUI) return;

    const {
      measureIndex,
      tupleIndex,
      beatsInput,
      subCount,
      soundId,
      mutedSubdivisions, // Add this
      addNested,
      nestedBeatsInput,
      nestedSubCount,
      nestedSoundId,
      nestedMutedSubdivisions, // Add this
    } = editingTupletUI;

    console.log("Original mutedSubdivisions:", mutedSubdivisions);

    // parse
    const selectedBeats = parseBeatsInput(beatsInput);
    const sc = parseInt(subCount, 10) || 1;
    // Parse mutedSubdivisions if it's a string, otherwise use as-is
    const mutedSubs =
      typeof mutedSubdivisions === "string"
        ? parseAccentString(mutedSubdivisions)
        : mutedSubdivisions || [];

    console.log("Parsed mutedSubs:", mutedSubs);

    // build new tuplet
    const newTuplet = {
      selectedBeats,
      subCount: sc,
      soundId: soundId || "clicker",
      nestedTuplets: [],
      muted: false,
      mutedSubdivisions: mutedSubs, // Add the parsed array
    };

    console.log("New tuplet with mutedSubdivisions:", newTuplet);

    if (addNested) {
      const nestedBeats = parseBeatsInput(nestedBeatsInput);
      const nsc = parseInt(nestedSubCount, 10) || 1;
      // Parse nestedMutedSubdivisions if present
      const nestedMutedSubs =
        typeof nestedMutedSubdivisions === "string"
          ? parseAccentString(nestedMutedSubdivisions)
          : nestedMutedSubdivisions || [];

      if (nestedBeats.length > 0) {
        newTuplet.nestedTuplets.push({
          selectedBeats: nestedBeats,
          subCount: nsc,
          soundId: nestedSoundId || "clicker",
          nestedTuplets: [],
          mutedSubdivisions: nestedMutedSubs, // Add this
        });
      }
    }

    // now update the measure's tuplets array
    setMeasures((prev) =>
      prev.map((mm, idx) => {
        if (idx !== measureIndex) return mm;
        const newTuples = [...mm.tuplets];
        newTuples[tupleIndex] = newTuplet;
        return { ...mm, tuplets: newTuples };
      })
    );

    setEditingTupletUI(null);
  }

  useEffect(() => {
    // If the user toggles Stress ON, re-apply volumes to all existing measures.
    if (stressOn) {
      setMeasures((prev) => {
        // Make a fresh copy
        const newMeasures = [...prev];
        // 1) Overwrite userVolume with stress patterns if the measure matches
        applyStressVolumesIfAny(newMeasures, true);
        // 2) Copy userVolume => beatVolume
        resetAllBeatVolumesToUserVolumes(newMeasures);
        // 3) Then apply measure-level cresc/dim
        applyAllDynamicsToAllMeasures(newMeasures);
        return newMeasures;
      });
    }
  }, [stressOn]);

  // Add keyboard shortcut for save (Ctrl+S / Cmd+S)
useEffect(() => {
  const handleKeyDown = (event) => {
    // Check for Ctrl+S or Cmd+S
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault(); // Prevent browser's default save dialog
      
      // Only save if we're on the metronome tab and viewing the FlowFrame
      // editor (Backing has its own state and shouldn't intercept Ctrl+S).
      if (activeTab === 'metronome' && metronomeView === 'flowframe') {
        if (!currentUser) {
          alert("Please log in to save patterns.");
          return;
        }
        
        // If no pattern name, show the save modal instead of error
        if (!patternName.trim()) {
          console.log('🔍 SAVE DEBUG - Keyboard shortcut triggered, showing save modal');
          setShowSaveModal(true);
          return;
        }
        
        console.log('🔍 SAVE DEBUG - Keyboard shortcut triggered (Ctrl/Cmd+S)');
        handleSavePattern();
      } else {
        // Optional: Show a brief message if not on the right tab
        console.log('Save shortcut only works on the FlowFrame tab');
      }
    }

    // Check for Space bar for start/stop
    if (event.key === ' ') {
      // Only handle space bar if we're on the metronome tab and in the
      // FlowFrame editor view (Metronome and Backing have their own
      // play/stop handling).
      if (activeTab === 'metronome' && metronomeView === 'flowframe') {
        // Don't trigger if user is typing in an input field - CHECK THIS FIRST!
        if (event.target.tagName === 'INPUT' || 
            event.target.tagName === 'TEXTAREA' || 
            event.target.tagName === 'SELECT' ||
            event.target.isContentEditable) {
          return; // Allow normal typing behavior
        }
        
        // Prevent default space bar behavior (scrolling) - ONLY if not in input field
        event.preventDefault();
        
        console.log('🎵 Space bar pressed - toggling start/stop');
        handleStartStop();
      }
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
      // Copy current measure (could be determined by scroll position)
      if (activeTab === 'metronome') {
        event.preventDefault();
        handleCopyMeasure(getCurrentVisibleMeasureIndex());
      }
    }
    
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      // Paste to current measure
      if (activeTab === 'metronome') {
        event.preventDefault();
        handlePasteMeasure(getCurrentVisibleMeasureIndex());
      }
    }
  };

  // Add event listener
  document.addEventListener('keydown', handleKeyDown);

  // Cleanup
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
}, [activeTab, patternName, currentUser, handleSavePattern]);

  function CurrentMeasureDisplay({
    measures,
    audioCtx,
    scheduledBeatsRef,
    isPlaying,
    startMeasureNumber,
  }) {
    const [currentMeasureIndex, setCurrentMeasureIndex] = useState(null);
    const [currentBeatIndex, setCurrentBeatIndex] = useState(null);
    const rafRef = useRef(null);
    const lastUpdateTimeRef = useRef(0);
  
    useEffect(() => {
      // When not playing, show the start measure
      if (!isPlaying) {
        setCurrentMeasureIndex(startMeasureNumber - 1);
        setCurrentBeatIndex(0);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        return;
      }
  
      function loop() {
        const nowSec = audioCtx.currentTime;
        const schedArr = scheduledBeatsRef.current;
  
        // Make sure we have valid scheduled beats
        if (Array.isArray(schedArr) && schedArr.length > 0) {
          // Find the current beat - look for the last beat that should be playing now
          let currentBeat = null;
          
          // Look through recent beats (within the last second)
          for (let i = schedArr.length - 1; i >= 0; i--) {
            const beat = schedArr[i];
            if (beat.startTimeSec <= nowSec && beat.endTimeSec >= nowSec) {
              currentBeat = beat;
              break;
            }
            // Don't look too far back in time
            if (nowSec - beat.startTimeSec > 1) break;
          }
  
          // If we found a current beat, update the display
          if (currentBeat) {
            setCurrentMeasureIndex(currentBeat.measureIndex);
            setCurrentBeatIndex(currentBeat.beatIndex);
            lastUpdateTimeRef.current = nowSec;
          } else {
            // If no current beat found but we have scheduled beats,
            // show the most recently scheduled beat
            const lastBeat = schedArr[schedArr.length - 1];
            if (lastBeat && nowSec - lastBeat.endTimeSec < 0.5) {
              setCurrentMeasureIndex(lastBeat.measureIndex);
              setCurrentBeatIndex(lastBeat.beatIndex);
            }
          }
        }
  
        rafRef.current = requestAnimationFrame(loop);
      }
  
      rafRef.current = requestAnimationFrame(loop);
      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }, [isPlaying, startMeasureNumber, audioCtx, scheduledBeatsRef]);
  
    // Only render if we have valid measure data
    if (currentMeasureIndex == null) return null;
    const measure = measures[currentMeasureIndex];
    if (!measure) return null;
  
    return (
      <div className="flex items-center text-xs space-x-2">
        <div className="font-semibold">Measure {measure.measureNumber}</div>
        <div className="flex items-center">
          <span>
            {measure.numerator}/{measure.denominator}
          </span>
        </div>
        <div className="grid grid-cols-8 gap-1">
          {Array.from({ length: measure.numerator }).map((_, i) => {
            const isActive = i === currentBeatIndex;
            return (
              <div
                key={i}
                className={`
                  w-3 h-3 rounded-full
                  flex items-center justify-center
                  transition-all duration-75 ease-out
                  transform
                  ${isActive 
                    ? "bg-brand-500 text-white scale-110 shadow-lg" 
                    : "bg-gray-300 text-gray-800 scale-100"}
                `}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------
  //  startEditNestedTuplet: when user clicks "Edit" on a nested tuplet
  // ---------------------------------------------------------
  function startEditNestedTuplet(
    measureIndex,
    parentTupletIndex,
    nestedTupletIndex
  ) {
    // Grab the measure holding the parent tuplet
    const measureObj = measures[measureIndex];
    if (!measureObj) return;

    const parentTuplet = measureObj.tuplets[parentTupletIndex];
    if (!parentTuplet) return;

    const nested = parentTuplet.nestedTuplets[nestedTupletIndex];
    if (!nested) return;

    // Build a comma-separated string from the existing selectedBeats
    const beatsStr = nested.selectedBeats.join(",");
    // Get mutedSubdivisions as comma string
    const mutedSubsStr = (nested.mutedSubdivisions || []).join(",");

    // Populate the editing UI state
    setEditingNestedTupletUI({
      measureIndex,
      parentTupletIndex,
      nestedTupletIndex,
      beatsInput: beatsStr,
      subCount: String(nested.subCount),
      soundId: nested.soundId,
      mutedSubdivisions: mutedSubsStr,
    });
  }

  // ---------------------------------------------------------
  //  saveEditedNestedTuplet: invoked when user presses "Save"
  // ---------------------------------------------------------
  function saveEditedNestedTuplet() {
    if (!editingNestedTupletUI) return;

    const {
      measureIndex,
      parentTupletIndex,
      nestedTupletIndex,
      beatsInput,
      subCount,
      soundId,
      mutedSubdivisions, // Add this
    } = editingNestedTupletUI;

    // Parse the user's beats input into an array of integers, e.g. [2,3,4]
    const parsedBeats = parseBeatsInput(beatsInput);
    if (!parsedBeats.length) {
      // If your code wants to handle blank input, do so here. For now, we'll just bail:
      setEditingNestedTupletUI(null);
      return;
    }

    const finalSubCount = parseInt(subCount, 10) || 1;
    // Parse mutedSubdivisions if it's a string, otherwise use as-is
    const mutedSubs =
      typeof mutedSubdivisions === "string"
        ? parseAccentString(mutedSubdivisions)
        : mutedSubdivisions || [];

    // Now update measures in state
    setMeasures((prevMeasures) =>
      prevMeasures.map((measure, mIdx) => {
        if (mIdx !== measureIndex) return measure; // not the right measure

        // Update only the correct measure
        const updatedTuplets = measure.tuplets.map((oldTuplet, pIdx) => {
          if (pIdx !== parentTupletIndex) return oldTuplet; // not the parent tuplet we need

          // Update only the correct nested tuplet
          const newNestedArr = oldTuplet.nestedTuplets.map(
            (oldNested, nIdx) => {
              if (nIdx !== nestedTupletIndex) return oldNested;
              // Overwrite fields with user's new inputs
              return {
                ...oldNested,
                selectedBeats: parsedBeats,
                subCount: finalSubCount,
                soundId: soundId,
                mutedSubdivisions: mutedSubs, // Add the parsed array
              };
            }
          );

          // Return the updated parent tuplet
          return {
            ...oldTuplet,
            nestedTuplets: newNestedArr,
          };
        });

        // Return the updated measure
        return {
          ...measure,
          tuplets: updatedTuplets,
        };
      })
    );

    // Finally, clear the UI so we don't keep editing
    setEditingNestedTupletUI(null);
  }

  async function handleImportMetronomeData(event) {
    const file = event.target.files?.[0];
    if (!file) return; // user canceled

    try {
      // 1) Read file as text and parse JSON
      const fileText = await file.text();
      const imported = JSON.parse(fileText);

      // 2) Basic shape/type checks (tweak as needed)

      // measures (required array)
      if (!Array.isArray(imported.measures)) {
        throw new Error(
          "Invalid file: 'measures' must be an array of measure objects."
        );
      }

      // patternName (optional string)
      if (
        "patternName" in imported &&
        typeof imported.patternName !== "string"
      ) {
        throw new Error("'patternName' must be a string if provided.");
      }

      // tempo (optional number)
      if ("tempo" in imported && typeof imported.tempo !== "number") {
        throw new Error("'tempo' must be a number if provided.");
      }

      // useTempoChanges (optional boolean)
      if (
        "useTempoChanges" in imported &&
        typeof imported.useTempoChanges !== "boolean"
      ) {
        throw new Error("'useTempoChanges' must be a boolean if provided.");
      }

      // enableAdditionalTempoChanges (optional boolean)
      if (
        "enableAdditionalTempoChanges" in imported &&
        typeof imported.enableAdditionalTempoChanges !== "boolean"
      ) {
        throw new Error(
          "'enableAdditionalTempoChanges' must be a boolean if provided."
        );
      }

      // tempoChanges (optional array)
      if ("tempoChanges" in imported && !Array.isArray(imported.tempoChanges)) {
        throw new Error("'tempoChanges' must be an array if provided.");
      }

      // aTuning (optional number)
      if ("aTuning" in imported && typeof imported.aTuning !== "number") {
        throw new Error("'aTuning' must be a number if provided.");
      }

      // muteRandomBeats (optional boolean)
      if (
        "muteRandomBeats" in imported &&
        typeof imported.muteRandomBeats !== "boolean"
      ) {
        throw new Error("'muteRandomBeats' must be a boolean if provided.");
      }

      // mutePercentage (optional number)
      if (
        "mutePercentage" in imported &&
        typeof imported.mutePercentage !== "number"
      ) {
        throw new Error("'mutePercentage' must be a number if provided.");
      }

      // loopMode (optional string)
      if ("loopMode" in imported && typeof imported.loopMode !== "string") {
        throw new Error("'loopMode' must be a string if provided.");
      }

      // startMeasureNumber (optional number)
      if (
        "startMeasureNumber" in imported &&
        typeof imported.startMeasureNumber !== "number"
      ) {
        throw new Error("'startMeasureNumber' must be a number if provided.");
      }

      // masterGain (optional number)
      if ("masterGain" in imported && typeof imported.masterGain !== "number") {
        throw new Error("'masterGain' must be a number if provided.");
      }

      // 3) Everything passed => fill in defaults if missing
      const safePatternName = imported.patternName || "Imported Pattern";
      const safeMeasures = imported.measures;
      const safeTempo = imported.tempo ?? 60;
      const safeUseTempoChanges = imported.useTempoChanges ?? false;
      const safeEnableAdditionalTempoChanges =
        imported.enableAdditionalTempoChanges ?? false;
      const safeTempoChanges = imported.tempoChanges ?? [];
      const safeATuning = imported.aTuning ?? 440;
      const safeMuteRandomBeats = imported.muteRandomBeats ?? false;
      const safeMutePercentage = imported.mutePercentage ?? 50;
      const safeLoopMode = imported.loopMode ?? "loop";
      const safeStartMeasure = imported.startMeasureNumber ?? 1;
      const safeMasterGain = imported.masterGain ?? 0.7;

      // 4) If user is NOT logged in, warn that unsaved work will be lost
      if (!currentUser) {
        const proceed = window.confirm(
          "Warning: You are not logged in. Any imported data will NOT be saved to the cloud. " +
            "You may lose unsaved changes. Do you wish to continue?"
        );
        if (!proceed) {
          return; // user canceled the import
        }
        // If they continue, just replace what's on screen locally
        setPatternName(safePatternName);
        setMeasures(safeMeasures);
        setTempo(safeTempo);
        setUseTempoChanges(safeUseTempoChanges);
        setEnableAdditionalTempoChanges(safeEnableAdditionalTempoChanges);
        setTempoChanges(safeTempoChanges);
        setATuning(safeATuning);
        setMuteRandomBeats(safeMuteRandomBeats);
        setMutePercentage(safeMutePercentage);
        setLoopMode(safeLoopMode);
        setStartMeasureNumber(safeStartMeasure);
        setMasterGain(safeMasterGain);
        setTimeSignatureColors(imported.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS);

        alert(
          "File imported (local only). You are not logged in, so this was not saved online."
        );

        
        setActiveTab("metronome");

      return;
    }

      // 5) If user IS logged in, replace what's on screen and also save to Firestore
      setPatternName(safePatternName);
      setMeasures(safeMeasures);
      setTempo(safeTempo);
      setUseTempoChanges(safeUseTempoChanges);
      setEnableAdditionalTempoChanges(safeEnableAdditionalTempoChanges);
      setTempoChanges(safeTempoChanges);
      setATuning(safeATuning);
      setMuteRandomBeats(safeMuteRandomBeats);
      setMutePercentage(safeMutePercentage);
      setLoopMode(safeLoopMode);
      setStartMeasureNumber(safeStartMeasure);
      setMasterGain(safeMasterGain);
      setTimeSignatureColors(imported.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS);

    // Example Firestore save with chunking support:
    const folderForImported = imported.folderName || "Imported";

    const importedPatternData = {
      name: safePatternName,
      folder: folderForImported,
      measures: safeMeasures,
      tempo: safeTempo,
      useTempoChanges: safeUseTempoChanges,
      enableAdditionalTempoChanges: safeEnableAdditionalTempoChanges,
      tempoChanges: safeTempoChanges,
      aTuning: safeATuning,
      muteRandomBeats: safeMuteRandomBeats,
      mutePercentage: safeMutePercentage,
      loopMode: safeLoopMode,
      startMeasureNumber: safeStartMeasure,
      masterGain: safeMasterGain,
      stressOn: imported.stressOn || false,
      selectedNoteValue: imported.selectedNoteValue || { label: "Quarter Note", factor: 1 },
      modulateOnLoop: imported.modulateOnLoop || false,
      modLoopInterval: imported.modLoopInterval || 0,
      modLoopDirection: imported.modLoopDirection || "up",
      rehearsalLetters: imported.rehearsalLetters || {},
      timeSignatureColors: imported.timeSignatureColors || DEFAULT_TIME_SIGNATURE_COLORS,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Handle large patterns with chunking
    const { mainDoc, chunks, needsChunking } = splitPatternForStorage(importedPatternData);

    const patternsRef = collection(db, "users", currentUser.uid, "patterns");
    const docRef = doc(patternsRef);

    if (needsChunking) {
      // Save main document
      await setDoc(docRef, mainDoc);
      
      // Save chunks as subcollection
      const batch = writeBatch(db);
      chunks.forEach((chunk, index) => {
        const chunkRef = doc(collection(docRef, 'chunks'), `chunk_${index}`);
        batch.set(chunkRef, chunk);
      });
      
      await batch.commit();
      console.log(`Imported chunked pattern with ${chunks.length} chunks`);
    } else {
      // Regular save for small patterns
      await setDoc(docRef, mainDoc);
    }

    // Update local state
    setSavedPatterns(prev => [...prev, {
      ...importedPatternData,
      id: docRef.id,
      createdAt: new Date(),
      updatedAt: new Date()
    }]);

    const sizeInfo = needsChunking ? ` Pattern was split into ${chunks.length} chunks for storage.` : '';
    alert(`Metronome data imported and saved to your account!${sizeInfo}`);

    setActiveTab("metronome");

    } catch (err) {
      console.error("Import error:", err);
      alert("Could not import file: " + err.message);
    }
  }

  // Tracks whether a PDF→OMR import is currently in flight. The progress
  // overlay reads this; the value is true from upload start until the OMR
  // service responds (or fails).
  const [importingSheetMusic, setImportingSheetMusic] = useState(false);
  // Whether the "Import Sheet Music" two-path picker modal is open. Shown
  // by the Pro-tier-gated hamburger entry; offers "from library" or
  // "upload new PDF" before kicking off OMR.
  const [showImportSheetMusicModal, setShowImportSheetMusicModal] =
    useState(false);

  // Build a FlowFrame measure from a parsed MusicXML measure (numerator,
  // denominator, position). Reuses the existing factory so any future change
  // to the measure/beat shape flows through automatically. Adjusts beatSounds
  // length to match the time signature, and assigns measureNumber by
  // sequential position — FlowFrame relies on measureNumber for navigation,
  // copy/paste, and tempo-change targeting; an undefined value cascades to
  // NaN through arithmetic-based propagation.
  function buildMeasureFromMusicXML(numerator, denominator, measureNumber) {
    const measure = createNewMeasure(stressOn);
    measure.numerator = numerator;
    measure.denominator = denominator;
    measure.measureNumber = measureNumber;

    const targetBeatCount = STRESS_PATTERNS[numerator]
      ? STRESS_PATTERNS[numerator].length
      : numerator;

    while (measure.beatSounds.length < targetBeatCount) {
      measure.beatSounds.push(createNewBeat());
    }
    if (measure.beatSounds.length > targetBeatCount) {
      measure.beatSounds.length = targetBeatCount;
    }

    if (stressOn && STRESS_PATTERNS[numerator]) {
      const pattern = STRESS_PATTERNS[numerator];
      measure.beatSounds.forEach((beat, idx) => {
        const v = idx < pattern.length ? pattern[idx] : 1.0;
        beat.userVolume = v;
        beat.beatVolume = v;
      });
    }

    return measure;
  }

  // Shared core for both MusicXML and PDF→OMR imports: parse the XML string
  // into the FlowFrame pattern shape, apply state, switch tabs, and surface
  // the user-facing summary alert.
  function applyMusicXmlImport(xmlString, fallbackName) {
    const ir = parseMusicXML(xmlString);

    const newMeasures = ir.measures.map((m, idx) =>
      buildMeasureFromMusicXML(m.numerator, m.denominator, idx + 1)
    );

    const newRehearsalLetters = {};
    ir.measures.forEach((m, idx) => {
      if (m.rehearsalLetter) {
        newRehearsalLetters[idx] = m.rehearsalLetter;
      }
    });

    const newPatternName = ir.title || fallbackName || "Imported MusicXML";
    const tempoState = buildTempoState(ir);

    setPatternName(newPatternName);
    setMeasures(newMeasures);
    setTempo(tempoState.tempo);
    setUseTempoChanges(tempoState.useTempoChanges);
    setEnableAdditionalTempoChanges(tempoState.enableAdditionalTempoChanges);
    setTempoChanges(tempoState.tempoChanges);
    setRehearsalLetters(newRehearsalLetters);
    setLoopMode(INITIAL_LOOP_MODE);
    setStartMeasureNumber(1);
    setActiveTab("metronome");

    const importedChangeCount = tempoState.useTempoChanges
      ? tempoState.tempoChanges.length
      : 0;
    const summaryParts = [
      `${newMeasures.length} measure${newMeasures.length === 1 ? "" : "s"}`,
      `${tempoState.tempo} BPM base tempo`,
    ];
    if (importedChangeCount > 0) {
      summaryParts.push(
        `${importedChangeCount} tempo change${importedChangeCount === 1 ? "" : "s"}`
      );
    }
    const notes = [
      "Heads up: this import is not flawless. Review your FlowFrame for accuracy — especially tempo changes, time signatures, and rehearsal letters — before relying on it for practice.",
    ];
    if (!currentUser) {
      notes.push("You are not logged in, so this import was not saved to the cloud.");
    }
    const noteText = "\n\n" + notes.join("\n\n");
    alert(
      `Imported "${newPatternName}" — ${summaryParts.join(", ")}.${noteText}`
    );
  }

  async function handleImportMusicXML(event) {
    const file = event.target.files?.[0];
    // Reset the input so re-selecting the same file fires onChange.
    if (event.target) event.target.value = "";
    if (!file) return;

    // .mxl is a zip-compressed MusicXML container; we don't unzip in v1.
    if (/\.mxl$/i.test(file.name)) {
      alert(
        "Compressed MusicXML (.mxl) is not supported yet. Please export an uncompressed .xml or .musicxml file."
      );
      return;
    }

    try {
      const fileText = await file.text();
      const fallbackName = file.name.replace(/\.(xml|musicxml)$/i, "");
      applyMusicXmlImport(fileText, fallbackName);
    } catch (err) {
      console.error("MusicXML import error:", err);
      alert("Could not import MusicXML: " + err.message);
    }
  }

  // Run the OMR + import pipeline for a sheet-music File. Accepts PDF
  // (full-page or whole-score) and PNG/JPG (cropped excerpt from
  // ScoreExcerptPicker). Either way, Audiveris is happy to ingest both.
  async function handleImportSheetMusicPDF(file) {
    if (!file) return;

    const isPdf =
      /\.pdf$/i.test(file.name) || file.type === "application/pdf";
    const isImage =
      /\.(png|jpg|jpeg)$/i.test(file.name) ||
      file.type === "image/png" ||
      file.type === "image/jpeg";

    if (!isPdf && !isImage) {
      alert("Please select a PDF, PNG, or JPG file.");
      return;
    }

    // Match the server-side cap so the user sees the limit before uploading.
    const MAX_BYTES = 32 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      alert(
        "File too large (32 MB max). Try a lower-resolution scan, fewer pages, or a smaller excerpt."
      );
      return;
    }

    // Page-count cap (PDFs only — irrelevant for image excerpts). Audiveris
    // time scales with page count; we cap so a user can't burn 10 minutes of
    // OMR time on a full symphony part. pdfjs.GlobalWorkerOptions.workerSrc
    // is configured by scoreUploadPipeline.js at module load.
    if (isPdf) {
      const MAX_PAGES = 10;
      let pageCount;
      try {
        const buffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: buffer.slice(0) }).promise;
        pageCount = pdf.numPages;
        pdf.destroy();
      } catch (err) {
        console.error("Could not read PDF for page count:", err);
        alert("Could not read this PDF. The file may be corrupted or password-protected.");
        return;
      }
      if (pageCount > MAX_PAGES) {
        alert(
          `This PDF has ${pageCount} pages. The OMR import is capped at ${MAX_PAGES} pages — try splitting it into shorter excerpts or selecting one with the excerpt picker.`
        );
        return;
      }
    }

    setImportingSheetMusic(true);
    try {
      const xmlString = await uploadPdfForOmr(file);
      const fallbackName = file.name.replace(/\.(pdf|png|jpg|jpeg)$/i, "");
      applyMusicXmlImport(xmlString, fallbackName);
    } catch (err) {
      console.error("Sheet music import error:", err);
      const userMessage =
        err instanceof OmrUploadError
          ? err.message
          : `Could not import sheet music: ${err.message}`;
      alert(userMessage);
    } finally {
      setImportingSheetMusic(false);
    }
  }

  // Render
  return (
    <div className="relative p-6 w-full min-h-screen bg-[#D9D9D9] rounded-xl shadow-md">
          {renderGlobalRecordingIndicator()}
          <UploadQueuePill
            queue={uploadQueue}
            onRetry={retryUpload}
            onDismiss={dismissUpload}
          />

          {importingSheetMusic && (
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60"
              role="dialog"
              aria-modal="true"
              aria-label="Importing sheet music"
            >
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4 text-center">
                <div className="mx-auto mb-4 h-12 w-12 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin"></div>
                <h3 className="text-lg font-semibold mb-2">Reading your sheet music…</h3>
                <p className="text-sm text-gray-600">
                  Optical music recognition typically takes 30–90 seconds depending on
                  score length. Please don't close this tab.
                </p>
              </div>
            </div>
          )}

          {showImportSheetMusicModal && currentUser && (
            <ImportSheetMusicModal
              userId={currentUser.uid}
              onPickFile={(file) => {
                setShowImportSheetMusicModal(false);
                handleImportSheetMusicPDF(file);
              }}
              onClose={() => setShowImportSheetMusicModal(false)}
            />
          )}


          {showSilentModeWarning && (
  <div className="fixed top-16 left-4 right-4 bg-yellow-500 text-white px-4 py-3 rounded-lg shadow-lg z-40 text-center">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="font-medium">⚠️ Audio may be muted</p>
        <p className="text-sm">
          If on iOS, check that silent mode is OFF. Swipe down from top-right and ensure the bell icon is not crossed out.
        </p>
      </div>
      <button
        onClick={() => setShowSilentModeWarning(false)}
        className="ml-4 text-white hover:text-gray-200"
      >
        ✕
      </button>
    </div>
  </div>
)}
      {/* Add the TutorialOverlay */}
      {showTutorial && activeTab !== "landing" && (
        <TutorialOverlay
          currentUser={currentUser}
          onComplete={() => {
            setShowTutorial(false);
            setTutorialCompleted(true);
          }}
        />
      )}

      {exportToLibraryPattern && !showUsernameModal && (
        <ExportToLibraryModal
          exportToLibraryPattern={exportToLibraryPattern}
          setExportToLibraryPattern={setExportToLibraryPattern}
          exportPatternToLibrary={exportPatternToLibrary}
          patternTags={patternTags}
          setPatternTags={setPatternTags}
        />
      )}
      {showExportMp3Modal && (
        <ExportMp3Modal
          measures={measures}
          isPlaying={isPlaying}
          onClose={() => setShowExportMp3Modal(false)}
          onExport={handleExportMp3}
        />
      )}
      {/* Add the UsernameSelectionModal */}
      {showUsernameModal && (
        <UsernameSelectionModal
          currentUser={currentUser}
          setShowUsernameModal={setShowUsernameModal}
          setLibraryUsername={setLibraryUsername}
          exportToLibraryPattern={exportToLibraryPattern}
          exportPatternToLibrary={exportPatternToLibrary}
          generatedUsername={generatedUsername}
          setGeneratedUsername={setGeneratedUsername}
          setExportToLibraryPattern={setExportToLibraryPattern}
        />
      )}
      {/* Time Signature Colors Modal */}
      {showTimeSignatureColorsModal && (
        <TimeSignatureColorsModal
          isOpen={showTimeSignatureColorsModal}
          onClose={() => setShowTimeSignatureColorsModal(false)}
          timeSignatureColors={timeSignatureColors}
          setTimeSignatureColors={setTimeSignatureColors}
        />
      )}

{/* Save Modal from Keyboard Shortcut */}
{showSaveModal && (
  <SavePatternModal
    onClose={() => setShowSaveModal(false)}
    patternName={patternName || "My Pattern"}
    setPatternName={setPatternName}
    folderName={folderName || ""}
    setFolderName={setFolderName}
    onSavePattern={async (options = {}) => {
      setShowSaveModal(false);
      await handleSavePattern(options);
    }}
    savedPatterns={savedPatterns}
    isAutosave={false}
    title="Save Your Pattern"
    subtitle="Give your pattern a name and choose a folder!"
  />
)}

      {/* Autosave Modal - Add this before the closing </div> */}
{showAutosaveModal && (
  <SavePatternModal
    onClose={handleAutosaveModalCancel}
    patternName={patternName || "My Pattern"}
    setPatternName={(name) => {}} // Dummy setter - handled by modal confirm
    folderName={folderName || ""}
    setFolderName={(folder) => {}} // Dummy setter - handled by modal confirm  
    onSavePattern={() => {}} // Handled by modal confirm callback
    savedPatterns={savedPatterns}
    isAutosave={true}
    onConfirm={handleAutosaveModalConfirm}
    title="🎵 Save Your Pattern"
    subtitle="Give your pattern a name and choose a folder to organize your work!"
  />
)}
                  {/* Toast Notifications */}
{toastMessage && (
  <ToastNotification
    message={toastMessage.message}
    type={toastMessage.type}
    onClose={() => setToastMessage(null)}
    duration={4000} // 4 seconds for autosave notifications
  />
)}

{/* Enhanced Autosave indicator */}
{currentUser && autoSaveEnabled && (
  <div className="fixed top-4 left-4 z-40">
    {isAutoSaving ? (
      // Saving notification - more prominent
      <div className="bg-blue-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 border-l-4 border-blue-300">
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        <div>
          <div className="font-medium">Saving pattern...</div>
          <div className="text-sm opacity-90">Your work is being saved to the cloud</div>
        </div>
      </div>
    ) : lastAutoSave && (
      // Last saved notification - less prominent but still visible
      <div className="bg-green-500 text-white px-3 py-2 rounded-lg shadow-md opacity-90 transition-opacity duration-300 hover:opacity-100">
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">
            Saved {new Date(lastAutoSave).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
        </div>
      </div>
    )}
  </div>
)}
      {/* 2) The tab buttons */}
      <div className="flex flex-wrap gap-2 mb-6 justify-between items-center card p-3">
        <div className="flex flex-wrap gap-1.5">
          <HamburgerActions
            patternName={patternName}
            deleteFolder={deleteFolder}
            selectedFolderForDeletion={selectedFolderForDeletion}
            setSelectedFolderForDeletion={setSelectedFolderForDeletion}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            setPatternName={setPatternName}
            newFolderName={newFolderName}
            setNewFolderName={setNewFolderName}
            folderName={folderName}
            setFolderName={setFolderName}
            savedPatterns={savedPatterns}
            onSavePattern={handleSavePattern}
            onRenamePattern={handleRenamePattern}
            onLoadPattern={handleLoadPattern}
            onDeletePattern={handleDeletePattern}
            onImportMIDI={onImportMIDI}
            onExportMIDI={handleExportMIDI}
            onImportMetronomeData={handleImportMetronomeData}
            onImportMusicXML={handleImportMusicXML}
            onOpenSheetMusicImport={() => setShowImportSheetMusicModal(true)}
            onExportMetronomeData={handleExportMetronomeData}
            onExportMp3={() => setShowExportMp3Modal(true)}
            aTuning={aTuning}
            setATuning={setATuning}
            stressOn={stressOn}
            setStressOn={setStressOn}
            subscriptionTier={subscriptionTier}
            handleUpgrade={handleUpgrade}
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
            handleGoogleSignIn={handleGoogleSignIn}
            handleSignOut={handleSignOut}
            setShowTutorial={setShowTutorial}
            setExportToLibraryPattern={setExportToLibraryPattern}
            measures={measures}
            tempo={tempo}
            useTempoChanges={useTempoChanges}
            enableAdditionalTempoChanges={enableAdditionalTempoChanges}
            tempoChanges={tempoChanges}
            muteRandomBeats={muteRandomBeats}
            mutePercentage={mutePercentage}
            loopMode={loopMode}
            startMeasureNumber={startMeasureNumber}
            selectedNoteValue={selectedNoteValue}
            timeSignatureColors={timeSignatureColors}
            setTimeSignatureColors={setTimeSignatureColors}
            showTimeSignatureColorsModal={showTimeSignatureColorsModal}
            setShowTimeSignatureColorsModal={setShowTimeSignatureColorsModal}
            rehearsalLetters={rehearsalLetters}
            modulateOnLoop={modulateOnLoop}
            modLoopInterval={modLoopInterval}
            modLoopDirection={modLoopDirection}
            autoSaveEnabled={autoSaveEnabled}
            setAutoSaveEnabled={setAutoSaveEnabled}
            isAutoSaving={isAutoSaving}
            lastAutoSave={lastAutoSave}
            triggerAutosave={triggerAutosave}
            showSaveModal={showSaveModal}   
            showSaveAsModal={showSaveAsModal}
            setShowSaveModal={setShowSaveModal}
            setShowSaveAsModal={setShowSaveAsModal}
            onManualSave={performManualSave}
            onSaveAs={performSaveAs}
            isPlaying={isPlaying} 
            enablePercentageScaling={enablePercentageScaling}
            setEnablePercentageScaling={setEnablePercentageScaling}
            announceRehearsalMarks={announceRehearsalMarks}
            setAnnounceRehearsalMarks={setAnnounceRehearsalMarks}
            rehearsalCountInMeasures={rehearsalCountInMeasures}
            setRehearsalCountInMeasures={setRehearsalCountInMeasures}
            setMeasures={setMeasures}
            setActiveTab={setActiveTab}
          />
          <button
            onClick={() => setActiveTab("metronome")}
            className={activeTab === "metronome" ? "nav-item-active" : "nav-item"}
          >
            FlowFrame
          </button>

          <button
            onClick={() => setActiveTab("practiceTracker")}
            className={`relative ${activeTab === "practiceTracker" ? "nav-item-active" : "nav-item"}`}
          >
            Journal
            {unreadFlowTrackerCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 rounded-full h-3 w-3" />
            )}
          </button>

          {/* FlowTrain button temporarily disabled
          <button
            onClick={() => setActiveTab("flowTrain")}
            className={activeTab === "flowTrain" ? "nav-item-active" : "nav-item"}
          >
            FlowTrain
          </button>
          */}

          <button
            onClick={() => setActiveTab("library")}
            className={activeTab === "library" ? "nav-item-active" : "nav-item"}
          >
            Library
          </button>

          <button
            onClick={() => setActiveTab("scores")}
            className={activeTab === "scores" ? "nav-item-active" : "nav-item"}
          >
            Scores
          </button>

          <button
            onClick={() => setActiveTab("practiceRoom")}
            className={activeTab === "practiceRoom" ? "nav-item-active" : "nav-item"}
          >
            Blog
          </button>

          <button
            onClick={() => setActiveTab("about")}
            className={activeTab === "about" ? "nav-item-active" : "nav-item"}
          >
            About
          </button>

          <button
            onClick={() => setActiveTab("community")}
            className={`relative ${activeTab === "community" ? "nav-item-active" : "nav-item"}`}
          >
            Community
            {hasUnreadCommunity && activeTab !== "community" && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
            )}
          </button>
        </div>

        {/* Auth buttons */}
        <div className="flex gap-2 items-center">
          {!currentUser ? (
            <>
              <button
                onClick={() => setShowLoginForm(true)}
                className="btn btn-secondary btn-sm"
              >
                Log In
              </button>
              <button
                onClick={() => {
                  setShowLoginForm(true);
                  setShowSignUpForm(true);
                }}
                className="btn btn-primary btn-sm"
              >
                Sign Up
              </button>
            </>
          ) : (
            <div className="px-3 py-1.5 text-sm rounded-md bg-surface-sunken text-gray-600 truncate max-w-[180px]">
              {currentUser.email}
            </div>
          )}
        </div>
      </div>
       {/* Email verification banner — shown when the signed-in user hasn't
           verified their email. Without verification, Firebase will silently
           swap their account if they later sign in with Google. */}
       {currentUser && !currentUser.emailVerified && !verifyBannerDismissed && (
        <div className="fixed top-0 left-0 right-0 z-[9998] bg-amber-50 border-b border-amber-200 px-4 py-3 shadow-sm">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-amber-900">
              <strong>Verify your email</strong> to keep your account secure.{' '}
              {currentUser.email && (
                <span className="font-medium">({currentUser.email})</span>
              )}
              {verifyStatus === 'sent' && (
                <span className="ml-2 text-green-700">✓ Sent — check your inbox (and spam folder).</span>
              )}
              {verifyStatus === 'error' && (
                <span className="ml-2 text-red-700">Something went wrong. Try again in a minute.</span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleSendVerificationEmail}
                disabled={verifyStatus === 'sending'}
                className="text-sm font-medium text-amber-900 underline hover:text-amber-700 disabled:opacity-50"
              >
                {verifyStatus === 'sending' ? 'Sending...' : 'Send verification email'}
              </button>
              <button
                type="button"
                onClick={handleCheckVerified}
                disabled={verifyStatus === 'checking'}
                className="text-sm font-medium text-amber-900 underline hover:text-amber-700 disabled:opacity-50"
              >
                {verifyStatus === 'checking' ? 'Checking...' : "I've verified"}
              </button>
              <button
                type="button"
                onClick={() => setVerifyBannerDismissed(true)}
                className="text-amber-700 hover:text-amber-900 text-lg leading-none"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

       {/* Login Modal */}
       {showLoginForm && !currentUser && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className="card-raised p-6 max-w-md w-full mx-4 animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {showSignUpForm ? "Sign Up" : "Log In"}
              </h2>
              <button
                onClick={() => {
                  setShowLoginForm(false);
                  setShowSignUpForm(false);
                  pendingGoogleCredentialRef.current = null;
                  setLinkAccountMessage(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {linkAccountMessage && (
              <div className="mb-4 p-3 bg-blue-50 text-blue-800 text-sm rounded border border-blue-200">
                {linkAccountMessage}
              </div>
            )}

            <LoginForm
              onLoginSuccess={async (user) => {
                const pendingCred = pendingGoogleCredentialRef.current;
                if (pendingCred) {
                  pendingGoogleCredentialRef.current = null;
                  try {
                    await linkWithCredential(user, pendingCred);
                  } catch (linkErr) {
                    console.error("Failed to link Google credential:", linkErr);
                  }
                }
                setLinkAccountMessage(null);
                setCurrentUser(user);
                setShowLoginForm(false);
                setShowSignUpForm(false);
              }}
              onGoogleSignIn={handleGoogleSignIn}
            />
          </div>
        </div>
      )}
      {/* 3) Show Metronome or About */}
      {activeTab === "metronome" && (
      <>
        <div className="text-center mb-3">
          <img
            src={flowFrameLogo}
            alt="FlowFrame Logo"
            className="inline-block w-48"
          />
        </div>

        <h2 className="text-2 mb-4 text-center">
          Empowering Confident Artistry
        </h2>

        {/* Toggle Component */}
        <div className="flex items-center justify-center mt-8 mb-4">
        <div className="tab-group">
          <button
            onClick={() => handleViewToggle('flowframe')}
            className={metronomeView === 'flowframe' ? 'tab-item-active' : 'tab-item'}
          >
            FlowFrame
          </button>
          <button
            onClick={() => handleViewToggle('metronome')}
            className={metronomeView === 'metronome' ? 'tab-item-active' : 'tab-item'}
          >
            Metronome
          </button>
          <button
            onClick={() => handleViewToggle('backing')}
            className={metronomeView === 'backing' ? 'tab-item-active' : 'tab-item'}
          >
            Backing
          </button>
          <button
            onClick={() => handleViewToggle('freeflow')}
            className={`relative ${metronomeView === 'freeflow' ? 'tab-item-active' : 'tab-item'}`}
          >
            FreeFlow
            <FreeFlowInvitationsBadge currentUser={currentUser} />
          </button>
        </div>
      </div>

    {/* Conditional Rendering */}
    {metronomeView === 'metronome' ? (
      <SimpleMetronome
      isPlaying={isPlaying}
      onStartStop={handleStartStop}
      tempo={tempo}
      setTempo={setTempo}
      currentBeat={currentBeat}
      measures={measures}
      updateMeasure={updateMeasure}
      setMeasures={setMeasures}
      selectedNoteValue={selectedNoteValue}
      setSelectedNoteValue={setSelectedNoteValue}
      NOTE_VALUE_OPTIONS={NOTE_VALUE_OPTIONS}
      audioContextRef={audioContextRef}
      scheduledBeatsRef={scheduledBeatsRef}
      createNewBeat={createNewBeat}
    />
    ) : metronomeView === 'backing' ? (
      <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
        <BackingTracksTab
          audioContextRef={audioContextRef}
          masterGainNodeRef={masterGainNodeRef}
          currentUser={currentUser}
          subscriptionTier={subscriptionTier}
        />
      </Suspense>
    ) : metronomeView === 'freeflow' ? (
      <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
        <FreeFlow
          currentUser={currentUser}
          subscriptionTier={subscriptionTier}
        />
      </Suspense>
    ) : (
      <>
          {/* Top bar: Start/Stop, Reset, Hamburger */}
          <div className="toolbar mb-4">
            {/* Top row: Start/Stop, Reset, and Hamburger */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {/* Start/Stop button */}
                <button
                  onClick={handleStartStop}
                  className="btn btn-primary btn-sm"
                >
                  {isPlaying ? (
                    <>
                      <Square className="w-4 h-4 mr-1" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-1" />
                      Start
                    </>
                  )}
                </button>

                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-accent-600"></div>
                      <p className="mt-2 text-gray-600">Loading...</p>
                    </div>
                  </div>
                )}

                <CurrentMeasureDisplay
                  measures={measures}
                  audioCtx={audioContextRef.current}
                  scheduledBeatsRef={scheduledBeatsRef}
                  isPlaying={isPlaying}
                  startMeasureNumber={startMeasureNumber}
                />
              </div>
            </div>

             {/* Second row: Start measure / Count-In */}
             <div className="flex items-center space-x-2 mt-2">
            {(() => {
              const minMeasureNumber = Math.min(...measures.map(m => m.measureNumber));
              const maxMeasureNumber = Math.max(...measures.map(m => m.measureNumber));
              
              return (
                <>
                   <label htmlFor="startMeasure" className="font-medium">
                    Start measure:
                  </label>
                  <input
                    id="startMeasure"
                    type="number"
                    min={minMeasureNumber}
                    max={maxMeasureNumber}
                    value={loopMode === "specific" ? loopStartMeasure : startMeasureInput}
                    onChange={(e) => {
                      // Allow any input including empty string
                      const value = e.target.value;
                      setStartMeasureInput(value);
                      
                      // Update the actual state only if it's a valid number
                      if (value !== '') {
                        const numValue = Number(value);
                        if (!isNaN(numValue) && measures.some(m => m.measureNumber === numValue)) {
                          setStartMeasureNumber(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Validate when the user finishes typing
                      const value = e.target.value.trim();
                      if (value === '' || isNaN(Number(value)) || !measures.some(m => m.measureNumber === Number(value))) {
                        // If field is empty or invalid, revert to the last valid value
                        const lastValid = lastValidStartMeasureRef.current;
                        setStartMeasureNumber(lastValid);
                        setStartMeasureInput(lastValid.toString());
                      } else {
                        // If valid, make sure both states are synchronized
                        const numValue = Number(value);
                        setStartMeasureNumber(numValue);
                        setStartMeasureInput(numValue.toString());
                      }
                    }}
                    disabled={isPlaying || loopMode === "specific"}
                    placeholder={minMeasureNumber.toString()}
                    className="px-3 py-1 border rounded w-20"
                  />

                  {/* Message when start measure is controlled by loop behavior */}
                  {loopMode === "specific" && (
                    <span className="text-xs text-gray-600 italic ml-2">
                      (via loop)
                    </span>
                  )}
                </>
              );
            })()}

              <label>Count-In:</label>
              <select
                id="count-in-select"
                value={countInMeasures}
                onChange={(e) => setCountInMeasures(Number(e.target.value))}
                disabled={isPlaying}
              >
                <option value={0}>None</option>
                <option value={1}>1 measure</option>
                <option value={2}>2 measures</option>
              </select>
            </div>
          </div>

{/* Pattern Overview - Modern Horizontal Scrolling */}
{(!isPlaying || !shouldSimplifyUI) && (
  <div className="relative">
<div
  ref={patternOverviewRef}
  className="pattern-overview mb-6"
  style={{
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    borderRadius: '20px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '2rem',
    fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }}
>
{/* Header Section - Improved Mobile Layout - ALWAYS VISIBLE */}
<div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-4 lg:space-y-0">
  {/* Title and Action Buttons */}
  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
    <div 
      className="font-bold text-xl text-center sm:text-left"
      style={{ 
        color: '#334155',
        fontFamily: '"Lato", sans-serif',
        fontWeight: '700',
        fontSize: isMobileView ? '1.1rem' : '1.25rem'
      }}
    >
      Pattern Overview
    </div>
    
    {/* Button Group */}
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
      <button
        type="button"
        onClick={handleReset}
        className={`btn btn-muted btn-md rounded-full ${isMobileView ? 'w-full' : ''}`}
        title="Reset pattern to default state"
      >
        <RotateCcw className="w-4 h-4 mr-2" />
        Reset Pattern
      </button>
    </div>
  </div>

  {/* Collapse Toggle - Right side on desktop, separate row on mobile */}
  <div className="flex justify-center lg:justify-end">
    <button
      type="button"
      onClick={() => setIsOverviewCollapsed(!isOverviewCollapsed)}
      className="p-3 rounded-full border-2 border-border text-gray-500 hover:text-gray-800 hover:border-border-strong hover:bg-surface-raised transition-all"
    >
      {isOverviewCollapsed ? (
        <ChevronDown className="w-5 h-5" />
      ) : (
        <ChevronUp className="w-5 h-5" />
      )}
    </button>
  </div>
</div>

  {/* Pattern Name - ALWAYS VISIBLE */}
  <div className="text-center mb-6 text-2xl font-bold text-gradient-brand tracking-tight">
    {patternName || "FlowFrame - Project"}
  </div>
 {/* Horizontal Scrolling Container - ONLY HIDDEN WHEN COLLAPSED */}
 {!isOverviewCollapsed && (
    <div className="relative">
     {/* Left Scroll Area */}
      <div 
        onClick={() => scrollOverview('left')}
        className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center z-20 cursor-pointer group transition-all"
        style={{ background: 'linear-gradient(to right, rgba(5,157,217,0.08), transparent)' }}
      >
        <ChevronLeft className="w-5 h-5 text-brand-500 opacity-50 group-hover:opacity-100 transition-opacity" />
      </div>

      {/* Right Scroll Area */}
      <div 
        onClick={() => scrollOverview('right')}
        className="absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center z-20 cursor-pointer group transition-all"
        style={{ background: 'linear-gradient(to left, rgba(5,157,217,0.08), transparent)' }}
      >
        <ChevronRight className="w-5 h-5 text-brand-500 opacity-50 group-hover:opacity-100 transition-opacity" />
      </div>

      {/* Horizontal Scrolling Measures Container */}
      <div
        ref={overviewScrollRef}
        className="flex overflow-x-auto gap-6 py-4 px-4"
        style={{
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        onScroll={(e) => setOverviewScrollPosition(e.target.scrollLeft)}
      >
        <style jsx>{`
          .flex.overflow-x-auto::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        {/* All Measures */}
        {measures.map((measure, measureIndex) => {
          const measureNum = measureIndex + 1;
          const isHighlighted = highlightedMeasures.includes(measureNum);
          const hasRehearsalLetter = rehearsalLetters[measureIndex];
          
          const timeSignatureKey = `${measure.numerator}/${measure.denominator}`;
          const timeSignatureStyle = getTimeSignatureColorStyle(timeSignatureKey, timeSignatureColors);
          const timeSignatureClass = getTimeSignatureColorClass(timeSignatureKey, timeSignatureColors);

          return (
            <div 
              key={measure.id} 
              className="flex flex-col items-center flex-shrink-0"
              style={{ minWidth: isMobileView ? '200px' : '280px' }}
            >
              {/* Rehearsal Letter Section */}
              <div className="h-12 flex items-center justify-center mb-3">
                {hasRehearsalLetter ? (
                  <button
                    onClick={() => {
                      setEditingRehearsalLetter(measureIndex);
                      setTempRehearsalLetter(rehearsalLetters[measureIndex]);
                    }}
                    className="w-9 h-9 rounded-full btn-accent-gradient text-white font-bold text-base transition-all hover:scale-110"
                    title="Click to edit or delete rehearsal letter"
                    disabled={isPlaying}
                  >
                    {rehearsalLetters[measureIndex]}
                  </button>
                ) : (
                  <button
                    onClick={() => setEditingRehearsalLetter(measureIndex)}
                    className="w-7 h-7 rounded-full bg-gray-200 text-gray-500 text-sm opacity-50 transition-all hover:opacity-100 hover:bg-accent-600 hover:text-white"
                    title="Add rehearsal letter"
                    disabled={isPlaying}
                  >
                    +
                  </button>
                )}
              </div>

              {/* Measure Card */}
              <div 
                className="w-full cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg"
                style={{
                  ...timeSignatureStyle,
                  border: isHighlighted ? '3px solid #ef4444' : '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-xl)',
                  padding: isMobileView ? '1rem' : '1.5rem',
                  boxShadow: 'var(--shadow-md)',
                }}
                onClick={() => jumpToMeasure(measureIndex)}
              >
                {/* Measure Header */}
                <div className="flex items-center justify-between mb-3">
                  {editingMeasureIndex === measureIndex ? (
                    <input
                      type="number"
                      value={tempNumber}
                      onChange={(e) => setTempNumber(e.target.value)}
                      onBlur={() => handleMeasureNumberChange(measureIndex, tempNumber)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleMeasureNumberChange(measureIndex, tempNumber);
                        }
                      }}
                      style={{
                        border: '2px solid #059dd9',
                        borderRadius: '8px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        textAlign: 'center',
                        fontFamily: '"Lato", sans-serif'
                      }}
                      autoFocus
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: '700',
                        color: '#334155',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontFamily: '"Lato", sans-serif'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingMeasureIndex(measureIndex);
                        setTempNumber(measure.measureNumber);
                      }}
                    >
                      Measure {measure.measureNumber}
                    </span>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        jumpToMeasure(measureIndex);
                      }}
                      className="icon-btn icon-btn-brand"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 5l7 7-7 7M5 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                    {/* Copy button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyMeasure(measureIndex);
                      }}
                      className="icon-btn icon-btn-brand"
                      title="Copy settings"
                      disabled={isPlaying}
                    >
                      <Copy className="w-4 h-4" />
                    </button>

                    {/* Paste button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePasteMeasure(measureIndex);
                      }}
                      className={`icon-btn ${copiedMeasure ? 'text-emerald-500 hover:text-emerald-700' : 'text-gray-300 cursor-not-allowed'}`}
                      title={copiedMeasure ? "Paste settings" : "No settings copied"}
                      disabled={isPlaying || !copiedMeasure}
                    >
                      <Clipboard className="w-4 h-4" />
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMeasureInOverview(measureIndex);
                      }}
                      className="icon-btn icon-btn-danger"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Time Signature and Beat Display */}
                <div className="flex items-center justify-center space-x-3">
                  {/* Time Signature Controls */}
                  <div 
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      borderRadius: '12px',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#334155',
                      fontFamily: '"Lato", sans-serif'
                    }}
                  >
                    {/* Numerator Controls */}
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMeasure(measureIndex, {
                            numerator: Math.max(1, measure.numerator - 1),
                          });
                        }}
                        style={{
                          background: '#e2e8f0',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontFamily: '"Lato", sans-serif'
                        }}
                      >
                        –
                      </button>
                      <span style={{ minWidth: '20px', textAlign: 'center' }}>
                        {measure.numerator}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMeasure(measureIndex, {
                            numerator: Math.min(32, measure.numerator + 1),
                          });
                        }}
                        style={{
                          background: '#e2e8f0',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontFamily: '"Lato", sans-serif'
                        }}
                      >
                        +
                      </button>
                    </div>

                    {/* Divider */}
                    <div 
                      style={{
                        height: '1px',
                        background: '#cbd5e1',
                        margin: '0.5rem 0'
                      }}
                    />

                    {/* Denominator Controls */}
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMeasure(measureIndex, {
                            denominator: getNextDenominatorDown(measure.denominator),
                          });
                        }}
                        style={{
                          background: '#e2e8f0',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontFamily: '"Lato", sans-serif'
                        }}
                      >
                        –
                      </button>
                      <span style={{ minWidth: '20px', textAlign: 'center' }}>
                        {measure.denominator}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMeasure(measureIndex, {
                            denominator: getNextDenominatorUp(measure.denominator),
                          });
                        }}
                        style={{
                          background: '#e2e8f0',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontFamily: '"Lato", sans-serif'
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Beat Circles */}
                  <div className="flex flex-wrap justify-center gap-1 max-w-32">
                    {Array.from({ length: measure.numerator }).map((_, bIdx) => {
                      const beatId = `m${measureIndex}-b${bIdx}`;
                      const beat = measure.beatSounds[bIdx];
                      const userVolume = typeof beat?.userVolume === 'number' ? beat.userVolume : 1.0;
                      // Map volume → visual saturation. Brand-500 #059dd9 is the base hue.
                      const isFull = userVolume >= 0.95;
                      const isMuted = userVolume <= 0.05;
                      const isLow = !isMuted && userVolume < 0.5;
                      const circleStyle = isMuted
                        ? {
                            background: 'transparent',
                            border: '1.5px dashed #cbd5e1',
                            color: '#94a3b8',
                          }
                        : isFull
                        ? {
                            background: 'linear-gradient(135deg, #059dd9, #0487be)',
                            border: '1.5px solid transparent',
                            color: '#ffffff',
                            boxShadow: '0 1px 4px rgba(5, 157, 217, 0.35)',
                          }
                        : {
                            background: `linear-gradient(135deg, rgba(5, 157, 217, ${userVolume}), rgba(4, 135, 190, ${userVolume}))`,
                            border: '1.5px solid transparent',
                            color: isLow ? '#1e3a8a' : '#ffffff',
                          };
                      const volumePct = Math.round(userVolume * 100);
                      return (
                        <div
                          key={bIdx}
                          data-beat-id={beatId}
                          role="button"
                          tabIndex={0}
                          aria-label={`Beat ${bIdx + 1} volume ${volumePct}%`}
                          title={`Beat ${bIdx + 1} — ${volumePct}% volume (click to change)`}
                          onClick={(e) => {
                            e.stopPropagation();
                            cycleBeatVolume(measureIndex, bIdx);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              cycleBeatVolume(measureIndex, bIdx);
                            }
                          }}
                          style={{
                            width: isMobileView ? '16px' : '24px',
                            height: isMobileView ? '16px' : '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            fontFamily: '"Lato", sans-serif',
                            cursor: 'pointer',
                            userSelect: 'none',
                            ...circleStyle,
                          }}
                        >
                          {bIdx + 1}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Dynamic Controls */}
                <div className="mt-4 flex items-center justify-center space-x-2">
                  <input
                    type="text"
                    value={measure.dynamicMarking}
                    onChange={(e) => {
                      updateMeasure(measureIndex, {
                        dynamicMarking: e.target.value,
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="dyn"
                    style={{
                      width: '40px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      padding: '0.25rem',
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      fontFamily: '"Lato", sans-serif'
                    }}
                  />

                  <label className="flex items-center space-x-1">
                    <input
                      type="checkbox"
                      checked={measure.cresc}
                      onChange={(e) =>
                        updateMeasure(measureIndex, {
                          cresc: e.target.checked,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span 
                      style={{ 
                        fontSize: '0.75rem',
                        fontFamily: '"Lato", sans-serif'
                      }}
                    >
                      cresc
                    </span>
                  </label>

                  <label className="flex items-center space-x-1">
                    <input
                      type="checkbox"
                      checked={measure.dim}
                      onChange={(e) =>
                        updateMeasure(measureIndex, {
                          dim: e.target.checked,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span 
                      style={{ 
                        fontSize: '0.75rem',
                        fontFamily: '"Lato", sans-serif'
                      }}
                    >
                      dim
                    </span>
                  </label>
                </div>
              </div>
            </div>
          );
        })}



        {/* Ghost Measure */}
        {measures.length > 0 && (
          <div 
            className="flex flex-col items-center flex-shrink-0"
            style={{ minWidth: isMobileView ? '240px' : '280px' }}
          >
            {/* Rehearsal Letter Section for Ghost */}
            <div className="h-12 flex items-center justify-center mb-3">
              {rehearsalLetters['ghost'] ? (
                <button
                  onClick={() => {
                    setEditingRehearsalLetter('ghost');
                    setTempRehearsalLetter(rehearsalLetters['ghost']);
                  }}
                  style={{
                    width: '36px',
                    height: '36px',
                    background: 'linear-gradient(135deg, #B71E4B, #CC235B)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '700',
                    boxShadow: '0 4px 12px rgba(183, 30, 75, 0.3)',
                    transition: 'all 0.3s ease',
                    fontFamily: '"Lato", sans-serif'
                  }}
                  title="Click to edit or delete rehearsal letter"
                  disabled={isPlaying}
                >
                  {rehearsalLetters['ghost']}
                </button>
              ) : (
                <button
                  onClick={() => setEditingRehearsalLetter('ghost')}
                  style={{
                    width: '28px',
                    height: '28px',
                    background: '#e2e8f0',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    opacity: 0.5,
                    transition: 'all 0.3s ease',
                    fontFamily: '"Lato", sans-serif'
                  }}
                  title="Add rehearsal letter to next measure"
                  disabled={isPlaying}
                >
                  +
                </button>
              )}
            </div>

            {/* Ghost Measure Card - Enhanced */}
            <div
              style={{
                background: (() => {
                  const ghostTimeSignatureKey = `${ghostNumer}/${ghostDenom}`;
                  const ghostTimeSignatureStyle = getTimeSignatureColorStyle(ghostTimeSignatureKey, timeSignatureColors);
                  return ghostTimeSignatureStyle.backgroundColor 
                    ? `linear-gradient(135deg, ${ghostTimeSignatureStyle.backgroundColor}40, rgba(255, 255, 255, 0.8))`
                    : 'linear-gradient(135deg, rgba(5, 157, 217, 0.1), rgba(255, 255, 255, 0.8))';
                })(),
                border: '3px dashed #059dd9', // More prominent border
                borderRadius: '16px',
                padding: '1.5rem',
                transition: 'all 0.3s ease',
                width: '100%',
                opacity: 0.9, // More visible
                position: 'relative'
              }}
              className="hover:opacity-100 hover:-translate-y-1 hover:shadow-lg transition-all"
            > 
              <div className="text-center mb-3 text-sm font-bold text-brand-500">
              
                Measure {measures[measures.length - 1].measureNumber + 1}
              </div>

              {/* Ghost Time Signature Controls */}
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div 
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#64748b',
                    fontFamily: '"Lato", sans-serif'
                  }}
                >
                  {/* Ghost Numerator */}
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGhostNumer((prev) => Math.max(1, prev - 1));
                      }}
                      style={{
                        background: '#e2e8f0',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontFamily: '"Lato", sans-serif'
                      }}
                    >
                      –
                    </button>
                    <span style={{ minWidth: '20px', textAlign: 'center' }}>
                      {ghostNumer}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGhostNumer((prev) => Math.min(32, prev + 1));
                      }}
                      style={{
                        background: '#e2e8f0',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontFamily: '"Lato", sans-serif'
                      }}
                    >
                      +
                    </button>
                  </div>

                  <div 
                    style={{
                      height: '1px',
                      background: '#cbd5e1',
                      margin: '0.5rem 0'
                    }}
                  />

                  {/* Ghost Denominator */}
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGhostDenom((prev) => getNextDenominatorDown(prev));
                      }}
                      style={{
                        background: '#e2e8f0',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontFamily: '"Lato", sans-serif'
                      }}
                    >
                      –
                    </button>
                    <span style={{ minWidth: '20px', textAlign: 'center' }}>
                      {ghostDenom}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGhostDenom((prev) => getNextDenominatorUp(prev));
                      }}
                      style={{
                        background: '#e2e8f0',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontFamily: '"Lato", sans-serif'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Ghost Beat Circles */}
                <div className="flex flex-wrap justify-center gap-1 max-w-32">
                  {Array.from({ length: ghostNumer }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#f1f5f9',
                        color: '#94a3b8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        fontFamily: '"Lato", sans-serif'
                      }}
                    >
                      {i + 1}
                    </div>
                  ))}
                </div>
              </div>

              {/* Add Measure Button */}
              <div className="text-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddCustomMeasure(ghostNumer, ghostDenom);
                  }}
                  className="btn btn-primary-gradient btn-sm"
                >
                  Add Measure
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scroll Indicators */}
      <div className="flex justify-center mt-4 space-x-2">
        {Array.from({ length: Math.ceil((measures.length + 1) / 5) }).map((_, index) => (
          <div
            key={index}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: index === Math.floor(overviewScrollPosition / 400) ? '#059dd9' : '#cbd5e1',
              transition: 'background-color 0.3s ease'
            }}
          />
        ))}
      </div>
    </div>
  )}

 {/* Full Pattern and Bulk Editor Buttons - Combined section */}
  <div className="flex justify-center mt-6 pt-4 border-t border-gray-200">
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
{/* Bulk Editor Button */}
<button
        type="button"
        onClick={() => setBulkEditorOpen(true)}
        className="btn btn-primary-gradient rounded-full px-6 py-3 text-sm"
        style={{ minHeight: '44px' }}
        title="Edit multiple measures at once"
      >
        <svg 
          className="w-4 h-4 mr-2" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        Bulk Editor
      </button>

      {/* Full Pattern Button */}
      <button
        type="button"
        onClick={() => setShowFullPatternModal(true)}
        className="btn btn-primary-gradient rounded-full px-6 py-3 text-sm"
        style={{ minHeight: '44px' }}
        title="View full pattern in modal"
      >
        <svg 
          className="w-4 h-4 mr-2" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
        Full Pattern
      </button>
    </div>
  </div>

  {/* Keep the existing Rehearsal Letter Editor Modal */}
  {editingRehearsalLetter !== null && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '2rem',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          maxWidth: '400px',
          width: '90%',
          fontFamily: '"Lato", sans-serif'
        }}
      >
        <h3 
          style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            marginBottom: '1rem',
            color: '#334155',
            textAlign: 'center'
          }}
        >
          {rehearsalLetters[editingRehearsalLetter] ? 'Edit' : 'Add'} Rehearsal Letter
          {editingRehearsalLetter === 'ghost' ? ' (Next Measure)' : ''}
        </h3>
        
        <div className="mb-6">
          <label 
            style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#475569'
            }}
          >
            Letter/Number:
          </label>
          <input
            type="text"
            maxLength="3"
            value={tempRehearsalLetter}
            onChange={(e) => setTempRehearsalLetter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              textAlign: 'center',
              fontSize: '1.25rem',
              fontWeight: '700',
              fontFamily: '"Lato", sans-serif',
              transition: 'border-color 0.3s ease',
              backgroundColor: 'transparent'
            }}
            placeholder="A"
            autoFocus
            onFocus={(e) => {
              e.target.select();
              e.target.style.borderColor = '#059dd9';
            }}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={saveRehearsalLetter}
            className="btn btn-primary-gradient flex-1 py-3 rounded-xl"
          >
            Save
          </button>
          {rehearsalLetters[editingRehearsalLetter] && (
            <button
              onClick={deleteRehearsalLetter}
              className="btn btn-danger py-3 px-4 rounded-xl"
            >
              Delete
            </button>
          )}
          <button
            onClick={() => {
              setEditingRehearsalLetter(null);
              setTempRehearsalLetter('');
            }}
            className="btn btn-muted py-3 px-4 rounded-xl"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )}

  {/* Keep existing BeatHighlighter */}
  {isPlaying && (
    <BeatHighlighter
      audioCtx={audioContextRef.current}
      scheduledBeatsRef={scheduledBeatsRef}
      leadTimeMs={50}
      isPlaying={isPlaying}
    />
  )}

  {isPlaying && announceRehearsalMarks && (
    <RehearsalAnnouncer
      audioCtx={audioContextRef.current}
      scheduledBeatsRef={scheduledBeatsRef}
      isPlaying={isPlaying}
      rehearsalLetters={rehearsalLetters}
      countInMeasures={rehearsalCountInMeasures}
      measuresLength={measures.length}
    />
  )}
</div>
</div>
)}

{shouldSimplifyUI &&(
  <div className="text-center py-8">
    <div className="text-lg font-medium text-gray-600">
      Pattern playing - UI simplified for patterns 20 measures and longer
    </div>
  </div>
)}

          <>
              <BulkEditorModal
                key={bulkEditorResetCount}
                isOpen={bulkEditorOpen}
                onClose={() => setBulkEditorOpen(false)}
                onApplyChanges={handleApplyBulkEditorChanges}
                onApplyPitches={handleApplyBulkEditorPitches}
                measures={measures}
            />
          </>

          {/* Tuning, tempo, volume, random mute, tempo changes, etc. */}
          <div className="mb-6">
            <div id="tempo-controls" className="mt-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      if (isTempoLocked || useTempoChanges) return;
                      const current = Number(tempoInput) || tempo;
                      const newVal = Math.max(10, current - 1);
                      if (scaleByPercentage) {
                        setBaseTempo(newVal);
                        setTempo(Math.round(newVal * (tempoPercentage / 100)));
                      } else {
                        setTempo(newVal);
                      }
                      setTempoInput(newVal.toString());
                    }}
                    disabled={isTempoLocked || useTempoChanges}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 hover:bg-gray-200 active:bg-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Decrease BPM"
                  >
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  </button>
                  <input
                    type="number"
                    min="10"
                    max="340"
                    value={tempoInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTempoInput(value);
                      if (value !== '') {
                        const numValue = Number(value);
                        if (!isNaN(numValue) && numValue >= 10 && numValue <= 340) {
                          if (scaleByPercentage) {
                            setBaseTempo(numValue);
                            setTempo(Math.round(numValue * (tempoPercentage / 100)));
                          } else {
                            setTempo(numValue);
                          }
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      const numValue = Number(value);
                      if (value === '' || isNaN(numValue) || numValue < 10 || numValue > 340) {
                        const lastValid = lastValidTempoRef.current;
                        if (scaleByPercentage) {
                          setBaseTempo(lastValid);
                          setTempo(Math.round(lastValid * (tempoPercentage / 100)));
                        } else {
                          setTempo(lastValid);
                        }
                        setTempoInput(lastValid.toString());
                      } else {
                        if (scaleByPercentage) {
                          setBaseTempo(numValue);
                          setTempo(Math.round(numValue * (tempoPercentage / 100)));
                        } else {
                          setTempo(numValue);
                        }
                        setTempoInput(numValue.toString());
                      }
                    }}
                    disabled={isTempoLocked || useTempoChanges}
                    className="p-2 border rounded w-20 text-center"
                  />
                  <button
                    onClick={() => {
                      if (isTempoLocked || useTempoChanges) return;
                      const current = Number(tempoInput) || tempo;
                      const newVal = Math.min(340, current + 1);
                      if (scaleByPercentage) {
                        setBaseTempo(newVal);
                        setTempo(Math.round(newVal * (tempoPercentage / 100)));
                      } else {
                        setTempo(newVal);
                      }
                      setTempoInput(newVal.toString());
                    }}
                    disabled={isTempoLocked || useTempoChanges}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 hover:bg-gray-200 active:bg-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Increase BPM"
                  >
                    <ChevronUp className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                <div className="flex items-center gap-1">
                  <label htmlFor="noteValueSelect" className="text-sm whitespace-nowrap">
                    =
                  </label>
                  <select
                    id="noteValueSelect"
                    value={selectedNoteValue?.label || ""}
                    onChange={(e) => {
                      const newChoice = NOTE_VALUE_OPTIONS.find(
                        (opt) => opt.label === e.target.value
                      );
                      setSelectedNoteValue(newChoice);
                    }}
                    disabled={isTempoLocked || useTempoChanges}
                    className="p-1 border rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {NOTE_VALUE_OPTIONS.map((opt) => (
                      <option key={opt.label} value={opt.label}>
                        {opt.label}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => setIsTempoLocked((prev) => !prev)}
                    disabled={useTempoChanges}
                    className="text-gray-600 hover:text-gray-800 ml-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={
                      useTempoChanges
                        ? "Locked while Tempo Changes are enabled"
                        : isTempoLocked
                        ? "Unlock tempo"
                        : "Lock tempo"
                    }
                  >
                    {isTempoLocked || useTempoChanges ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 11c1.657 0 3-1.343 3-3V5a3 3 0 10-6 0v3c0 1.657 1.343 3 3 3zm6 0H6a2 2 0 00-2 2v5a2 2 0 002 2h12a2 2 0 002-2v-5a2 2 0 00-2-2z"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11V7a3 3 0 10-6 0m6 0a3 3 0 00-6 0m6 4H6a2 2 0 00-2 2v5a2 2 0 002 2h12a2 2 0 002-2v-5a2 2 0 00-2-2z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <input
                type="range"
                min="10"
                max="340"
                value={tempo}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (scaleByPercentage) {
                    // When scaling by percentage, update base tempo and keep current percentage
                    setBaseTempo(newValue);
                    const effectiveTempo = Math.round(newValue * (tempoPercentage / 100));
                    setTempo(effectiveTempo);
                    setTempoInput(effectiveTempo.toString());
                  } else {
                    setTempo(newValue);
                    setTempoInput(newValue.toString());
                  }
                }}
                disabled={isTempoLocked || useTempoChanges}
                className="w-full max-w-md mt-2"
                style={{ accentColor: "#307BBF" }}
              />

{(enablePercentageScaling || useTempoChanges) && (
               <div className="mt-3 p-3 bg-gray-50 rounded border mb-3">
                 <div className="flex items-center space-x-2 mb-2">
                   <input
                     type="checkbox"
                     id="scaleByPercentage"
                     checked={scaleByPercentage || useTempoChanges}
                     onChange={(e) => {
                       const enabled = e.target.checked;
                       setScaleByPercentage(enabled);
                       if (enabled) {
                         // Store current tempo changes as originals when first enabling
                         setOriginalTempoChanges([...tempoChanges]);
                         setBaseTempo(tempo);
                         setTempoPercentage(100);
                       } else {
                         // When disabling, restore original tempo changes if they exist
                         if (originalTempoChanges.length > 0) {
                           setTempoChanges([...originalTempoChanges]);
                           setOriginalTempoChanges([]);
                         }
                       }
                     }}
                     disabled={isTempoLocked || useTempoChanges}
                     className="rounded"
                   />
                   <label htmlFor="scaleByPercentage" className="text-sm font-medium">
                     Scale by percentage
                   </label>
                 </div>

                 {(scaleByPercentage || useTempoChanges) && (
                   <div className="space-y-2">
                     <div className="flex items-center space-x-2">
                       <span className="text-sm">Measure 1 BPM:</span>
                       <input
                         type="number"
                         min="10"
                         max="340"
                         value={baseTempo}
                         onChange={(e) => {
                           const newBaseTempo = Number(e.target.value);
                           setBaseTempo(newBaseTempo);
                           const effectiveTempo = Math.round(newBaseTempo * (tempoPercentage / 100));
                           setTempo(effectiveTempo);
                           setTempoInput(effectiveTempo.toString());
                         }}
                         disabled={isTempoLocked}
                         className="p-1 border rounded w-20 text-sm"
                       />
                     </div>
                    
                     <div className="flex items-center space-x-2">
                       <span className="text-sm">Scale:</span>
                       <input
                         type="range"
                         min="25"
                         max="200"
                         value={tempoPercentage}
                         onChange={(e) => {
                           const newPercentage = Number(e.target.value);
                           setTempoPercentage(newPercentage);
                           setTempo(Math.round(baseTempo * (newPercentage / 100)));
                           if (scaleByPercentage && originalTempoChanges.length > 0) {
                             scaleTempoChangesFromOriginals(newPercentage);
                           }
                         }}
                         disabled={isTempoLocked}
                         className="flex-1"
                         style={{ accentColor: "#307BBF" }}
                       />
                       <input
                         type="number"
                         min="25"
                         max="200"
                         value={tempoPercentage}
                         onChange={(e) => {
                           const newPercentage = Number(e.target.value);
                           setTempoPercentage(newPercentage);
                           setTempo(Math.round(baseTempo * (newPercentage / 100)));
                           if (scaleByPercentage && originalTempoChanges.length > 0) {
                             scaleTempoChangesFromOriginals(newPercentage);
                           }
                         }}
                         disabled={isTempoLocked}
                         className="p-1 border rounded w-16 text-sm"
                       />
                       <span className="text-sm">%</span>
                     </div>
                    
                     <div className="text-sm text-gray-600">
                       Tempo at Start Measure:{" "}
                       <span className="font-medium">
                         {Math.round(
                           computeEffectiveTempo(
                             Math.max(0, (startMeasureNumber || 1) - 1),
                             0,
                             4,
                             tempo,
                             useTempoChanges,
                             tempoChanges,
                             { current: 0 }
                           )
                         )}
                       </span>
                     </div>
                   </div>
                 )}
               </div>
             )}


              {/* Tap Tempo Button(s) */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleTapTempo}
                  disabled={isPlaying || useTempoChanges}
                  className="px-2 py-1 bg-blue-100 border rounded disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Tap Tempo
                </button>
                <button
                  onClick={handleResetTap}
                  disabled={isPlaying}
                  className="px-2 py-1 bg-gray-200 border rounded"
                >
                  Reset Tap
                </button>
              </div>
            </div>

             {/* Loop Behavior Section */}
             <div className="mt-4 flex flex-col">
              <label className="text-sm font-medium mb-2">
                Loop Behavior
              </label>

              <select
                value={loopMode}
                onChange={(e) => setLoopMode(e.target.value)}
                className="p-2 border rounded"
                disabled={isPlaying}
              >
                <option value="loop">Loop to Beginning</option>
                <option value="continue">Continue Last Measure</option>
                <option value="specific">Loop Specific Measures</option>
              </select>

              {/* Only show these inputs if loopMode === 'specific' */}
              {loopMode === "specific" && (
                <div className="mt-2 space-y-3">
                  <div className="flex space-x-2">
                    <label>Loop start measure:</label>
                    <input
                      type="number"
                      value={loopStartMeasure}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value, 10);
                        const val = Number.isNaN(raw) || raw < 1 ? 1 : raw;
                        setLoopStartMeasure(val);
                      }}
                      min="1"
                      className="border p-1 rounded w-16"
                      disabled={isPlaying}
                    />

                    <label>Loop end measure:</label>
                    <input
                      type="number"
                      value={loopEndMeasure}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value, 10);
                        const val = Number.isNaN(raw) || raw < 1 ? 1 : raw;
                        setLoopEndMeasure(val);
                      }}
                      min="1"
                      className="border p-1 rounded w-16"
                      disabled={isPlaying}
                    />
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="loopCountIn"
                        checked={loopCountInEnabled}
                        onChange={(e) => setLoopCountInEnabled(e.target.checked)}
                        disabled={isPlaying || countInMeasures === 0}
                        className="mr-2"
                      />
                      <label htmlFor="loopCountIn" className="text-sm font-medium">
                        Include count-in on each loop restart
                      </label>
                      {countInMeasures === 0 && (
                        <span className="ml-2 text-xs text-gray-500">
                          (Set count-in measures above to enable)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Incremental Tempo Option */}
                  <div className="border-t pt-3">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="incrementalLoop"
                        checked={loopIncrementalSettings.enabled}
                        onChange={(e) => setLoopIncrementalSettings(prev => ({
                          ...prev,
                          enabled: e.target.checked
                        }))}
                        disabled={isPlaying}
                        className="mr-2"
                      />
                      <label htmlFor="incrementalLoop" className="text-sm font-medium">
                        Incremental Tempo Changes
                      </label>
                    </div>

                    {loopIncrementalSettings.enabled && (
                      <div className="ml-6 space-y-3 bg-gray-50 p-3 rounded">
                        {/* Scope Selection */}
                        <div>
                          <label className="block text-sm font-medium mb-1">Scope:</label>
                          <select
                            value={loopIncrementalSettings.scope}
                            onChange={(e) => setLoopIncrementalSettings(prev => ({
                              ...prev,
                              scope: e.target.value
                            }))}
                            className="p-1 border rounded w-full"
                            disabled={isPlaying}
                          >
                            <option value="specific">Per Loop (within range)</option>
                            <option value="whole">Per Complete Pattern</option>
                          </select>
                        </div>

                        {/* Tempo Settings */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-sm font-medium mb-1">Start Tempo:</label>
                            <input
                              type="number"
                              value={loopIncrementalSettings.startTempo}
                              onChange={(e) => {
                                setLoopIncrementalSettings(prev => ({
                                  ...prev,
                                  startTempo: Number(e.target.value)
                                }));
                              }}
                              className="border p-1 rounded w-full"
                              disabled={isPlaying}
                              min="20"
                              max="400"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">End Tempo:</label>
                            <input
                              type="number"
                              value={loopIncrementalSettings.endTempo}
                              onChange={(e) => {
                                setLoopIncrementalSettings(prev => ({
                                  ...prev,
                                  endTempo: Number(e.target.value)
                                }));
                              }}
                              className="border p-1 rounded w-full"
                              disabled={isPlaying}
                              min="20"
                              max="400"
                            />
                          </div>
                        </div>

                        {/* Step Settings */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-sm font-medium mb-1">Step Size (BPM):</label>
                            <input
                              type="number"
                              value={loopIncrementalSettings.stepSize}
                              onChange={(e) => {
                                setLoopIncrementalSettings(prev => ({
                                  ...prev,
                                  stepSize: Number(e.target.value)
                                }));
                              }}
                              className="border p-1 rounded w-full"
                              disabled={isPlaying}
                              min="1"
                              max="50"
                            />
                          </div>
  
                          <div>
                            <label className="block text-sm font-medium mb-1">Step Every:</label>
                            <input
                              type="number"
                              value={loopIncrementalSettings.stepEvery}
                              onChange={(e) => setLoopIncrementalSettings(prev => ({
                                ...prev,
                                stepEvery: Number(e.target.value)
                              }))}
                              className="border p-1 rounded w-full"
                              disabled={isPlaying}
                              min="1"
                            />
                          </div>
                        </div>

                        <div className="text-xs text-gray-600">
                          {loopIncrementalSettings.scope === "specific" 
                            ? "Tempo increases each time through the selected measures"
                            : "Tempo increases each time the entire pattern completes"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          
           

            {/* tempo changes toggle */}
            <div id="tempochanges" className="position: relative mt-4">
              {subscriptionTier === "pro" ? (
                <label className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    checked={useTempoChanges}
                    onChange={(e) => {
                      setUseTempoChanges(e.target.checked);
                      if (!e.target.checked) {
                        setEnableAdditionalTempoChanges(false);
                        setTempoChanges([{ ...DEFAULT_TEMPO_CHANGE }]);
                      }
                    }}
                    disabled={isPlaying}
                  />
                  <span className="text-sm font-medium">
                    Enable Tempo Changes
                  </span>
                  <button
                    type="button"
                    className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-700 
                 hover:bg-gray-300 text-xs font-bold"
                    onClick={() => setShowInfo(!showInfo)}
                  >
                    i
                  </button>
                </label>
              ) : (
                <p className="text-xs text-gray-500 mt-2">
                  <strong>Enable Tempo Changes</strong> is a Basic feature.
                  <button
                    onClick={() => handleUpgrade(currentUser)}
                    className="ml-1 underline text-blue-600"
                  >
                    Upgrade
                  </button>
                </p>
              )}

              {/* Info Pop-up (renders only if showInfo is true) */}
              {showInfo && (
                <div
                  className="absolute z-10 bg-white border border-gray-300 
                     p-2 rounded shadow-md text-sm w-64"
                  style={{ top: "3rem", left: 0 }}
                >
                  <p>
                    Tempo changes scale proportionally to the base BPM. For
                    example, if the tempo change goes from 60 to 120, and the
                    base BPM is set to 30, the tempo change will go from 30 to
                    60.
                  </p>
                  {/* Close button inside the pop-up */}
                  <div className="text-right mt-2">
                    <button
                      onClick={() => setShowInfo(false)}
                      className="text-xs text-blue-500 hover:underline"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {useTempoChanges && (
                <div
                  className="tempo-changes-section mb-6"
                  style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                    padding: '2rem',
                    fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                >
                  {/* Header Section */}
                  <div className="flex justify-between items-center mb-6">
                    <div 
                      className="font-bold text-xl"
                      style={{ 
                        color: '#334155',
                        fontFamily: '"Lato", sans-serif',
                        fontWeight: '700',
                        fontSize: isMobileView ? '1rem' : '1.25rem'
                      }}
                    >
                      Tempo Changes
                    </div>
                    <button
                      onClick={() => {
                        setTempoChanges((prev) => {
                          const newTempoChanges = [...prev, { ...DEFAULT_TEMPO_CHANGE }];
                          
                          // Scroll to the new tempo change after state update
                          setTimeout(() => {
                            const container = tempoChangesScrollRef.current;
                            if (!container) return;
                            
                            const cardWidth = isMobileView ? 280 : 400;
                            const gap = 32;
                            const newIndex = newTempoChanges.length - 1;
                            
                            // Calculate position to center the new tempo change
                            const cardPosition = newIndex * (cardWidth + gap);
                            const containerWidth = container.clientWidth;
                            const targetScrollPosition = cardPosition - (containerWidth / 2) + (cardWidth / 2);
                            
                            container.scrollTo({
                              left: Math.max(0, targetScrollPosition),
                              behavior: 'smooth'
                            });
                          }, 150); // Slightly longer delay for more reliable DOM update
                          
                          return newTempoChanges;
                        });
                      }}
                      className="btn btn-accent-gradient rounded-full px-6 py-3 text-sm"
                      disabled={isPlaying}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Tempo Change
                    </button>
                  </div>

                                    {/* Horizontal Scrolling Container */}
                                    <div className="relative">
                    {/* Left Scroll Area */}
                    <div 
                      onClick={() => scrollTempoChanges('left')}
                      className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center z-20 cursor-pointer group transition-all"
                      style={{ background: 'linear-gradient(to right, rgba(5,157,217,0.08), transparent)' }}
                    >
                      <ChevronLeft className="w-5 h-5 text-brand-500 opacity-50 group-hover:opacity-100 transition-opacity" />
                    </div>

                    {/* Right Scroll Area */}
                    <div 
                      onClick={() => scrollTempoChanges('right')}
                      className="absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center z-20 cursor-pointer group transition-all"
                      style={{ background: 'linear-gradient(to left, rgba(5,157,217,0.08), transparent)' }}
                    >
                      <ChevronRight className="w-5 h-5 text-brand-500 opacity-50 group-hover:opacity-100 transition-opacity" />
                    </div>

                    {/* Horizontal Scrolling Tempo Changes Container */}
                    <div
                      ref={tempoChangesScrollRef}
                      className="flex overflow-x-auto gap-8 py-4 px-4"
                      style={{
                        scrollBehavior: 'smooth',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                      }}
                      onScroll={(e) => setTempoChangesScrollPosition(e.target.scrollLeft)}
                    >
                      <style jsx>{`
                        .flex.overflow-x-auto::-webkit-scrollbar {
                          display: none;
                        }
                      `}</style>

                      {tempoChanges.map((tc, i) => (
                        <div
                          key={i}
                          className="flex-shrink-0"
                          style={{ 
                            minWidth: isMobileView ? '280px' : '400px', 
                            maxWidth: isMobileView ? '300px' : '400px' 
                          }}
                        >
                          <div
                            className="card p-6 transition-all hover:-translate-y-1 hover:shadow-lg"
                            style={{ height: 'fit-content' }}
                          >
                            {/* Header */}
                            <div className="flex justify-between items-center mb-4">
                              <h3 className={`${isMobileView ? 'text-base' : 'text-lg'} font-bold text-gradient-brand`}>
                                Tempo Change {i + 1}
                              </h3>
                              <button
                                onClick={() => setTempoChanges((prev) => prev.filter((_, idx) => idx !== i))}
                                className="icon-btn icon-btn-danger"
                                disabled={isPlaying}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Type Selection */}
                            <div className="mb-4">
                              <label 
                                className="block text-sm font-medium mb-2"
                                style={{ 
                                  color: '#475569', 
                                  fontFamily: '"Lato", sans-serif',
                                  fontWeight: '600'
                                }}
                              >
                                Type
                              </label>
                              <select
                                value={tc.type}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  setTempoChanges((prev) =>
                                    prev.map((item, idx) => {
                                      if (idx === i) {
                                        // Create a clean object based on the new type
                                        const baseObject = {
                                          startMeasure: item.startMeasure,
                                          endMeasure: item.endMeasure,
                                          startTempo: item.startTempo,
                                          endTempo: item.endTempo,
                                          type: newType
                                        };
                                        
                                        // Add type-specific properties
                                        if (newType === "incremental") {
                                          return {
                                            ...baseObject,
                                            scope: "specific", // or "whole" - set a proper default
                                            stepSize: item.stepSize || 5,
                                            stepEvery: item.stepEvery || 1,
                                            useStepEvery: item.useStepEvery || false,
                                            localLoopCount: 0 // Initialize this properly
                                          };
                                        } else {
                                          // For linear, exponential, and sudden types
                                          return baseObject;
                                        }
                                      }
                                      return item;
                                    })
                                  );
                                }}
                                disabled={isPlaying}
                                style={{
                                  padding: '0.5rem',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '8px',
                                  width: '100%',
                                  fontFamily: '"Lato", sans-serif',
                                  transition: 'border-color 0.3s ease',
                                  backgroundColor: 'transparent',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#059dd9'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                              >
                                <option value="linear">Linear</option>
                                <option value="exponential">Exponential</option>
                                <option value="sudden">Sudden</option>
                              </select>
                            </div>

                            {/* Form Fields Container with max height and scroll */}
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {tc.type === "sudden" ? (
                                <>
                                  {/* Sudden type fields */}
                                  <div className="mb-4" style={{ background: 'transparent' }}>
                                    <label 
                                      className="block text-sm font-medium mb-2"
                                      style={{ 
                                        color: '#475569', 
                                        fontFamily: '"Lato", sans-serif',
                                        fontWeight: '600',
                                        backgroundColor: 'transparent'
                                      }}
                                    >
                                      Measure of Change
                                    </label>
                                    <input
                                      type="number"
                                      style={{
                                        padding: '0.5rem',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        width: '100%',
                                        fontFamily: '"Lato", sans-serif',
                                        transition: 'border-color 0.3s ease',
                                        backgroundColor: 'transparent'
                                      }}
                                      onFocus={(e) => e.target.style.borderColor = '#059dd9'}
                                      onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                      value={tc.startMeasure || ''}
                                      onChange={(e) => {
                                        const val = e.target.value === '' ? '' : Number(e.target.value);
                                        setTempoChanges((prev) =>
                                          prev.map((item, idx) =>
                                            idx === i ? { ...item, startMeasure: val } : item
                                          )
                                        );
                                      }}
                                      disabled={isPlaying}
                                    />
                                  </div>

                                  <div className="mb-4" style={{ background: 'transparent' }}>
                                    <label 
                                      className="block text-sm font-medium mb-2"
                                      style={{ 
                                        color: '#475569', 
                                        fontFamily: '"Lato", sans-serif',
                                        fontWeight: '600',
                                        backgroundColor: 'transparent'
                                      }}
                                    >
                                      End Tempo
                                    </label>
                                    <input
                                      type="number"
                                      style={{
                                        padding: '0.5rem',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        width: '100%',
                                        fontFamily: '"Lato", sans-serif',
                                        transition: 'border-color 0.3s ease',
                                        backgroundColor: 'transparent'
                                      }}
                                      onFocus={(e) => e.target.style.borderColor = '#059dd9'}
                                      onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                      value={tc.endTempo || ''}
                                      onChange={(e) => {
                                        const val = e.target.value === '' ? '' : Number(e.target.value);
                                        setTempoChanges((prev) =>
                                          prev.map((item, idx) =>
                                            idx === i ? { ...item, endTempo: val } : item
                                          )
                                        );
                                      }}
                                      disabled={isPlaying}
                                    />
                                  </div>
                                  <div className="mb-4" style={{ background: 'transparent' }}>
                                    <label className="block text-sm font-medium mb-2" style={{ background: 'transparent' }}>
                                      Note Value
                                    </label>
                                    <div className="text-xs text-gray-600 mb-2" style={{ fontFamily: '"Lato", sans-serif' }}>
                                      Use current note value
                                    </div>
                                    <select
                                      value={tc.noteValue?.label || ""}
                                      onChange={(e) => {
                                        const newNoteValue = e.target.value 
                                          ? NOTE_VALUE_OPTIONS.find(opt => opt.label === e.target.value)
                                          : null;
                                        setTempoChanges((prev) =>
                                          prev.map((item, idx) =>
                                            idx === i ? { ...item, noteValue: newNoteValue } : item
                                          )
                                        );
                                      }}
                                      disabled={isPlaying}
                                      style={{
                                        padding: '0.5rem',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        width: '100%',
                                        fontFamily: '"Lato", sans-serif',
                                        transition: 'border-color 0.3s ease',
                                        backgroundColor: 'transparent'
                                      }}
                                    >
                                      <option value="">Use Current Note Value ({selectedNoteValue?.label || "Quarter Note"})</option>
                                      {NOTE_VALUE_OPTIONS.map((opt) => (
                                        <option key={opt.label} value={opt.label}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* Linear/Exponential type fields */}
                                  <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div>
                                      <label 
                                        className="block text-sm font-medium mb-2"
                                        style={{ 
                                          color: '#475569', 
                                          fontFamily: '"Lato", sans-serif',
                                          fontWeight: '600'
                                        }}
                                      >
                                        Start Measure
                                      </label>
                                      <input
                                        type="number"
                                        style={{
                                          padding: '0.5rem',
                                          border: '1px solid #cbd5e1',
                                          borderRadius: '8px',
                                          width: '100%',
                                          fontFamily: '"Lato", sans-serif',
                                          transition: 'border-color 0.3s ease',
                                          backgroundColor: 'transparent'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#059dd9'}
                                        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                        value={tc.startMeasure || ''}
                                        onChange={(e) => {
                                          const val = e.target.value === '' ? '' : Number(e.target.value);
                                          setTempoChanges((prev) =>
                                            prev.map((item, idx) =>
                                              idx === i ? { ...item, startMeasure: val } : item
                                            )
                                          );
                                        }}
                                        disabled={isPlaying}
                                      />
                                    </div>

                                    <div>
                                      <label 
                                        className="block text-sm font-medium mb-2"
                                        style={{ 
                                          color: '#475569', 
                                          fontFamily: '"Lato", sans-serif',
                                          fontWeight: '600'
                                        }}
                                      >
                                        End Measure
                                      </label>
                                      <input
                                        type="number"
                                        style={{
                                          padding: '0.5rem',
                                          border: '1px solid #cbd5e1',
                                          borderRadius: '8px',
                                          width: '100%',
                                          fontFamily: '"Lato", sans-serif',
                                          transition: 'border-color 0.3s ease',
                                          backgroundColor: 'transparent'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#059dd9'}
                                        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                        value={tc.endMeasure || ''}
                                        onChange={(e) => {
                                          const val = e.target.value === '' ? '' : Number(e.target.value);
                                          setTempoChanges((prev) =>
                                            prev.map((item, idx) =>
                                              idx === i ? { ...item, endMeasure: val } : item
                                            )
                                          );
                                        }}
                                        disabled={isPlaying}
                                      />
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div>
                                      <label 
                                        className="block text-sm font-medium mb-2"
                                        style={{ 
                                          color: '#475569', 
                                          fontFamily: '"Lato", sans-serif',
                                          fontWeight: '600'
                                        }}
                                      >
                                        Start Tempo
                                      </label>
                                      <input
                                        type="number"
                                        style={{
                                          padding: '0.5rem',
                                          border: '1px solid #cbd5e1',
                                          borderRadius: '8px',
                                          width: '100%',
                                          fontFamily: '"Lato", sans-serif',
                                          transition: 'border-color 0.3s ease',
                                          backgroundColor: 'transparent'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#059dd9'}
                                        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                        value={tc.startTempo || ''}
                                        onChange={(e) => {
                                          const val = e.target.value === '' ? '' : Number(e.target.value);
                                          setTempoChanges((prev) =>
                                            prev.map((item, idx) =>
                                              idx === i ? { ...item, startTempo: val } : item
                                            )
                                          );
                                        }}
                                        disabled={isPlaying}
                                      />
                                    </div>

                                    <div>
                                      <label 
                                        className="block text-sm font-medium mb-2"
                                        style={{ 
                                          color: '#475569', 
                                          fontFamily: '"Lato", sans-serif',
                                          fontWeight: '600'
                                        }}
                                      >
                                        End Tempo
                                      </label>
                                      <input
                                        type="number"
                                        style={{
                                          padding: '0.5rem',
                                          border: '1px solid #cbd5e1',
                                          borderRadius: '8px',
                                          width: '100%',
                                          fontFamily: '"Lato", sans-serif',
                                          transition: 'border-color 0.3s ease',
                                          backgroundColor: 'transparent'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#059dd9'}
                                        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                        value={tc.endTempo || ''}
                                        onChange={(e) => {
                                          const val = e.target.value === '' ? '' : Number(e.target.value);
                                          setTempoChanges((prev) =>
                                            prev.map((item, idx) =>
                                              idx === i ? { ...item, endTempo: val } : item
                                            )
                                          );
                                        }}
                                        disabled={isPlaying}
                                      />
                                    </div>
                                  </div>
                                  <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2">
                                      Note Value
                                    </label>
                                    <select
                                      value={tc.noteValue?.label || ""}
                                      onChange={(e) => {
                                        const newNoteValue = e.target.value 
                                          ? NOTE_VALUE_OPTIONS.find(opt => opt.label === e.target.value)
                                          : null;
                                        setTempoChanges((prev) =>
                                          prev.map((item, idx) =>
                                            idx === i ? { ...item, noteValue: newNoteValue } : item
                                          )
                                        );
                                      }}
                                      disabled={isPlaying}
                                    >
                                      <option value="">Use Previous Note Value</option>
                                      {NOTE_VALUE_OPTIONS.map((opt) => (
                                        <option key={opt.label} value={opt.label}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Scroll Indicators */}
                    <div className="flex justify-center mt-4 space-x-2">
                      {Array.from({ length: Math.ceil(tempoChanges.length / 3) }).map((_, index) => (
                        <div
                          key={index}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: index === Math.floor(tempoChangesScrollPosition / 400) ? '#059dd9' : '#cbd5e1',
                            transition: 'background-color 0.3s ease'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

{/* Practice Tools Section - Collapsible */}
<div className="mt-6 border border-gray-200 rounded-lg">
  {/* Header with toggle button */}
  <div 
    className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 rounded-t-lg"
    onClick={() => setIsPracticeToolsExpanded(!isPracticeToolsExpanded)}
  >
    <h3 className="text-lg font-semibold text-gray-900">Practice Tools</h3>
    <div className="flex items-center space-x-2">
      <span className="text-sm text-gray-500">
        {isPracticeToolsExpanded ? 'Click to collapse' : 'Click to expand'}
      </span>
      <svg 
        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isPracticeToolsExpanded ? 'rotate-180' : ''}`}
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>

  {/* Collapsible Content */}
  {isPracticeToolsExpanded && (
    <div className="p-4">
      {/* Audio Controls */}
      <div className="mb-6">
        <h4 className="text-md font-semibold mb-3 text-gray-900">Audio Controls</h4>

        <label className="block text-sm font-medium mb-2 mt-4">
          Master Gain: {masterGain.toFixed(2)}
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={masterGain}
          onChange={(e) => setMasterGain(Number(e.target.value))}
          className="w-full max-w-md"
          style={{ accentColor: "#307BBF" }}
        />
        
        {/* Basic Mute Options */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <span className="text-sm text-gray-600">Mute:</span>
          
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={muteBeats}
              onChange={(e) => setMuteBeats(e.target.checked)}
              className="mr-1"
            />
            <span className="text-sm">Beat</span>
          </label>

          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={muteSubs}
              onChange={(e) => setMuteSubs(e.target.checked)}
              className="mr-1"
            />
            <span className="text-sm">Subdivisions</span>
          </label>

          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={muteDrones}
              onChange={(e) => setMuteDrones(e.target.checked)}
              className="mr-1"
            />
            <span className="text-sm">Drones</span>
          </label>

          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={muteTuplets}
              onChange={(e) => setMuteTuplets(e.target.checked)}
              className="mr-1"
            />
            <span className="text-sm">Tuplets</span>
          </label>
        </div>

        {/* Random Muting Controls */}
        <div className="space-y-4">
          {/* Random Beats */}
          <div className="border-t pt-3">
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={muteRandomBeats}
                onChange={(e) => {
                  setMuteRandomBeats(e.target.checked);
                  if (!e.target.checked) {
                    globalMeasureCountRef.current = 0;
                  }
                }}
                disabled={isPlaying}
              />
              <span className="text-sm font-medium">Mute Random Beats</span>
            </label>
            {muteRandomBeats && (
              <div className="ml-6 space-y-2">
                <label className="block text-sm text-gray-600">
                  Mute Percentage: {mutePercentage}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={mutePercentage}
                  onChange={(e) => setMutePercentage(Number(e.target.value))}
                  className="w-full max-w-xs"
                  disabled={isPlaying}
                  style={{ accentColor: "#307BBF" }}
                />
                <p className="text-xs text-gray-500">
                  Randomization begins gradually after 3 measures
                </p>
              </div>
            )}
          </div>

          {/* Random Drones */}
          <div className="border-t pt-3">
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={muteRandomDrones}
                onChange={(e) => updateMuteRandomDrones(e.target.checked)}
                disabled={isPlaying}
              />
              <span className="text-sm font-medium">Mute Random Drones/Chords</span>
            </label>
            {muteRandomDrones && (
              <div className="ml-6 space-y-2">
                <label className="block text-sm text-gray-600">
                  Drone Mute Percentage: {muteRandomDronePercentage}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={muteRandomDronePercentage}
                  onChange={(e) => updateMuteRandomDronePercentage(Number(e.target.value))}
                  className="w-full max-w-xs"
                  disabled={isPlaying}
                  style={{ accentColor: "#307BBF" }}
                />
                <p className="text-xs text-gray-500">
                  Randomization begins gradually after 3 measures
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Subdivisions */}
      <div className="border-t pt-4 pb-6">
        <h4 className="text-md font-semibold mb-3 text-gray-900">Quick Subdivisions</h4>

        {quickSubRules.length === 0 && (
          <p className="text-xs text-gray-500 mb-2">
            No subdivision rules yet. Add one to subdivide every beat in measures with a given denominator.
          </p>
        )}

        <div className="space-y-2">
          {quickSubRules.map((rule) => (
            <div key={rule.id} className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center" title={rule.enabled ? "Turn off" : "Turn on"}>
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={(e) => updateQuickSubRule(rule.id, { enabled: e.target.checked })}
                  className="mr-1"
                />
                <span className="text-sm">On</span>
              </label>

              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-600">Bottom</label>
                <select
                  value={rule.denominator}
                  onChange={(e) => updateQuickSubRule(rule.id, { denominator: Number(e.target.value) })}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {[1, 2, 4, 8, 16, 32].map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-600">Subdivide into</label>
                <select
                  value={rule.subCount}
                  onChange={(e) => updateQuickSubRule(rule.id, { subCount: Number(e.target.value) })}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {[2, 3, 4, 5, 6, 7].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => removeQuickSubRule(rule.id)}
                className="text-gray-500 hover:text-red-600 text-sm"
                aria-label="Remove rule"
                title="Remove rule"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addQuickSubRule}
          disabled={quickSubRules.length >= 4}
          className="mt-3 px-3 py-1 rounded text-sm font-medium bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          + Add subdivision
        </button>
        {quickSubRules.length >= 4 && (
          <p className="text-xs text-gray-500 mt-1">Maximum of 4 subdivision rules.</p>
        )}
      </div>

      {/* Modulation Controls */}
      <div className="border-t pt-4">
        <h4 className="text-md font-semibold mb-3 text-gray-900">Pitch Modulation Controls</h4>
        
        <div className="flex items-center space-x-4 mb-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Interval</label>
            <select
              value={modLoopInterval}
              onChange={(e) => setModLoopInterval(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm"
              disabled={isPlaying}
            >
              <option value={0}>Off</option>
              <option value={1}>m2 (1 semitone)</option>
              <option value={2}>M2 (2 semitones)</option>
              <option value={3}>m3 (3 semitones)</option>
              <option value={4}>M3 (4 semitones)</option>
              <option value={5}>P4 (5 semitones)</option>
              <option value={6}>TT (6 semitones)</option>
              <option value={7}>P5 (7 semitones)</option>
              <option value={8}>m6 (8 semitones)</option>
              <option value={9}>M6 (9 semitones)</option>
              <option value={10}>m7 (10 semitones)</option>
              <option value={11}>M7 (11 semitones)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Direction</label>
            <select
              value={modLoopDirection}
              onChange={(e) => setModLoopDirection(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              disabled={isPlaying}
            >
              <option value="up">Up</option>
              <option value="down">Down</option>
            </select>
          </div>
        </div>

        {modLoopInterval > 0 && (
          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={modulateOnLoop}
                onChange={(e) => setModulateOnLoop(e.target.checked)}
                disabled={isPlaying}
              />
              <span className="text-sm">Apply only on loop</span>
            </label>
            
            {subscriptionTier !== "pro" && (
              <p className="text-xs text-gray-500">
                <strong>Modulation</strong> is a Pro feature.
                <button
                  onClick={() => handleUpgrade(currentUser)}
                  className="text-blue-600 underline ml-1"
                >
                  Upgrade
                </button>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Tuner */}
      <div className="border-t pt-4">
        <h4 className="text-md font-semibold mb-3 text-gray-900">Tuner</h4>
        <button
          onClick={openTuner}
          className="px-4 py-2 rounded font-medium text-sm bg-gray-800 text-white hover:bg-gray-700 transition-colors"
        >
          Open Tuner
        </button>
      </div>
    </div>
  )}
</div>
          </div>

          {modulationDisplay.isActive && (
            <div className="p-3 mb-4 bg-blue-50 border border-blue-200 rounded text-center">
              <span className="font-medium text-blue-600">
                Active Modulation:
              </span>
              <span className="ml-2">
                {modulationDisplay.fromName} → {modulationDisplay.toName}
              </span>
            </div>
          )}

        {/* Measures list & editor - Modern Horizontal Scrolling */}
        {!isPlaying && (
  <div className="measures-detail-editor">
    {/* Add a notice for very large patterns */}
    {measures.length > 50 && (
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Large Pattern ({measures.length} measures)</strong> - 
          Use the collapse/expand buttons to manage sections for better performance.
        </p>
      </div>
    )}
<div
  className="measures-detail-editor mb-6"
  style={{
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    borderRadius: '20px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: isMobileView ? '1rem' : '2rem',
    fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }}
>
  {/* Header Section */}
  <div className="flex justify-between items-center mb-6">
    <div className="flex items-center space-x-4">
      <div 
        className="font-bold text-xl"
        style={{ 
          color: '#334155',
          fontFamily: '"Lato", sans-serif',
          fontWeight: '700',
          fontSize: isMobileView ? '1rem' : '1.25rem'
        }}
      >
        Measure Detail Editor
      </div>
      <div className="flex space-x-2">
        <button
          onClick={collapseAllMeasures}
          className="btn btn-muted btn-sm rounded-full text-xs"
          disabled={isPlaying}
        >
          Collapse All
        </button>
        <button
          onClick={expandAllMeasures}
          className="btn btn-muted btn-sm rounded-full text-xs"
          disabled={isPlaying}
        >
          Expand All
        </button>
      </div>
    </div>
  </div>

{/* Horizontal Scrolling Container */}
<div className="relative">
  {/* Left Scroll Area */}
  <div 
    onClick={() => scrollDetail('left')}
    className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center z-20 cursor-pointer group transition-all"
    style={{ background: 'linear-gradient(to right, rgba(5,157,217,0.08), transparent)' }}
  >
    <ChevronLeft className="w-5 h-5 text-brand-500 opacity-50 group-hover:opacity-100 transition-opacity" />
  </div>

  {/* Right Scroll Area */}
  <div 
    onClick={() => scrollDetail('right')}
    className="absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center z-20 cursor-pointer group transition-all"
    style={{ background: 'linear-gradient(to left, rgba(5,157,217,0.08), transparent)' }}
  >
    <ChevronRight className="w-5 h-5 text-brand-500 opacity-50 group-hover:opacity-100 transition-opacity" />
  </div>

  {/* Horizontal Scrolling Measures Container */}
  <div
    ref={detailScrollRef}
    className="flex overflow-x-auto gap-8 py-4 px-4"
    style={{
      scrollBehavior: 'smooth',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }}
    onScroll={(e) => setDetailScrollPosition(e.target.scrollLeft)}
  >
    <style jsx>{`
      .flex.overflow-x-auto::-webkit-scrollbar {
        display: none;
      }
    `}</style>

    {/* All Measures */}
      {measures.map((measure, idx) => {
        const isLast = idx === measures.length - 1;
        const insertLabel = isLast ? "Add measure" : "Insert measure";

        return (
          <div
            key={idx}
            ref={(el) => {
              measureDetailRefs.current[idx] = el;
            }}
            className="flex-shrink-0"
            style={{ 
              minWidth: isMobileView ? '280px' : '600px', 
              maxWidth: isMobileView ? '300px' : '600px' 
            }}
          >
            <div
              className="card p-8 transition-all hover:-translate-y-1 hover:shadow-lg"
              style={{ height: 'fit-content' }}
            >
              {/* Measure Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className={`${isMobileView ? 'text-base' : 'text-lg'} font-bold text-gradient-brand`}>
                  Measure {measure.measureNumber}
                </h3>

                <div className="flex items-center space-x-1">

                    {/* Copy button */}
                    <button
                      onClick={() => handleCopyMeasure(idx)}
                      className="icon-btn icon-btn-brand"
                      title="Copy settings"
                      disabled={isPlaying}
                    >
                      <Copy className="w-4 h-4" />
                    </button>

                    {/* Paste button */}
                    <button
                      onClick={() => handlePasteMeasure(idx)}
                      className={`icon-btn ${copiedMeasure ? 'text-emerald-500 hover:text-emerald-700' : 'text-gray-300 cursor-not-allowed'}`}
                      title={copiedMeasure ? "Paste settings" : "No settings copied"}
                      disabled={isPlaying || !copiedMeasure}
                    >
                      <Clipboard className="w-4 h-4" />
                    </button>
                  {measures.length > 1 && (
                    <button
                      onClick={() =>
                        setMeasures((prev) => {
                          const newMeasures = prev.filter((_, i2) => i2 !== idx);
                          newMeasures.forEach((measure, index) => {
                            measure.measureNumber = index + 1;
                          });
                          return newMeasures;
                        })
                      }
                      className="icon-btn icon-btn-danger"
                      disabled={isPlaying}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Time Signature Controls */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#475569', fontFamily: '"Lato", sans-serif' }}
                  >
                    Beats/measure
                  </label>
                  <select
                    value={measure.numerator}
                    onChange={(e) =>
                      updateMeasure(idx, {
                        numerator: Number(e.target.value),
                      })
                    }
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      width: '100%',
                      fontFamily: '"Lato", sans-serif'
                    }}
                    disabled={isPlaying}
                  >
                    {Array.from({ length: 24 }, (_, i2) => i2 + 1).map(
                      (num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#475569', fontFamily: '"Lato", sans-serif' }}
                  >
                    Beat unit
                  </label>
                  <select
                    value={measure.denominator}
                    onChange={(e) =>
                      updateMeasure(idx, {
                        denominator: Number(e.target.value),
                      })
                    }
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      width: '100%',
                      fontFamily: '"Lato", sans-serif'
                    }}
                    disabled={isPlaying}
                  >
                    {[1, 2, 4, 8, 16, 32].map((den) => (
                      <option key={den} value={den}>
                        1/{den}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Groupings for 5/x time signatures */}
              {measure.numerator === 5 &&
                [4, 8, 16, 32].includes(measure.denominator) && (
                  <div className="mt-2 mb-4">
                    <div 
                      className="font-medium mb-2 text-sm"
                      style={{ color: '#475569', fontFamily: '"Lato", sans-serif' }}
                    >
                      Groupings for {measure.numerator}/{measure.denominator}
                    </div>

                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!measure.g2plus3}
                          onChange={(e) =>
                            handleGroupingCheckbox(
                              idx,
                              "g2plus3",
                              e.target.checked
                            )
                          }
                          className="mr-2"
                        />
                        <span className="text-sm">2+3</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!measure.g3plus2}
                          onChange={(e) =>
                            handleGroupingCheckbox(
                              idx,
                              "g3plus2",
                              e.target.checked
                            )
                          }
                          className="mr-2"
                        />
                        <span className="text-sm">3+2</span>
                      </label>
                    </div>

                    {/* Show "Remove all non-accented beats" checkbox only when a grouping is active */}
                    {(measure.g2plus3 || measure.g3plus2) && (
                      <div className="mt-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={!!measure.removeNonAccents}
                            onChange={(e) =>
                              handleGroupingCheckbox(
                                idx,
                                "removeNonAccents",
                                e.target.checked
                              )
                            }
                            className="mr-2"
                          />
                          <span className="text-sm">Remove all non-accented beats</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}

             {/* Groupings for 7/x time signatures */}
             {measure.numerator === 7 &&
                [4, 8, 16, 32].includes(measure.denominator) && (
                  <div className="mt-2 mb-4">
                    <div 
                      className="font-medium mb-2 text-sm"
                      style={{ color: '#475569', fontFamily: '"Lato", sans-serif' }}
                    >
                      Groupings for {measure.numerator}/{measure.denominator}
                    </div>

                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!measure.g2p2p3}
                          onChange={(e) =>
                            handleGroupingCheckbox(
                              idx,
                              "g2p2p3",
                              e.target.checked
                            )
                          }
                          className="mr-2"
                        />
                        <span className="text-sm">2+2+3</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!measure.g3p2p2}
                          onChange={(e) =>
                            handleGroupingCheckbox(
                              idx,
                              "g3p2p2",
                              e.target.checked
                            )
                          }
                          className="mr-2"
                        />
                        <span className="text-sm">3+2+2</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={!!measure.g2p3p2}
                          onChange={(e) =>
                            handleGroupingCheckbox(
                              idx,
                              "g2p3p2",
                              e.target.checked
                            )
                          }
                          className="mr-2"
                        />
                        <span className="text-sm">2+3+2</span>
                      </label>
                    </div>

                    {/* Show "Remove all non-accented beats" checkbox only when a grouping is active */}
                    {(measure.g2p2p3 || measure.g3p2p2 || measure.g2p3p2) && (
                      <div className="mt-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={!!measure.removeNonAccents}
                            onChange={(e) =>
                              handleGroupingCheckbox(
                                idx,
                                "removeNonAccents",
                                e.target.checked
                              )
                            }
                            className="mr-2"
                          />
                          <span className="text-sm">Remove all non-accented beats</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}

              {/* Compound time signatures: 3/8, 6/8, 9/8, 12/8, 15/8 */}
              {[3, 6, 9, 12, 15].includes(measure.numerator) && measure.denominator === 8 && (
                <div className="mt-2 mb-4">
                  <div 
                    className="font-medium mb-2 text-sm"
                    style={{ color: '#475569', fontFamily: '"Lato", sans-serif' }}
                  >
                    Compound Time: {measure.numerator}/8
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs text-gray-600">
                      Stressed beats: {
                        Array.from({ length: measure.numerator }, (_, i) => i + 1)
                          .filter(beat => (beat - 1) % 3 === 0)
                          .join(', ')
                      }
                    </div>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={!!measure.removeNonAccents}
                        onChange={(e) =>
                          handleGroupingCheckbox(
                            idx,
                            "removeNonAccents",
                            e.target.checked
                          )
                        }
                        className="mr-2"
                      />
                      <span className="text-sm">Remove all non-accented beats</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Beats Section */}
              <div className="flex items-center justify-between mb-3">
                <h4 
                  className="font-bold text-sm"
                  style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                >
                  Beats
                </h4>
                <button
                  type="button"
                  onClick={() => toggleBeatsCollapse(idx)}
                  className="icon-btn"
                >
                  {collapsedBeats[idx] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Beats Grid - Conditionally rendered if NOT collapsed */}
              {!collapsedBeats[idx] && (
                <div className="grid grid-cols-1 gap-3 mb-4" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {Array.from({ length: measure.numerator }).map(
                    (_, bIndex) => {
                      const beatSound =
                        measure.beatSounds[bIndex] || createNewBeat();

                      return (
                        <div
                          key={bIndex}
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '1rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          <div 
                            className="font-semibold mb-2"
                            style={{ color: '#059dd9', fontFamily: '"Lato", sans-serif' }}
                          >
                            Beat {bIndex + 1}
                          </div>

                          {/* Beat Sound Selection */}
                          <div className="flex w-full items-center mb-3">
                            <select
                              key={`${idx}-${bIndex}-${beatSound.id}-${Date.now()}`}
                              value={beatSound.id}
                              onChange={(e) => {
                                const chosen =
                                  SOUND_OPTIONS.find(
                                    (s) => s.id === e.target.value
                                  ) || SOUND_OPTIONS[0];
                                updateBeatSound(idx, bIndex, {
                                  ...beatSound,
                                  ...chosen,
                                });
                              }}
                              style={{
                                flex: 1,
                                padding: '0.5rem',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontFamily: '"Lato", sans-serif'
                              }}
                              disabled={isPlaying}
                            >
                              {SOUND_OPTIONS.map((sOpt) => (
                                <option key={sOpt.id} value={sOpt.id}>
                                  {sOpt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Click Volume */}
                          <label 
                            className="block mb-2"
                            style={{ color: '#475569', fontFamily: '"Lato", sans-serif' }}
                          >
                            Click Volume: {beatSound.userVolume.toFixed(2)}
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={beatSound.userVolume}
                            style={{ accentColor: "#059dd9" }}
                            onChange={(e) =>
                              updateBeatSound(idx, bIndex, {
                                ...beatSound,
                                userVolume: Number(e.target.value),
                              })
                            }
                            disabled={isPlaying}
                            className="w-full mb-3"
                          />

                          {/* Subdivisions */}
                          {beatSound.subdivisions.map((sub, subIndex) => (
                            <div
                              key={subIndex}
                              style={{
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '0.75rem',
                                marginBottom: '0.75rem',
                                fontSize: '0.75rem'
                              }}
                            >
                              <label 
                                className="block mb-2 text-sm font-medium"
                                style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                              >
                                Subdivision {subIndex + 1}
                              </label>

                              <select
                                value={sub.subCount}
                                onChange={(e) => {
                                  const newSubCount = Number(e.target.value);
                                  updateSubdivision(idx, bIndex, subIndex, {
                                    subCount: newSubCount,
                                  });
                                }}
                                style={{
                                  padding: '0.25rem',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  width: '100%',
                                  marginBottom: '0.5rem',
                                  fontFamily: '"Lato", sans-serif'
                                }}
                              >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                                  <option key={n} value={n}>
                                    {n === 1 ? "No subdiv" : n}
                                  </option>
                                ))}
                              </select>

                              <label 
                                className="block mb-1 text-sm font-medium"
                                style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                              >
                                Accent these subdivisions
                              </label>
                              <AccentInput
                                defaultValue={sub.accentedSubs.join(",")}
                                onAccentsChange={(parsedArray) => {
                                  updateSubdivision(idx, bIndex, subIndex, {
                                    accentedSubs: parsedArray,
                                  });
                                }}
                              />

                              {subscriptionTier === "pro" ? (
                                <>
                                  <label 
                                    className="block mb-1 text-sm font-medium mt-2"
                                    style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                                  >
                                    Mute these subdivisions
                                  </label>
                                  <AccentInput
                                    defaultValue={sub.mutedSubs.join(",")}
                                    onAccentsChange={(parsedArray) => {
                                      updateSubdivision(idx, bIndex, subIndex, {
                                        mutedSubs: parsedArray,
                                      });
                                    }}
                                  />
                                </>
                              ) : (
                                <p className="text-xs text-gray-400 mt-2">
                                  <strong>Mute subdivisions</strong> is a Pro feature.
                                  <button
                                    onClick={() => handleUpgrade(currentUser)}
                                    className="text-blue-600 underline ml-1"
                                  >
                                    Upgrade
                                  </button>
                                </p>
                              )}

                              {/* Remove Button */}
                              {beatSound.subdivisions.length > 1 && (
                                <button
                                  onClick={() =>
                                    removeSubdivision(idx, bIndex, subIndex)
                                  }
                                  style={{
                                    marginTop: '0.5rem',
                                    padding: '0.25rem 0.5rem',
                                    background: '#9F2C07',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    fontFamily: '"Lato", sans-serif'
                                  }}
                                >
                                  Remove This Subdivision
                                </button>
                              )}
                            </div>
                          ))}

                          {/* Add Subdivision Button */}
                          {beatSound.subdivisions.length < 5 &&
                            beatSound.subdivisions[0] &&
                            beatSound.subdivisions[0].subCount > 1 && (
                              <button
                                onClick={() => addNewSubdivision(idx, bIndex)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'linear-gradient(135deg, #B71E4B, #CC235B)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  fontFamily: '"Lato", sans-serif',
                                  marginBottom: '1rem'
                                }}
                              >
                                + Add Another Subdivision
                              </button>
                            )}

                          {/* Drone Sound */}
                          <div className="mb-3">
                            <label 
                              className="block text-sm font-medium mb-1"
                              style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                            >
                              Drone Sound:
                            </label>
                            <select
                              value={beatSound.droneSound?.id || 'sine'}
                              onChange={(e) => {
                                const selectedId = e.target.value;
                                const chosen = APP_DRONE_SOUND_OPTIONS.reduce((found, category) => {
                                  const sound = category.sounds.find(s => s.id === selectedId);
                                  return sound || found;
                                }, APP_DRONE_SOUND_OPTIONS[0].sounds[0]);
                                
                                updateBeatSound(idx, bIndex, {
                                  ...beatSound,
                                  droneSound: chosen
                                });
                              }}
                              disabled={isPlaying}
                              style={{
                                padding: '0.25rem',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                width: '100%',
                                fontFamily: '"Lato", sans-serif'
                              }}
                            >
                              {APP_DRONE_SOUND_OPTIONS.map((category) => (
                                <optgroup key={category.category} label={category.category}>
                                  {category.sounds.map((sound) => (
                                    <option key={sound.id} value={sound.id}>
                                      {sound.name}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>

                            <label className="flex items-center space-x-2 mt-2 mb-2">
                              <input
                                type="checkbox"
                                checked={beatSound.applyDroneSoundToAllBeats || false}
                                onChange={(e) =>
                                  updateBeatSound(idx, bIndex, {
                                    ...beatSound,
                                    applyDroneSoundToAllBeats: e.target.checked,
                                  })
                                }
                                disabled={isPlaying}
                              />
                              <span className="text-xs">Apply this drone sound to all beats</span>
                            </label>
                          </div>

                          {/* Drone Pitch */}
                          <label 
                            className="block mb-1 text-xs font-medium mt-2"
                            style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                          >
                            Drone Pitch:
                          </label>
                          <div className="flex space-x-2 mb-3">
                            <select
                              value={
                                beatSound.dronePitchNote === null
                                  ? "null"
                                  : beatSound.dronePitchNote
                              }
                              onChange={(e) =>
                                updateBeatSound(idx, bIndex, {
                                  ...beatSound,
                                  dronePitchNote:
                                    e.target.value === "null"
                                      ? null
                                      : Number(e.target.value),
                                })
                              }
                              disabled={isPlaying}
                              style={{
                                padding: '0.25rem',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                fontFamily: '"Lato", sans-serif'
                              }}
                            >
                              {NOTE_OPTIONS.map((opt) => (
                                <option
                                  key={opt.value}
                                  value={opt.value === null ? "null" : opt.value}
                                >
                                  {opt.label}
                                </option>
                              ))}
                            </select>

                            <select
                              value={beatSound.dronePitchOctave}
                              onChange={(e) =>
                                updateBeatSound(idx, bIndex, {
                                  ...beatSound,
                                  dronePitchOctave: Number(e.target.value),
                                })
                              }
                              disabled={
                                isPlaying || beatSound.dronePitchNote === null
                              }
                              style={{
                                padding: '0.25rem',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                fontFamily: '"Lato", sans-serif'
                              }}
                            >
                              {OCTAVE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Drone Volume */}
                          <label 
                            className="block mt-2 text-xs font-medium"
                            style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                          >
                            Drone Volume: {beatSound.droneVolume.toFixed(2)}
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={beatSound.droneVolume}
                            style={{ accentColor: "#059dd9" }}
                            onChange={(e) =>
                              updateBeatSound(idx, bIndex, {
                                ...beatSound,
                                droneVolume: Number(e.target.value),
                              })
                            }
                            disabled={isPlaying}
                            className="w-full mb-3"
                          />

                          {/* Harmony */}
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              checked={beatSound.hasHarmony}
                              onChange={(e) =>
                                updateBeatSound(idx, bIndex, {
                                  ...beatSound,
                                  hasHarmony: e.target.checked,
                                })
                              }
                              disabled={
                                isPlaying || beatSound.dronePitchNote === null
                              }
                            />
                            <span>Harmony</span>
                          </div>

                          {beatSound.hasHarmony && (
                            <div className="mb-3">
                              <label 
                                className="block mb-1 text-xs font-medium"
                                style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                              >
                                Chord Type:
                              </label>
                              <select
                                value={beatSound.selectedHarmony}
                                onChange={(e) =>
                                  updateBeatSound(idx, bIndex, {
                                    ...beatSound,
                                    selectedHarmony: e.target.value,
                                  })
                                }
                                disabled={isPlaying}
                                style={{
                                  padding: '0.25rem',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  width: '100%',
                                  fontFamily: '"Lato", sans-serif'
                                }}
                              >
                                {Object.keys(CHORDS_ET).map((chName) => (
                                  <option key={chName} value={chName}>
                                    {chName}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Any Notes */}
                          <div className="my-3">
                            <label className="flex items-center space-x-2 mb-2">
                              <input
                                type="checkbox"
                                checked={beatSound.useCustomNotes}
                                onChange={(e) =>
                                  updateBeatSound(idx, bIndex, {
                                    ...beatSound,
                                    useCustomNotes: e.target.checked,
                                  })
                                }
                                disabled={isPlaying}
                              />
                              <span>Any notes</span>
                            </label>

                            {/* Piano-like keyboard (simplified for horizontal layout) */}
                            {beatSound.useCustomNotes && (
                              <div className="mt-3 mb-3">
                                <div 
                                  className="text-sm font-medium mb-2"
                                  style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                                >
                                  Note Selection
                                </div>

                                {/* Compact keyboard for horizontal layout */}
                                <div className="border border-gray-300 rounded-md overflow-hidden shadow-sm bg-white max-h-32 overflow-y-auto">
                                  {[6, 5, 4, 3, 2, 1].map(octave => (
                                    <div key={octave} className="piano-row flex border-b border-gray-300 last:border-b-0">
                                      <div className="w-6 flex items-center justify-center bg-gray-100 border-r border-gray-300 text-xs font-medium">
                                        {octave}
                                      </div>
                                      
                                      <div className="flex flex-1">
                                        {["C", "D", "E", "F", "G", "A", "B"].map((note, noteIdx) => {
                                          const semitoneMap = {C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11};
                                          const semitone = semitoneMap[note];
                                          const midiNote = octave * 12 + semitone;
                                          const isSelected = beatSound.selectedCustomNotes.includes(midiNote);
                                          const isEKey = note === "E";
                                          
                                          return (
                                            <div key={note} className="relative flex-1 h-6">
                                              <div 
                                                onClick={() => !isPlaying && toggleCustomNoteWithOctave(idx, bIndex, semitone, octave)}
                                                className={`
                                                  absolute inset-0 
                                                  ${isEKey ? 'border-r-2 border-r-gray-400' : 'border-r border-gray-200'} 
                                                  last:border-r-0
                                                  flex items-center justify-center
                                                  ${isSelected ? 'bg-brand-500 bg-opacity-20 hover:bg-opacity-30' : 'bg-white hover:bg-gray-50'}
                                                  transition-colors cursor-pointer
                                                `}
                                              >
                                                <span className="text-xs font-medium">{note}</span>
                                              </div>
                                              
                                              {(note === "C" || note === "D" || note === "F" || note === "G" || note === "A") && (
                                                <BlackKey
                                                  octave={octave}
                                                  semitone={semitone + 1}
                                                  isPlaying={isPlaying}
                                                  toggleNote={toggleCustomNoteWithOctave}
                                                  idx={idx}
                                                  bIndex={bIndex}
                                                  isSelected={beatSound.selectedCustomNotes.includes(octave * 12 + semitone + 1)}
                                                  isMobile={true} // Force compact view
                                                />
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Selected notes display - more compact */}
                                <div className="mt-2 flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                                  {beatSound.selectedCustomNotes.length > 0 ? (
                                    beatSound.selectedCustomNotes
                                      .sort((a, b) => a - b)
                                      .map(midiNote => {
                                        const octave = Math.floor(midiNote / 12);
                                        const semitone = midiNote % 12;
                                        const noteNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
                                        const noteName = noteNames[semitone];
                                        
                                        return (
                                          <div 
                                            key={midiNote} 
                                            className="px-1 py-0.5 bg-brand-500 bg-opacity-10 border border-brand-500 border-opacity-30 rounded text-xs flex items-center"
                                          >
                                            <span>{noteName}{octave}</span>
                                            <button 
                                              onClick={() => !isPlaying && toggleCustomNoteWithOctave(idx, bIndex, semitone, octave)}
                                              className="ml-1 text-accent-600 hover:text-accent-500"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        );
                                      })
                                  ) : (
                                    <div className="text-xs text-gray-500 italic">No notes selected</div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Tuning System */}
                            <div className="mt-2">
                              <label 
                                className="block text-xs font-medium mb-1"
                                style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                              >
                                Tuning System:
                              </label>
                              <select
                                value={beatSound.temperamentSystem || "Equal Temperament"}
                                onChange={(e) =>
                                  updateBeatSound(idx, bIndex, {
                                    ...beatSound,
                                    temperamentSystem: e.target.value,
                                    justIntonation: e.target.value === "Pure/Just Intonation"
                                  })
                                }
                                disabled={isPlaying}
                                style={{
                                  width: '100%',
                                  padding: '0.25rem',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontFamily: '"Lato", sans-serif'
                                }}
                              >
                                {Object.keys(TEMPERAMENT_SYSTEMS).map((system) => (
                                  <option key={system} value={system}>
                                    {system}
                                  </option>
                                ))}
                              </select>
                              
                              <p className="text-xs text-gray-500 mt-1">
                                {TEMPERAMENT_SYSTEMS[beatSound.temperamentSystem || "Equal Temperament"].description}
                              </p>
                              
                              {TEMPERAMENT_SYSTEMS[beatSound.temperamentSystem || "Equal Temperament"].needsReference && (
                                <div className="mt-2 ml-4">
                                  <label 
                                    className="block text-xs font-medium mb-1"
                                    style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                                  >
                                    Reference Note:
                                  </label>
                                  <select
                                    value={beatSound.jiReferencePitch || 0}
                                    onChange={(e) => updateBeatSound(idx, bIndex, {
                                      ...beatSound,
                                      jiReferencePitch: Number(e.target.value),
                                    })}
                                    disabled={isPlaying}
                                    style={{
                                      width: '100%',
                                      padding: '0.25rem',
                                      border: '1px solid #cbd5e1',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      fontFamily: '"Lato", sans-serif'
                                    }}
                                  >
                                    {NOTE_OPTIONS_12.filter(opt => opt.value >= 0).map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Partials */}
                          <div className="block mb-3">
                            <label className="flex items-center space-x-2 mb-2">
                              <input
                                type="checkbox"
                                checked={beatSound.usePartials}
                                onChange={(e) =>
                                  updateBeatSound(idx, bIndex, {
                                    ...beatSound,
                                    usePartials: e.target.checked,
                                  })
                                }
                                disabled={
                                  isPlaying || beatSound.dronePitchNote === null
                                }
                              />
                              <span>Partials</span>
                            </label>

                            {beatSound.usePartials && (
                              <div>
                                <div className="mb-2">
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={beatSound.selectedPartials.length === 14}
                                      onChange={(e) =>
                                        toggleAllPartials(idx, bIndex, e.target.checked)
                                      }
                                      disabled={isPlaying}
                                    />
                                    <span className="text-sm font-medium">Select All Partials</span>
                                  </label>
                                </div>
                                
                                <div className="flex flex-wrap gap-1">
                                  {Array.from({ length: 14 }, (_, i) => i + 1).map((partial) => (
                                    <div key={partial} className="mr-1">
                                      <label className="flex items-center space-x-1">
                                        <input
                                          type="checkbox"
                                          checked={beatSound.selectedPartials.includes(partial)}
                                          onChange={() => togglePartial(idx, bIndex, partial)}
                                          disabled={isPlaying}
                                        />
                                        <span className="text-xs">{partial}</span>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {subscriptionTier === "pro" ? (
                              <div className="mt-2">
                                <label className="flex items-center space-x-2 mb-2">
                                  <input
                                    type="checkbox"
                                    checked={beatSound.useQuarterTones}
                                    onChange={(e) =>
                                      updateBeatSound(idx, bIndex, {
                                        ...beatSound,
                                        useQuarterTones: e.target.checked,
                                      })
                                    }
                                  />
                                  <span>Quarter Tones</span>
                                </label>

                                {beatSound.useQuarterTones && (
                                  <>
                                    <div className="mb-2">
                                      <label 
                                        className="block text-xs font-semibold mb-1"
                                        style={{ color: '#334155', fontFamily: '"Lato", sans-serif' }}
                                      >
                                        Quarter Tone Octave
                                      </label>
                                      <select
                                        value={beatSound.quarterToneOctave ?? 4}
                                        onChange={(e) =>
                                          updateBeatSound(idx, bIndex, {
                                            ...beatSound,
                                            quarterToneOctave: Number(e.target.value),
                                          })
                                        }
                                        disabled={isPlaying}
                                        style={{
                                          border: '1px solid #cbd5e1',
                                          padding: '0.25rem',
                                          borderRadius: '4px',
                                          fontFamily: '"Lato", sans-serif'
                                        }}
                                      >
                                        {[2, 3, 4, 5, 6].map((oct) => (
                                          <option key={oct} value={oct}>
                                            Octave {oct}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="flex flex-wrap gap-1">
                                      {QUARTER_SHARP_OPTIONS.map((qt) => {
                                        const isSelected = beatSound.selectedQuarterTones.includes(qt.value);
                                        return (
                                          <label
                                            key={qt.value}
                                            className="flex items-center mr-1 mb-1"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => {
                                                let newSelected = [...beatSound.selectedQuarterTones];
                                                if (isSelected) {
                                                  newSelected = newSelected.filter(val => val !== qt.value);
                                                } else {
                                                  newSelected.push(qt.value);
                                                }
                                                updateBeatSound(idx, bIndex, {
                                                  ...beatSound,
                                                  useQuarterTones: true,
                                                  selectedQuarterTones: newSelected,
                                                });
                                              }}
                                            />
                                            <span className="ml-1 text-xs">{qt.label}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500 mb-2">
                                <strong>Quarter tones</strong> are a Pro feature.
                                <button
                                  onClick={() => handleUpgrade(currentUser)}
                                  className="text-blue-600 underline ml-1"
                                >
                                  Upgrade
                                </button>
                              </p>
                            )}

                            {/* Beat 1 specific controls */}
                            {bIndex === 0 && (
                              <>
                                <div className="flex items-center space-x-2 mb-2">
                                  <input
                                    type="checkbox"
                                    checked={beatSound.noPitchAccent}
                                    onChange={(e) =>
                                      updateBeatSound(idx, bIndex, {
                                        ...beatSound,
                                        noPitchAccent: e.target.checked,
                                      })
                                    }
                                    disabled={isPlaying}
                                  />
                                  <span>Remove Beat 1 Emphasis</span>
                                </div>

                                <label className="flex items-center space-x-2 mb-2">
                                  <input
                                    type="checkbox"
                                    checked={measure.applyBeatOneDroneToAllBeats}
                                    onChange={(e) =>
                                      updateMeasure(idx, {
                                        applyBeatOneDroneToAllBeats: e.target.checked,
                                      })
                                    }
                                    disabled={isPlaying}
                                  />
                                  <span className="text-xs">Apply Harmony to All Beats</span>
                                </label>
                              </>
                            )}
                          </div>

                          {/* Copy to next beat */}
                          <label className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              checked={beatSound.copyDroneHarmonyPartialsToNext}
                              onChange={(e) =>
                                updateBeatSound(idx, bIndex, {
                                  ...beatSound,
                                  copyDroneHarmonyPartialsToNext: e.target.checked,
                                })
                              }
                              disabled={
                                isPlaying || bIndex === measure.numerator - 1
                              }
                            />
                            <span className="text-xs">Copy all settings to next beat</span>
                          </label>
                        </div>
                      );
                    }
                  )}
                </div>
              )}

              {/* Metric Modulation */}
<div 
  style={{
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: isMobileView ? '1rem' : '1.5rem',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    marginTop: '1rem',
    marginBottom: '1rem',
    fontFamily: '"Lato", sans-serif'
  }}
>
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 space-y-3 sm:space-y-0">
    <div 
      className="font-bold text-lg"
      style={{ 
        color: '#334155',
        fontFamily: '"Lato", sans-serif',
        fontWeight: '700',
        fontSize: isMobileView ? '1rem' : '1.125rem'
      }}
    >
      Metric Modulation
    </div>

    {subscriptionTier === "basic" || subscriptionTier === "pro" ? (
      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={measure.hasModulation}
            onChange={(e) =>
              updateMeasure(idx, {
                hasModulation: e.target.checked,
              })
            }
            disabled={isPlaying}
            className="mr-2 w-4 h-4 accent-brand-500"
          />
          <span 
            className="font-medium"
            style={{ 
              fontSize: isMobileView ? '0.875rem' : '0.875rem',
              color: '#475569',
              fontFamily: '"Lato", sans-serif'
            }}
          >
            Enable metric modulation to next measure
          </span>
        </label>
      </div>
    ) : (
      <div className="text-center sm:text-right">
        <p className="text-xs text-gray-500 mb-1">
          <strong>Metric Modulation</strong> is a Basic feature.
        </p>
        <button
          onClick={() => handleUpgrade(currentUser)}
          className="text-blue-600 underline text-xs hover:text-blue-800 transition-colors"
        >
          Upgrade to Basic
        </button>
      </div>
    )}
  </div>

  {(subscriptionTier === "basic" || subscriptionTier === "pro") &&
    measure.hasModulation && (
      <div 
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: isMobileView ? '1rem' : '1.25rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          fontFamily: '"Lato", sans-serif'
        }}
      >
        <div 
          className="text-sm font-medium mb-4"
          style={{ 
            color: '#059dd9',
            fontFamily: '"Lato", sans-serif'
          }}
        >
          Configure Modulation Settings
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ 
                color: '#334155', 
                fontFamily: '"Lato", sans-serif',
                fontWeight: '600'
              }}
            >
              From Subdivision
              <span 
                className="block text-xs font-normal mt-1"
                style={{ color: '#64748b' }}
              >
                (this measure)
              </span>
            </label>
            <select
              value={measure.fromSubdivision.value}
              onChange={(e) => {
                const newVal = parseFloat(e.target.value);
                const found = ALL_SUBDIVISIONS.find(
                  (sub) => sub.value === newVal
                );
                if (found) {
                  updateMeasure(idx, {
                    fromSubdivision: found,
                  });
                }
              }}
              disabled={isPlaying}
              style={{
                padding: isMobileView ? '0.75rem' : '0.5rem',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                width: '100%',
                fontSize: isMobileView ? '1rem' : '0.875rem',
                fontFamily: '"Lato", sans-serif',
                backgroundColor: '#ffffff',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                minHeight: isMobileView ? '44px' : 'auto',
                backgroundColor: 'transparent'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#059dd9';
                e.target.style.boxShadow = '0 0 0 3px rgba(5, 157, 217, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#cbd5e1';
                e.target.style.boxShadow = 'none';
              }}
            >
              {ALL_SUBDIVISIONS.map((sub) => (
                <option key={sub.value} value={sub.value}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ 
                color: '#334155', 
                fontFamily: '"Lato", sans-serif',
                fontWeight: '600'
              }}
            >
              To Subdivision
              <span 
                className="block text-xs font-normal mt-1"
                style={{ color: '#64748b' }}
              >
                (next measure)
              </span>
            </label>
            <select
              value={measure.toSubdivision.value}
              onChange={(e) => {
                const newVal = parseFloat(e.target.value);
                const found = ALL_SUBDIVISIONS.find(
                  (sub) => sub.value === newVal
                );
                if (found) {
                  updateMeasure(idx, {
                    toSubdivision: found,
                  });
                }
              }}
              disabled={isPlaying}
              style={{
                padding: isMobileView ? '0.75rem' : '0.5rem',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                width: '100%',
                fontSize: isMobileView ? '1rem' : '0.875rem',
                fontFamily: '"Lato", sans-serif',
                backgroundColor: '#ffffff',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                minHeight: isMobileView ? '44px' : 'auto',
                backgroundColor: 'transparent'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#059dd9';
                e.target.style.boxShadow = '0 0 0 3px rgba(5, 157, 217, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#cbd5e1';
                e.target.style.boxShadow = 'none';
              }}
            >
              {ALL_SUBDIVISIONS.map((sub) => (
                <option key={sub.value} value={sub.value}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Visual indicator of modulation */}
        <div 
          className="mt-4 p-3 rounded-lg text-center"
          style={{
            background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
            border: '1px solid #bfdbfe',
            color: '#1e40af'
          }}
        >
          <div className="flex items-center justify-center space-x-2">
            <span 
              className="font-medium"
              style={{ fontFamily: '"Lato", sans-serif' }}
            >
              {measure.fromSubdivision.name}
            </span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            <span 
              className="font-medium"
              style={{ fontFamily: '"Lato", sans-serif' }}
            >
              {measure.toSubdivision.name}
            </span>
          </div>
          <div 
            className="text-xs mt-1"
            style={{ color: '#64748b' }}
          >
            Modulation will occur at the end of this measure
          </div>
        </div>
      </div>
    )}
</div>

             {/* Tuplets */}
<div 
  style={{
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: isMobileView ? '1rem' : '1.5rem',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    fontFamily: '"Lato", sans-serif'
  }}
>
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
    <div 
      className="font-bold text-lg"
      style={{ 
        color: '#334155',
        fontFamily: '"Lato", sans-serif',
        fontWeight: '700',
        fontSize: isMobileView ? '1rem' : '1.125rem'
      }}
    >
      Tuplets
    </div>

    {subscriptionTier === "pro" ? (
      <button
        onClick={() => {
          if (!isPlaying) {
            setTupletsUI({
              measureIndex: idx,
              beatsInput: "",
              subCount: "",
              soundId: "clicker",
              addNested: false,
              nestedBeatsInput: "",
              nestedSubCount: "",
              nestedSoundId: "clicker",
              mutedSubdivisions: [],
            });
          }
        }}
        disabled={isPlaying}
        className={`btn btn-accent-gradient rounded-full ${isMobileView ? 'w-full py-3 px-6 text-sm' : 'py-2 px-4 text-xs'}`}
      >
        <Plus className="w-3 h-3 mr-2" />
        Add Tuplet
      </button>
    ) : (
      <div className="text-center sm:text-right">
        <p className="text-xs text-gray-500">
          <strong>Tuplets</strong> are a Pro feature.
        </p>
        <button
          onClick={() => handleUpgrade(currentUser)}
          className="text-blue-600 underline text-xs mt-1"
        >
          Upgrade to Pro
        </button>
      </div>
    )}
  </div>

  {measure.tuplets && measure.tuplets.length > 0 && (
    <div className="space-y-3">
      {measure.tuplets.map((tup, pIdx) => {
        const parentSoundOption = SOUND_OPTIONS.find(
          (o) => o.id === tup.soundId
        );
        const parentSoundName = parentSoundOption
          ? parentSoundOption.name
          : tup.soundId;

        return (
          <div 
            key={pIdx} 
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: isMobileView ? '1rem' : '1.25rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              fontFamily: '"Lato", sans-serif'
            }}
          >
            {/* Main Tuplet Info */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-3 sm:space-y-0">
              <div className="flex-1">
                <div 
                  className="font-medium mb-2"
                  style={{ 
                    color: '#059dd9',
                    fontSize: isMobileView ? '0.875rem' : '1rem',
                    fontWeight: '600'
                  }}
                >
                  Tuplet {pIdx + 1}
                </div>
                <div className="space-y-1 text-sm" style={{ color: '#64748b' }}>
                  <div><strong>Beats:</strong> [{tup.selectedBeats.join(", ")}]</div>
                  <div><strong>Count:</strong> {tup.subCount}</div>
                  <div><strong>Sound:</strong> {parentSoundName}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={tup.muted ?? false}
                    onChange={(e) => {
                      setMeasures((prev) =>
                        prev.map((mm, i2) => {
                          if (i2 !== idx) return mm;
                          const newTuplets = mm.tuplets.map(
                            (oldTup, t2) => {
                              if (t2 !== pIdx) return oldTup;
                              return {
                                ...oldTup,
                                muted: e.target.checked,
                              };
                            }
                          );
                          return {
                            ...mm,
                            tuplets: newTuplets,
                          };
                        })
                      );
                    }}
                    disabled={isPlaying}
                    className="mr-1"
                  />
                  <span 
                    className="text-sm"
                    style={{ color: '#64748b', fontFamily: '"Lato", sans-serif' }}
                  >
                    Mute
                  </span>
                </label>

                <button
                  onClick={() => startEditTuplet(idx, pIdx)}
                  disabled={isPlaying}
                  className="btn btn-primary-gradient btn-sm rounded-full text-xs py-1 px-3"
                >
                  Edit
                </button>

                <button
                  onClick={() => {
                    setMeasures((prev) =>
                      prev.map((mm, i2) => {
                        if (i2 !== idx) return mm;
                        return {
                          ...mm,
                          tuplets: mm.tuplets.filter(
                            (_, i3) => i3 !== pIdx
                          ),
                        };
                      })
                    );
                  }}
                  disabled={isPlaying}
                  className="btn btn-danger btn-sm rounded-full text-xs py-1 px-3"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Nested Tuplets */}
            {tup.nestedTuplets && tup.nestedTuplets.length > 0 && (
              <div className="mt-4 ml-4 border-l-2 border-gray-200 pl-4">
                <div 
                  className="font-medium mb-3 text-sm"
                  style={{ color: '#64748b', fontFamily: '"Lato", sans-serif' }}
                >
                  Nested Tuplets
                </div>
                <div className="space-y-2">
                  {tup.nestedTuplets.map((nest, nIdx) => {
                    const nestedSoundOption = SOUND_OPTIONS.find(
                      (o) => o.id === nest.soundId
                    );
                    const nestedSoundName = nestedSoundOption
                      ? nestedSoundOption.name
                      : nest.soundId;

                    return (
                      <div
                        key={nIdx}
                        className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-50 rounded-lg space-y-2 sm:space-y-0"
                      >
                        <div className="text-sm space-y-1" style={{ color: '#64748b' }}>
                          <div><strong>Beats:</strong> [{nest.selectedBeats.join(", ")}]</div>
                          <div><strong>Count:</strong> {nest.subCount}, <strong>Sound:</strong> {nestedSoundName}</div>
                        </div>
                        
                        <div className="flex gap-2 justify-start sm:justify-end">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={nest.muted ?? false}
                              onChange={(e) => {
                                setMeasures((prev) =>
                                  prev.map((mm, i2) => {
                                    if (i2 !== idx) return mm;
                                    const newTuplets = mm.tuplets.map(
                                      (oldTup, t2) => {
                                        if (t2 !== pIdx) return oldTup;
                                        const updatedNested = oldTup.nestedTuplets.map(
                                          (oldNest, n2) => {
                                            if (n2 !== nIdx) return oldNest;
                                            return {
                                              ...oldNest,
                                              muted: e.target.checked,
                                            };
                                          }
                                        );
                                        return {
                                          ...oldTup,
                                          nestedTuplets: updatedNested,
                                        };
                                      }
                                    );
                                    return {
                                      ...mm,
                                      tuplets: newTuplets,
                                    };
                                  })
                                );
                              }}
                              disabled={isPlaying}
                              className="mr-1"
                            />
                            <span className="text-xs">Mute</span>
                          </label>

                          <button
                            onClick={() => startEditNestedTuplet(idx, pIdx, nIdx)}
                            disabled={isPlaying}
                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => {
                              setMeasures((prev) =>
                                prev.map((mm, i2) => {
                                  if (i2 !== idx) return mm;
                                  const newTuplets = mm.tuplets.map(
                                    (oldTup, t2) => {
                                      if (t2 !== pIdx) return oldTup;
                                      return {
                                        ...oldTup,
                                        nestedTuplets: oldTup.nestedTuplets.filter(
                                          (_, i3) => i3 !== nIdx
                                        ),
                                      };
                                    }
                                  );
                                  return {
                                    ...mm,
                                    tuplets: newTuplets,
                                  };
                                })
                              );
                            }}
                            disabled={isPlaying}
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add Nested Tuplet Button */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => {
                  if (!isPlaying) {
                    setNestedTupletsUI({
                      measureIndex: idx,
                      parentTupletIndex: pIdx,
                      beatsInput: "",
                      subCount: "",
                      soundId: "clicker",
                      mutedSubdivisions: [],
                    });
                  }
                }}
                disabled={isPlaying}
                className="btn btn-muted btn-sm rounded-full text-xs"
              >
                + Add Nested Tuplet
              </button>
            </div>
          </div>
        );
      })}
    </div>
  )}

                    {/* Add new tuplet UI */}
                    {tupletsUI && tupletsUI.measureIndex === idx && (
                      <div className="mt-3 border border-gray-300 p-2 rounded text-xs bg-white">
                        <div>
                          <label className="block font-medium mb-1">
                            Over which beats? (e.g. "2,3" or "3-5")
                          </label>
                          <AccentInput
                            defaultValue={tupletsUI.beatsInput}
                            onAccentsChange={(parsedArray) =>
                              setTupletsUI((prev) => ({
                                ...prev,
                                beatsInput: parsedArray.join(","),
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label className="block font-medium mt-1 mb-1">
                            Tuplet (e.g. "3" is a triplet)
                          </label>
                          <AccentInput
                            defaultValue={String(tupletsUI.subCount || "")}
                            onAccentsChange={(parsedArray) =>
                              setTupletsUI((prev) => ({
                                ...prev,
                                subCount:
                                  parsedArray.length > 0
                                    ? String(parsedArray[0])
                                    : "",
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label className="block font-medium mb-1 mt-2">
                            Mute these tuplets
                          </label>
                          <AccentInput
                            defaultValue={tupletsUI.mutedSubdivisions || ""} // Change from editingTupletUI to tupletsUI
                            onAccentsChange={(parsedArray) =>
                              setTupletsUI((prev) => ({
                                // Change from setEditingTupletUI to setTupletsUI
                                ...prev,
                                mutedSubdivisions: parsedArray,
                              }))
                            }
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter subdivision numbers to mute (e.g. "2,4" or
                            "1-3")
                          </p>
                        </div>

                        <div>
                          <label className="block font-medium mb-1">
                            Tuplet Sound
                          </label>
                          <select
                            value={tupletsUI.soundId}
                            onChange={(e) =>
                              setTupletsUI((prev) => ({
                                ...prev,
                                soundId: e.target.value,
                              }))
                            }
                            className="p-1 border rounded w-full"
                          >
                            {SOUND_OPTIONS.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={() => {
                              const beatsArr = parseBeatsInput(
                                tupletsUI.beatsInput
                              );
                              const sc = parseInt(tupletsUI.subCount, 10);
                              if (!beatsArr.length || !sc) {
                                setTupletsUI(null);
                                return;
                              }
                              const newTuplet = {
                                selectedBeats: beatsArr,
                                subCount: sc,
                                soundId: tupletsUI.soundId || "clicker",
                                nestedTuplets: [],
                                mutedSubdivisions:
                                  tupletsUI.mutedSubdivisions || [],
                              };
                              if (tupletsUI.addNested) {
                                const nestedArr = parseBeatsInput(
                                  tupletsUI.nestedBeatsInput
                                );
                                const nsc = parseInt(
                                  tupletsUI.nestedSubCount,
                                  10
                                );
                                if (nestedArr.length && nsc) {
                                  newTuplet.nestedTuplets.push({
                                    selectedBeats: nestedArr,
                                    subCount: nsc,
                                    soundId:
                                      tupletsUI.nestedSoundId || "clicker",
                                    nestedTuplets: [],
                                    mutedSubdivisions:
                                      tupletsUI.mutedSubdivisions || [],
                                  });
                                }
                              }
                              setMeasures((prev) =>
                                prev.map((mm, i2) => {
                                  if (i2 !== idx) return mm;
                                  return {
                                    ...mm,
                                    tuplets: [...mm.tuplets, newTuplet],
                                  };
                                })
                              );
                              setTupletsUI(null);
                            }}
                            className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setTupletsUI(null)}
                            className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Editing existing tuplet UI */}
                    {editingTupletUI &&
                      editingTupletUI.measureIndex === idx && (
                        <div className="mt-3 border p-2 rounded text-xs bg-white">
                          <div>
                            <label className="block font-medium mb-1">
                              Over which beats? (e.g. "2,3" or "3-5")
                            </label>
                            {/* Replace standard input with AccentInput */}
                            <AccentInput
                              defaultValue={editingTupletUI.beatsInput}
                              onAccentsChange={(parsedArray) =>
                                setEditingTupletUI((prev) => ({
                                  ...prev,
                                  beatsInput: parsedArray.join(","), // Store as comma string for consistency
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Tuplet (e.g. "3" is a triplet)
                            </label>
                            <AccentInput
                              defaultValue={String(
                                editingTupletUI.subCount || ""
                              )}
                              onAccentsChange={(parsedArray) =>
                                setEditingTupletUI((prev) => ({
                                  ...prev,
                                  subCount:
                                    parsedArray.length > 0
                                      ? String(parsedArray[0])
                                      : "",
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label className="block font-medium mb-1 mt-2">
                              Mute these tuplets
                            </label>
                            <AccentInput
                              defaultValue={
                                editingTupletUI.mutedSubdivisions || ""
                              }
                              onAccentsChange={(parsedArray) =>
                                setEditingTupletUI((prev) => ({
                                  ...prev,
                                  mutedSubdivisions: parsedArray,
                                }))
                              }
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Enter subdivision numbers to mute (e.g. "2,4" or
                              "1-3")
                            </p>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Tuplet Sound
                            </label>
                            <select
                              value={editingTupletUI.soundId}
                              onChange={(e) =>
                                setEditingTupletUI((prev) => ({
                                  ...prev,
                                  soundId: e.target.value,
                                }))
                              }
                              className="p-1 border rounded w-full"
                            >
                              {SOUND_OPTIONS.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="flex space-x-2 mt-2">
                            <button
                              onClick={saveEditedTuplet}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingTupletUI(null)}
                              className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                    {nestedTupletsUI &&
                      nestedTupletsUI.measureIndex === idx && (
                        <div className="mt-2 ml-6 border p-2 rounded text-xs bg-white">
                          <div>
                            <label className="block font-medium mb-1">
                              Nested Beats (e.g. "2,3" or "2-4")
                            </label>
                            <AccentInput
                              defaultValue={nestedTupletsUI.beatsInput}
                              onAccentsChange={(parsedArray) =>
                                setNestedTupletsUI((prev) => ({
                                  ...prev,
                                  beatsInput: parsedArray.join(","),
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Nested Tuplet
                            </label>
                            <AccentInput
                              defaultValue={String(
                                nestedTupletsUI.subCount || ""
                              )}
                              onAccentsChange={(parsedArray) =>
                                setNestedTupletsUI((prev) => ({
                                  ...prev,
                                  subCount:
                                    parsedArray.length > 0
                                      ? String(parsedArray[0])
                                      : "",
                                }))
                              }
                            />
                          </div>

                          <div>
                            <label className="block font-medium mb-1 mt-2">
                              Mute these subdivisions
                            </label>
                            <AccentInput
                              defaultValue={
                                nestedTupletsUI.mutedSubdivisions || ""
                              } // Change from editingNestedTupletUI to nestedTupletsUI
                              onAccentsChange={(parsedArray) =>
                                setNestedTupletsUI((prev) => ({
                                  // Change from setEditingNestedTupletUI to setNestedTupletsUI
                                  ...prev,
                                  mutedSubdivisions: parsedArray,
                                }))
                              }
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Enter subdivision numbers to mute (e.g. "2,4" or
                              "1-3")
                            </p>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Nested Sound
                            </label>
                            <select
                              value={nestedTupletsUI.soundId}
                              onChange={(e) =>
                                setNestedTupletsUI((prev) => ({
                                  ...prev,
                                  soundId: e.target.value,
                                }))
                              }
                              className="p-1 border rounded w-full"
                            >
                              {SOUND_OPTIONS.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="flex space-x-2 mt-2">
                            <button
                              onClick={() => {
                                const beatsArr = parseBeatsInput(
                                  nestedTupletsUI.beatsInput
                                );
                                const sc = parseInt(
                                  nestedTupletsUI.subCount,
                                  10
                                );
                                if (!beatsArr.length || !sc) {
                                  setNestedTupletsUI(null);
                                  return;
                                }
                                // Build the new nested tuplet object
                                const newNested = {
                                  selectedBeats: beatsArr,
                                  subCount: sc,
                                  soundId: nestedTupletsUI.soundId || "clicker",
                                  nestedTuplets: [],
                                  mutedSubdivisions:
                                    nestedTupletsUI.mutedSubdivisions || [],
                                };
                                // Insert into the parent's nestedTuplets
                                setMeasures((prev) =>
                                  prev.map((mm, i2) => {
                                    if (i2 !== idx) return mm;
                                    const newTuplets = mm.tuplets.map(
                                      (oldTup, t2) => {
                                        if (
                                          t2 !==
                                          nestedTupletsUI.parentTupletIndex
                                        )
                                          return oldTup;
                                        return {
                                          ...oldTup,
                                          nestedTuplets: [
                                            ...(oldTup.nestedTuplets || []),
                                            newNested,
                                          ],
                                        };
                                      }
                                    );
                                    return { ...mm, tuplets: newTuplets };
                                  })
                                );
                                // Close the UI
                                setNestedTupletsUI(null);
                              }}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setNestedTupletsUI(null)}
                              className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                    {editingNestedTupletUI &&
                      editingNestedTupletUI.measureIndex === idx && (
                        <div className="mt-2 ml-6 border p-2 rounded text-xs bg-white">
                          <div>
                            <label className="block font-medium mb-1">
                              Nested Beats (e.g. "2,3" or "2-4")
                            </label>
                            <AccentInput
                              defaultValue={editingNestedTupletUI.beatsInput}
                              onAccentsChange={(parsedArray) =>
                                setEditingNestedTupletUI((prev) => ({
                                  ...prev,
                                  beatsInput: parsedArray.join(","),
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Nested Tuplet
                            </label>
                            <AccentInput
                              defaultValue={String(
                                editingNestedTupletUI.subCount || ""
                              )}
                              onAccentsChange={(parsedArray) =>
                                setEditingNestedTupletUI((prev) => ({
                                  ...prev,
                                  subCount:
                                    parsedArray.length > 0
                                      ? String(parsedArray[0])
                                      : "",
                                }))
                              }
                            />
                          </div>

                          <div>
                            <label className="block font-medium mb-1 mt-2">
                              Mute these subdivisions
                            </label>
                            <AccentInput
                              defaultValue={
                                editingNestedTupletUI.mutedSubdivisions || ""
                              }
                              onAccentsChange={(parsedArray) =>
                                setEditingNestedTupletUI((prev) => ({
                                  ...prev,
                                  mutedSubdivisions: parsedArray,
                                }))
                              }
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Enter subdivision numbers to mute (e.g. "2,4" or
                              "1-3")
                            </p>
                          </div>
                          <div>
                            <label className="block font-medium mb-1">
                              Nested Sound
                            </label>
                            <select
                              value={editingNestedTupletUI.soundId}
                              onChange={(e) =>
                                setEditingNestedTupletUI((prev) => ({
                                  ...prev,
                                  soundId: e.target.value,
                                }))
                              }
                              className="p-1 border rounded w-full"
                            >
                              {SOUND_OPTIONS.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="flex space-x-2 mt-2">
                            <button
                              onClick={saveEditedNestedTuplet}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingNestedTupletUI(null)}
                              className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                  

              </div>

              {/* Insert / Copy measure buttons */}
              <div className="mt-6 flex justify-center space-x-3">
                <button
                  onClick={() => insertMeasureBelow(idx)}
                  className="btn btn-primary-gradient rounded-full py-3 px-6 text-sm"
                  disabled={isPlaying}
                >
                  {insertLabel}
                </button>
                <button
                  onClick={() => copyMeasureBelow(idx)}
                  className="btn btn-primary-gradient rounded-full py-3 px-6 text-sm"
                  disabled={isPlaying}
                >
                  Duplicate measure
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>

      {/* Scroll Indicators */}
  <div className="flex justify-center mt-4 space-x-2">
    {Array.from({ length: Math.ceil(measures.length / 3) }).map((_, index) => (
      <div
        key={index}
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: index === Math.floor(detailScrollPosition / 600) ? '#059dd9' : '#cbd5e1',
          transition: 'background-color 0.3s ease'
        }}
      />
    ))}
  </div>
  </div>
</div>
</div>
    )}
        </>
      )}
      
      </>
      )}
      {activeTab === "about" && <AboutTab />}
      {activeTab === "library" && (
        <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
        <LibraryTab
          currentUser={currentUser}
          savedPatterns={savedPatterns}
          subscriptionTier={subscriptionTier}
          instrumentations={INSTRUMENTATIONS}
          genres={GENRES}
          onSwitchTab={setActiveTab}
          onImportPracticePlan={async (libraryPlan, action) => {
            if (!currentUser?.uid) {
              alert("Please log in to import practice plans.");
              return;
            }
            try {
              // Build a fresh, user-owned copy of the library plan. We
              // generate a new id so the Firestore doc and localStorage
              // mirror it cleanly, and we drop any source-side metadata.
              const newId =
                (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
                `plan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              const planForUser = {
                id: newId,
                label: libraryPlan.label || "Imported Plan",
                items: Array.isArray(libraryPlan.items) ? libraryPlan.items : [],
                updatedAt: Date.now(),
                importedFromLibrary: {
                  sourceId: libraryPlan.id || null,
                  sourceCreator: libraryPlan.creatorName || "Anonymous",
                  importedAt: Date.now(),
                },
              };

              await setDoc(
                doc(db, "users", currentUser.uid, "practicePlans", newId),
                planForUser
              );

              // Mirror into localStorage so PracticeTrackerTab picks it up
              // on its next mount without waiting for the Firestore refetch.
              try {
                const existingRaw =
                  storageManager.getUserItem("practicePlans_v2") || "[]";
                const existing = JSON.parse(existingRaw);
                const merged = Array.isArray(existing)
                  ? [...existing, planForUser]
                  : [planForUser];
                storageManager.setUserItem(
                  "practicePlans_v2",
                  JSON.stringify(merged)
                );
              } catch (e) {
                console.warn("Failed to mirror imported plan to localStorage:", e);
              }

              if (action === "import-and-load") {
                storageManager.setUserItem("currentPlanId_v2", newId);
                setActiveTab("practiceTracker");
              } else {
                alert(
                  `Practice plan "${planForUser.label}" imported to your plans!`
                );
              }
            } catch (error) {
              console.error("Error importing practice plan:", error);
              alert("Failed to import practice plan. Please try again.");
            }
          }}
          onImportPatternOnly={async (pattern) => {
            try {
              await saveImportedPattern(pattern);
              alert(`Pattern "${pattern.name}" imported to your library!`);
            } catch (error) {
              alert("Failed to import pattern. Please try again.");
            }
          }}
          onImportPattern={(pattern) => {
            // Apply backward compatibility like handleLoadPattern does
            const compatibleMeasures = applyBackwardCompatibility(pattern.measures);
            
            // Load into current session with proper defaults
            setPatternName(pattern.name);
            setFolderName(pattern.folder || "Imported");
            setMeasures(compatibleMeasures);
            setTempo(pattern.tempo || 120);
            setUseTempoChanges(pattern.useTempoChanges || false);
            setEnableAdditionalTempoChanges(pattern.enableAdditionalTempoChanges || false);
            setTempoChanges(pattern.tempoChanges || []);
            setATuning(pattern.aTuning || 440);
            setMuteRandomBeats(pattern.muteRandomBeats || false);
            setMutePercentage(pattern.mutePercentage || 0);
            setLoopMode(pattern.loopMode || "none");
            setStartMeasureNumber(pattern.startMeasureNumber || 1);
            setStressOn(pattern.stressOn || false);
            setModulateOnLoop(pattern.modulateOnLoop || false);
            setModLoopInterval(pattern.modLoopInterval || 0);
            setModLoopDirection(pattern.modLoopDirection || "up");
            setSelectedNoteValue(pattern.selectedNoteValue || { label: "Quarter Note", factor: 1 });
            setRehearsalLetters(pattern.rehearsalLetters || {});
            setTimeSignatureColors(pattern.timeSignatureColors || {});
            
            // Cache the pattern like handleLoadPattern does
            const cacheKey = `${pattern.name}-${pattern.folder || "Imported"}`;
            if (patternCache.current.size > 20) {
              const firstKey = patternCache.current.keys().next().value;
              patternCache.current.delete(firstKey);
            }
            patternCache.current.set(cacheKey, compatibleMeasures);
            
            // Auto-collapse for large patterns (over 50 measures on mobile, over 100 measures on any device)
            const isMobile = window.innerWidth < 768;
            if (compatibleMeasures && ((compatibleMeasures.length > 50 && isMobile) || compatibleMeasures.length > 100)) {
              setIsOverviewCollapsed(true);
              const newCollapsedState = {};
              for (let i = 0; i < compatibleMeasures.length; i++) {
                newCollapsedState[i] = true;
              }
              setCollapsedBeats(newCollapsedState);
              setTimeout(() => {
                const reason = compatibleMeasures.length > 100 
                  ? "This large pattern has been automatically collapsed for better performance. You can expand any section by clicking on the Chevron (V) icon."
                  : "This large pattern has been automatically collapsed for better mobile performance. You can expand any section by clicking on the Chevron (V) icon.";
                alert(reason);
              }, 100);
            }
            
            // Reset playback state like handleLoadPattern does
            setIsPlaying(false);
            setCurrentBeat(0);
            setCurrentBar(0);
            specificLoopCountRef.current = 0;
            
            // Ask if they want to save and switch tabs
            const wantToSave = window.confirm(
              `Pattern "${pattern.name}" loaded! Save to your library?`
            );
            if (wantToSave) {
              handleSavePattern();
            }
            setActiveTab("metronome");
          }}
        />
        </Suspense>
      )}
   

{activeTab === "practiceTracker" && (
  <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
        <PracticeTrackerTab
          onPlayPattern={handleLoadPattern}
          onStopPattern={() => {
            setIsPlaying(false);
            setCurrentBeat(0);
            setCurrentBar(0);
            specificLoopCountRef.current = 0;
          }}
          isPatternPlaying={isPlaying}
          currentPlayingPatternId={
            patternName ? `${folderName}/${patternName}` : null
          }
          userPatterns={savedPatterns}
          switchToMetronomeTab={() => setActiveTab("metronome")}
          user={currentUser}
          isLoggedIn={!!currentUser}
          subscriptionTier={subscriptionTier}
          showLoginModal={() => setShowLoginForm(true)}
          showSubscribeModal={() => handleUpgrade(currentUser)}
          // Updated function names
          globalRecordingState={globalRecordingState}
          onStartRecording={handleStartPracticeRecording}
          onStopRecording={handleStopPracticeRecording}
          globalCompletedRecordings={globalCompletedRecordings}
          onDeleteGlobalRecording={handleDeleteGlobalRecording}
          globalRecordingService={globalRecordingService}
          uploadQueue={uploadQueue}
          onRetryUpload={retryUpload}
          featureFlags={featureFlags}
          // Add alarm props
          alarmSettings={alarmSettings}
          setAlarmSettings={setAlarmSettings}
          createChimeSound={createChimeSound}
          globalAlarmedItems={globalAlarmedItems}
          onPracticeSessionUpdate={handlePracticeSessionUpdate}
          onPracticeProjectionChange={handlePracticeProjectionChange}
          onOpenGlobalScoreViewer={openGlobalScoreViewer}
          onMinimizeGlobalScoreViewer={minimizeGlobalScoreViewer}
          onCloseGlobalScoreViewer={closeGlobalScoreViewer}
          onUnreadShareCount={setUnreadFlowTrackerCount}
          onOpenTuner={openTuner}
          pendingPerfNav={pendingPerfNav}
          clearPendingPerfNav={clearPendingPerfNav}
        />
      </Suspense>
      )}

      {activeTab === "practiceRoom" && <PracticeRoomTab />}
      {/* {activeTab === "flowTrain" && <FlowTrain />} */}

      {activeTab === "community" && (
         <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
        <CommunityTab
          currentUser={currentUser}
          subscriptionTier={subscriptionTier}
        />
        </Suspense>
      )}

      {activeTab === "scores" && (
        <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
          <ScoresTab
            user={currentUser}
            subscriptionTier={subscriptionTier}
            showLoginModal={() => setShowLoginForm(true)}
            onOpenGlobalViewer={openGlobalScoreViewer}
            globalViewerActive={!!globalScoreViewer}
          />
        </Suspense>
      )}

      {activeTab === "orgAdmin" && currentUser && (
        <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
          <OrgAdminDashboard
            user={{ uid: currentUser.uid, email: currentUser.email, organizationId: userOrgId, isOrgAdmin, canCreateOrg }}
            onOrgChange={() => fetchUserTier(currentUser.uid)}
          />
        </Suspense>
      )}

      {activeTab === "joinOrg" && joinInviteCode && (
        <Suspense fallback={<div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
          <JoinOrgPage
            inviteCode={joinInviteCode}
            user={currentUser}
            onComplete={() => {
              setJoinInviteCode(null);
              setActiveTab("metronome");
              if (currentUser) fetchUserTier(currentUser.uid);
            }}
          />
        </Suspense>
      )}

{activeTab === "landing" && (
  <LandingPage
    onNavigateToMetronome={() => setActiveTab('metronome')}
    onNavigateToTracker={() => setActiveTab('practiceTracker')}
    onNavigateToLibrary={() => setActiveTab('library')}
    onNavigateToCommunity={() => setActiveTab('community')}
    onNavigateToAbout={() => setActiveTab('about')}
    onNavigateToScores={() => setActiveTab('scores')}
    onGetStarted={() => setShowLoginForm(true)}
    showLoginModal={() => setShowLoginForm(true)}
    user={currentUser}
    isLoggedIn={!!currentUser}
  />
)}

      {/* Global Score Viewer (persists across tab changes) */}
      {globalScoreViewer && !globalScoreViewer.minimized && (
        <div className="fixed inset-0 z-[9995] bg-black">
          <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>}>
            <ScoreViewer
              fileUrl={globalScoreViewer.fileUrl}
              title={globalScoreViewer.title}
              initialPage={globalScoreViewer.pageNumber || globalScoreViewer.initialPage || 1}
              userId={globalScoreViewer.userId}
              scoreId={globalScoreViewer.scoreId}
              setlist={globalScoreViewer.setlist}
              setlistTitle={globalScoreViewer.setlistTitle}
              onClose={closeGlobalScoreViewer}
              onMinimize={minimizeGlobalScoreViewer}
              onPageChange={handleGlobalScorePageChange}
            />
          </Suspense>
        </div>
      )}

      {globalScoreViewer && globalScoreViewer.minimized && (
        <Suspense fallback={null}>
          <ScoreViewerMini
            title={globalScoreViewer.title}
            pageNumber={globalScoreViewer.pageNumber || 1}
            numPages={globalScoreViewer.numPages}
            onMaximize={maximizeGlobalScoreViewer}
            onClose={closeGlobalScoreViewer}
            onPrev={() => setGlobalScoreViewer((prev) => prev && prev.pageNumber > 1 ? { ...prev, pageNumber: prev.pageNumber - 1 } : prev)}
            onNext={() => setGlobalScoreViewer((prev) => prev && prev.pageNumber < (prev.numPages || 1) ? { ...prev, pageNumber: prev.pageNumber + 1 } : prev)}
          />
        </Suspense>
      )}

      {/* Global Tuner (persists across tab changes) */}
      {showTuner && (
        <Suspense fallback={null}>
          <Tuner onClose={closeTuner} />
        </Suspense>
      )}

      <button
        onClick={scrollToTop}
        className="fixed bottom-4 right-4 p-3 bg-brand-500 text-white rounded-full shadow-lg hover:bg-brand-600 hover:shadow-xl hover:scale-105 transition-all z-[9999] group"
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
      </button>

      <button
  onClick={() => setActiveTab("landing")}
  className={activeTab === "landing" ? "nav-item-active" : "nav-item"}
>
  Home
</button>

    {/* Full Pattern Modal */}
    <FullPatternModal
      isOpen={showFullPatternModal}
      onClose={() => setShowFullPatternModal(false)}
      // Pattern data
      measures={measures}
      patternName={patternName}
      isOverviewCollapsed={isOverviewCollapsed}
      setIsOverviewCollapsed={setIsOverviewCollapsed}
      
      // Rehearsal letters
      rehearsalLetters={rehearsalLetters}
      editingRehearsalLetter={editingRehearsalLetter}
      setEditingRehearsalLetter={setEditingRehearsalLetter}
      tempRehearsalLetter={tempRehearsalLetter}
      setTempRehearsalLetter={setTempRehearsalLetter}
      saveRehearsalLetter={saveRehearsalLetter}
      deleteRehearsalLetter={deleteRehearsalLetter}
      
      // Measure editing
      editingMeasureIndex={editingMeasureIndex}
      setEditingMeasureIndex={setEditingMeasureIndex}
      tempNumber={tempNumber}
      setTempNumber={setTempNumber}
      handleMeasureNumberChange={handleMeasureNumberChange}
      jumpToMeasure={jumpToMeasure}
      handleDeleteMeasureInOverview={handleDeleteMeasureInOverview}
      updateMeasure={updateMeasure}
      getNextDenominatorUp={getNextDenominatorUp}
      getNextDenominatorDown={getNextDenominatorDown}
      
      // Ghost measure
      ghostNumer={ghostNumer}
      setGhostNumer={setGhostNumer}
      ghostDenom={ghostDenom}
      setGhostDenom={setGhostDenom}
      handleAddCustomMeasure={handleAddCustomMeasure}
      
      // Time signature colors
      timeSignatureColors={timeSignatureColors}
      
      // Highlighting
      highlightedMeasures={highlightedMeasures}
      highlightInput={highlightInput}
      setHighlightInput={setHighlightInput}
      handleAddHighlights={handleAddHighlights}
      removeSingleHighlight={removeSingleHighlight}
      
      // Loop controls
      loopMode={loopMode}
      setLoopMode={setLoopMode}
      loopStartMeasure={loopStartMeasure}
      setLoopStartMeasure={setLoopStartMeasure}
      loopEndMeasure={loopEndMeasure}
      setLoopEndMeasure={setLoopEndMeasure}
      
      // Beat highlighting
      isPlaying={isPlaying}
      audioContextRef={audioContextRef}
      scheduledBeatsRef={scheduledBeatsRef}
      
      // Actions
      handleReset={handleReset}
    />

<PWAInstallPrompt />
<PWAInstallInstructions 
  isOpen={showPWAInstructions} 
  onClose={() => setShowPWAInstructions(false)} 
/>
    </div>
  );
}